import {
  Alert,
  AlertIcon,
  Box, Button,
  ButtonGroup,
  Checkbox,
  Image as ChakraImage,
  Container, Flex, Heading,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody, Td,
  Text,
  Th, Thead, Tr,
  useDisclosure,
  useToast,
  VStack
} from '@chakra-ui/react';
import React, { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// グラフ
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LegendItem,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';

import { Bar, Chart, Line } from 'react-chartjs-2';

// 日付操作
import dayjs from 'dayjs';
// 浮動小数点型
import Decimal from 'decimal.js';
// 言語設定
import { supportedLngs } from '../../utils/i18n';

// PDF関連
import { generatePDF, addReviewBlockFlow, prewarmPdfFont } from './pdf/generatePdf';
import type { TotalYearlyData, ConfirmedPerson, ReviewFlowCursor } from './pdf/generatePdf';
import { convertHtmlToImageJpeg } from './pdf/pdfUtils';
import { aggregateChartDatasets, aggregateSummaryValues } from './pdf/reviewAggregation';
import { buildLifecycleFillPlugin, buildCumulativeFillPlugin } from './pdf/reviewChartFillPlugins';
import PdfReviewContent from './pdf/PdfReviewContent';
import type jsPDF from 'jspdf';
import {
  DIVISOR_VALUE,
  QUANTITY_PRICE_PARTNO_TABLE_PREFIX,
  QUANTITY_PRICE_MOLD_TABLE_PREFIX,
  QUANTITY_PRICE_DEV_TABLE_PREFIX,
  QUANTITY_PRICE_EQUIPMENT_TABLE_PREFIX,
  QUANTITY_PRICE_PROJECT_TOTAL_TABLE_PREFIX,
  PROFIT_AND_LOSS_PARTNO_TABLE_PREFIX,
  PROFIT_AND_LOSS_PARTNO_OUTSOURC_TABLE_PREFIX,
  PROFIT_AND_LOSS_PARTNO_INHOUSE_TABLE_PREFIX,
  PROFIT_AND_LOSS_MOLD_TABLE_PREFIX,
  PROFIT_AND_LOSS_DEV_TABLE_PREFIX,
  PROFIT_AND_LOSS_EQUIPMENT_TABLE_PREFIX,
  PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX,
  PROFIT_AND_LOSS_DCF_TABLE_PREFIX,
  PROFIT_AND_LOSS_PAYBACK_PERIOD_TABLE_PREFIX,
  PROFIT_AND_LOSS_COLLECT_SIMULATION_TABLE_PREFIX,
  UPDATE_IMPROVEMENT_EFFECT_TABLE_PREFIX,
  UPDATE_IMPROVEMENT_EFFECT_PROCESS_ITEM_PREFIX,
  UPDATE_IMPROVEMENT_INDICATOR_PROCESS_PREFIX,
  UPDATE_IMPROVEMENT_COST_PROCESS_PREFIX,
  UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID,
  RATIONALIZATION_BEFORE_PROFIT_LOSS_ID,
  RATIONALIZATION_AFTER_PROFIT_LOSS_ID,
  RATIONALIZATION_IMPROVEMENT_INDICATOR_PROCESS_PREFIX,
  RATIONALIZATION_IMPROVEMENT_COST_PROCESS_PREFIX,
  RATIONALIZATION_IMPROVEMENT_EFFECT_TOTAL_ID,
  UPDATE_SUMMARY_DATA_LIST_PREFIX,
  UPDATE_SUMMARY_ITEM_PREFIX,
  PRODUCTION_CAPACITY_PROCESS_ITEM_PREFIX,
  RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX,
  RATIONALIZATION_SUMMARY_CHARTS_PER_PAGE,
  CONFIRMER_LIST_BOX_PREFIX,
} from './pdf/pdfConstants';
import { useFileService } from "../../utils/FileService";

// ReactFlow
import { ReactFlowInstance } from 'reactflow';

// コンテキスト
import { getPermissions, ProjectContext } from "./InvestmentScreeningDetail";

import {
  competitiveEstimate,
  CurrencyNameJPY,
  equipmentComparisonItems,
  equipmentType,
  EstimateCheckListCategory,
  FlowKb,
  formatPricingAgreementForm,
  getApplicableNameByCode,
  getEquipmentCategoryNamesByCodes,
  getPurposeTypeNameByCode,
  inHouseReview,
  ourProductUsageStatus,
  pastPerformance,
  paymentType,
  procurementApproval,
  reverseAuction,
  DCFItems,
  PaybackPeriodItems,
  PricingAgreementCategory,
  ProfitAndLossItems,
  ProfitRateChartYAxisMax,
  RecoverySimulationItems,
  ProfitRateChartYAxisMin,
  PurposeType,
  SettingKey,
  ShiftSystem,
  supplier,
  WorkflowId,
  EquipmentUsageType,
  isSimpleApplicationForm,
  isProfitLossHidden,
  getPlannedBudgetTypeNameByCode,
  isScanBlocked,
  ComfirmDivisions,
} from "../../contexts/constants";
import { hasSalesRestriction, parseViewRestriction } from '../../utils/viewRestriction';

import { useNavigate } from "react-router-dom";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { useAppSelector } from '../../store/hooks';

import annotationPlugin, { AnnotationOptions, LabelPosition } from "chartjs-plugin-annotation";

// 型定義
import type {
  BomData,
  ApproverList,
  CheckListDataType,
  ConfirmorInfo,
  Currency,
  CustomerProduct,
  DevelopmentCostTable,
  EquipmentCostTable,
  EquipmentSupplierAndEqIdType,
  EquipmentType,
  EstimateCheckListFormType,
  FacilityItem,
  Material,
  MoldTable,
  NonWorkingHoursType,
  OriginalProfitAndLossTable,
  PagePermissions,
  PartNo,
  ProductionCapacityBenchmark,
  ProductionLineType,
  LargeChartData,
  ProjectAdditionalInfo,
  ProjectContextType,
  ProjectWithRdsData,
  QuantityAndPriceTable,
  ReviewScheduleItem,
  ScheduleItem,
  TScenario,
  UpdateSummaryInputData,
  UpdateInvestmentProfitLossData,
  WorkingDaysType,
  RationalizationInvestmentProfitLossData,
  LanguageCode,
} from '../../contexts/types';

import {
  convertCurrency,
  convertChartDatasets,
} from '../../utils/CurrencyConverter';

import { convertCurrencyByMonth } from '../../utils/reviewCalculation';

import {
  formatNumber,
  formatRecoveryMonths,
  hasDecimal,
  getLocalCurrencyId,
  getTaxRateFromSnapshot,
  parseCompanyPlantSnapshot,
  formatLocalDateTime,
  updateChartLabels,
  formatDateByLocale,
  splitEscapedList,
  resolveChartDatasetLabels,
  resolveDisplayPartNo,
  resolveDisplayPartNoByKey,
} from '../../utils/utils';
import { exportBomSnapshotFiles } from '../../utils/bomExport';
import { getConfirmor, updateConfirm } from '../../utils/workflow';
import { getMultilingualDisplayName, getBilingualDisplayName, getDivisionHierarchyDisplay } from '../../utils/i18nDisplay';
import { getValidFlowDivisionPairs } from '../Approve/AssignTabModal';

// CSS
import '../common.css';
import './ReportAndDraft.css';

/* スケジュールコンポーネント */
import ScheduleTable from './ScheduleTable';
import { groupByYear, calcMonthList } from '../../utils/scheduleUtils';
/* 設備投資プロセスフローコンポーネント */
import ProcessFlowComponent from './ProcessFlowComponent';
/* 承認者一覧コンポーネント */
import ApprovalListComponent from './ApprovalListComponent';
/* 承認確認コンポーネント */
import InvestmentScreeningDetailModal from './InvestmentScreeningDetailModal';

import ConfirmorList from "./ConfirmorList";
import ScanStatusBadge from "../../components/ScanStatusBadge";

// 計算関数
import {
  calcDevSalesByPartNoMonth,
  calcMoldSalesByPartNoMonth,
  calcPartNoSalesMergeByYear,
  calcProfitRate,
  calcThroughputRate,
  calcTotalDevSalesByYear,
  calcTotalMaterialCostsByPartNoWithMonthlyData,
  calcTotalMoldSalesByYear,
  calcTotalPartNoSalesByYear,
  calcTotalSalesByPartNoMonth,
  calcTotalSalesByPartNoYear,
  calculateInhouseRatioByPartNo,
  calculateProjectSalesSum,
  convertChartData,
  convertEquipmentData,
  generateYearMonthList,
  getMaterialCostByPartNoByMonth,
  getMaterialCostByPartNoYear,
  getPricesByPartNoMonth,
  getPricesByPartNoYear,
  getProjectSalesByMonth,
  getQuantitiesByPartNoMonth,
  getQuantitiesByPartNoYear,
  getTotalFiscalYearsByProject,
  getFiscalYearsByProjectDates,
  getTotalSalesByMonth,
  getTotalSalesMoldDevByMonth,
  getTotalSalesMoldDevByYear,
  mergeSalesAndSubtractCosts
} from "../../utils/calculation";
import { applyExistingLineCapacityChartStyle } from '../../utils/existingLineCapacityChart';
import { calcTotalProfitAndRate, calcYAxisRange, calcYearlyRoundedProfitTotal, generateAnnotationStarLabels, generateRecoveryPointAnnotationOptions } from '../../utils/chartDataCalculation';
import { formatYearMonthByLocale } from '../../utils/utils';
import { findMissingPastPartNos } from '../../utils/partNoConsistency';

// １０か条チェックリスト（新形式）の確認フラグキー（CheckList.tsx と同一の値）
const IDLE_EQUIPMENT_CHECK_KEY = 'idleEquipmentChecked';
const TEN_PRINCIPLES_CHECK_KEY = 'tenPrinciplesChecked';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

// 精度を50桁に設定
// 製品売上高(25桁)×PartNo運用MAX(10個) = 35桁＋α
Decimal.set({ precision: 50 });

/**
 * levelからインデント値（px）を計算
 * @param level 階層レベル（1-4）
 * @returns インデント値（px）
 */
const getLevelIndent = (level: number): number => {
  switch (level) {
    case 1: return 15;
    case 2: return 30;
    case 3: return 45;
    case 4: return 60;
    default: return 15;
  }
};

type CBomData = {
  "seq": number | string;
  "ItemNo": string;
  "ItemName": string;
  "Usage": number;
  "UnitPrice": number;
  "Amount": number;
  "InternalAdjustedAmount": number;

  // ****** GoogleSpreadSheetData ******
  outsourcingUnitPrice: number;
  inhouseUnitPrice: number;
  outsourcingAmount: number;
  inhouseAmount: number;
  inhouseRatio: number | string;
  yield: number | string;
};

// -----------------------------
// インタフェース
// -----------------------------
interface ReportAndDraftProp {
  projectId: string;
  monthlyRateMap?: Map<string, Currency[]>;
}

interface AnnotationConfig {
  zeroLine: {
    type: string;
    yMin: number;
    yMax: number;
    borderColor: string;
    borderDash: number[];
    borderWidth: number;
  };
  firstPositiveMonth?: {
    type: string;
    xMin: string;
    xMax: string;
    borderColor: string;
    borderWidth: number;
    label: {
      content: string;
      display: boolean;
      position: string;
    };
  };
}

// 設備一覧プロパティ
interface EquipmentAmount {
  id: number;
  amount: number;
}

interface SelectedCurrency {
  id: string;
  name: string;
}

interface EquipmentTableProps {
  equipmentAmounts: EquipmentType[];
  supplierData: EquipmentSupplierAndEqIdType[];
  selectedCurrency: SelectedCurrency;
  processItemNameMap?: { [processId: string]: string };
  processItemNoMap?: { [processId: string]: string };  // 工程ID → ItemNo(uniqueProcessName)
  processSeqMap?: { [processId: string]: number };
}

interface Confirmor {
  reportAndDraft?: {
    companyDivision?: string[];
    salesDivision?: string[];
    procurementDivision?: string[];
  };
}

// -----------------------------
// 定数
// -----------------------------
// 画面タイトル / 必須メッセージ: コンポーネント内のt()で定義

/** 年度表示名（i18n対応のため、コンポーネント内で t() を使用） */

// グラフ色
const colors = [
  { backgroundColor: 'rgba(54, 162, 235, 0.5)', borderColor: 'rgba(54, 162, 235, 1)' },// 青
  { backgroundColor: 'rgba(75, 192, 192, 0.5)', borderColor: 'rgba(75, 192, 192, 1)' },// 緑
  { backgroundColor: 'rgba(255, 206, 86, 0.5)', borderColor: 'rgba(255, 206, 86, 1)' },// 黄
  { backgroundColor: 'rgba(153, 102, 255, 0.5)', borderColor: 'rgba(153, 102, 255, 1)' },// 紫
  { backgroundColor: 'rgba(255, 159, 64, 0.5)', borderColor: 'rgba(255, 159, 64, 1)' },// オレンジ
  { backgroundColor: 'rgba(255, 99, 132, 0.5)', borderColor: 'rgba(255, 99, 132, 1)' },// 赤
  { backgroundColor: 'rgba(201, 203, 207, 0.5)', borderColor: 'rgba(201, 203, 207, 1)' },// 灰
  { backgroundColor: 'rgba(0, 204, 102, 0.5)', borderColor: 'rgba(0, 204, 102, 1)' },// エメラルドグリーン
  { backgroundColor: 'rgba(102, 51, 153, 0.5)', borderColor: 'rgba(102, 51, 153, 1)' },// 濃い紫
  { backgroundColor: 'rgba(255, 102, 0, 0.5)', borderColor: 'rgba(255, 102, 0, 1)' },// 濃いオレンジ
];

// １０か条チェックリスト 高さ
const CHECKLIST_ROW_HEIGHT = 60;

/**
 * 申請書画面
 * @returns
 */
const ReportAndDraft: React.FC<ReportAndDraftProp> = ({ projectId, monthlyRateMap }) => {
  /** トースト表示用 */
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const divisorStr = t('common.label.thousandSuffix');

  /** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];

  /** スナップショット共用: 現在の言語に応じてコンテンツ名を返す */
  const getChecklistContentName = (item: { contentNameJa?: string; contentNameEn?: string }): string =>
    (i18n.language === 'en' && item.contentNameEn) ? item.contentNameEn : (item.contentNameJa ?? "");

  /** スナップショット共用: 現在の言語に応じて懸念点リストを返す（旧キー後方互換あり） */
  const getChecklistConflictingConsiderations = (item: { conflictingConsiderationsJa?: string[]; conflictingConsiderationsEn?: string[]; conflictingConsiderations?: string[] }): string[] => {
    const jaList = item.conflictingConsiderationsJa ?? item.conflictingConsiderations ?? [];
    return (i18n.language === 'en' && item.conflictingConsiderationsEn?.length) ? item.conflictingConsiderationsEn : jaList;
  };
  /** 見積チェックリスト：MMI内製会社名 */
  const EC_IN_HOUSE = "MMI内製";
  /** 見積チェックリスト：優位性表示用ヘルパー */
  const displaySuperiority = (value: string | undefined, companies: { companyName: string | null }[]) => {
    if (!value || value === "-") return value ?? "";
    const match = companies.find(c => value === `${c.companyName}が優位`);
    if (match) {
      const displayName = match.companyName === EC_IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : (match.companyName ?? "");
      return t('components.EstimateCheckList.option.superiority', { companyName: displayName });
    }
    return value;
  };
  /** 年度表示名 */
  const YEARS_UNIT = t('features.InvestmentScreening.ReportAndDraft.constant.fiscalYearSuffix');
  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.ReportAndDraft.title');
  /** 必須データを促すメッセージ */
  const REQUIRED_MESSAGE = t('features.InvestmentScreening.ReportAndDraft.message.registerFrom');
  const WebAPI = useContext(WebAPIContext);
  const navigate = useNavigate();

  // -----------------------------
  // useRef
  // -----------------------------
  const pdfRef = useRef<HTMLDivElement>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const equipmentFlowRef = useRef(null);
  const reactFlowRef = useRef<ReactFlowInstance | null>(null);
  const benchmarkRefs = useRef<Array<HTMLDivElement | null>>([]);
  const estimateCheckListRefs = useRef<(HTMLDivElement | null)[]>([]);
  const checkListRefs = useRef<(HTMLDivElement | null)[]>([]);
  const simulationRef = useRef(null);
  const simulationRef2 = useRef(null);
  const simulationRef3 = useRef(null);
  // 振り返りモード用：振り返り3画面コンテンツのキャプチャ用Ref（PDF出力に使用）
  const reviewSalesRef = useRef<HTMLDivElement>(null);
  const reviewBusinessRef = useRef<HTMLDivElement>(null);
  const reviewFutureRef = useRef<HTMLDivElement>(null);
  /** PDF出力専用：振り返り3画面コンテンツを isPdfRendering 時のみマウントするref */
  const pdfReviewRef = useRef<HTMLDivElement>(null);
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  // -----------------------------
  // useDisclosure
  // -----------------------------
  /** モーダル表示制御用：部署確認モーダル */
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure()
  /** モーダル表示制御用：承認確認モーダル */
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  /** モーダル表示制御用：起案申請前確認モーダル（クロスチェック・資材担当役員確認） */
  const { isOpen: isOpenPreCheckModal, onOpen: onOpenPreCheckModal, onClose: onClosePreCheckModal } = useDisclosure()

  /** モーダル表示制御用：承認依頼モーダル */
  const { isOpen: isOpenApprovalReqModal, onOpen: onOpenApprovalReqModal, onClose: onCloseApprovalReqModal } = useDisclosure()

  // -----------------------------
  // useAppSelector
  // -----------------------------
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  const loginUserDivisionId = useAppSelector((state) => state.employee.divisionId);

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate } = useContext(ProjectContext) as ProjectContextType;

  // -----------------------------
  // useFileService
  // -----------------------------
  /** ファイル操作用（振り返りモード時は reviewDate を渡して振り返り分も含めた累積取得を行う） */
  const { uploadedFilesByFolder, fetchFiles, fetchAllBase64FromS3 } = useFileService(TITLE, projectId, reviewDate ?? undefined);

  // -----------------------------
  // useState
  // -----------------------------
  // 振り返りモード用：振り返り3画面コンテンツ（reviewDate 連動で取得）
  // - salesChartData: ReviewSales がローカル通貨で保存した Chart.js データ（compareMode 両モード分）
  // - businessChartData: ReviewBusiness が保存したグラフ・テーブルデータ（compareMode 両モード分）
  // - futureOutlookText: ReviewFuture が保存した8項目テキスト（既存 future_outlook カラム）
  // - salesReview / businessReview: ReviewSales / ReviewBusiness が保存したテキスト・コメント JSON
  const [reviewSalesChartData, setReviewSalesChartData] = useState<any>(null);
  const [reviewBusinessChartData, setReviewBusinessChartData] = useState<any>(null);
  const [reviewFutureOutlook, setReviewFutureOutlook] = useState<any>(null);
  const [reviewSalesText, setReviewSalesText] = useState<any>(null);
  const [reviewBusinessText, setReviewBusinessText] = useState<any>(null);
  // 振り返り3画面コンテンツの compareMode（営業視点・事業視点共通の切替）。
  // C-2-5'-1: 縦並べ全モード表示 → 選択モードのみ表示に変更。Sticky配置の ButtonGroup で切り替える。
  const [selectedReviewCompareMode, setSelectedReviewCompareMode] = useState<"previous" | "originalPlan">("previous");
  // C-2-5'-2: ReviewSales 用 PartNo セレクタ
  //   - 販売単価: PartNoのみ（"all" なし）、初期値は先頭PartNo（reviewSalesChartData ロード後に初期化）
  //   - 販売数量: PartNo + "all"（全PartNo合算）、初期値は "all"
  const [selectedReviewPriceByPartNo, setSelectedReviewPriceByPartNo] = useState<string>("");
  const [selectedReviewQtyByPartNo, setSelectedReviewQtyByPartNo] = useState<string>("all");
  // C-2-5'-3: ReviewBusiness 用 PartNo セレクタ
  //   - 生産数量: PartNo + "all"（全PartNo合算）、初期値 "all"
  //   - 歩留・CT: PartNo + "all"（"all"=統計表示・個別=PartNo別単一値表示）、初期値 "all"
  const [selectedReviewProdCapByPartNo, setSelectedReviewProdCapByPartNo] = useState<string>("all");
  const [selectedReviewYieldCtByPartNo, setSelectedReviewYieldCtByPartNo] = useState<string>("all");
  // PDF出力中フラグ。true の間、ScheduleTable等のキャプチャ対象コンポーネントを
  // Tooltip を外した軽量モードで再描画してキャプチャ時間を短縮する。
  const [isPdfRendering, setIsPdfRendering] = useState<boolean>(false);
  // 振り返り3画面PDF専用レンダリングフラグ。
  // isPdfRendering とは別に管理し、captureJobs 完了後にのみ true にすることで
  // PdfReviewContent（85ブロック・多数canvas）が captureJobs の DOM クローン対象に含まれないようにする。
  const [isPdfReviewRendering, setIsPdfReviewRendering] = useState<boolean>(false);
  // Projectデータ
  const [projectData, setProjectData] = useState<ProjectWithRdsData>();
  /** 簡易モード判定 */
  const isSimpleMode = isSimpleApplicationForm(projectData?.purpose, projectData?.applicationFormType);
  /** 損益非表示モード判定（簡易様式かつ「表示しない」選択時。損益試算・Simulationのセクションを非表示） */
  const isProfitLossHiddenMode = isProfitLossHidden(projectData?.purpose, projectData?.applicationFormType, projectData?.profitLossDisplayType);
  /** ローカル通貨ID（会社/工場スナップショットから取得） */
  const localCurrencyId = getLocalCurrencyId(projectData?.companyPlantSnapshot);
  /** 汎用設備かどうか */
  const isGeneralPurpose = projectData?.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code;
  /** 数量・単価のラベル（汎用設備：想定、それ以外：販売） */
  const labelQuantity = isGeneralPurpose ? t('features.InvestmentScreening.ReportAndDraft.label.estimatedQuantity') : t('features.InvestmentScreening.ReportAndDraft.label.salesQuantity');
  const labelPrice = isGeneralPurpose ? t('features.InvestmentScreening.ReportAndDraft.label.estimatedPrice') : t('features.InvestmentScreening.ReportAndDraft.label.salesPrice');
  const labelQuantityAndPrice = isGeneralPurpose ? t('features.InvestmentScreening.ReportAndDraft.label.estimatedQuantityAndPrice') : t('features.InvestmentScreening.ReportAndDraft.label.salesQuantityAndPrice');
  // 事業部ID
  const [divisionId, setDivisionId] = useState<string>();
  // PartNoデータ
  const [partNoData, setPartNoData] = useState<PartNo[]>([]);
  /** 過去プロジェクトのPart Noのうち、本プロジェクトに含まれないPart No（不整合なしは空配列） */
  const [missingPastPartNos, setMissingPastPartNos] = useState<string[]>([]);
  /** 不整合トーストを表示済みかどうか（再レンダリングでの重複表示を防ぐ） */
  const pastPartNoToastShownRef = useRef<boolean>(false);
  // 通貨データ
  const [currencyData, setCurrencyData] = useState<Currency[]>([]);

  // 振り返りモード用: 月別為替レート変換ラッパー。reviewDate + yearMonth があれば月別レート、なければ固定レート
  const convertWithRate = useCallback(
    (amount: number | Decimal, from: string, to: string, yearMonth?: string): Decimal => {
      if (reviewDate && yearMonth && monthlyRateMap && monthlyRateMap.size > 0) {
        return convertCurrencyByMonth(amount, from, to, yearMonth, monthlyRateMap, currencyData);
      }
      return convertCurrency(amount, from, to, currencyData);
    },
    [reviewDate, monthlyRateMap, currencyData]
  );

  // 為替レート種別（actual: 実勢レート, plan: 計画レート）
  const [rateType, setRateType] = useState<string>('');
  // 為替レート期間（実勢レート: YYYY-MM, 計画レート: YYYY）
  const [ratePeriod, setRatePeriod] = useState<string>('');
  // 選択通貨
  const [selectedCurrency, setSelectedCurrency] = useState<{ id: string; name: string }>({ id: "", name: "" });

  // 販売単価・販売数量テーブル
  const [quantityAndPriceTables, setQuantityAndPriceTables] = useState<QuantityAndPriceTable[]>([]);
  // 販売単価・販売数量テーブル(為替変換後)
  const [convQuantityAndPriceTables, setConvQuantityAndPriceTables] = useState<QuantityAndPriceTable[]>([]);
  // 金型テーブル
  const [moldTables, setMoldTables] = useState<MoldTable[]>([]);
  // 金型テーブル(為替変換後)
  // const [convMoldTables, setConvMoldTables] = useState<MoldTable[]>([]);
  // 開発費テーブル
  const [developmentCostTables, setDevelopmentCostTables] = useState<DevelopmentCostTable[]>([]);
  // 開発費テーブル(為替変換後)
  // const [convDevelopmentCostTables, setConvDevelopmentCostTables] = useState<DevelopmentCostTable[]>([]);
  // 設備費テーブル
  const [equipmentCostTables, setEquipmentCostTables] = useState<EquipmentCostTable[]>([]);

  // 年最大値
  const [maxYears, setMaxYears] = useState<number[]>([]);
  // 基準年度
  const [baseStartYear, setBaseStartYear] = useState<number>(0);
  // 通貨小数有無
  const [decimalCurrency, setDecimalCurrency] = useState<boolean | null>(null);

  // 横軸に表示する "YYYY-MM" の一覧 (顧客スケジュール + 設備投資スケジュール の最小～最大を1か月拡張)
  const [monthList, setMonthList] = useState<string[]>([]);
  // 顧客スケジュール (単一日付)
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([{ id: "", name: "", date: "" }]);
  // 設備投資スケジュール (開始日・終了日)
  const [facilityItems, setFacilityItems] = useState<FacilityItem[]>([{ id: "", name: "", startDate: "", endDate: "" }]);
  // 振り返りスケジュール
  const [reviewItems, setReviewItems] = useState<ReviewScheduleItem[]>([]);

  /** 材料情報 */
  const [materials, setMaterials] = useState<Material[]>([]);
  // 顧客取り決め事項 結合文字列
  const [pricingAgreementSentence, setPricingAgreementSentence] = useState<any>();
  // 顧客国籍名
  const [customerNationalityName, setCustomerNationalityName] = useState<string>("");

  // 製造ライン情報
  const [productionLineData, setProductionLineData] = useState<ProductionLineType>({});

  /** BOMデータ */
  const [bomData, setBomData] = useState<BomData[]>([]);

  // 稼働時間情報
  const [workingDays, setWorkingDays] = useState<WorkingDaysType | null>(null);

  // 非稼働時間情報
  const [nonWorkingHours, setNonWorkingHours] = useState<NonWorkingHoursType>({});

  /** 設備情報 */
  const [equipmentAmounts, setEquipmentAmounts] = useState<EquipmentType[]>([]);

  /** 設備情報(為替変換後) */
  const [convEquipmentAmounts, setConvEquipmentAmounts] = useState<EquipmentType[]>([]);

  /** 増分キャッシュフロー 設備投資額 */
  const [capexAmountByYear, setCapexAmountByYear] = useState<Decimal[]>([]);

  /** 増分キャッシュフロー 累積EBITDA+設備投資額 */
  const [recoverySimulationByYear, setRecoverySimulationByYear] = useState<Decimal[]>([]);

  /** サプライヤー情報 */
  const [supplierData, setSupplierData] = useState<EquipmentSupplierAndEqIdType[]>([]);

  /** 工程ID → itemName マッピング */
  const [processItemNameMap, setProcessItemNameMap] = useState<{ [processId: string]: string }>({});

  /** 工程ID → ItemNo(uniqueProcessName) マッピング */
  const [processItemNoMap, setProcessItemNoMap] = useState<{ [processId: string]: string }>({});

  /** 工程ID → seq マッピング（並び順用） */
  const [processSeqMap, setProcessSeqMap] = useState<{ [processId: string]: number }>({});

  /** 製品イメージファイル名 */
  const [imageFileName, setImageFileName] = useState<string>("");
  /** 製品イメージファイル */
  const [uploadedImageFileContent, setUploadedImageFileContent] = useState<string | null>(null);

  /** 実行税率 */
  const [taxRate, setTaxRate] = useState<Decimal>(new Decimal(0));
  /** 割引率 */
  const [discountRate, setDiscountRate] = useState<Decimal>(new Decimal(0));

  /** 対象PJの損益試算画面データ */
  const [originalProfitAndLoss, setOriginalProfitAndLoss] = useState<OriginalProfitAndLossTable>({
    profitAndLossByYear: {              // 年度別損益
      totalSales: [],                   // 売上合計
      productSales: [],                 // 製品売上
      moldSales: [],                    // PJ単位金型回収
      devSales: [],                     // PJ単位開発費回収
      equipmentSales: [],               // PJ単位設備費回収
      salesCost: [],                    // 売上原価
      totalMaterialCost: [],            // 材料費
      outsideCost: [],                  // 外注加工費
      laborCost: [],                    // 生産工場労務費
      domesticLaborCost: [],            // 国内工場労務費
      factoryExpenses: [],              // 生産工場経費
      overHeadCost: [],                 // 生産工場経費-償却費以外の工場経費
      depreciation: [],                 // 生産工場経費-減価償却費
      equipmentDepreciation: [],        // 生産工場経費-投資対象設備
      factoryCost: [],                  // 生産工場経費-工場建物
      pastEquipmentCost: [],            // 生産工場経費-過去PJの投資対象設備
      otherEquipmentCost: [],           // 生産工場経費-その他の設備
      domesticFactoryExpenses: [],      // 国内工場経費
      domesticOverHeadCost: [],         // 国内工場経費-償却費以外の工場経費
      domesticDepreciation: [],         // 国内工場経費-減価償却費
      domesticInvestmentDepreciation: [], // 国内工場経費-投資対象設備
      domesticFactoryBuildingDepreciation: [], // 国内工場経費-工場建物
      domesticOtherDepreciation: [],    // 国内工場経費-その他の設備
      scrapCost: [],                    // 仕損費
      sGAExpenses: [],                  // 販売費及び一般管理費
      appliedLaborCost: [],             // 生産工場販管費
      domesticSgaCost: [],              // 国内工場販管費
      salesManagementCost: [],          // 本社・販社販管費
      devCost: [],                      // 開発費
      royalty: [],                      // ロイヤリティ
      otherCost: [],                    // その他の経費
      profit: [],                       // 営業利益
      profitRate: [],                   // 営業利益率
      realOperatingProfitRate: [],      // 実質的営業利益率
    },
    roicByYear: {                       // ROIC
      nopatByYear: [],                  // 税引後営業利益
      profit: [],                       // 営業利益
      setTaxRate: [],                   // 目標税率（実効税率）
      undepreciatedByYear: [],          // 投下資本
      totalCapexAmount: [],             // 累計設備投資額
      equipmentDepreciation: [],        // 投資対象設備の減価償却累計額
      roicByYear: [],                   // ROIC（簡易計算）
      hurdleRate: [],                   // ハードルレート
    },
    dcf: {                              //  DCF法
      freeCashFlow: [],                 // フリーキャッシュフロー
      profit: [],                       // 営業利益
      setTaxRate: [],                   // 目標税率（実効税率）
      depreciation: [],                 // 減価償却費
      capexAmount: [],                  // 設備投資額
      discountRate: [],                 // 加重平均資本コスト
      presentValue: [],                 // 現在価値
      netPresentValue: "",              // 正味現在価値
    },
    paybackPeriodByYear: {
      interestRateByYear: [],           // 金利
      paybackPeriod: "",                // 回収期間
    },
    RecoverySimulationByYear: {         // 回収シミュレーション
      capexAmount: [],                  // 設備投資額
      recoveredResources: [],           // 回収原資
      profit: [],                       // 営業利益
      depreciation: [],                 // 減価償却費
      factoryAndOtherEquipmentCost: [], // 工場建物・その他設備の減価償却費
      uncollectedAmount: [],            // 未回収額
      collectionPeriod: "",             // 回収期間
    },
  });

  /** 対象PJの損益試算画面データ - JPY版（二重通貨変換回避用） */
  const [displayOriginalProfitAndLoss, setDisplayOriginalProfitAndLoss] = useState<OriginalProfitAndLossTable | null>(null);

  /** 対象PJの損益試算画面データ（表示） */
  const [dispProfitAndLoss, setDispProfitAndLoss] = useState<OriginalProfitAndLossTable>({
    profitAndLossByYear: {              // 年度別損益
      totalSales: [],                   // 売上合計
      productSales: [],                 // 製品売上
      moldSales: [],                    // PJ単位金型回収
      devSales: [],                     // PJ単位開発費回収
      equipmentSales: [],               // PJ単位設備費回収
      salesCost: [],                    // 売上原価
      totalMaterialCost: [],            // 材料費
      outsideCost: [],                  // 外注加工費
      laborCost: [],                    // 生産工場労務費
      domesticLaborCost: [],            // 国内工場労務費
      factoryExpenses: [],              // 生産工場経費
      overHeadCost: [],                 // 生産工場経費-償却費以外の工場経費
      depreciation: [],                 // 生産工場経費-減価償却費
      equipmentDepreciation: [],        // 生産工場経費-投資対象設備
      factoryCost: [],                  // 生産工場経費-工場建物
      pastEquipmentCost: [],            // 生産工場経費-過去PJの投資対象設備
      otherEquipmentCost: [],           // 生産工場経費-その他の設備
      domesticFactoryExpenses: [],      // 国内工場経費
      domesticOverHeadCost: [],         // 国内工場経費-償却費以外の工場経費
      domesticDepreciation: [],         // 国内工場経費-減価償却費
      domesticInvestmentDepreciation: [], // 国内工場経費-投資対象設備
      domesticFactoryBuildingDepreciation: [], // 国内工場経費-工場建物
      domesticOtherDepreciation: [],    // 国内工場経費-その他の設備
      scrapCost: [],                    // 仕損費
      sGAExpenses: [],                  // 販売費及び一般管理費
      appliedLaborCost: [],             // 生産工場販管費
      domesticSgaCost: [],              // 国内工場販管費
      salesManagementCost: [],          // 本社・販社販管費
      devCost: [],                      // 開発費
      royalty: [],                      // ロイヤリティ
      otherCost: [],                    // その他の経費
      profit: [],                       // 営業利益
      profitRate: [],                   // 営業利益率
      realOperatingProfitRate: [],      // 実質的営業利益率
    },
    roicByYear: {                       // ROIC
      nopatByYear: [],                  // 税引後営業利益
      profit: [],                       // 営業利益
      setTaxRate: [],                   // 目標税率（実効税率）
      undepreciatedByYear: [],          // 投下資本
      totalCapexAmount: [],             // 累計設備投資額
      equipmentDepreciation: [],        // 投資対象設備の減価償却累計額
      roicByYear: [],                   // ROIC（簡易計算）
      hurdleRate: [],                   // ハードルレート
    },
    dcf: {                              //  DCF法
      freeCashFlow: [],                 // フリーキャッシュフロー
      profit: [],                       // 営業利益
      setTaxRate: [],                   // 目標税率（実効税率）
      depreciation: [],                 // 減価償却費
      capexAmount: [],                  // 設備投資額
      discountRate: [],                 // 加重平均資本コスト
      presentValue: [],                 // 現在価値
      netPresentValue: "",              // 正味現在価値
    },
    paybackPeriodByYear: {               // 回収期間
      interestRateByYear: [],            // 金利
      paybackPeriod: "",                 // 回収期間
    },
    RecoverySimulationByYear: {         // 回収シミュレーション
      capexAmount: [],                  // 設備投資額
      recoveredResources: [],           // 回収原資
      profit: [],                       // 営業利益
      depreciation: [],                 // 減価償却費
      factoryAndOtherEquipmentCost: [], // 工場建物・その他設備の減価償却費
      uncollectedAmount: [],            // 未回収額
      collectionPeriod: "",             // 回収期間
    },
  });

  /** P&L項目 PartNo毎の表示データ */
  const [partNoDataMap, setPartNoDataMap] = useState<Map<string, any> | null>(null);

  /** P&L項目 年度別総計、回収シミュレーションの表示データ */
  const [totalYearlyData, setTotalYearlyData] = useState<TotalYearlyData>({
    totalSales: {} as { [key: number]: Decimal }, // 売上合計
    totalMaterialCost: {} as { [key: number]: Decimal },  // 材料費
    totalThroughput: {} as { [key: number]: Decimal },
    appliedHeadOfficeSalesManagementRatio: {} as { [key: number]: Decimal },
    ebitda: {} as { [key: number]: Decimal },
    capexRow: {} as { [key: number]: Decimal },
    cumulativeEbitdaAndCapex: {} as { [key: number]: Decimal },
    depreciation: {} as { [key: number]: Decimal },
    depreciationDcf: {} as { [key: number]: Decimal },
    ebit: {} as { [key: number]: Decimal },
    nopat: {} as { [key: number]: Decimal },
    undepreciated: {} as { [key: number]: Decimal },
    roic: {} as { [key: number]: Decimal },
    tax: {} as { [key: number]: Decimal },
    fcf: {} as { [key: number]: Decimal },
    pv: {} as { [key: number]: Decimal },
    dcf: {} as { [key: number]: Decimal },
  });

  /** 回収期間 */
  const [recoveryPeriod, setRecoveryPeriod] = useState<string>('');

  /** 増産投資量産開始年月（★マーク表示位置） */
  const [increasedProductionStartYearMonth, setIncreasedProductionStartYearMonth] = useState<string>("");
  /** 更新投資量産開始年月（★マーク表示位置） */
  const [updateAndOtherStartYearMonth, setUpdateAndOtherStartYearMonth] = useState<string>("");
  /** 合理化投資量産開始年月（★マーク表示位置） */
  const [rationalizationStartYearMonth, setRationalizationStartYearMonth] = useState<string>("");

  /** 投資開始年月（purposeに応じて切り替え） */
  const investmentStartYearMonth = useMemo(() => {
    if (projectData?.purpose === PurposeType.UpdateAndOther.code) {
      return updateAndOtherStartYearMonth;
    }
    if (projectData?.purpose === PurposeType.Rationalization.code) {
      return rationalizationStartYearMonth;
    }
    return increasedProductionStartYearMonth;
  }, [projectData?.purpose, increasedProductionStartYearMonth, updateAndOtherStartYearMonth, rationalizationStartYearMonth]);

  // 売上チャートデータ
  /** 売上（当初計画） */
  const [salesChartData, setSalesChartData] = useState<any>(null);
  /** 売上（ダウンサイド） */
  const [salesChartDataModified, setSalesChartDataModified] = useState<any>(null);
  /** 売上（プロジェクト全体投資回収） */
  const [salesChartDataAll, setSalesChartDataAll] = useState<any>(null);
  /** 売上（当初投資・回収） */
  const [salesChartDataPast, setSalesChartDataPast] = useState<any>(null);

  // 売上チャートデータ raw版（ローカル通貨、営業利益合計の合計→変換方式で使用）
  /** 売上（当初計画）raw版 */
  const [rawSalesChartData, setRawSalesChartData] = useState<any>(null);
  /** 売上（ダウンサイド）raw版 */
  const [rawSalesChartDataModified, setRawSalesChartDataModified] = useState<any>(null);
  /** 売上（プロジェクト全体投資回収）raw版 */
  const [rawSalesChartDataAll, setRawSalesChartDataAll] = useState<any>(null);
  /** 売上（当初投資・回収）raw版 */
  const [rawSalesChartDataPast, setRawSalesChartDataPast] = useState<any>(null);

  // 投資回収チャートデータ
  /** 増分キャッシュフロー（当初計画） */
  const [combinedChartData, setCombinedChartData] = useState<any>(null);
  /** 増分キャッシュフロー（ダウンサイド） */
  const [combinedChartDataModified, setCombinedChartDataModified] = useState<any>(null);
  /** 投資回収（プロジェクト全体投資回収） */
  const [combinedChartDataAll, setCombinedChartDataAll] = useState<any>(null);
  /** 投資回収（当初投資・回収） */
  const [combinedChartDataPast, setCombinedChartDataPast] = useState<any>(null);

  // 販売数量（Part別 月次数量）チャートデータ (オリジナル / 修正後) の2種類
  /** 販売数量（当初計画） */
  const [quantitiesChartDataOriginal, setQuantitiesChartDataOriginal] = useState<any>(null);
  /** 販売数量（ダウンサイド） */
  const [quantitiesChartDataModified, setQuantitiesChartDataModified] = useState<any>(null);

  // 販売単価と材料単価（Price & Material）チャートデータ (オリジナル / 修正後) の2種類
  /** 販売単価と材料単価（当初計画） */
  const [mergedChartDataOriginal, setMergedChartDataOriginal] = useState<any>(null);
  /** 販売単価と材料単価（ダウンサイド） */
  const [mergedChartDataModified, setMergedChartDataModified] = useState<any>(null);

  /** 顧客需要と設備キャパシティ（当初計画） */
  const [gapChartDataOriginal, setGapChartDataOriginal] = useState<any>(null);
  /** 顧客需要と設備キャパシティ（ダウンサイド） */
  const [gapChartDataModified, setGapChartDataModified] = useState<any>(null);

  /** 設備生産数量からのギャップ（当初計画） */
  const [productionQuantityChartDataOriginal, setProductionQuantityChartDataOriginal] = useState<any>(null);
  /** 設備生産数量からのギャップ（ダウンサイド） */
  const [productionQuantityChartDataModified, setProductionQuantityChartDataModified] = useState<any>(null);

  /** 生産キャパグラフ（当初計画）。Simulation画面でOFFのまま事業部確認した場合はnull */
  const [existingLineCapacityChartDataOriginal, setExistingLineCapacityChartDataOriginal] = useState<any>(null);
  /** 生産キャパグラフ（ダウンサイド） */
  const [existingLineCapacityChartDataModified, setExistingLineCapacityChartDataModified] = useState<any>(null);

  /** 生産能力（ライン全体）（当初計画） */
  const [productionCapacityChartData, setProductionCapacityChartData] = useState<any>(null);
  /** 生産能力（ライン全体）（ダウンサイド） */
  const [productionCapacityChartDataModified, setProductionCapacityChartDataModified] = useState<any>(null);

  // 保存済み設定グラフデータ（配列形式: { configId: string; name: string; data: any }[]）
  /** 売上（保存済み設定用） */
  const [salesChartDataForSavedConfig, setSalesChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 投資回収（保存済み設定用） */
  const [combinedChartDataForSavedConfig, setCombinedChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 数量（保存済み設定用） */
  const [quantitiesChartDataForSavedConfig, setQuantitiesChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 販売単価と材料単価（保存済み設定用） */
  const [mergedChartDataForSavedConfig, setMergedChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 顧客需要と設備キャパシティ（保存済み設定用） */
  const [gapChartDataForSavedConfig, setGapChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 設備生産数量からのギャップ（保存済み設定用） */
  const [productionQuantityChartDataForSavedConfig, setProductionQuantityChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 生産キャパグラフ（保存済み設定用） */
  const [existingLineCapacityChartDataForSavedConfig, setExistingLineCapacityChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 生産能力（ライン全体）（保存済み設定用） */
  const [productionCapacityChartDataForSavedConfig, setProductionCapacityChartDataForSavedConfig] = useState<{ configId: string; name: string; data: any }[]>([]);
  /** 生産能力（プロセス）更新前/後 */
  const [productionCapacityProcessChartDataList, setProductionCapacityProcessChartDataList] = useState<{
    processId: string;
    processName: string;
    beforeData: any;
    beforeDownsideData: any;
    afterData: any;
    afterDownsideData: any;
  }[]>([]);
  /** 合理化前後サマリーチャートデータ（processId単位） */
  const [rationalizationSummaryChartDataList, setRationalizationSummaryChartDataList] = useState<{
    processId: string;
    itemNo: string;
    labels: string[];
    items: {
      itemId: string;
      itemName: string;
      unit: string;
      beforeData: number[];
      afterData: number[];
      group: "cost" | "indicator" | "improvementEffect";
      depreciationData?: number[];
    }[];
  }[]>([]);
  /** 生産能力（プロセス）（保存済み設定用） */
  const [productionCapacityProcessChartDataForSavedConfig, setProductionCapacityProcessChartDataForSavedConfig] = useState<{
    configId: string;
    name: string;
    processData: {
      processId: string;
      processName: string;
      afterData: any;
    }[];
  }[]>([]);
  /** 回収時期（保存済み設定用） */
  const [monthOfRecoveryForSavedConfig, setMonthOfRecoveryForSavedConfig] = useState<{ configId: string; name: string; data: number[] }[]>([]);

  /** どの部署の確認かを表す */
  const [confirmSource, setConfirmSource] = useState<string>("");

  // チェックリスト非表示設備IDのリスト
  const [oldFormatEquipmentIds, setOldFormatEquipmentIds] = useState<string[]>([]);

  /** 承認者一覧情報 */
  const [approverList, setApproverList] = useState<ApproverList[]>([]);

  /** 事業部長情報 */
  const [executiveManager, setExecutiveManager] = useState<ApproverList[]>([]);

  /** 承認確認画面 Open */
  const handleDetailModalOpen = () => {
    setIsDetailModalOpen(true);
  };
  /** 承認確認画面 Close */
  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
  };

  // 確認担当者
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  // 確認ボタン横幅
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  // 確認ボタン下担当者名
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    companyHeadDivision: [],
    salesDivision: [],
    procurementDivision: [],
  });

  // 確認済み者一覧（確認者セクション表示用）
  const [confirmedPersonsList, setConfirmedPersonsList] = useState<ConfirmedPerson[]>([]);

  /** 権限用 フロー区分 */
  const [flowKb, setFlowKb] = useState<string>(FlowKb.Confirmation);
  /** 権限用 フローID */
  const [flowId, setFlowId] = useState<string>(WorkflowId.ReportAndDraft);
  /** 権限用 画面編集モードフラグ */
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  // 【画面項目】 PartNo別 販売数量配列
  const [quantitiesByPartNo, setQuantitiesByPartNo] = useState<{ partNo: string, quantities: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 販売単価
  const [pricesByPartNo, setPricesByPartNo] = useState<{ partNo: string, prices: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 製品売上高
  const [totalSalesByPartNo, setTotalSalesByPartNo] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】 PartNo金型合計(年度)
  const [moldSalesPartNoYear, setMoldSalesPartNoYear] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】 PartNo開発費合計(年度)
  const [devSalesPartNoYear, setDevSalesPartNoYear] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);

  // 【画面項目】 Project単位 金型金額
  const [moldSalesByProjectYear, setMoldSalesByProjectYear] = useState<Decimal[]>([]);
  // 【画面項目】 Project単位 開発費金額
  const [devSalesByProjectYear, setDevSalesByProjectYear] = useState<Decimal[]>([]);
  // 【画面項目】 Project単位 設備費金額
  const [equipmentSalesByProjectYear, setEquipmentSalesByProjectYear] = useState<Decimal[]>([]);

  // 【画面項目】 プロジェクト合計 製品売上高(年度別総計 売上合計でも使用)
  const [projectTotalSalesByYear, setProjectTotalSalesByYear] = useState<Decimal[]>([]);
  // 【画面項目】 プロジェクト合計 金型(PartNo単位)
  const [projectTotalMoldSalesByYear, setProjectTotalMoldSalesByYear] = useState<Decimal[]>([]);
  // 【画面項目】 プロジェクト合計 開発費(PartNo単位)
  const [projectTotalDevSalesByYear, setProjectTotalDevSalesByYear] = useState<Decimal[]>([]);
  // 【画面項目】 プロジェクト合計 総合計(製品売上高、PartNo金型、PartNo開発費)
  const [projectGrandTotalByYear, setProjectGrandTotalByYear] = useState<Decimal[]>([]);

  // 【画面項目】 PartNo別 材料単価（年度別加重平均）
  const [materialCostByPartNo, setMaterialCostByPartNo] = useState<{ partNo: string, materialCosts: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 材料費
  const [totalMaterialCostsByPartNo, setTotalMaterialCostsByPartNo] = useState<{ partNo: string, totalMaterialCosts: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 製品売上高、金型、開発費合計
  const [mergePartNoSalesByYear, setMergePartNoSalesByYear] = useState<{ partNo: string, totalSales: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 スループット額
  const [throughputsByPartNo, setThroughputsByPartNo] = useState<{ partNo: string, throughputs: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 スループット率
  const [throughputRatesByPartNo, setThroughputRatesByPartNo] = useState<{ partNo: string, throughputRate: Decimal[] }[]>([]);

  // 【画面項目】 PartNo別 材料単価(外注のみ)(年度別加重平均)
  const [outsourcingUnitMaterialCostByPartNo, setOutsourcingUnitMaterialCostByPartNo] = useState<{ partNo: string, materialCosts: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 材料費(外注のみ)
  const [outsourcingMaterialCostsByPartNo, setOutsourcingTotalMaterialCostsByPartNo] = useState<{ partNo: string, totalMaterialCosts: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 スループット額 材料費(外注のみ)
  const [outsourcingThroughputsByPartNo, setOutsourcingThroughputsByPartNo] = useState<{ partNo: string, throughputs: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 スループット率 材料費(外注のみ)
  const [outsourcingThroughputRatesByPartNo, setOutsourcingThroughputRatesByPartNo] = useState<{ partNo: string, throughputRate: Decimal[] }[]>([]);

  // 【画面項目】 PartNo別 材料単価(内製のみ)(年度別加重平均)
  const [inhouseUnitMaterialCostByPartNo, setInhouseUnitMaterialCostByPartNo] = useState<{ partNo: string, materialCosts: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 材料費(内製のみ)
  const [inhouseMaterialCostsByPartNo, setInhouseTotalMaterialCostsByPartNo] = useState<{ partNo: string, totalMaterialCosts: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 スループット額 材料費(内製のみ)
  const [inhouseThroughputsByPartNo, setInhouseThroughputsByPartNo] = useState<{ partNo: string, throughputs: Decimal[] }[]>([]);
  // 【画面項目】 PartNo別 スループット率 材料費(内製のみ)
  const [inhouseThroughputRatesByPartNo, setInhouseThroughputRatesByPartNo] = useState<{ partNo: string, throughputRate: Decimal[] }[]>([]);

  // 【画面項目】 見積チェックリスト
  const [estimateCheckListData, setEstimateCheckListData] = useState<{
    [equipmentId: string]: { estimateCheckList: any };
  }>({});

  // 【画面項目】 １０カ条チェックリスト
  const [checkListDataList, setCheckListDataList] = useState<{
    [equipmentId: string]: { checkList: any };
  }>({});

  /** 【過去ベストベンチマーク】データ */
  const [benchmarkData, setBenchmarkData] = useState<ProductionCapacityBenchmark[]>([]);

  /** 【更新前後サマリー】データ */
  const [updateSummaryData, setUpdateSummaryData] = useState<UpdateSummaryInputData[]>([]);

  /** 【更新投資損益試算】データ（ローカル通貨版） */
  const [updateInvestmentProfitLossData, setUpdateInvestmentProfitLossData] = useState<UpdateInvestmentProfitLossData | null>(null);
  /** 【更新投資損益試算】データ（JPY版） */
  const [displayUpdateInvestmentProfitLossData, setDisplayUpdateInvestmentProfitLossData] = useState<UpdateInvestmentProfitLossData | null>(null);

  /** 【合理化投資損益試算】データ（ローカル通貨版） */
  const [rationalizationInvestmentProfitLossData, setRationalizationInvestmentProfitLossData] = useState<RationalizationInvestmentProfitLossData | null>(null);
  /** 【合理化投資損益試算】データ（JPY版） */
  const [displayRationalizationInvestmentProfitLossData, setDisplayRationalizationInvestmentProfitLossData] = useState<RationalizationInvestmentProfitLossData | null>(null);

  /** EstimateCheckList Table スタイル */
  const ecTableStyle = {
    tableLayout: "fixed" as "fixed",
    borderCollapse: "separate" as "separate",
    borderSpacing: "0",
    width: "100%",
  }

  /** EstimateCheckList Th スタイル */
  const ecThStyle = {
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden" as "hidden",
    textOverflow: "ellipsis" as "ellipsis",
    wordBreak: "break-word" as "break-word",
    position: "relative" as "relative",
    textTransform: "none" as "none",
  }

  /** EstimateCheckList Td スタイル */
  const ecTdStyle = {
    whiteSpace: "normal" as "normal",
    overflow: "hidden" as "hidden",
    textOverflow: "ellipsis" as "ellipsis",
    wordBreak: "break-word" as "break-word",
  }

  /** EstimateCheckList Td スタイル テキストエリア用 */
  const ecTdStyleTextArea = {
    whiteSpace: "break-spaces" as "break-spaces",
    wordBreak: "break-word" as "break-word",
  }

  /** CheckList Td スタイル テキストエリア用 */
  const cTdStyleTextArea = {
    whiteSpace: "break-spaces" as "break-spaces",
    wordBreak: "break-word" as "break-word",
  }

  // -----------------------------
  // useEffect
  // -----------------------------

  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.ReportAndDraft, projectData?.purpose, employeeId, loginUserDivisionId, projectData?.registrantDivisionId, undefined, projectData?.approvalStatus ?? undefined);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, loginUserDivisionId, projectData]);

  /**
   * 別案件・別の振り返りを表示した際、過去Part No不整合の通知済みフラグをリセットする
   */
  useEffect(() => {
    pastPartNoToastShownRef.current = false;
    setMissingPastPartNos([]);
  }, [projectId, reviewDate]);

  /**
   * 過去プロジェクトのPart No不整合をトーストで通知する（画面表示時に1回のみ）
   * pagePermissionsが未設定の間は承認完了かどうかを判定できないため待つ（権限判定の競合防止）
   */
  useEffect(() => {
    if (pastPartNoToastShownRef.current) return;
    if (missingPastPartNos.length === 0) return;
    if ((userPermissions?.length || 0) <= 0) return;
    if (!pagePermissions) return;

    const partNos = missingPastPartNos.join(", ");
    const permissionFlowKb = userPermissions[0].flowKb;

    if (permissionFlowKb === FlowKb.Confirmation) {
      // 確認者・起案者：基本情報画面での紐づけ直しを促す
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ReportAndDraft.message.pastPartNoMismatchForConfirmer', { partNos }),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    } else if (permissionFlowKb === FlowKb.Approval) {
      if (pagePermissions?.currentFlowId !== WorkflowId.ApproveComplete) {
        // 承認者：差戻しを促す
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ReportAndDraft.message.pastPartNoMismatchForApprover', { partNos }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // 承認完了：通知せずログのみ
        console.warn(`For the approved request, the PartNo[${partNos}] of the Project linked to a past project does not match the PartNo of the current project.`);
      }
    }
    pastPartNoToastShownRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingPastPartNos, userPermissions, pagePermissions]);

  // 画面初期表示時のデータ取得
  // getDispData 完了後にPDFフォントのウォームアップを開始する。
  // データ取得・加工処理と競合しないよう、完了を待ってから実行する。
  useEffect(() => {
    console.log("ReportAndDraft start");
    getDispData().then(() => {
      prewarmPdfFont();
    });
  }, []);

  // 振り返りモード時：選択中の reviewDate の振り返り3画面コンテンツを取得
  // ReviewSales / ReviewBusiness が「確認」ボタン押下時に保存した salesChartData / businessChartData と
  // ReviewFuture が保存した futureOutlook（テキスト）を読み込む。未保存の場合は null のまま。
  useEffect(() => {
    if (!projectId || !reviewDate) {
      setReviewSalesChartData(null);
      setReviewBusinessChartData(null);
      setReviewFutureOutlook(null);
      setReviewSalesText(null);
      setReviewBusinessText(null);
      return;
    }
    (async () => {
      try {
        const resp = await (WebAPI as any).request({
          query: `
            query getReviewListByProjectId($projectId: String!) {
              getReviewListByProjectId(projectId: $projectId) {
                reviewDate
                salesReview
                businessReview
                salesChartData
                businessChartData
                futureOutlook
              }
            }
          `,
          variables: { projectId },
        });
        const list: Array<{ reviewDate: string; salesReview?: string | null; businessReview?: string | null; salesChartData?: string | null; businessChartData?: string | null; futureOutlook?: string | null }>
          = resp?.data?.getReviewListByProjectId ?? [];
        const target = list.find((r) => r.reviewDate === reviewDate);
        if (!target) {
          setReviewSalesChartData(null);
          setReviewBusinessChartData(null);
          setReviewFutureOutlook(null);
          setReviewSalesText(null);
          setReviewBusinessText(null);
          return;
        }

        // 振り返り日リスト
        const reviewDateList = list.map(r => r.reviewDate);
        const isPrevious = reviewDateList.findIndex(r => r === reviewDate) > 0;

        // 事前翻訳(共通)
        const previousValueLabel = t("common.label.previousValue");
        const originalPlanLabel = t("common.label.originalPlan");
        const previousOrOriginalPlanLabel = isPrevious ? previousValueLabel : originalPlanLabel;
        const currentValueLabel = t("common.label.currentValue");

        // 営業視点
        const parsedSalesChart = target.salesChartData ? JSON.parse(target.salesChartData) : null;

        const translationKeysReviewSalesChart: { [mode: string]: string[] } = {
          "previousMode": [previousOrOriginalPlanLabel, currentValueLabel],
          "originalPlanMode": [originalPlanLabel, currentValueLabel],
        };

        // 営業視点チャートデータラベル多言語対応
        const updateSalesChartLabels = (data: any, mode: string) => {
          updateChartLabels(data, (label, labelIdx, _) => {
            const resultLabel = translationKeysReviewSalesChart[mode][labelIdx];
            return resultLabel;
          }, t, 0);
        }

        for (const mode of Object.keys(translationKeysReviewSalesChart)) {
          // 営業視点チャートデータラベル多言語対応（売上計画の対比）
          updateSalesChartLabels(parsedSalesChart[mode].sections, mode);

          // 営業視点チャートデータラベル多言語対応(パーツNo別（販売単価）)
          updateSalesChartLabels(parsedSalesChart[mode].pricePartNoSections, mode);

          // 営業視点チャートデータラベル多言語対応(パーツNo別（ 販売数量）)
          updateSalesChartLabels(parsedSalesChart[mode].qtyPartNoSections, mode);
        }

        setReviewSalesChartData(parsedSalesChart);

        // 事業視点
        const parsedBusinessChart = target.businessChartData ? JSON.parse(target.businessChartData) : null;

        // 事前翻訳
        const actualsLabel = t("common.label.actuals");
        const currentOutlookLabel = t("common.label.currentOutlook");

        const translationKeysReviewBusinessChart1: { [mode: string]: string[][] } = {
          previousMode: [
            [previousOrOriginalPlanLabel, actualsLabel, currentOutlookLabel],
            [previousOrOriginalPlanLabel, currentValueLabel],
          ],
          originalPlanMode: [
            [originalPlanLabel, actualsLabel, currentOutlookLabel],
            [originalPlanLabel, currentValueLabel],
          ]
        };
        const translationKeysReviewBusinessChart2: { [mode: string]: string[][] } = {
          previousMode: [
            [previousOrOriginalPlanLabel, actualsLabel, currentOutlookLabel],
            [
              t("features.InvestmentScreening.Review.common.cumulativeWithLabel", { label: previousOrOriginalPlanLabel }),
              t("common.label.actualsCumulative"),
              t("common.label.outlookCumulative"),
            ],
          ],
          originalPlanMode: [
            [originalPlanLabel, actualsLabel, currentOutlookLabel],
            [
              t("features.InvestmentScreening.Review.common.cumulativeWithLabel", { label: originalPlanLabel }),
              t("common.label.actualsCumulative"),
              t("common.label.outlookCumulative"),
            ],
          ]
        };

        for (const mode of Object.keys(translationKeysReviewSalesChart)) {
          // 事業視点チャートデータラベル多言語対応（営業利益 / 製造原価）
          updateChartLabels(parsedBusinessChart[mode].sections, (label, labelIdx, datasetIdx) => {
            const resultLabel = translationKeysReviewBusinessChart1[mode][datasetIdx][labelIdx];
            return resultLabel;
          }, t, 0);

          // 事業視点チャートデータラベル多言語対応(パーツNo別（生産数量 / 累積生産数量）)
          updateChartLabels(parsedBusinessChart.nonCurrencyCharts[mode].productionCapacityByPartNo, (label, labelIdx, datasetIdx) => {
            const resultLabel = translationKeysReviewBusinessChart2[mode][datasetIdx % 2][labelIdx];
            return resultLabel;
          }, t, 0);

          // 事業視点チャートデータラベル多言語対応(パーツNo別（ 設備稼働率）)
          updateChartLabels(parsedBusinessChart.nonCurrencyCharts[mode].operatingRate, (label, labelIdx, _) => {
            const resultLabel = translationKeysReviewBusinessChart1[mode][1][labelIdx];
            return resultLabel;
          }, t, 0);
        }

        setReviewBusinessChartData(parsedBusinessChart);
        setReviewFutureOutlook(target.futureOutlook ? JSON.parse(target.futureOutlook) : null);
        // C-2-5'-2: 販売単価セレクタの初期値を先頭PartNoに設定（"all"オプションは販売単価には存在しない仕様）
        const firstPricePartNo = parsedSalesChart?.previousMode?.pricePartNoSections?.[0]?.partNo
          ?? parsedSalesChart?.originalPlanMode?.pricePartNoSections?.[0]?.partNo
          ?? "";
        setSelectedReviewPriceByPartNo(firstPricePartNo);
        setReviewSalesText(target.salesReview ? JSON.parse(target.salesReview) : null);
        setReviewBusinessText(target.businessReview ? JSON.parse(target.businessReview) : null);
      } catch (err) {
        console.error("振り返り3画面データ取得失敗:", err);
        setReviewSalesChartData(null);
        setReviewBusinessChartData(null);
        setReviewFutureOutlook(null);
        setReviewSalesText(null);
        setReviewBusinessText(null);
      }
    })();
  }, [projectId, reviewDate]);

  // 振り返り売上チャート 表示通貨変換済みデータ（メモ化）
  // - 通貨マスタ未取得 / 表示通貨未確定の段階では null を返し、convertCurrency への不正引数呼び出しを防ぐ
  // - 同パターン: SimulationProductivityImprovement.tsx の displaySalesChartData ほか
  // - sections（売上計画の対比）と pricePartNoSections（PartNo別販売単価）はローカル通貨ベース → 表示通貨へ変換
  // - qtyPartNoSections（PartNo別販売数量）は通貨非依存なのでそのまま素通し
  const displayReviewSalesChartData = useMemo(() => {
    if (!reviewSalesChartData) return null;
    if (!selectedCurrency.name || currencyData.length === 0) return null;
    const sourceCurrency = reviewSalesChartData.localCurrencyName || selectedCurrency.name;
    const convertSectionList = (list: any[] | undefined) => (list ?? []).map((sec: any) => ({
      ...sec,
      chartData: sec.chartData
        ? convertChartDatasets(sec.chartData, sourceCurrency, selectedCurrency.name, currencyData)
        : null,
    }));
    const convertMode = (modeKey: "previousMode" | "originalPlanMode") => {
      const modeData = reviewSalesChartData[modeKey];
      if (!modeData) return modeData ?? null;
      return {
        ...modeData,
        sections: convertSectionList(modeData.sections),
        pricePartNoSections: convertSectionList(modeData.pricePartNoSections),
        // qty は通貨非依存だが、構造を揃えるためそのまま渡す（既に申請書で受け取れる形）
        qtyPartNoSections: modeData.qtyPartNoSections ?? [],
      };
    };
    return {
      ...reviewSalesChartData,
      previousMode: convertMode("previousMode"),
      originalPlanMode: convertMode("originalPlanMode"),
    };
  }, [reviewSalesChartData, selectedCurrency.name, currencyData]);

  // 振り返り事業性チャート 表示通貨変換済みデータ（メモ化）
  // - 通貨依存セクション（営業利益・製造原価）の chartData のみ convertChartDatasets で変換
  // - nonCurrencyCharts / tables / textValues / comments はそのまま素通し（通貨非依存）
  const displayReviewBusinessChartData = useMemo(() => {
    if (!reviewBusinessChartData) return null;
    if (!selectedCurrency.name || currencyData.length === 0) return null;
    const sourceCurrency = reviewBusinessChartData.localCurrencyName || selectedCurrency.name;
    const convertMode = (modeKey: "previousMode" | "originalPlanMode") => {
      const modeData = reviewBusinessChartData[modeKey];
      if (!modeData?.sections?.length) return modeData ?? null;
      return {
        ...modeData,
        sections: modeData.sections.map((sec: any) => ({
          ...sec,
          chartData: sec.chartData
            ? convertChartDatasets(sec.chartData, sourceCurrency, selectedCurrency.name, currencyData)
            : null,
        })),
      };
    };
    return {
      ...reviewBusinessChartData,
      previousMode: convertMode("previousMode"),
      originalPlanMode: convertMode("originalPlanMode"),
    };
  }, [reviewBusinessChartData, selectedCurrency.name, currencyData]);

  /**
   * 権限情報
   */
  useEffect(() => {
    // フロー区分
    let initFlowKb: string = FlowKb.Confirmation;
    // フローID
    let currentFlowId: string = WorkflowId.ReportAndDraft;
    // 編集権限
    let editPermission = false;

    if (userPermissions && userPermissions.length !== 0) {
      if (userPermissions[0].flowKb) {
        initFlowKb = userPermissions[0].flowKb;
      }
      // 確認フローの場合
      if (initFlowKb === FlowKb.Confirmation) {
        const filteredPermissions = userPermissions.filter(permission => permission.flowId === WorkflowId.ReportAndDraft);
        // 自身の従業員IDが含まれるかチェック
        editPermission = filteredPermissions.some(permission => permission.employeeId === employeeId);

      } else if (initFlowKb === FlowKb.Approval) {
        // 承認フローの場合（並列承認中はログインユーザーのflow_idを優先）
        const myPermission = userPermissions.find(p => p.employeeId === employeeId);
        currentFlowId = myPermission?.flowId || userPermissions[0].flowId || currentFlowId;
        editPermission = userPermissions.some(permission => permission.employeeId === employeeId);
      }
    }
    setFlowKb(initFlowKb);
    setFlowId(currentFlowId);
    setIsEditMode(editPermission);
  }, [userPermissions, employeeId]);


  // Projectデータ設定
  useEffect(() => {

    // 金型回収テーブル設定
    if (projectData?.moldSalesByProject) {
      console.log("projectData.moldSalesByProject:", projectData.moldSalesByProject);
      let moldSalesByProject = projectData.moldSalesByProject;
      try {
        moldSalesByProject = JSON.parse(moldSalesByProject);
      } catch (error) {
        console.error("Failed to parse moldSalesByProject:", error);
        return; // パースに失敗した場合は処理を中断
      }
      // moldSalesByProjectが配列であるか確認
      if (Array.isArray(moldSalesByProject)) {
        // データ形式を検証
        const isValid = moldSalesByProject.every((item: MoldTable) =>
          item.id && item.moldName && Array.isArray(item.years) && typeof item.data === 'object'
        );
        if (isValid) {
          setMoldTables(moldSalesByProject);
        }
      } else {
        console.error("moldSalesByProject is not an array");
      }
    }

    // 開発費回収テーブル設定
    if (projectData?.developmentSalesByProject) {
      console.log("projectData.developmentSalesByProject:", projectData.developmentSalesByProject);
      let developmentSalesByProject = projectData.developmentSalesByProject;
      try {
        developmentSalesByProject = JSON.parse(developmentSalesByProject);
      } catch (error) {
        console.error("Failed to parse developmentSalesByProject:", error);
        return;
      }
      if (Array.isArray(developmentSalesByProject)) {
        // データ形式を検証
        const isValid = developmentSalesByProject.every((item: DevelopmentCostTable) =>
          item.id && item.developmentCostName && Array.isArray(item.years) && typeof item.data === 'object'
        );
        if (isValid) {
          setDevelopmentCostTables(developmentSalesByProject);
        }
      } else {
        console.error("developmentSalesByProject is not an array");
      }
    }

    // 設備費回収テーブル設定
    if (projectData?.equipmentSalesByProject) {
      console.log("projectData.equipmentSalesByProject:", projectData.equipmentSalesByProject);
      let equipmentSalesByProject = projectData.equipmentSalesByProject;
      try {
        equipmentSalesByProject = JSON.parse(equipmentSalesByProject);
      } catch (error) {
        console.error("Failed to parse equipmentSalesByProject:", error);
        return;
      }
      if (Array.isArray(equipmentSalesByProject)) {
        // データ形式を検証
        const isValid = equipmentSalesByProject.every((item: EquipmentCostTable) =>
          item.id && item.equipmentCostName && Array.isArray(item.years) && typeof item.data === 'object'
        );
        if (isValid) {
          setEquipmentCostTables(equipmentSalesByProject);
        }
      } else {
        console.error("equipmentSalesByProject is not an array");
      }
    }

    // 年度表示の設定（3月期基準）
    // - 1〜3月の場合は当年を会計年度（○○年3月期）として扱う
    // - 4〜12月の場合は翌年を会計年度（○○年3月期）として扱う
    // 例: 2024-04-01 → 2025年3月期、2025-01-01 → 2025年3月期
    if (projectData?.projectStartDate) {
      const startDate = new Date(projectData.projectStartDate);
      const year = startDate.getFullYear();
      const month = startDate.getMonth() + 1;
      const fiscalYear = month >= 4 ? year + 1 : year;
      setBaseStartYear(fiscalYear);
    }
  }, [projectData]);



  // quantityAndPriceTablesデータ設定
  useEffect(() => {
    if (!quantityAndPriceTables || quantityAndPriceTables.length === 0 || !selectedCurrency || !selectedCurrency.name || !currencyData || currencyData.length === 0) {
      return; // データが不足している場合は処理を行わない
    }

    // 販売通貨
    const salesCurrencyData = currencyData.find(currency => currency.currencyId === projectData?.currencyId);
    if (!salesCurrencyData || !salesCurrencyData.currencyName) { return; }
    const salesCurrency = salesCurrencyData.currencyName;
    // 表示通貨
    const dispCurrency = selectedCurrency.name;

    const updatedConvQuantityAndPriceTables = quantityAndPriceTables.map(item => {
      const updatedData = { ...item.data };

      Object.keys(updatedData).forEach(key => {
        const index = parseInt(key, 10);
        if (!isNaN(index)) {
          const price = new Decimal(updatedData[index]?.price || 0);
          const updatedPrice = convertCurrency(price, salesCurrency, dispCurrency, currencyData).toDecimalPlaces(4);
          updatedData[index] = { ...updatedData[index], price: updatedPrice.toNumber() };
        }
      });

      // 金型費用 (`partNoMoldTables`) の変換
      const updatedMoldTables = item.partNoMoldTables.map(moldTable => {
        const updatedData = { ...moldTable.data };

        Object.keys(updatedData).forEach(key => {
          const index = parseInt(key, 10);
          if (!isNaN(index)) {
            const price = new Decimal(updatedData[index]?.price || 0);
            const updatedPrice = convertCurrency(price, salesCurrency, dispCurrency, currencyData).toDecimalPlaces(4);
            updatedData[index] = { ...updatedData[index], price: updatedPrice.toNumber() };
          }
        });

        return { ...moldTable, data: updatedData };
      });

      // 開発費用 (`partNoDevelopmentCostTables`) の変換
      const updatedDevelopmentCostTables = item.partNoDevelopmentCostTables.map(developmentTable => {
        const updatedData = { ...developmentTable.data };

        Object.keys(updatedData).forEach(key => {
          const index = parseInt(key, 10);
          if (!isNaN(index)) {
            const price = new Decimal(updatedData[index]?.price || 0);
            const updatedPrice = convertCurrency(price, salesCurrency, dispCurrency, currencyData).toDecimalPlaces(4);
            updatedData[index] = { ...updatedData[index], price: updatedPrice.toNumber() };
          }
        });

        return { ...developmentTable, data: updatedData };
      });

      return {
        ...item,
        data: updatedData,
        partNoMoldTables: updatedMoldTables,
        partNoDevelopmentCostTables: updatedDevelopmentCostTables
      };
    });

    setConvQuantityAndPriceTables(updatedConvQuantityAndPriceTables);
  }, [quantityAndPriceTables, selectedCurrency, currencyData]);

  useEffect(() => {
    if (!projectData || !originalProfitAndLoss ||
      (Array.isArray(originalProfitAndLoss) && originalProfitAndLoss.length === 0) ||
      !selectedCurrency?.id || !selectedCurrency?.name || maxYears.length === 0 ||
      !currencyData || currencyData.length === 0) {
      return;
    }

    // 選択通貨がJPYの場合はJPY版データを使用（二重通貨変換回避）
    const useDisplayVersion = displayOriginalProfitAndLoss && selectedCurrency.name === CurrencyNameJPY;

    // ディープコピー（JPY版があればそちらを使用）
    const profitAndLoss: OriginalProfitAndLossTable = JSON.parse(JSON.stringify(
      useDisplayVersion ? displayOriginalProfitAndLoss : originalProfitAndLoss
    ));

    // 通貨変換
    const localCurrency = currencyData.find(c => c.currencyId === localCurrencyId);

    if (!localCurrency) {
      return;
    }

    // 為替変換と千単位変換を行う処理
    const convertValue = (value: string) => {
      if (!value) {
        return "0";
      }

      let decimalValue = new Decimal(value);

      // JPY版を使用する場合は通貨変換をスキップ
      if (!useDisplayVersion && selectedCurrency.id !== localCurrencyId) {
        // 為替変換
        decimalValue = convertCurrency(decimalValue, localCurrency?.currencyName!, selectedCurrency.name, currencyData);
      }

      // 千単位変換
      return divideValue(decimalValue).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed();
    }

    // 年ループ
    maxYears.forEach((_, index) => {

      /***************************************************************************************************************************** */
      // 年度別損益
      /***************************************************************************************************************************** */

      // 売上合計
      profitAndLoss.profitAndLossByYear.totalSales[index] = convertValue(profitAndLoss.profitAndLossByYear.totalSales[index]);

      //  製品売上
      profitAndLoss.profitAndLossByYear.productSales[index] = convertValue(profitAndLoss.profitAndLossByYear.productSales[index]);

      //  PJ単位金型・設備費回収
      profitAndLoss.profitAndLossByYear.moldSales[index] = convertValue(profitAndLoss.profitAndLossByYear.moldSales[index]);

      //  PJ単位開発費回収
      profitAndLoss.profitAndLossByYear.devSales[index] = convertValue(profitAndLoss.profitAndLossByYear.devSales[index]);

      //  PJ単位設備費回収
      profitAndLoss.profitAndLossByYear.equipmentSales[index] = convertValue(profitAndLoss.profitAndLossByYear.equipmentSales[index]);

      // 売上原価
      profitAndLoss.profitAndLossByYear.salesCost[index] = convertValue(profitAndLoss.profitAndLossByYear.salesCost[index]);

      //  材料費
      profitAndLoss.profitAndLossByYear.totalMaterialCost[index] = convertValue(profitAndLoss.profitAndLossByYear.totalMaterialCost[index]);

      //  外注加工費
      profitAndLoss.profitAndLossByYear.outsideCost[index] = convertValue(profitAndLoss.profitAndLossByYear.outsideCost[index]);

      //  労務費
      profitAndLoss.profitAndLossByYear.laborCost[index] = convertValue(profitAndLoss.profitAndLossByYear.laborCost[index]);

      //  国内工場労務費
      if (profitAndLoss.profitAndLossByYear.domesticLaborCost?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticLaborCost[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticLaborCost[index]);
      }

      //  工場経費
      profitAndLoss.profitAndLossByYear.factoryExpenses[index] = convertValue(profitAndLoss.profitAndLossByYear.factoryExpenses[index]);

      //    償却費以外の工場経費
      profitAndLoss.profitAndLossByYear.overHeadCost[index] = convertValue(profitAndLoss.profitAndLossByYear.overHeadCost[index]);

      //    減価償却費
      profitAndLoss.profitAndLossByYear.depreciation[index] = convertValue(profitAndLoss.profitAndLossByYear.depreciation[index]);

      //      投資対象設備
      profitAndLoss.profitAndLossByYear.equipmentDepreciation[index] = convertValue(profitAndLoss.profitAndLossByYear.equipmentDepreciation[index]);

      //      工場建物
      profitAndLoss.profitAndLossByYear.factoryCost[index] = convertValue(profitAndLoss.profitAndLossByYear.factoryCost[index]);

      //      過去PJの投資対象設備
      if (profitAndLoss.profitAndLossByYear.pastEquipmentCost?.[index]) {
        profitAndLoss.profitAndLossByYear.pastEquipmentCost[index] = convertValue(profitAndLoss.profitAndLossByYear.pastEquipmentCost[index]);
      }

      //      その他の設備
      profitAndLoss.profitAndLossByYear.otherEquipmentCost[index] = convertValue(profitAndLoss.profitAndLossByYear.otherEquipmentCost[index]);

      //  国内工場経費
      if (profitAndLoss.profitAndLossByYear.domesticFactoryExpenses?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticFactoryExpenses[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticFactoryExpenses[index]);
      }

      //    国内工場経費-償却費以外の工場経費
      if (profitAndLoss.profitAndLossByYear.domesticOverHeadCost?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticOverHeadCost[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticOverHeadCost[index]);
      }

      //    国内工場経費-減価償却費
      if (profitAndLoss.profitAndLossByYear.domesticDepreciation?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticDepreciation[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticDepreciation[index]);
      }

      //      国内工場経費-投資対象設備
      if (profitAndLoss.profitAndLossByYear.domesticInvestmentDepreciation?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticInvestmentDepreciation[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticInvestmentDepreciation[index]);
      }

      //      国内工場経費-工場建物
      if (profitAndLoss.profitAndLossByYear.domesticFactoryBuildingDepreciation?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticFactoryBuildingDepreciation[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticFactoryBuildingDepreciation[index]);
      }

      //      国内工場経費-その他の設備
      if (profitAndLoss.profitAndLossByYear.domesticOtherDepreciation?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticOtherDepreciation[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticOtherDepreciation[index]);
      }

      //  仕損費
      if (profitAndLoss.profitAndLossByYear.scrapCost?.[index]) {
        profitAndLoss.profitAndLossByYear.scrapCost[index] = convertValue(profitAndLoss.profitAndLossByYear.scrapCost[index]);
      }

      // 販売費及び一般管理費
      profitAndLoss.profitAndLossByYear.sGAExpenses[index] = convertValue(profitAndLoss.profitAndLossByYear.sGAExpenses[index]);

      //  工場販管費
      profitAndLoss.profitAndLossByYear.appliedLaborCost[index] = convertValue(profitAndLoss.profitAndLossByYear.appliedLaborCost[index]);

      //  国内工場販管費
      if (profitAndLoss.profitAndLossByYear.domesticSgaCost?.[index]) {
        profitAndLoss.profitAndLossByYear.domesticSgaCost[index] = convertValue(profitAndLoss.profitAndLossByYear.domesticSgaCost[index]);
      }

      //  本社・販社販管費
      profitAndLoss.profitAndLossByYear.salesManagementCost[index] = convertValue(profitAndLoss.profitAndLossByYear.salesManagementCost[index]);

      // 開発費
      profitAndLoss.profitAndLossByYear.devCost[index] = convertValue(profitAndLoss.profitAndLossByYear.devCost[index]);

      // ロイヤリティ
      if (profitAndLoss.profitAndLossByYear.royalty?.[index]) {
        profitAndLoss.profitAndLossByYear.royalty[index] = convertValue(profitAndLoss.profitAndLossByYear.royalty[index]);
      }

      // その他の経費
      profitAndLoss.profitAndLossByYear.otherCost[index] = convertValue(profitAndLoss.profitAndLossByYear.otherCost[index]);

      // 営業利益
      profitAndLoss.profitAndLossByYear.profit[index] = convertValue(profitAndLoss.profitAndLossByYear.profit[index]);

      // 営業利益率	
      // profitAndLoss.profitAndLossByYear.profitRate[index] = convertValue(profitAndLoss.profitAndLossByYear.profitRate[index]);

      /***************************************************************************************************************************** */
      // ROIC
      /***************************************************************************************************************************** */

      // 税引後営業利益
      profitAndLoss.roicByYear.nopatByYear[index] = convertValue(profitAndLoss.roicByYear.nopatByYear[index]);

      //  営業利益
      profitAndLoss.roicByYear.profit[index] = convertValue(profitAndLoss.roicByYear.profit[index]);

      //  目標税率（実効税率）
      // profitAndLoss.roicByYear.setTaxRate[index] = convertValue(profitAndLoss.roicByYear.setTaxRate[index]);

      // 投下資本
      profitAndLoss.roicByYear.undepreciatedByYear[index] = convertValue(profitAndLoss.roicByYear.undepreciatedByYear[index]);

      //  累計設備投資額
      profitAndLoss.roicByYear.totalCapexAmount[index] = convertValue(profitAndLoss.roicByYear.totalCapexAmount[index]);

      //  投資対象設備の減価償却累計額
      profitAndLoss.roicByYear.equipmentDepreciation[index] = convertValue(profitAndLoss.roicByYear.equipmentDepreciation[index]);

      // ROIC（簡易計算）
      // profitAndLoss.roicByYear.roicByYear[index] = convertValue(profitAndLoss.roicByYear.roicByYear[index]);

      // ハードルレート
      // profitAndLoss.roicByYear.hurdleRate[index] = convertValue(profitAndLoss.roicByYear.hurdleRate[index]);

      /***************************************************************************************************************************** */
      // DCF
      /***************************************************************************************************************************** */

      // フリーキャッシュフロー
      profitAndLoss.dcf.freeCashFlow[index] = convertValue(profitAndLoss.dcf.freeCashFlow[index]);

      //  営業利益
      profitAndLoss.dcf.profit[index] = convertValue(profitAndLoss.dcf.profit[index]);

      //  目標税率（実効税率）
      // profitAndLoss.dcf.setTaxRate[index] = convertValue(profitAndLoss.dcf.setTaxRate[index]);

      //  減価償却費
      profitAndLoss.dcf.depreciation[index] = convertValue(profitAndLoss.dcf.depreciation[index]);

      //  設備投資額
      profitAndLoss.dcf.capexAmount[index] = convertValue(profitAndLoss.dcf.capexAmount[index]);

      // 加重平均資本コスト
      // profitAndLoss.dcf.discountRate[index] = convertValue(profitAndLoss.dcf.discountRate[index]);

      // 現在価値
      profitAndLoss.dcf.presentValue[index] = convertValue(profitAndLoss.dcf.presentValue[index]);

      /***************************************************************************************************************************** */
      // 増分キャッシュフロー
      /***************************************************************************************************************************** */

      // 設備投資額
      profitAndLoss.RecoverySimulationByYear.capexAmount[index] = convertValue(profitAndLoss.RecoverySimulationByYear.capexAmount[index]);

      // 回収原資
      profitAndLoss.RecoverySimulationByYear.recoveredResources[index] = convertValue(profitAndLoss.RecoverySimulationByYear.recoveredResources[index]);

      //  営業利益
      profitAndLoss.RecoverySimulationByYear.profit[index] = convertValue(profitAndLoss.RecoverySimulationByYear.profit[index]);

      //  減価償却費
      profitAndLoss.RecoverySimulationByYear.depreciation[index] = convertValue(profitAndLoss.RecoverySimulationByYear.depreciation[index]);

      //  工場建物・その他設備の減価償却費
      profitAndLoss.RecoverySimulationByYear.factoryAndOtherEquipmentCost[index] = convertValue(profitAndLoss.RecoverySimulationByYear.factoryAndOtherEquipmentCost[index]);

      // 未回収額
      profitAndLoss.RecoverySimulationByYear.uncollectedAmount[index] = convertValue(profitAndLoss.RecoverySimulationByYear.uncollectedAmount[index]);

      // 回収期間：金利
      if (profitAndLoss.paybackPeriodByYear?.interestRateByYear?.[index]) {
        profitAndLoss.paybackPeriodByYear.interestRateByYear[index] = convertValue(profitAndLoss.paybackPeriodByYear.interestRateByYear[index]);
      }
    });

    // DCF法：0年目を含むため、最後の要素（index=maxYears.length）も変換
    const dcfLastIndex = maxYears.length;
    if (profitAndLoss.dcf.freeCashFlow[dcfLastIndex] !== undefined) {
      profitAndLoss.dcf.freeCashFlow[dcfLastIndex] = convertValue(profitAndLoss.dcf.freeCashFlow[dcfLastIndex]);
      profitAndLoss.dcf.profit[dcfLastIndex] = convertValue(profitAndLoss.dcf.profit[dcfLastIndex]);
      profitAndLoss.dcf.depreciation[dcfLastIndex] = convertValue(profitAndLoss.dcf.depreciation[dcfLastIndex]);
      profitAndLoss.dcf.capexAmount[dcfLastIndex] = convertValue(profitAndLoss.dcf.capexAmount[dcfLastIndex]);
      profitAndLoss.dcf.presentValue[dcfLastIndex] = convertValue(profitAndLoss.dcf.presentValue[dcfLastIndex]);
    }

    // DCF：正味現在価値
    profitAndLoss.dcf.netPresentValue = convertValue(profitAndLoss.dcf.netPresentValue);

    // 増分キャッシュフロー：回収期間
    // profitAndLoss.RecoverySimulationByYear.collectionPeriod = convertValue(profitAndLoss.RecoverySimulationByYear.collectionPeriod);

    // 表示用データセット
    setDispProfitAndLoss(profitAndLoss);
  }, [projectData, selectedCurrency, maxYears, currencyData, displayOriginalProfitAndLoss]);


  // // MoldTables通貨変換
  // const convertMoldTablesCurrency = (
  //   moldTables: MoldTable[],
  //   salesCurrency: string,
  //   dispCurrency: string,
  //   currencyData: Currency[]
  // ): MoldTable[] => {
  //   if (!moldTables || !salesCurrency || !dispCurrency || !currencyData) {
  //     return [];
  //   }

  //   // moldTables の価格を為替変換
  //   return moldTables.map((mold) => {
  //     const updatedData = { ...mold.data };

  //     mold.years.forEach((year) => {
  //       const price = new Decimal(updatedData[year]?.price || 0);
  //       const convertedPrice = convertCurrency(price, salesCurrency, dispCurrency, currencyData);
  //       updatedData[year] = { ...updatedData[year], price: convertedPrice.toNumber() };
  //     });

  //     return {
  //       ...mold,
  //       data: updatedData, // 更新したデータを上書き
  //     };
  //   });
  // };

  // // developmentCostTables通貨変換
  // const convertDevelopmentCostTablesCurrency = (
  //   developmentCostTables: DevelopmentCostTable[],
  //   salesCurrency: string,
  //   dispCurrency: string,
  //   currencyData: Currency[]
  // ): DevelopmentCostTable[] => {
  //   if (!developmentCostTables || !salesCurrency || !dispCurrency || !currencyData) {
  //     return [];
  //   }

  //   // developmentCostTables の価格を為替変換
  //   return developmentCostTables.map((development) => {
  //     const updatedData = { ...development.data };

  //     development.years.forEach((year) => {
  //       const price = new Decimal(updatedData[year]?.price || 0);
  //       const convertedPrice = convertCurrency(price, salesCurrency, dispCurrency, currencyData);
  //       updatedData[year] = { ...updatedData[year], price: convertedPrice.toNumber() };
  //     });

  //     return {
  //       ...development,
  //       data: updatedData, // 更新したデータを上書き
  //     };
  //   });
  // };

  // 通貨設定
  useEffect(() => {
    if (localCurrencyId && currencyData.length > 0) {
      const selected = currencyData.find(currency => currency.currencyName === CurrencyNameJPY);
      if (selected && selected.currencyId && selected.currencyName) {
        setSelectedCurrency({ id: selected.currencyId, name: selected.currencyName });
      }
    }
  }, [projectData, currencyData]);

  // PartNoデータ設定
  useEffect(() => {
    if (partNoData.length !== 0) {
      const parsedTables: QuantityAndPriceTable[] = partNoData.map(item => {

        if (!item.quantityAndPrice) { return null; }
        const quantityAndPriceJson = JSON.parse(item.quantityAndPrice);
        // jsonParse安全チェック
        const safeParse = (v: any) => {
          try {
            return typeof v === "string" ? JSON.parse(v) : null;
          } catch {
            console.log("JSON.paeseで失敗しました。");
            return null;
          }
        };
        // jsonParse
        const currencyArray = safeParse(quantityAndPriceJson?.currencyAndRatio);
        // ratioが最大の通貨を設定
        const maxCurrencyAndRatio =
          Array.isArray(currencyArray) && currencyArray.length
            ? currencyArray.reduce((max, c) => (c.ratio > max.ratio ? c : max))
            : null;

        let parsedMoldSales = [];
        let parsedDevelopmentSales = [];
        if (item.moldSalesByPartNo) {
          try {
            parsedMoldSales = JSON.parse(item.moldSalesByPartNo);
            // 配列でない場合は配列に変換
            if (!Array.isArray(parsedMoldSales)) { parsedMoldSales = [parsedMoldSales]; }
          } catch (error) {
            console.error("Failed to parse JSON:", error);
          }
        }

        if (item.developmentSalesByPartNo) {
          try {
            parsedDevelopmentSales = JSON.parse(item.developmentSalesByPartNo);
            // 配列でない場合は配列に変換
            if (!Array.isArray(parsedDevelopmentSales)) { parsedDevelopmentSales = [parsedDevelopmentSales]; }
          } catch (error) {
            console.error("Failed to parse JSON:", error);
          }
        }

        return {
          id: quantityAndPriceJson.id,
          partNo: quantityAndPriceJson.partNo,
          years: quantityAndPriceJson.years,
          data: quantityAndPriceJson.data,
          currencyAndRatio: maxCurrencyAndRatio,
          moldRadioValue: quantityAndPriceJson.moldRadioValue,
          developmentRadioValue: quantityAndPriceJson.developmentRadioValue,
          partNoMoldTables: parsedMoldSales,
          partNoDevelopmentCostTables: parsedDevelopmentSales
        };
      }).filter(item => item !== null);

      setQuantityAndPriceTables(parsedTables);
    }
  }, [partNoData]);

  // 販売数量・販売単価 PartNo、金型、開発費、設備費の年最大値を取得
  useEffect(() => {
    // 簡易モード時はプロジェクト日付から年数を算出
    if (isSimpleMode && projectData) {
      const years = getFiscalYearsByProjectDates(projectData.projectStartDate, projectData.projectEndDate);
      setMaxYears(Array.from({ length: years }, (_, i) => i + 1));
      return;
    }
    // 各テーブルのyearsの最大値を取得
    const maxYearsQuantityAndPrice = quantityAndPriceTables.reduce((max, table) => Math.max(max, table.years.length), 0);
    const maxYearsMold = moldTables.reduce((max, table) => Math.max(max, table.years.length), 0);
    const maxYearsDevelopmentCost = developmentCostTables.reduce((max, table) => Math.max(max, table.years.length), 0);
    const maxYearsEquipmentCost = equipmentCostTables.reduce((max, table) => Math.max(max, table.years.length), 0);

    // 最大のyearsの長さを取得
    const maxYearsLength = Math.max(maxYearsQuantityAndPrice, maxYearsMold, maxYearsDevelopmentCost, maxYearsEquipmentCost);

    // 最大のyearsの長さに基づいてyears列を設定
    const newYears = Array.from({ length: maxYearsLength }, (_, i) => i + 1);
    setMaxYears(newYears);
  }, [isSimpleMode, projectData, quantityAndPriceTables, moldTables, developmentCostTables, equipmentCostTables]);

  // 顧客スケジュール & 設備投資スケジュールの最小日付・最大日付を取得し、
  // そこから1か月前・1か月後を算出して monthList を更新
  useEffect(() => {
    setMonthList(calcMonthList(scheduleItems, facilityItems, reviewItems));
  }, [scheduleItems, facilityItems, reviewItems]);

  /* 製品ファイル取得API */
  useEffect(() => {
    // 画像ファイルの取得
    if (imageFileName) {
      const fetchData = async () => {
        try {
          const result = await WebAPI.request({
            query: `
          query getCustomerProductFileByCustomerProductId($customerId: String, $customerProductId: String, $imageFileName: String) {
            getCustomerProductFileByCustomerProductId(customerId: $customerId, customerProductId: $customerProductId, imageFileName: $imageFileName) {
              customerProductId
              customerId
              imageFileName
              content
            }
          }
        `,
            variables: {
              customerId: projectData?.customerId,
              customerProductId: projectData?.customerProductId,
              imageFileName
            }
          });
          if (result.data?.getCustomerProductFileByCustomerProductId) {
            setUploadedImageFileContent(result.data.getCustomerProductFileByCustomerProductId.content ?? null);
          }
        } catch (e) {
          toast({
            title: t('common.label.failed'),
            description: t('features.InvestmentScreening.ReportAndDraft.message.fetchProductImageFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
      fetchData();
    }
  }, [imageFileName])

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.reportAndDraft) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.reportAndDraft?.[division as keyof Confirmor['reportAndDraft']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  // 全画面の確認者データを統合して確認済み者一覧を作成
  useEffect(() => {
    if (!confirmor) {
      setConfirmedPersonsList([]);
      return;
    }

    // 確認区分の定義
    const divisions = [
      { id: "company", key: "companyDivision", name: t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.company') },
      { id: "companyHead", key: "companyHeadDivision", name: t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.companyHead') },
      { id: "sales", key: "salesDivision", name: t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.sales') },
      { id: "procurement", key: "procurementDivision", name: t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.procurement') },
    ];

    // 全画面のキー
    const screenKeys = [
      "quantityAndPrice",
      "pricingAgreementForm",
      "scheduling",
      "productionProcess",
      "equipment",
      "profitAndLoss",
      "projectProfitablityStatement",
      "simulationNewProduct",
      "simulationProductivityImprovement",
      "reportAndDraft",
    ];

    const confirmedList: ConfirmedPerson[] = [];

    // 各確認区分について、全画面から確認済みの者を収集
    divisions.forEach(({ id, key, name }) => {
      // employeeName → latestConfirmTime のマップ
      const confirmedMap = new Map<string, string | null>();

      screenKeys.forEach((screenKey) => {
        const screenData = (confirmor as any)?.[screenKey];
        if (screenData) {
          const divisionData = screenData[key] as ConfirmorInfo[] | undefined;
          if (Array.isArray(divisionData)) {
            divisionData.forEach((person) => {
              if (person.isConfirmor && person.employeeName?.trim()) {
                const currentLatest = confirmedMap.get(person.employeeName);
                // 最新の確認日時を更新
                if (person.confirmTime) {
                  if (!currentLatest || dayjs(person.confirmTime).isAfter(dayjs(currentLatest))) {
                    confirmedMap.set(person.employeeName, person.confirmTime);
                  }
                } else if (!confirmedMap.has(person.employeeName)) {
                  confirmedMap.set(person.employeeName, null);
                }
              }
            });
          }
        }
      });

      // 確認済みの者をリストに追加
      confirmedMap.forEach((latestConfirmTime, employeeName) => {
        confirmedList.push({
          divisionId: id,
          confirmKbn: name,
          employeeName,
          latestConfirmTime,
        });
      });
    });

    setConfirmedPersonsList(confirmedList);
  }, [confirmor]);

  // -----------------------------
  // API
  // -----------------------------
  // 画面情報取得
  const getDispData = async () => {
    try {
      if (projectId) {
        await getProjectData();
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ReportAndDraft.message.registerFromBasicInfo'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    } catch (error) {
      console.error('getDispData:', error);
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // プロジェクト情報取得API
  const getProjectData = async () => {
    try {
      console.log(`getProjectData():start`);
      // TODO:getProjectByIdを必要なものだけ取得するように修正
      // プロジェクト情報にRDSのデータを含める(useRDSData:true)
      const payload = {
        query: `
          query fetchReportData($projectId: String!, $useRDSData: Boolean, $hasDeleted: Boolean, $targets: [String!]!, $reviewDate: String) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              projectId
              projectName
              applicationNumber
              equipmentPlanNumber
              plannedBudget
              budgetAmount
              divisionHierarchy
              divisionHierarchyEn
              customerId
              customerProductId
              productionLineId
              revisedOpportunityNameByRevision
              purpose
              purposeOfInvestment
              carModel
              applicationMonth
              projectStartDate
              projectEndDate
              eolDate
              equipmentCategory
              increasedProductionStartDate
              updateAndOtherStartDate
              rationalizationStartDate
              moldSalesByProject
              developmentSalesByProject
              equipmentSalesByProject
              approvalStatus
              customerNationality
              pricingAgreement
              applicationCategory
              specificationAndPerformance
              customerSchedule
              equipmentInstallationSchedule
              reviewSchedule
              originalProfitAndLoss
              displayOriginalProfitAndLoss
              updateInvestmentProfitLossData
              displayUpdateInvestmentProfitLossData
              rationalizationProfitLossData
              displayRationalizationProfitLossData
              profitablityStatementChartData
              quantitiesChartDataOriginal
              quantitiesChartDataModified
              mergedChartDataOriginal
              mergedChartDataModified
              salesChartData
              salesChartDataModified
              salesChartDataAll
              salesChartDataPast
              combinedChartData
              combinedChartDataModified
              combinedChartDataAll
              combinedChartDataPast
              monthOfRecoveryOriginal
              monthOfRecoveryModified
              monthOfRecoveryAll
              monthOfRecoveryPast
              gapChartDataOriginal
              gapChartDataModified
              productionQuantityChartDataOriginal
              productionQuantityChartDataModified
              salesChartDataForSavedConfig
              combinedChartDataForSavedConfig
              quantitiesChartDataForSavedConfig
              mergedChartDataForSavedConfig
              gapChartDataForSavedConfig
              productionQuantityChartDataForSavedConfig
              productionCapacityChartData
              productionCapacityChartDataModified
              productionCapacityChartDataForSavedConfig
              productionCapacityProcessChartDataList
              productionCapacityProcessChartDataForSavedConfig
              monthOfRecoveryForSavedConfig
              downsideRisk
              baseLaborCostIncreaseRate
              salesManagementRate
              createDate
              updateDate
              currencyId
              companyPlantId
              companyPlantSnapshot
              baseInterestRate
              quantityPriceFreeText
              equipmentUsageType
              applicationFormType
              profitLossDisplayType
              pastProjectId
              pastReviewDate
              screenViewRestriction
              # RDS Data
              opportunityName
              customerProductName
              divisionId
              divisionName
              endUserName
              customerIndustryHierarchy
              registrantEmployeeName
              registrantDivisionId
              updateDateRDS
              finalInvestmentReason
              productionFactoryLaborUnitPrice
              rateType
              ratePeriod
              approverAssignees
              crossCheckRequired
              crossCheckDepartments
              crossCheckAssignees
              materialOfficerRequired
              materialOfficerAssignees
            }
            getMaterial(hasDeleted: true) {
              materialId
              materialName { ${langs.join(' ')} }
              category { ${langs.join(' ')} }
              sortOrder
              deleted
            }
            getCompetitor(hasDeleted: $hasDeleted) {
              competitorId
              competitorName
              deleted
            }
            getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
              quantityAndPrice
              moldSalesByPartNo
              developmentSalesByPartNo
              createDate
              updateDate
            }
            getCurrencyById(projectId: $projectId) {
              currencyId
              currencyName
              exchangeRate
              order
            }
            getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              equipmentId
              equipmentName
              quantity
              quantityUnit
              unitPrice
              amount
              currencyId
              numberOfPeopleRequiredForEquipment
              unskilledWorker
              multiSkilledWorker
              technician
              depreciationPeriod
              depreciationStartMonth
              acceptanceMonth
              applicationPlannedMonth
              processId
              displayOrder
            }
            getUniqueProcessByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              uniqueProcessId
              uniqueProcessName
              uniqueProcessSeq
              uniqueProcessCycleTimeInSecondByPartNo
            }
            getSupplierNameByProjectId(projectId: $projectId) {
              equipmentSupplierId
              equipmentSupplierName {
                ja
                en
              }
              equipmentId
            }
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              benchmarkData
              updateSummaryData
              updateDate
            }
            getLargeChartDataById(projectId: $projectId, targets: $targets, reviewDate: $reviewDate) {
              rationalizationSummaryChartData
              simulationChartData
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          hasDeleted: false,
          targets: ["rationalizationSummaryChartData", "simulationChartData"],
          ...(reviewDate && { reviewDate }),
        }
      };

      // プロジェクト情報取得（dynamodb、RDS）
      const response = await WebAPI.request(payload);

      // 大容量チャートデータ取得（RDS: r_ceas_project_additional_info）
      const largeChartData: LargeChartData | undefined = response?.data?.getLargeChartDataById;

      // プロジェクト情報
      if (response && response.data && response.data.getProjectById) {

        // アップロード済みファイル情報取得（S3）
        await fetchFiles(true);

        const projectInfo = response.data.getProjectById;

        // チャートデータのソースを決定（RDS優先、DynamoDBフォールバック）
        const rdsSimulationChartData = largeChartData?.simulationChartData ? JSON.parse(largeChartData.simulationChartData) : null;
        // RDSチャートデータがある場合はprojectInfoに上書きして使用（monthOfRecovery等も含む）
        const chartDataSource: any = rdsSimulationChartData ? { ...projectInfo, ...rdsSimulationChartData } : projectInfo;

        // 事業部ID
        const divisionId = projectInfo.divisionId ? projectInfo.divisionId : "";
        // スケジュール表示関連
        const scheduleItems = projectInfo.customerSchedule ? JSON.parse(projectInfo.customerSchedule) : [];
        const facilityItems = projectInfo.equipmentInstallationSchedule ? JSON.parse(projectInfo.equipmentInstallationSchedule) : [];
        const reviewItems = projectInfo.reviewSchedule ? JSON.parse(projectInfo.reviewSchedule) : [];

        // 顧客製品画像検索用ID
        const customerProductId = projectInfo.customerProductId ? projectInfo.customerProductId : "";

        // 対象PJの製品売上試算
        const originalProfitAndLoss = projectInfo.originalProfitAndLoss ? JSON.parse(projectInfo.originalProfitAndLoss) : [];
        // JPY版データ（二重通貨変換回避用）
        const displayProfitAndLoss = projectInfo.displayOriginalProfitAndLoss ? JSON.parse(projectInfo.displayOriginalProfitAndLoss) : null;
        const updateInvestmentProfitLoss = projectInfo.updateInvestmentProfitLossData ? JSON.parse(projectInfo.updateInvestmentProfitLossData) : null;
        const displayUpdateInvestmentProfitLoss = projectInfo.displayUpdateInvestmentProfitLossData ? JSON.parse(projectInfo.displayUpdateInvestmentProfitLossData) : null;
        const rationalizationInvestmentProfitLoss = projectInfo.rationalizationProfitLossData ? JSON.parse(projectInfo.rationalizationProfitLossData) : null;
        const displayRationalizationInvestmentProfitLoss = projectInfo.displayRationalizationProfitLossData ? JSON.parse(projectInfo.displayRationalizationProfitLossData) : null;
        const salesChartData = chartDataSource.salesChartData ? JSON.parse(chartDataSource.salesChartData) : null;

        // 増産投資量産開始年月ライン（★マーク位置）
        if (projectInfo?.increasedProductionStartDate) {
          const increasedProductionStartDate = new Date(projectInfo?.increasedProductionStartDate);
          const starLabel = `${increasedProductionStartDate.getFullYear()}/${String(increasedProductionStartDate.getMonth() + 1).padStart(2, "0")}`;
          setIncreasedProductionStartYearMonth(starLabel);
        }
        // 更新投資量産開始年月ライン（★マーク位置）
        if (projectInfo?.updateAndOtherStartDate) {
          const updateAndOtherStartDate = new Date(projectInfo.updateAndOtherStartDate);
          const updateLabel = `${updateAndOtherStartDate.getFullYear()}/${String(updateAndOtherStartDate.getMonth() + 1).padStart(2, "0")}`;
          setUpdateAndOtherStartYearMonth(updateLabel);
        }
        // 合理化投資量産開始年月ライン（★マーク位置）
        if (projectInfo?.rationalizationStartDate) {
          const rationalizationStartDate = new Date(projectInfo.rationalizationStartDate);
          const rationalizationLabel = `${rationalizationStartDate.getFullYear()}/${String(rationalizationStartDate.getMonth() + 1).padStart(2, "0")}`;
          setRationalizationStartYearMonth(rationalizationLabel);
        }

        // シミュレーショングラフ（RDS優先、DynamoDBフォールバック）
        const salesChartDataModified = chartDataSource.salesChartDataModified ? JSON.parse(chartDataSource.salesChartDataModified) : null;
        const salesChartDataAll = chartDataSource.salesChartDataAll ? JSON.parse(chartDataSource.salesChartDataAll) : null;
        const salesChartDataPast = chartDataSource.salesChartDataPast ? JSON.parse(chartDataSource.salesChartDataPast) : null;
        const combinedChartData = chartDataSource.combinedChartData ? JSON.parse(chartDataSource.combinedChartData) : null;
        const combinedChartDataModified = chartDataSource.combinedChartDataModified ? JSON.parse(chartDataSource.combinedChartDataModified) : null;
        const combinedChartDataAll = chartDataSource.combinedChartDataAll ? JSON.parse(chartDataSource.combinedChartDataAll) : null;
        const combinedChartDataPast = chartDataSource.combinedChartDataPast ? JSON.parse(chartDataSource.combinedChartDataPast) : null;
        // PartNo一覧（displayPartNo解決用、No.435対応）
        const partNoListForChart: PartNo[] = response.data.getPartNo || [];
        const quantitiesChartDataOriginal = chartDataSource.quantitiesChartDataOriginal ? JSON.parse(chartDataSource.quantitiesChartDataOriginal) : null;
        const quantitiesChartDataModified = chartDataSource.quantitiesChartDataModified ? JSON.parse(chartDataSource.quantitiesChartDataModified) : null;
        const mergedChartDataOriginal = chartDataSource.mergedChartDataOriginal ? JSON.parse(chartDataSource.mergedChartDataOriginal) : null;
        const mergedChartDataModified = chartDataSource.mergedChartDataModified ? JSON.parse(chartDataSource.mergedChartDataModified) : null;
        const gapChartDataOriginal = chartDataSource.gapChartDataOriginal ? JSON.parse(chartDataSource.gapChartDataOriginal) : null;
        const gapChartDataModified = chartDataSource.gapChartDataModified ? JSON.parse(chartDataSource.gapChartDataModified) : null;
        const productionQuantityChartDataOriginal = chartDataSource.productionQuantityChartDataOriginal ? JSON.parse(chartDataSource.productionQuantityChartDataOriginal) : null;
        const productionQuantityChartDataModified = chartDataSource.productionQuantityChartDataModified ? JSON.parse(chartDataSource.productionQuantityChartDataModified) : null;
        // 生産キャパグラフ（RDSのsimulationChartDataにのみ存在。getProjectById の型には無い）
        const existingLineCapacityChartDataOriginal = chartDataSource.existingLineCapacityChartDataOriginal ? JSON.parse(chartDataSource.existingLineCapacityChartDataOriginal) : null;
        const existingLineCapacityChartDataModified = chartDataSource.existingLineCapacityChartDataModified ? JSON.parse(chartDataSource.existingLineCapacityChartDataModified) : null;
        const productionCapacityChartData = chartDataSource.productionCapacityChartData ? JSON.parse(chartDataSource.productionCapacityChartData) : null;
        const productionCapacityChartDataModified = chartDataSource.productionCapacityChartDataModified ? JSON.parse(chartDataSource.productionCapacityChartDataModified) : null;
        const productionCapacityProcessChartDataList = chartDataSource.productionCapacityProcessChartDataList ? JSON.parse(chartDataSource.productionCapacityProcessChartDataList) : [];

        // 保存済み設定グラフデータ（配列形式）
        const savedConfigSalesChartData = chartDataSource.salesChartDataForSavedConfig ? JSON.parse(chartDataSource.salesChartDataForSavedConfig) : [];
        const savedConfigCombinedChartData = chartDataSource.combinedChartDataForSavedConfig ? JSON.parse(chartDataSource.combinedChartDataForSavedConfig) : [];
        const savedConfigQuantitiesChartData = chartDataSource.quantitiesChartDataForSavedConfig ? JSON.parse(chartDataSource.quantitiesChartDataForSavedConfig) : [];
        const savedConfigMergedChartData = chartDataSource.mergedChartDataForSavedConfig ? JSON.parse(chartDataSource.mergedChartDataForSavedConfig) : [];
        const savedConfigGapChartData = chartDataSource.gapChartDataForSavedConfig ? JSON.parse(chartDataSource.gapChartDataForSavedConfig) : [];
        const savedConfigProductionQuantityChartData = chartDataSource.productionQuantityChartDataForSavedConfig ? JSON.parse(chartDataSource.productionQuantityChartDataForSavedConfig) : [];
        const savedConfigExistingLineCapacityChartData = chartDataSource.existingLineCapacityChartDataForSavedConfig ? JSON.parse(chartDataSource.existingLineCapacityChartDataForSavedConfig) : [];


        const savedConfigProductionCapacityChartData = chartDataSource.productionCapacityChartDataForSavedConfig ? JSON.parse(chartDataSource.productionCapacityChartDataForSavedConfig) : [];
        const savedConfigProductionCapacityProcessChartData = chartDataSource.productionCapacityProcessChartDataForSavedConfig ? JSON.parse(chartDataSource.productionCapacityProcessChartDataForSavedConfig) : [];
        const savedConfigMonthOfRecovery = chartDataSource.monthOfRecoveryForSavedConfig ? JSON.parse(chartDataSource.monthOfRecoveryForSavedConfig) : [];

        // シナリオデータを取得して、設定名を取得
        const scenarioData = await fetchScenarioData();
        const scenarioNameMap = new Map(scenarioData.map(scenario => [scenario.scenarioId, scenario.scenarioName]));

        // 保存済み設定グラフデータに設定名を追加
        const addScenarioName = (configArray: { configId: string; data: any }[]) => {
          return configArray.map(item => ({
            configId: item.configId,
            name: scenarioNameMap.get(item.configId) || item.configId,
            data: item.data
          }));
        };

        const savedConfigSalesWithName = addScenarioName(savedConfigSalesChartData);
        const savedConfigCombinedWithName = addScenarioName(savedConfigCombinedChartData);
        const savedConfigQuantitiesWithName = addScenarioName(savedConfigQuantitiesChartData);
        const savedConfigMergedWithName = addScenarioName(savedConfigMergedChartData);
        const savedConfigGapWithName = addScenarioName(savedConfigGapChartData);
        const savedConfigProductionQuantityWithName = addScenarioName(savedConfigProductionQuantityChartData);
        const savedConfigExistingLineCapacityWithName = addScenarioName(savedConfigExistingLineCapacityChartData);
        const savedConfigProductionCapacityWithName = addScenarioName(savedConfigProductionCapacityChartData);
        const savedConfigMonthOfRecoveryWithName = addScenarioName(savedConfigMonthOfRecovery);

        // プロジェクトテーブル情報（RDSチャートデータで上書き済み）
        setProjectData(chartDataSource);
        setDivisionId(divisionId);

        // スケジュール
        setScheduleItems(scheduleItems);
        setFacilityItems(facilityItems);
        setReviewItems(reviewItems);

        // 対象PJの製品売上試算
        setOriginalProfitAndLoss(originalProfitAndLoss);
        // JPY版データ（二重通貨変換回避用）
        setDisplayOriginalProfitAndLoss(displayProfitAndLoss);
        setUpdateInvestmentProfitLossData(updateInvestmentProfitLoss);
        setDisplayUpdateInvestmentProfitLossData(displayUpdateInvestmentProfitLoss);
        setRationalizationInvestmentProfitLossData(rationalizationInvestmentProfitLoss);
        setDisplayRationalizationInvestmentProfitLossData(displayRationalizationInvestmentProfitLoss);
        setSalesChartData(salesChartData);
        setRawSalesChartData(salesChartData);

        // シミュレーショングラフ多言語対応（顧客需要と設備キャパシティ）
        updateSimulationChartLabels(  // 当初計画
          gapChartDataOriginal, [
          "features.InvestmentScreening.SimulationNewProduct.chart.salesQuantityOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.capacity",
          "features.InvestmentScreening.SimulationNewProduct.chart.surplusCapacityOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.full24hCapacity",
        ]);
        updateSimulationChartLabels(  // ダウンサイド
          gapChartDataModified, [
          "features.InvestmentScreening.SimulationNewProduct.chart.salesQuantityModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.capacity",
          "features.InvestmentScreening.SimulationNewProduct.chart.surplusCapacityModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.full24hCapacity",
        ]);
        updateSimulationChartLabels(  // 設定
          savedConfigGapWithName, [
          "features.InvestmentScreening.SimulationNewProduct.chart.salesQuantityModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.capacity",
          "features.InvestmentScreening.SimulationNewProduct.chart.surplusCapacityModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.full24hCapacity",
        ]);

        // シミュレーショングラフ多言語対応（設備生産数量からのギャップ）
        updateSimulationChartLabels(  // 当初計画
          productionQuantityChartDataOriginal, [
          "features.InvestmentScreening.SimulationNewProduct.chart.requiredShipmentOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.workingDayProductionOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.holidayProductionOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.yieldImprovedProductionOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.availabilityImprovedProductionOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.cycleTimeReducedProductionOriginal",
          "features.InvestmentScreening.SimulationNewProduct.chart.full24hCapacity",
        ]);
        updateSimulationChartLabels(  // ダウンサイド
          productionQuantityChartDataModified, [
          "features.InvestmentScreening.SimulationNewProduct.chart.requiredShipmentModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.workingDayProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.holidayProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.yieldImprovedProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.availabilityImprovedProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.cycleTimeReducedProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.full24hCapacity",
        ]);
        updateSimulationChartLabels(  // 設定
          savedConfigProductionQuantityWithName, [
          "features.InvestmentScreening.SimulationNewProduct.chart.requiredShipmentModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.workingDayProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.holidayProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.yieldImprovedProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.availabilityImprovedProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.cycleTimeReducedProductionModified",
          "features.InvestmentScreening.SimulationNewProduct.chart.full24hCapacity",
        ]);

        // シミュレーショングラフ多言語対応（生産キャパグラフ）
        // 棒はPartNo（翻訳不要）で本数が案件ごとに異なるため、折れ線（生産能力）のラベルのみ置き換える
        // あわせて配色・描画順をSimulation画面の現行仕様に揃える（配色変更前に保存されたデータも同じ表示にするため）
        [
          existingLineCapacityChartDataOriginal,
          existingLineCapacityChartDataModified,
          ...savedConfigExistingLineCapacityWithName.map(item => item.data),
        ].forEach((chartData: any) => {
          chartData?.datasets?.forEach((dataset: any) => {
            if (dataset.type === 'line') {
              dataset.label = t('features.InvestmentScreening.SimulationNewProduct.chart.productionCapacity');
            }
          });
          applyExistingLineCapacityChartStyle(chartData);
        });

        // シミュレーショングラフ多言語対応（増産：生産能力（ライン全体））
        updateSimulationChartLabels(  // 当初計画
          productionCapacityChartData, [
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.preIncreaseProductionPast",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.customerDemand",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.holidayProduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.workingDayProduction",
        ]);
        updateSimulationChartLabels(  // ダウンサイド
          productionCapacityChartDataModified, [
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.preIncreaseProductionPast",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.customerDemand",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.holidayProduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.workingDayProduction",
        ]);
        updateSimulationChartLabels(  // 設定
          savedConfigProductionCapacityWithName, [
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.preIncreaseProductionPast",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.customerDemand",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.holidayProduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.workingDayProduction",
        ]);

        // シミュレーショングラフ多言語対応（更新：生産能力（プロセス））
        updateSimulationChartLabels(  // 更新前 / 更新後：当初計画 / ダウンサイド
          productionCapacityProcessChartDataList, [
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.customerDemand",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.nonWorkingCapacityReduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.holidayProduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.workingDayProduction",
        ]);
        updateSimulationChartLabels(  // 設定
          savedConfigProductionCapacityProcessChartData, [
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.customerDemand",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.nonWorkingCapacityReduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.holidayProduction",
          "features.InvestmentScreening.SimulationProductivityImprovement.chart.workingDayProduction",
        ]);

        // シミュレーショングラフ
        setSalesChartDataModified(salesChartDataModified);
        setRawSalesChartDataModified(salesChartDataModified);
        setSalesChartDataAll(salesChartDataAll);
        setRawSalesChartDataAll(salesChartDataAll);
        setSalesChartDataPast(salesChartDataPast);
        setRawSalesChartDataPast(salesChartDataPast);
        setCombinedChartData(combinedChartData);
        setCombinedChartDataModified(combinedChartDataModified);
        setCombinedChartDataAll(combinedChartDataAll);
        setCombinedChartDataPast(combinedChartDataPast);
        setQuantitiesChartDataOriginal(quantitiesChartDataOriginal);
        setQuantitiesChartDataModified(quantitiesChartDataModified);
        setMergedChartDataOriginal(mergedChartDataOriginal);
        setMergedChartDataModified(mergedChartDataModified);
        setGapChartDataOriginal(gapChartDataOriginal);
        setGapChartDataModified(gapChartDataModified);
        setProductionQuantityChartDataOriginal(productionQuantityChartDataOriginal);
        setProductionQuantityChartDataModified(productionQuantityChartDataModified);
        setExistingLineCapacityChartDataOriginal(existingLineCapacityChartDataOriginal);
        setExistingLineCapacityChartDataModified(existingLineCapacityChartDataModified);
        setProductionCapacityChartData(productionCapacityChartData);
        setProductionCapacityChartDataModified(productionCapacityChartDataModified);
        setProductionCapacityProcessChartDataList(productionCapacityProcessChartDataList);

        // 保存済み設定グラフデータをセット
        setSalesChartDataForSavedConfig(savedConfigSalesWithName);
        setCombinedChartDataForSavedConfig(savedConfigCombinedWithName);
        setQuantitiesChartDataForSavedConfig(savedConfigQuantitiesWithName);
        setMergedChartDataForSavedConfig(savedConfigMergedWithName);
        setGapChartDataForSavedConfig(savedConfigGapWithName);
        setProductionQuantityChartDataForSavedConfig(savedConfigProductionQuantityWithName);
        setExistingLineCapacityChartDataForSavedConfig(savedConfigExistingLineCapacityWithName);
        setProductionCapacityChartDataForSavedConfig(savedConfigProductionCapacityWithName);
        setProductionCapacityProcessChartDataForSavedConfig(
          savedConfigProductionCapacityProcessChartData.map((item: any) => ({
            ...item,
            name: scenarioNameMap.get(item.configId) || item.configId,
          }))
        );
        setMonthOfRecoveryForSavedConfig(savedConfigMonthOfRecoveryWithName);

        // 顧客製品IDが取得できた場合、画像ダウンロード
        if (customerProductId) {
          getCustomerProductData(customerProductId);
        }

        if (divisionId) {
          getApproverData(divisionId, projectInfo)
        }
      } else {
        showApiError(`プロジェクト情報`, `getProjectById`, response.errors);
      }

      const projectInfo = response.data.getProjectById;

      // 実効税率（会社/工場スナップショットから取得）
      const taxRate = new Decimal(getTaxRateFromSnapshot(projectInfo.companyPlantSnapshot) ?? 0);
      setTaxRate(taxRate);
      // 割引率
      const discountRate = new Decimal(await getSettingValueByKey(SettingKey.discountRate) || 0);
      setDiscountRate(discountRate);

      // 製造ライン情報取得
      const productionLineData = await getProductionLineById(projectInfo.productionLineId);
      setProductionLineData(productionLineData);

      if (productionLineData?.workingDays) {
        const workingDays = JSON.parse(productionLineData?.workingDays);
        // 非稼働時間情報設定
        setWorkingDays(workingDays);
      }

      if (productionLineData?.nonWorkingHours) {
        const nonWorkingHours = JSON.parse(productionLineData?.nonWorkingHours);
        // 非稼働時間情報設定
        setNonWorkingHours(nonWorkingHours);
      }

      // 材料情報
      if (response && response.data && response.data.getMaterial && response.data.getCompetitor) {
        // const projectInfo = response.data.getProjectById;
        const pricingAgreement = projectInfo.pricingAgreement ? JSON.parse(projectInfo.pricingAgreement) : [];
        if (pricingAgreement && Object.keys(pricingAgreement).length) {
          // 材料情報セット
          setMaterials(response.data.getMaterial);
          // 顧客取り決め事項の文章結合
          const pricingAgreementFormFormat = formatPricingAgreementForm(pricingAgreement, response.data.getMaterial, response.data.getCompetitor);
          setPricingAgreementSentence(pricingAgreementFormFormat);
        }
      } else {
        showApiError(`材料情報`, `getMaterial`, response.errors);
      }

      // 顧客国籍名を取得
      if (projectInfo.customerNationality) {
        try {
          const natPayload = {
            query: `
              query getNationalityById($nationalityId: String) {
                getNationalityById(nationalityId: $nationalityId) {
                  nationalityId
                  nationalityNameJpn
                  nationalityNameEng
                }
              }
            `,
            variables: { nationalityId: projectInfo.customerNationality },
          };
          const natResponse = await WebAPI.request(natPayload);
          const customerNationalityName = getBilingualDisplayName(
            natResponse?.data?.getNationalityById,
            'nationalityNameJpn',
            'nationalityNameEng',
          );
          setCustomerNationalityName(customerNationalityName ?? "");
        } catch (error) {
          console.error("getNationalityById error:", error);
        }
      }

      // 通貨情報
      if (response && response.data && response.data.getCurrencyById) {
        const currencyData = response.data.getCurrencyById;
        setCurrencyData(currencyData);

      } else {
        showApiError(`通貨情報`, `getCurrencyById`, response.errors);
      }

      // 為替レート種別・期間
      if (projectInfo.rateType) {
        setRateType(projectInfo.rateType);
      }
      if (projectInfo.ratePeriod) {
        setRatePeriod(projectInfo.ratePeriod);
      }

      // 設備情報取得
      let equipmentData = null;
      if (response && response.data && response.data.getEquipmentByProjectId) {
        equipmentData = response.data.getEquipmentByProjectId;
        setEquipmentAmounts(equipmentData);
      } else {
        showApiError(`設備情報`, `getEquipmentByProjectId`, response.errors);
      }

      // サプライヤー情報取得
      if (response && response.data && response.data.getSupplierNameByProjectId) {
        const supplierData = response.data.getSupplierNameByProjectId;
        setSupplierData(supplierData);
      } else {
        showApiError(`サプライヤー情報`, `getSupplierNameByProjectId`, response.errors);
      }

      // 工程情報取得（processId → itemName/ItemNo/seq マッピング作成）
      if (response && response.data && response.data.getUniqueProcessByProjectId) {
        const uniqueProcessData = response.data.getUniqueProcessByProjectId;
        const itemNameMap: { [processId: string]: string } = {};
        const itemNoMap: { [processId: string]: string } = {};  // ItemNo(uniqueProcessName)マッピング
        const seqMap: { [processId: string]: number } = {};
        uniqueProcessData.forEach((process: { uniqueProcessId: string; uniqueProcessName?: string; uniqueProcessSeq: number; uniqueProcessCycleTimeInSecondByPartNo: string }) => {
          if (process.uniqueProcessId) {
            // seq マッピング
            seqMap[process.uniqueProcessId] = process.uniqueProcessSeq ?? 9999;

            // ItemNo(uniqueProcessName) マッピング
            if (process.uniqueProcessName) {
              itemNoMap[process.uniqueProcessId] = process.uniqueProcessName;
            }
            console.log("process", process)

            // itemName マッピング
            if (process.uniqueProcessCycleTimeInSecondByPartNo) {
              try {
                const cycleTimeList = JSON.parse(process.uniqueProcessCycleTimeInSecondByPartNo);
                // 最初のitemNameを取得（複数ある場合は最初のものを使用）
                const firstItemName = cycleTimeList.find((item: { itemName?: string }) => item.itemName)?.itemName;
                if (firstItemName) {
                  itemNameMap[process.uniqueProcessId] = firstItemName;
                }
              } catch (e) {
                console.error('Failed to parse uniqueProcessCycleTimeInSecondByPartNo:', e);
              }
            }
          }
        });
        setProcessItemNameMap(itemNameMap);
        setProcessItemNoMap(itemNoMap);
        setProcessSeqMap(seqMap);
      }

      // PartNo情報
      if (response && response.data && response.data.getPartNo) {
        const partNoList: PartNo[] = response.data.getPartNo;

        // 各PartNoのmaterialCostを取得してマージ
        const partNoWithMaterialCost = await Promise.all(
          partNoList.map(async (part) => {
            const materialCostData = await getMaterialCost(projectId, part.partNo!);
            return {
              ...part,
              materialCost: materialCostData?.materialCost,
              materialCostWithInhouseProfit: materialCostData?.materialCostWithInhouseProfit,
            };
          })
        );

        setPartNoData(partNoWithMaterialCost);

        // 過去プロジェクトとのPart No整合性チェック（画面表示を待たせないためawaitしない）
        // 振り返りモードは対象外（フェーズ1は紐づけ直し案内が成立する通常モードのみ対応）
        if (projectInfo?.pastProjectId && !reviewDate) {
          void checkPastPartNoConsistency(projectInfo.pastProjectId, projectInfo.pastReviewDate ?? null, partNoWithMaterialCost);
        } else {
          setMissingPastPartNos([]);
        }
      } else {
        showApiError(`PartNo`, `getPartNo`, response.errors);
      }

      // 見積チェックリスト・１０か条チェックリスト取得（設備一覧確定後にバッチ分割で取得）
      const equipmentIds: string[] = (equipmentData ?? []).map((eq: any) => eq.equipmentId).filter(Boolean);
      if (equipmentIds.length > 0) {
        await fetchCheckListByIdBatch(equipmentIds, equipmentData);
      }

      // ベンチマーク情報取得
      let allMatch = true;
      const projectAdditionalInfo: ProjectAdditionalInfo | undefined = response?.data?.getProjectAdditionalInfoById;
      if (projectAdditionalInfo) {
        if (projectAdditionalInfo.benchmarkData) {
          // JSONパース
          const benchmarkData = JSON.parse(projectAdditionalInfo.benchmarkData);
          if (!benchmarkData?.length || !equipmentData?.length || benchmarkData[0]?.inputType !== "equipment") {
            allMatch = true;
          } else {
            // 全件登録必須チェック（設備の全equipmentIdがベンチマークに一致していないと通過できない仕様）は
            // 「1件以上必須チェック」のみ残す方針のためコメントアウト
            // 両方のIDを取得
            // const capexIds = equipmentData.map((eq: any) => eq.equipmentId);
            // const benchmarkIds = benchmarkData.map((b: any) => b.equipmentId);
            // 両方のID配列が完全一致かチェック
            // allMatch =
            //   capexIds.length === benchmarkIds.length && // 要素数が同じ
            //   capexIds.every((id: any) => benchmarkIds.includes(id)); // capexのIDが全てbenchmarkにある
            // 上記の全件一致チェックは撤廃し、1件以上登録されていればtrueとする
            allMatch = true;
          }

          setBenchmarkData(benchmarkData);
        } else {
          setBenchmarkData([]);
        }

        // 更新前後サマリー情報取得
        if (projectAdditionalInfo.updateSummaryData) {
          // JSONパース
          const updateSummary = JSON.parse(projectAdditionalInfo.updateSummaryData);
          setUpdateSummaryData(updateSummary);
        } else {
          setUpdateSummaryData([]);
        }
      } else {
        showApiError(`ベンチマーク情報`, `getProjectAdditionalInfoById`, response.errors);
      };

      // 更新投資の場合、更新前後サマリーチェック（設備のprocessIdと更新前後サマリーのproductionLineIdが一致するか）
      const parsedUpdateSummaryData = projectAdditionalInfo?.updateSummaryData
        ? JSON.parse(projectAdditionalInfo.updateSummaryData)
        : [];
      const updateSummaryMatch = calcUpdateSummaryMatch(projectInfo.purpose, parsedUpdateSummaryData, equipmentData, projectInfo.applicationFormType);

      // 画面間入力チェック（RDSチャートデータを含むデータソースを使用）
      const rdsChartData = largeChartData?.simulationChartData ? JSON.parse(largeChartData.simulationChartData) : null;
      const projectDataForCheck: any = rdsChartData ? { ...projectInfo, ...rdsChartData } : projectInfo;
      requiredDataNotice(projectDataForCheck, partNoData, equipmentData, allMatch, updateSummaryMatch);

      // 合理化前後サマリーチャートデータ取得（RDS）
      if (largeChartData?.rationalizationSummaryChartData) {
        const rationalizationSummaryChartDataList = JSON.parse(largeChartData.rationalizationSummaryChartData);

        // チャートデータラベル多言語対応
        for (const processInfo of rationalizationSummaryChartDataList) {
          for (const item of processInfo.items) {
            switch (item.itemId) {
              case "salesQuantity_indicator":           // 売上数量
                item.itemName = t("components.RationalizationSummaryInputPopover.options.indicator.salesQuantity");
                break;
              case "productionQuantity_indicator":      // 生産数
                item.itemName = t("components.RationalizationSummaryInputPopover.options.indicator.productionQuantity");
                break;

              case "targetEquipmentDepreciation_cost":  // 投資対象設備の償却費
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.targetEquipmentDepreciation");
                break;
              case "otherDepreciation_cost":            // その他の償却費
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.otherDepreciation");
                break;
              case "laborCost_cost_laborCost":          // 労務費
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.laborCost");
                break;
              case "repairCost_cost":                   // 修繕費
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.repairCost");
                break;
              case "consumablesCost_cost":              // 消耗品費
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.consumablesCost");
                break;
              case "maintenanceCost_cost":              // 保守・メンテナンス費
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.maintenanceCost");
                break;
              case "scrapCost_cost":                    // スクラップ費用
                item.itemName = t("components.RationalizationSummaryInputPopover.options.cost.scrapCost");
                break;
              case "laborCost_cost_improvementEffect":  // 労務費と減価償却費
                item.itemName = t("features.InvestmentScreening.SimulationProductivityImprovement.label.laborCostAndDepreciation");
                break;
            }
          }
        }

        setRationalizationSummaryChartDataList(rationalizationSummaryChartDataList);
      } else {
        setRationalizationSummaryChartDataList([]);
      }
      // BOMデータ取得
      const bomDataResult = await getBomByProjectId(projectId);
      setBomData(bomDataResult);

    } catch (error) {
      console.error("データ取得エラー:", error);
    }
  };

  /** 顧客製品情報取得API */
  const getCustomerProductData = async (customerProductId: string) => {
    try {
      console.log(`getCustomerProductData():start`);

      const payload = {
        query: `
          query getCustomerProductByCustomerProductId($customerProductId: String) {
            getCustomerProductByCustomerProductId(customerProductId: $customerProductId) {
              customerProductId
              imageFileName
            }
          }`,
        variables: { customerProductId }
      };

      // 顧客製品情報取得（dynamodb、RDS）
      const response = await WebAPI.request(payload);
      // データ取得に成功した場合
      if (response && response.data && response.data.getCustomerProductByCustomerProductId) {
        const customerProductData: CustomerProduct = response.data.getCustomerProductByCustomerProductId;

        if (customerProductData.imageFileName) { setImageFileName(customerProductData.imageFileName); }

      } else {
        // 顧客製品名: 自由入力化に伴い、マスタに存在しない場合はエラーを表示せずスキップ
        console.log(`getCustomerProductData(): customerProductId="${customerProductId}" はマスタに存在しないため画像取得をスキップ`);
      }
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
  };

  // 承認者一覧データ取得（DynamoDBベース + r_ceas_workflowの承認結果をマージ）
  const getApproverData = async (divisionId: string, projectInfo?: any) => {
    try {
      // DynamoDBのapproverAssigneesから承認者名を構築
      const approverAssigneesJson = projectInfo?.approverAssignees;

      // flow_idとcategoryIdのマッピング
      const categoryToFlowId: Record<string, string> = {
        productDirector: WorkflowId.ProductDirector,       // "20"
        divisionManager: WorkflowId.BusinessManager,        // "21"
        salesHeadquarters: WorkflowId.SalesHQManager,       // "22"
        businessHeadquarters: WorkflowId.BusinessHQManager, // "23"
      };

      // DynamoDBから承認者一覧を構築
      let dynamoApprovers: ApproverList[] = [];
      let flowOrder = 1;

      // 承認者（製品統括/事業部長/営業本部長/事業本部長）
      if (approverAssigneesJson) {
        try {
          const assignees = JSON.parse(approverAssigneesJson);
          for (const category of assignees) {
            const flowId = categoryToFlowId[category.categoryId];
            if (flowId && category.employees) {
              for (const emp of category.employees) {
                dynamoApprovers.push({
                  employeeId: emp.employeeId,
                  employeeName: emp.employeeName,
                  flowId,
                  flowOrder: flowOrder++,
                  organizationName: emp.divisionName || "",
                  positionName: emp.positionName || "",
                  approvalResult: "",
                  confirmTime: "",
                  comment: "",
                } as ApproverList);
              }
            }
          }
        } catch (e) { console.warn('approverAssignees parse error:', e); }
      }

      // CC担当者（要の場合）
      if (projectInfo?.crossCheckRequired && projectInfo?.crossCheckAssignees) {
        try {
          const assignees = JSON.parse(projectInfo.crossCheckAssignees);
          for (const category of assignees) {
            if (category.employees) {
              for (const emp of category.employees) {
                dynamoApprovers.push({
                  employeeId: emp.employeeId,
                  employeeName: emp.employeeName,
                  flowId: WorkflowId.CrossCheck,
                  flowOrder: flowOrder++,
                  organizationName: emp.divisionName || "",
                  positionName: emp.positionName || "",
                  approvalResult: "",
                  confirmTime: "",
                  comment: "",
                  categoryName: category.categoryName || "",
                } as ApproverList);
              }
            }
          }
        } catch (e) { console.warn('crossCheckAssignees parse error:', e); }
      }

      // 資材担当（要の場合）
      if (projectInfo?.materialOfficerRequired && projectInfo?.materialOfficerAssignees) {
        try {
          const assignees = JSON.parse(projectInfo.materialOfficerAssignees);
          for (const category of assignees) {
            if (category.employees) {
              for (const emp of category.employees) {
                dynamoApprovers.push({
                  employeeId: emp.employeeId,
                  employeeName: emp.employeeName,
                  flowId: WorkflowId.MaterialOfficer,
                  flowOrder: flowOrder++,
                  organizationName: emp.divisionName || "",
                  positionName: emp.positionName || "",
                  approvalResult: "",
                  confirmTime: "",
                  comment: "",
                } as ApproverList);
              }
            }
          }
        } catch (e) { console.warn('materialOfficerAssignees parse error:', e); }
      }

      // r_ceas_workflowから承認結果をマージ（起案申請後のみデータあり）
      const payload = {
        query: `query getApproverList($projectId: String, $divisionId: String, $reviewDate: String) {
          getApproverList(projectId: $projectId, divisionId: $divisionId, reviewDate: $reviewDate) {
            employeeId
            employeeName
            positionName
            flowId
            organizationName
            confirm
            approvalResult
            confirmTime
            comment
            organizationId
            divisionId
          }
        }`,
        variables: { projectId, divisionId, ...(reviewDate && { reviewDate }) }
      };
      const response = await WebAPI.request(payload);
      const workflowApprovers: ApproverList[] = response?.data?.getApproverList || [];

      // 承認フローのflow_id一覧
      const approvalFlowIds = [
        WorkflowId.ProductDirector, WorkflowId.BusinessManager,
        WorkflowId.CrossCheck, WorkflowId.MaterialOfficerRequester, WorkflowId.MaterialOfficer,
        WorkflowId.SalesHQManagerRequester, WorkflowId.SalesHQManager,
        WorkflowId.BusinessHQManagerRequester, WorkflowId.BusinessHQManager,
        WorkflowId.PreReview, WorkflowId.HQReview, WorkflowId.PresidentReport,
      ] as string[];

      // CC部門名マッピング（division_id → 表示名）
      const divisionIdToName: Record<string, string> = {
        "manufacturing": t('features.InvestmentScreening.ReportAndDraft.ccDivision.manufacturing'),
        "engineering": t('features.InvestmentScreening.ReportAndDraft.ccDivision.engineering'),
        "it": t('features.InvestmentScreening.ReportAndDraft.ccDivision.it'),
      };

      // r_ceas_workflowにデータがあれば優先使用（起案申請後 or 差戻し後）
      const filteredWorkflow = workflowApprovers.filter(
        (wf: ApproverList) => wf.flowId && approvalFlowIds.includes(wf.flowId)
      );
      if (filteredWorkflow.length > 0) {
        // CC担当者にcategoryName（部門名）を付与
        for (const wf of filteredWorkflow) {
          if (wf.flowId === WorkflowId.CrossCheck && (wf as any).divisionId) {
            (wf as any).categoryName = divisionIdToName[(wf as any).divisionId] || (wf as any).divisionId;
          }
        }
        setApproverList(filteredWorkflow);
      } else {
        // r_ceas_workflowにデータなし（初回・起案申請前）→ DynamoDBベースの表示
        // flow_id昇順にソートして新フロー順で表示
        dynamoApprovers.sort((a, b) => parseInt(a.flowId || "0") - parseInt(b.flowId || "0"));
        setApproverList(dynamoApprovers);
      }

      // 申請者表示用: 製品統括の名前
      const allApprovers = filteredWorkflow.length > 0 ? filteredWorkflow : dynamoApprovers;
      const productDirector = allApprovers.find(a => a.flowId === WorkflowId.ProductDirector);
      if (productDirector) {
        setExecutiveManager([productDirector]);
      } else if (workflowApprovers.length > 0) {
        const exec = workflowApprovers.filter((item: ApproverList) => item.flowId == WorkflowId.ProductDirector);
        setExecutiveManager(exec);
      }
    } catch (error) {
      console.error("データ取得エラー:", error);
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /** 設定値取得API */
  const getSettingValueByKey = async (settingKey: string): Promise<string | void> => {
    const functionName = "getSettingValueByKey";
    try {
      console.log(`${functionName}():start`);

      const payload = {
        query: `
          query getSettingValueByKey($settingKey: String, $hasDeleted: Boolean) {
            getSettingValueByKey(settingKey: $settingKey, hasDeleted: $hasDeleted) {
              settingValue
            }
          }`,
        variables: { settingKey, hasDeleted: false }
      };

      // 設定値取得（dynamodb）
      const response = await WebAPI.request(payload);

      // データ取得に失敗した場合
      if (!response?.data[functionName]?.settingValue) {
        showApiError(`設定値`, functionName, response?.errors);
      }

      return response?.data[functionName]?.settingValue;

    } catch (error) {
      console.error("データ取得エラー:", error);
    }
  };

  /**
   * 製造ライン情報取得
   * @param productionLineId 製造ラインID
   */
  const getProductionLineById = async (productionLineId: string): Promise<ProductionLineType> => {

    const functionName = "getProductionLineById";

    try {
      console.log(`${functionName}():start`);

      const payload = {
        query: `
          query getProductionLineById($projectId: String, $productionLineId: String, $reviewDate: String) {
            getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate) {
              productionLineName
              nonWorkingHours
              workingDays
            }
          }`,
        variables: { projectId, productionLineId, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      // 製造ライン情報取得
      if (!response?.data[functionName]) {
        showApiError(`製造ライン情報情報`, functionName, response?.errors);
      }

      return response.data[functionName];

    } catch (error) {
      console.error("データ取得エラー:", error);
      return {};
    }
  }

  // 材料費情報取得API（PartNo単位）
  const getMaterialCost = async (projectId: string, partNo: string): Promise<{ materialCost?: string, materialCostWithInhouseProfit?: string } | null> => {
    const functionName = "getMaterialCost";
    try {
      const response = await WebAPI.request({
        query: `
          query getMaterialCost($projectId: String, $partNo: String, $reviewDate: String) {
            getMaterialCost(projectId: $projectId, partNo: $partNo, reviewDate: $reviewDate) {
              materialCost
              materialCostWithInhouseProfit
            }
          }`,
        variables: { projectId, partNo, ...(reviewDate && { reviewDate }) }
      });

      if (!response?.data?.getMaterialCost) {
        console.log(`${functionName} データなし: partNo=${partNo}`);
        return null;
      }

      return response?.data?.getMaterialCost;
    } catch (error) {
      console.error(`${functionName} error: partNo=${partNo}`, error);
      return null;
    }
  };

  /**
   * シナリオデータを取得する。
   * @returns {Promise<TScenario[]>} 取得したシナリオデータのリスト
   */
  const fetchScenarioData = async (): Promise<TScenario[]> => {
    const functionName = "fetchScenarioData";

    // GraphQLクエリ
    const query = {
      query: `
      query getScenarioByProjectId($projectId: String, $hasDeleted: Boolean, $reviewDate: String) {
        getScenarioByProjectId(projectId: $projectId, hasDeleted: $hasDeleted, reviewDate: $reviewDate) {
          scenarioId
          scenarioName
        }
      }
    `,
      variables: { projectId, hasDeleted: false, ...(reviewDate && { reviewDate }) }
    };

    try {
      // APIリクエストの実行
      const response = await WebAPI.request(query);
      console.log(`${functionName} response: `, JSON.stringify(response));

      // データ取得失敗時の処理
      if (!response?.data?.getScenarioByProjectId) {
        toast({
          title: t('features.InvestmentScreening.ReportAndDraft.toast.dataFetchError'),
          description: t('features.InvestmentScreening.ReportAndDraft.message.scenarioFetchFailed'),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        console.error(`${functionName} ERROR: Failed to fetch scenario data`, response?.errors);
        return [];
      }

      // データ取得成功時の処理
      console.log(`${functionName} SUCCESS: Data fetched successfully`);
      return response.data.getScenarioByProjectId;

    } catch (error) {
      toast({
        title: t('features.InvestmentScreening.ReportAndDraft.toast.dataFetchError'),
        description: t('features.InvestmentScreening.ReportAndDraft.message.dataFetchErrorDesc'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error(`${functionName} ERROR:`, error);
      return [];
    }
  };

  /**
   * 見積チェックリスト・１０か条チェックリストを設備件数に応じてバッチ分割で取得する
   * BE側（getCheckListById）が累積サイズ（OCTET_LENGTH）を見ながら閾値内でバッチを返却するため、
   * hasMoreがtrueである限りremainingEquipmentIdsを次回equipmentIdsとして渡すループで全件を取得する
   * @param equipmentIds 対象設備IDの全件
   * @param equipmentData 設備一覧データ（旧形式チェックリスト検出時の設備名解決に使用）
   */
  const fetchCheckListByIdBatch = async (equipmentIds: string[], equipmentData: any[]) => {
    try {
      const allItems: any[] = [];
      let remainingEquipmentIds = equipmentIds;

      // hasMoreがfalseになるまでバッチ取得を繰り返す
      while (remainingEquipmentIds.length > 0) {
        const payload = {
          query: `
            query getCheckListById($projectId: String, $reviewDate: String, $equipmentIds: [String!]) {
              getCheckListById(projectId: $projectId, reviewDate: $reviewDate, equipmentIds: $equipmentIds) {
                items {
                  equipmentId
                  estimateCheckList
                  checkList
                }
                hasMore
                remainingEquipmentIds
              }
            }`,
          variables: {
            projectId: projectId,
            equipmentIds: remainingEquipmentIds,
            ...(reviewDate && { reviewDate }),
          }
        };

        const response = await WebAPI.request(payload);

        if (!response?.data?.getCheckListById) {
          showApiError(`見積チェックリスト・１０か条チェックリスト`, `getCheckListById`, response?.errors);
          return;
        }

        const result = response.data.getCheckListById;
        allItems.push(...(result.items ?? []));
        remainingEquipmentIds = result.hasMore ? (result.remainingEquipmentIds ?? []) : [];
      }

      // 見積チェックリスト取得
      const estimateCheckListData = allItems.reduce((acc: any, item: any) => {
        if (item.equipmentId && (item.estimateCheckList || null)) {
          acc[item.equipmentId] = {
            estimateCheckList: JSON.parse(item.estimateCheckList),
          };
        }
        return acc;
      }, {});
      // 見積チェックリスト設定
      setEstimateCheckListData(estimateCheckListData);

      // １０か条チェックリスト取得
      const checkListData = allItems.reduce((acc: any, item: any) => {
        if (item.equipmentId && (item.checkList || null)) {
          acc[item.equipmentId] = {
            checkList: JSON.parse(item.checkList),
          };
        }
        return acc;
      }, {});

      // １０か条チェックリスト設定
      setCheckListDataList(checkListData);

      // 旧形式チェックリストの設備を検出
      // 新形式（外部リンク＋確認方式）は idleEquipmentChecked / tenPrinciplesChecked の
      // ２つの確認フラグのみを保持する構造。過去の表形式チェックリスト（大項目キー・nodeIdキー等）が
      // 1つでも残っている場合は旧形式として扱い、表形式のフォールバック表示を維持する。
      // （旧形式データを開いて確認フラグだけ保存した場合も、大項目キーはそのまま残るため旧形式のまま表示され続ける）
      const oldFormatEquipmentIds = Object.entries(checkListData)
        .filter(([, value]: [string, any]) => {
          const cl = value.checkList;
          if (!cl || typeof cl !== 'object') return false;
          return Object.keys(cl).some(
            (key) => key !== IDLE_EQUIPMENT_CHECK_KEY && key !== TEN_PRINCIPLES_CHECK_KEY
          );
        })
        .map(([equipmentId]) => equipmentId); // 設備IDを取得
      setOldFormatEquipmentIds(oldFormatEquipmentIds);

      const oldFormatEquipmentNames = oldFormatEquipmentIds.map((equipmentId) => {
        const equipment = equipmentData?.find((eq: any) => eq.equipmentId === equipmentId);
        return equipment?.equipmentName ?? equipmentId;
      });

      if (oldFormatEquipmentNames.length > 0) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ReportAndDraft.message.validationCheckListOldFormat', {
            equipmentNames: oldFormatEquipmentNames.join('、'),
          }),
          status: 'warning',
          duration: 10000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(`fetchCheckListByIdBatch ERROR:`, error);
    }
  };

  /**
  * エラー情報をユーザー通知する
  * @param dataName データ論理名
  * @param apiName API名称
  * @param responseErrors レスポンスエラー情報
  */
  const showApiError = (dataName: string, apiName: string, responseErrors: any) => {
    toast({
      title: t('features.InvestmentScreening.ReportAndDraft.toast.dataFetchError'),
      description: t('features.InvestmentScreening.ReportAndDraft.message.fetchFailed', { name: dataName }),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

    console.error(`Failed to call API ${apiName}. errors = ${JSON.stringify(responseErrors)}`);
  }

  /**
   * 紐づけた過去プロジェクトのPart Noを取得し、本プロジェクトのPart Noとの不整合を判定する。
   * 判定結果はmissingPastPartNosに保持する（トースト表示はuseEffectで行う）。
   * データ取得が重いReportAndDraftの画面表示を遅らせないよう、呼び出し元ではawaitしない。
   *
   * @param pastProjectId 過去プロジェクトID
   * @param pastReviewDate 紐づけ時点の承認済み振り返り日（申請のみの場合はnull）
   * @param capexPartNo 本プロジェクトのPart No情報
   */
  const checkPastPartNoConsistency = async (pastProjectId: string, pastReviewDate: string | null, capexPartNo: PartNo[]) => {
    try {
      const payload = {
        query: `
          query getPartNo($projectId: String, $reviewDate: String, $referencingProjectId: String) {
            getPartNo(projectId: $projectId, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
              partNo
            }
          }`,
        variables: {
          projectId: pastProjectId,
          ...(pastReviewDate && { reviewDate: pastReviewDate }),
          referencingProjectId: projectId,
        }
      };

      const response = await WebAPI.request(payload);
      const pastPartNo: PartNo[] = response?.data?.getPartNo ?? [];

      setMissingPastPartNos(findMissingPastPartNos(capexPartNo, pastPartNo));
    } catch (error) {
      console.error("checkPastPartNoConsistency error:", error);
      setMissingPastPartNos([]);
    }
  }

  // -----------------------------
  // ロジック
  // -----------------------------
  /** ヘッダボタン押下時のスクロール移動 */
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // --------- 販売数量・販売単価関連 ---------
  /** PartNo 数量、製品売上高の合計計算 */
  const calculateTotal = (values: (Decimal | undefined)[]): Decimal => {
    if (!values) {
      return new Decimal(0);
    }
    // undefined を除外して合計を計算
    const filteredValues = values.filter((value): value is Decimal => value !== undefined);
    return filteredValues.reduce((total: Decimal, value: Decimal) => {
      return total.plus(new Decimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP));
    }, new Decimal(0));
  };

  /** 企画数量 PartNo毎の数量合計 */
  const calculateTotalQuantity = (table: QuantityAndPriceTable): Decimal => {
    return table.years.reduce((total, year) => {
      const quantity = table.data[year]?.quantity || 0;
      return total.plus(new Decimal(quantity));
    }, new Decimal(0));
  };

  /** Project合計 製品売上高合計 */
  const calculateTotalSales = (tables: QuantityAndPriceTable[], yearIndex: number): Decimal => {
    const total = tables.reduce((sum: Decimal, table) => {
      const year = table.years[yearIndex];
      const quantity = new Decimal(table.data[year]?.quantity || 0);
      const price = new Decimal(table.data[year]?.price || 0);
      const sales = quantity.times(price);
      const dividePrice = divideValue(sales).toDecimalPlaces(2, Decimal.ROUND_DOWN);
      //const dividePrice = divideValue(price);
      return sum.plus(dividePrice);
    }, new Decimal(0));

    return total;
  };

  /** Project合計 金型合計 */
  const calculateTotalMoldSales = (tables: QuantityAndPriceTable[], moldTables: MoldTable[], yearIndex: number): Decimal => {
    const partNoMoldSales = tables.reduce((total: Decimal, table) => {
      const year = table.years[yearIndex];
      const moldValue = table.partNoMoldTables.reduce((acc: Decimal, partNoMoldTable: MoldTable) => {
        const price = new Decimal(partNoMoldTable.data[year]?.price || 0);
        const dividedPrice = price.dividedBy(DIVISOR_VALUE).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
        return acc.plus(dividedPrice);
      }, new Decimal(0));
      return total.plus(moldValue);
    }, new Decimal(0));

    const projectMoldSales = (Array.isArray(moldTables) ? moldTables : []).reduce((total: Decimal, mold) => {
      const year = mold.years[yearIndex];
      const price = new Decimal(mold.data[year]?.price || 0);
      const dividedPrice = price.dividedBy(DIVISOR_VALUE).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
      return total.plus(dividedPrice);
    }, new Decimal(0));

    return partNoMoldSales.plus(projectMoldSales);
  };

  /** Project合計 開発費合計 */
  const calculateTotalDevelopmentCost = (tables: QuantityAndPriceTable[], developmentCostTables: DevelopmentCostTable[], yearIndex: number): Decimal => {
    const partNoDevelopmentCost = tables.reduce((total: Decimal, table) => {
      const year = table.years[yearIndex];
      const developmentCostValue = table.partNoDevelopmentCostTables.reduce((acc: Decimal, partNoDevelopmentCostTable: DevelopmentCostTable) => {
        const price = new Decimal(partNoDevelopmentCostTable.data[year]?.price || 0);
        const dividedPrice = price.dividedBy(DIVISOR_VALUE).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
        return acc.plus(dividedPrice);
      }, new Decimal(0));
      return total.plus(developmentCostValue);
    }, new Decimal(0));

    const projectDevelopmentCost = (Array.isArray(developmentCostTables) ? developmentCostTables : []).reduce((total: Decimal, developmentCost) => {
      const year = developmentCost.years[yearIndex];
      const price = new Decimal(developmentCost.data[year]?.price || 0);
      const dividedPrice = price.dividedBy(DIVISOR_VALUE).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
      return total.plus(dividedPrice);
    }, new Decimal(0));

    return partNoDevelopmentCost.plus(projectDevelopmentCost);
  };

  /** 開発費(プロジェクト単位)合計 */
  const calculateTotalDevelopmentCostByYear = (developmentCostTables: DevelopmentCostTable[]): { [key: number]: Decimal } => {
    const totalDevelopmentCostByYear: { [key: number]: Decimal } = {};
    if (developmentCostTables === null) { return totalDevelopmentCostByYear; }
    developmentCostTables.forEach((developmentCost) => {
      developmentCost.years.forEach((year) => {
        if (!totalDevelopmentCostByYear[year]) {
          totalDevelopmentCostByYear[year] = new Decimal(0);
        }
        const price = new Decimal(developmentCost.data[year]?.price || 0);
        const dividedPrice = divideValue(price).toDecimalPlaces(3, Decimal.ROUND_DOWN);
        totalDevelopmentCostByYear[year] = totalDevelopmentCostByYear[year].plus(dividedPrice);
      });
    });
    return totalDevelopmentCostByYear;
  };

  /** 金型(プロジェクト単位)合計 */
  const calculateTotalMoldSalesByYear = (moldTables: MoldTable[]): { [key: number]: Decimal } => {
    const totalMoldSalesByYear: { [key: number]: Decimal } = {};
    if (moldTables === null) { return totalMoldSalesByYear; }
    moldTables.forEach((mold) => {
      mold.years.forEach((year) => {
        if (!totalMoldSalesByYear[year]) {
          totalMoldSalesByYear[year] = new Decimal(0);
        }
        const price = new Decimal(mold.data[year]?.price || 0);
        const dividedPrice = divideValue(price).toDecimalPlaces(3, Decimal.ROUND_DOWN);
        totalMoldSalesByYear[year] = totalMoldSalesByYear[year].plus(dividedPrice);
      });
    });
    return totalMoldSalesByYear;
  };

  /** 総合計 */
  const calculateTotalOverall = (tables: QuantityAndPriceTable[], moldTables: MoldTable[], developmentCostTables: DevelopmentCostTable[], yearIndex: number): Decimal => {
    const totalSales = calculateTotalSales(tables, yearIndex);
    const totalMoldSales = calculateTotalMoldSales(tables, moldTables, yearIndex);
    const totalDevelopmentCost = calculateTotalDevelopmentCost(tables, developmentCostTables, yearIndex);
    return totalSales.plus(totalMoldSales).plus(totalDevelopmentCost);
  };

  /** 設備の合計金額(投資額)を算出する */
  const calculateTotalAmount = (equipmentAmounts: { amount: string }[]): Decimal => {
    const totalAmount = equipmentAmounts.reduce((total: Decimal, equipment: EquipmentType) => {
      const amount = new Decimal(equipment.amount ?? 0);
      return total.plus(amount);
    }, new Decimal(0));

    return totalAmount;
  };

  /**単純工合計人数*/
  const totalNumberOfSimpleLabor = (equipmentAmounts: { unskilledWorker: number }[]): number => {
    const totalPeople = equipmentAmounts.reduce((total: number, equipment: { unskilledWorker: number }) => {
      const peopleCount = equipment.unskilledWorker ?? 0;
      return total + peopleCount;
    }, 0);

    return totalPeople;
  };

  /**多能工合計人数*/
  const totalNumberOfMultiSkilledWorkerse = (equipmentAmounts: { multiSkilledWorker: number }[]): number => {
    const totalPeople = equipmentAmounts.reduce((total: number, equipment: { multiSkilledWorker: number }) => {
      const peopleCount = equipment.multiSkilledWorker ?? 0;
      return total + peopleCount;
    }, 0);

    return totalPeople;
  };

  /**テクニシャン合計人数*/
  const totalNumberOfTechnicians = (equipmentAmounts: { technician: number }[]): number => {
    const totalPeople = equipmentAmounts.reduce((total: number, equipment: { technician: number }) => {
      const peopleCount = equipment.technician ?? 0;
      return total + peopleCount;
    }, 0);

    return totalPeople;
  };

  /**設備に必要な人員数の総合計を計算*/
  const calculatePersonnelTotals = (equipmentAmounts: any[]): { simpleLabor: number; multiSkilled: number; technicians: number; total: number } => {
    const unskilledWorker = equipmentAmounts.filter((item): item is { unskilledWorker: number } => item.amount !== undefined);
    const multiSkilledWorker = equipmentAmounts.filter((item): item is { multiSkilledWorker: number } => item.amount !== undefined);
    const technician = equipmentAmounts.filter((item): item is { technician: number } => item.amount !== undefined);

    const simpleLabor = totalNumberOfSimpleLabor(unskilledWorker);
    const multiSkilled = totalNumberOfMultiSkilledWorkerse(multiSkilledWorker);
    const technicians = totalNumberOfTechnicians(technician);

    return {
      simpleLabor,
      multiSkilled,
      technicians,
      total: simpleLabor + multiSkilled + technicians
    };
  };

  // --------- スケジュール関連 ---------

  /** グラフデータ（チャートデータ）の凡例ラベル多言語対応 */
  const updateSimulationChartLabels = (chartData: any, keys: string[], suffix?: string) => {
    if (!chartData) {
      return;
    }

    // チャートデータラベル多言語対応
    updateChartLabels(chartData, (label: string, labelIdx: number, datasetIdx: number) => {
      // 多言語対応
      const resultLabel = t(
        keys[labelIdx],
        { "suffix": suffix });
      return resultLabel;
    }, t, 0);
  };

  // --------- 設備投資関連 ---------
  /** サプライヤー名の重複削除 */
  const removeDuplicateSupplierNames = (suppliers: EquipmentSupplierAndEqIdType[]) => {
    const uniqueNamesSet = new Set<string>();
    suppliers.forEach(supplier => {
      const displayName = getMultilingualDisplayName(supplier.equipmentSupplierName);
      if (displayName) {
        uniqueNamesSet.add(displayName);
      }
    });
    return Array.from(uniqueNamesSet);
  };
  const uniqueSupplierNames = removeDuplicateSupplierNames(supplierData).join('、');

  // --------- P&L関連 ---------
  /** 桁数丸め処理 */
  const divideValue = (value: Decimal) => {
    return value.dividedBy(DIVISOR_VALUE);
  };

  useEffect(() => {

    if (!projectData || partNoData.length === 0 || !equipmentAmounts || !selectedCurrency.name) {
      return; // データが不足している場合は処理を行わない
    }

    /** 画面表示項目の計算、設定(DB検索後に呼び出される) */
    const calculatePartNoData = async (
      projectData: ProjectWithRdsData,
      partNoData: PartNo[],
      equipmentData: EquipmentType[]
    ): Promise<void> => {

      // -----------------------------
      // 初期値設定
      // -----------------------------
      // プロジェクト開始日 YYYY-MM-DD形式
      const startDate = projectData?.projectStartDate || "";

      // 入力方式（標準モード・簡易モード）を取得
      const isSimple = isSimpleApplicationForm(projectData.purpose, projectData.applicationFormType);

      // 最大年数を取得
      // 標準モード時はPartNo, PJ別金型費回収, PJ別開発費回収, PJ別設備費回収から年度数を算出
      // 簡易モード時は販売数量・販売単価 未入力のため、量産開始日〜EOL予定日から年度数を算出
      const maxYears = isSimple
        ? getFiscalYearsByProjectDates(projectData.projectStartDate, projectData.projectEndDate)
        : getTotalFiscalYearsByProject(partNoData, projectData.moldSalesByProject,
          projectData.developmentSalesByProject, projectData.equipmentSalesByProject);

      // ローカル通貨（販売通貨より先に算出、簡易モードの fallback で使用）
      const localCurrencyData = currencyData.find(currency => currency.currencyId === localCurrencyId);
      if (!localCurrencyData || !localCurrencyData.currencyName) { return; }
      const localCurrency = localCurrencyData.currencyName;

      // 販売通貨（簡易モード時は currencyId が空のため localCurrency をフォールバックとして使用）
      const salesCurrencyData = currencyData.find(currency => currency.currencyId === projectData.currencyId);
      if (!isSimple && (!salesCurrencyData || !salesCurrencyData.currencyName)) { return; }
      const salesCurrency = salesCurrencyData?.currencyName || localCurrency;

      // 表示通貨
      const dispCurrency = selectedCurrency.name;
      // 通貨の小数点表示判定 (1000丸めで表示するためtrue)
      const decimalCurrency = true;
      //const decimalCurrency = hasDecimal(dispCurrency);

      // -----------------------------
      // PartNo毎の計算
      // -----------------------------
      // 数量を取得（月毎、年度毎）
      const quantitiesByPartNoMonth = getQuantitiesByPartNoMonth(partNoData, startDate);
      const quantitiesByPartNoYear = getQuantitiesByPartNoYear(quantitiesByPartNoMonth);
      setQuantitiesByPartNo(quantitiesByPartNoYear);

      // 販売単価を取得（月毎、年度毎）
      // 振り返りモード: convertWithRate を渡して月別レート変換、通常モード: 未指定で固定レート
      const pricesByPartNoMonth = getPricesByPartNoMonth(
        partNoData,
        dispCurrency,
        currencyData,
        startDate,
        reviewDate ? convertWithRate : undefined,
      );
      const pricesByPartNoYear = getPricesByPartNoYear(pricesByPartNoMonth, quantitiesByPartNoMonth);
      // 販売単価は千で丸めず、小数点以下4桁で表示
      const roundPricesByPartNoYear = pricesByPartNoYear.map(({ partNo, prices }) => ({
        partNo,
        prices: prices.map(price => price.toDecimalPlaces(4, Decimal.ROUND_HALF_UP))
      }));
      setPricesByPartNo(roundPricesByPartNoYear);

      // 製品売上高を取得（月毎、年度毎）
      const totalSalesByPartNoMonth = calcTotalSalesByPartNoMonth(quantitiesByPartNoMonth, pricesByPartNoMonth);
      const totalSalesByPartNoYear = calcTotalSalesByPartNoYear(totalSalesByPartNoMonth, decimalCurrency);
      const roundTotalSalesByPartNoYear = totalSalesByPartNoYear.map(({ partNo, totalSales }) => ({
        partNo,
        totalSales: totalSales.map(salse => divideValue(salse).toDecimalPlaces(2, Decimal.ROUND_HALF_UP))
      }));
      setTotalSalesByPartNo(roundTotalSalesByPartNoYear);

      // 年月リスト生成（年度）
      const yearMonthListByFiscalYear = generateYearMonthList(startDate, maxYears);

      // 材料単価の取得（月別）
      // 振り返りモード: 関数内で月別レートで表示通貨に変換
      // 通常モード: ローカル通貨のまま（後続の convertCurrency で固定レート変換）
      const materialCostByPartNoByMonth = reviewDate
        ? getMaterialCostByPartNoByMonth(
          partNoData,
          localCurrency,
          dispCurrency,
          currencyData,
          yearMonthListByFiscalYear,
          "normal",
          convertWithRate,
        )
        : getMaterialCostByPartNoByMonth(
          partNoData,
          undefined,
          undefined,
          undefined,
          yearMonthListByFiscalYear
        );

      // PartNo別 材料単価（年度別加重平均）を計算（小数点以下4桁で丸め）
      const materialCostByPartNoYearData = getMaterialCostByPartNoYear(
        materialCostByPartNoByMonth,
        quantitiesByPartNoMonth,
        yearMonthListByFiscalYear
      );
      // 振り返りモード: 既に表示通貨で変換済み、通常モード: ローカル通貨で丸め後、表示通貨に固定レート変換
      const roundMaterialCostByPartNoYearData = materialCostByPartNoYearData.map(({ partNo, materialCosts }) => ({
        partNo,
        materialCosts: materialCosts.map(cost =>
          reviewDate
            ? cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
            : convertCurrency(cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP), localCurrency, dispCurrency, currencyData).toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
        )
      }));
      setMaterialCostByPartNo(roundMaterialCostByPartNoYearData);

      // 材料費の算出（月別データ対応版）
      const { totalMaterialCostsByPartNoYear } = calcTotalMaterialCostsByPartNoWithMonthlyData(
        quantitiesByPartNoMonth,
        materialCostByPartNoByMonth,
        yearMonthListByFiscalYear
      );

      // 振り返りモード: 既に表示通貨、通常モード: ローカル通貨を表示通貨に固定レート変換
      const roundTotalMaterialByPartNoYear = totalMaterialCostsByPartNoYear.map(({ partNo, totalMaterialCosts }) => ({
        partNo,
        totalMaterialCosts: totalMaterialCosts.map(price =>
          reviewDate
            ? divideValue(price)
            : divideValue(convertCurrency(price, localCurrency, dispCurrency, currencyData))
        )
      }));
      setTotalMaterialCostsByPartNo(roundTotalMaterialByPartNoYear);

      // 金型年度合計(PartNo単位)を取得
      // 振り返りモード: convertWithRate を渡して月別レート変換、通常モード: 未指定で固定レート
      const moldSalesPartNoMonth = calcMoldSalesByPartNoMonth(startDate, partNoData, dispCurrency, currencyData, reviewDate ? convertWithRate : undefined);
      const moldSalesPartNoYear = calcTotalSalesByPartNoYear(moldSalesPartNoMonth, decimalCurrency);
      const roundMoldSalesPartNoYear = moldSalesPartNoYear.map(({ partNo, totalSales }) => ({
        partNo,
        totalSales: totalSales.map(sales => divideValue(sales).toDecimalPlaces(2, Decimal.ROUND_HALF_UP))
      }));
      setMoldSalesPartNoYear(roundMoldSalesPartNoYear);

      // 開発費年度合計(PartNo単位)を取得
      // 振り返りモード: convertWithRate を渡して月別レート変換、通常モード: 未指定で固定レート
      const devSalesPartNoMonth = calcDevSalesByPartNoMonth(startDate, partNoData, dispCurrency, currencyData, reviewDate ? convertWithRate : undefined);
      const devSalesPartNoYear = calcTotalSalesByPartNoYear(devSalesPartNoMonth, decimalCurrency);
      const roundDevSalesPartNoYear = devSalesPartNoYear.map(({ partNo, totalSales }) => ({
        partNo,
        totalSales: totalSales.map(sales => divideValue(sales).toDecimalPlaces(2, Decimal.ROUND_HALF_UP))
      }));
      setDevSalesPartNoYear(roundDevSalesPartNoYear);

      // 製品売上高、金型(PartNo単位)、開発費合計(PartNo単位)を取得
      const mergePartNoSalesByYear = calcPartNoSalesMergeByYear(roundTotalSalesByPartNoYear, roundMoldSalesPartNoYear, roundDevSalesPartNoYear);
      setMergePartNoSalesByYear(mergePartNoSalesByYear);

      // スループット額(PartNo単位)の算出
      const throughputsByPartNo = mergeSalesAndSubtractCosts(mergePartNoSalesByYear, roundTotalMaterialByPartNoYear);
      setThroughputsByPartNo(throughputsByPartNo);

      // スループット率(PartNo単位)の算出
      const throughputRatesByPartNo = calcThroughputRate(throughputsByPartNo, mergePartNoSalesByYear);
      setThroughputRatesByPartNo(throughputRatesByPartNo);

      // -----------------------------
      // プロジェクト単位の計算
      // -----------------------------
      let sumMoldSales: Decimal[] = [];        // プロジェクト単位の金型
      let sumDevSales: Decimal[] = [];         // プロジェクト単位の開発費
      let sumEquipmentSales: Decimal[] = [];   // プロジェクト単位の設備費

      // 金型（プロジェクト単位）
      // 振り返りモード: convertWithRate を渡して月別レート変換、通常モード: 未指定で固定レート
      const projectMoldSalesByMonth = getProjectSalesByMonth(startDate, projectData.moldSalesByProject, dispCurrency, currencyData, reviewDate ? convertWithRate : undefined);
      sumMoldSales = calculateProjectSalesSum(projectMoldSalesByMonth, decimalCurrency);
      const convSumMoldSales = sumMoldSales.map((moldSales: Decimal) => {
        return divideValue(moldSales);
      });
      setMoldSalesByProjectYear(convSumMoldSales);

      // 開発費（プロジェクト単位）
      // 振り返りモード: convertWithRate を渡して月別レート変換、通常モード: 未指定で固定レート
      const projectDevSalesByMonth = getProjectSalesByMonth(startDate, projectData.developmentSalesByProject, dispCurrency, currencyData, reviewDate ? convertWithRate : undefined);
      sumDevSales = calculateProjectSalesSum(projectDevSalesByMonth, decimalCurrency);
      const convSumDevSales = sumDevSales.map((devSales: Decimal) => {
        return divideValue(devSales);
      });
      setDevSalesByProjectYear(convSumDevSales);

      // 設備費（プロジェクト単位）
      // 振り返りモード: convertWithRate を渡して月別レート変換、通常モード: 未指定で固定レート
      const projectEquipmentSalesByMonth = getProjectSalesByMonth(startDate, projectData.equipmentSalesByProject, dispCurrency, currencyData, reviewDate ? convertWithRate : undefined);
      sumEquipmentSales = calculateProjectSalesSum(projectEquipmentSalesByMonth, decimalCurrency);
      const convSumEquipmentSales = sumEquipmentSales.map((equipmentSales: Decimal) => {
        return divideValue(equipmentSales);
      });
      setEquipmentSalesByProjectYear(convSumEquipmentSales);

      // -----------------------------
      // プロジェクト合計
      // -----------------------------
      // 売上合計(年度別)
      const totalSalesByYear = calcTotalPartNoSalesByYear(roundTotalSalesByPartNoYear);
      const totalSalesByMonth = getTotalSalesByMonth(totalSalesByPartNoMonth);
      setProjectTotalSalesByYear(adjustArrayLength(totalSalesByYear, maxYears, new Decimal(0)));

      // 全PartNo金型合計(年度別)
      let totalPartNoMoldSalesByYear = calcTotalMoldSalesByYear(roundMoldSalesPartNoYear);
      totalPartNoMoldSalesByYear = adjustArrayLength(totalPartNoMoldSalesByYear, maxYears, new Decimal(0));
      setProjectTotalMoldSalesByYear(adjustArrayLength(totalPartNoMoldSalesByYear, maxYears, new Decimal(0)));

      // 全PartNo開発費合計(年度別)
      let totalPartNoDevSalesByYear = calcTotalDevSalesByYear(roundDevSalesPartNoYear);
      totalPartNoDevSalesByYear = adjustArrayLength(totalPartNoDevSalesByYear, maxYears, new Decimal(0));
      setProjectTotalDevSalesByYear(adjustArrayLength(totalPartNoDevSalesByYear, maxYears, new Decimal(0)));

      // プロジェクト合計-総合計(年度別)
      const totalMoldSalesByMonth = getTotalSalesByMonth(moldSalesPartNoMonth);   // 全てのPartNoの金型売上（月別）
      const totalDevSalesByMonth = getTotalSalesByMonth(devSalesPartNoMonth);     // 全てのPartNoの開発費売上（月別）
      const totalSalesMoldDevByMonth = getTotalSalesMoldDevByMonth(totalSalesByMonth, totalMoldSalesByMonth,
        totalDevSalesByMonth, projectMoldSalesByMonth, projectDevSalesByMonth, projectEquipmentSalesByMonth);
      const projectTotalByYearRaw = getTotalSalesMoldDevByYear(totalSalesByPartNoYear, moldSalesPartNoYear, devSalesPartNoYear, sumMoldSales, sumDevSales, sumEquipmentSales);
      const projectTotalByYear = projectTotalByYearRaw.map((sales: Decimal) => {
        return divideValue(sales);
      });
      setProjectGrandTotalByYear(adjustArrayLength(projectTotalByYear, maxYears, new Decimal(0)));

      // -----------------------------
      // P&L 外注のみ、内製のみ材料費（月別データ対応）
      // -----------------------------
      // 材料単価(外注のみ) 月別データを取得
      // 振り返りモード: 関数内で月別レートで表示通貨に変換、通常モード: ローカル通貨のまま
      const outsourcingMaterialCostsByMonth = reviewDate
        ? getMaterialCostByPartNoByMonth(
          partNoData, localCurrency, dispCurrency, currencyData, yearMonthListByFiscalYear, "outsourcing", convertWithRate,
        )
        : getMaterialCostByPartNoByMonth(
          partNoData, undefined, undefined, undefined, yearMonthListByFiscalYear, "outsourcing",
        );
      // 材料単価(外注のみ) 年度別加重平均を計算後、表示通貨に変換（小数点以下4桁で丸め）
      const outsourcingMaterialCostsByYear = getMaterialCostByPartNoYear(
        outsourcingMaterialCostsByMonth, quantitiesByPartNoMonth, yearMonthListByFiscalYear
      );
      // 振り返りモード: 既に表示通貨、通常モード: ローカル通貨→表示通貨に固定レート変換
      const roundOutsourcingMatCostsByYear = outsourcingMaterialCostsByYear.map(({ partNo, materialCosts }) => ({
        partNo,
        materialCosts: materialCosts.map(cost =>
          reviewDate
            ? cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
            : convertCurrency(cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP), localCurrency, dispCurrency, currencyData).toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
        )
      }));
      setOutsourcingUnitMaterialCostByPartNo(roundOutsourcingMatCostsByYear);

      // 材料費(外注のみ)（月別材料単価を使用）
      const { totalMaterialCostsByPartNoYear: totalOutsourcingCostsByYear } = calcTotalMaterialCostsByPartNoWithMonthlyData(
        quantitiesByPartNoMonth, outsourcingMaterialCostsByMonth, yearMonthListByFiscalYear
      );
      const roundTotalOutsourcingCostsByPartNoYear = totalOutsourcingCostsByYear.map(({ partNo, totalMaterialCosts }) => ({
        partNo,
        totalMaterialCosts: totalMaterialCosts.map(price =>
          reviewDate
            ? divideValue(price)
            : divideValue(convertCurrency(price, localCurrency, dispCurrency, currencyData))
        )
      }));
      setOutsourcingTotalMaterialCostsByPartNo(roundTotalOutsourcingCostsByPartNoYear);

      // 材料費(外注のみ) スループット額
      const outsourcingThroughputsByPartNo = mergeSalesAndSubtractCosts(mergePartNoSalesByYear, roundTotalOutsourcingCostsByPartNoYear);
      setOutsourcingThroughputsByPartNo(outsourcingThroughputsByPartNo);

      // 材料費(外注のみ) スループット率
      const outsourcingThroughputRatesByPartNo = calcThroughputRate(outsourcingThroughputsByPartNo, mergePartNoSalesByYear);
      setOutsourcingThroughputRatesByPartNo(outsourcingThroughputRatesByPartNo);

      // 材料単価(内製のみ) 月別データを取得
      // 振り返りモード: 関数内で月別レートで表示通貨に変換、通常モード: ローカル通貨のまま
      const inhouseMaterialCostsByMonth = reviewDate
        ? getMaterialCostByPartNoByMonth(
          partNoData, localCurrency, dispCurrency, currencyData, yearMonthListByFiscalYear, "inhouse", convertWithRate,
        )
        : getMaterialCostByPartNoByMonth(
          partNoData, undefined, undefined, undefined, yearMonthListByFiscalYear, "inhouse",
        );
      // 材料単価(内製のみ) 年度別加重平均を計算後、表示通貨に変換（小数点以下4桁で丸め）
      const inhouseMaterialCostsByYear = getMaterialCostByPartNoYear(
        inhouseMaterialCostsByMonth, quantitiesByPartNoMonth, yearMonthListByFiscalYear
      );
      // 振り返りモード: 既に表示通貨、通常モード: ローカル通貨→表示通貨に固定レート変換
      const roundInhouseMatCostsByYear = inhouseMaterialCostsByYear.map(({ partNo, materialCosts }) => ({
        partNo,
        materialCosts: materialCosts.map(cost =>
          reviewDate
            ? cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
            : convertCurrency(cost.toDecimalPlaces(4, Decimal.ROUND_HALF_UP), localCurrency, dispCurrency, currencyData).toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
        )
      }));
      setInhouseUnitMaterialCostByPartNo(roundInhouseMatCostsByYear);

      // 材料費(内製のみ)（月別材料単価を使用）
      const { totalMaterialCostsByPartNoYear: totalInhouseCostsByYear } = calcTotalMaterialCostsByPartNoWithMonthlyData(
        quantitiesByPartNoMonth, inhouseMaterialCostsByMonth, yearMonthListByFiscalYear
      );
      const roundTotalInhouseCostsByPartNoYear = totalInhouseCostsByYear.map(({ partNo, totalMaterialCosts }) => ({
        partNo,
        totalMaterialCosts: totalMaterialCosts.map(price =>
          reviewDate
            ? divideValue(price)
            : divideValue(convertCurrency(price, localCurrency, dispCurrency, currencyData))
        )
      }));
      setInhouseTotalMaterialCostsByPartNo(roundTotalInhouseCostsByPartNoYear);

      // 材料費(内製のみ) スループット額
      const inhouseThroughputsByPartNo = mergeSalesAndSubtractCosts(mergePartNoSalesByYear, roundTotalInhouseCostsByPartNoYear);
      setInhouseThroughputsByPartNo(inhouseThroughputsByPartNo);

      // 材料費(内製のみ) スループット率
      const inhouseThroughputRatesByPartNo = calcThroughputRate(inhouseThroughputsByPartNo, mergePartNoSalesByYear);
      setInhouseThroughputRatesByPartNo(inhouseThroughputRatesByPartNo);

      // PartNo毎の材料内製比率
      const partNoInhouseRatios = calculateInhouseRatioByPartNo(partNoData);

      // PartNoごとの年度別データを計算
      const partNoDataMap = new Map<string, any>();
      const totalYearlyData: TotalYearlyData = {
        totalSales: {},
        totalMaterialCost: {},
        totalThroughput: {},
        appliedHeadOfficeSalesManagementRatio: {},
        ebitda: {},
        capexRow: {},
        cumulativeEbitdaAndCapex: {},
        depreciation: {},
        depreciationDcf: {},
        ebit: {},
        nopat: {},
        undepreciated: {},
        roic: {},
        tax: {},
        fcf: {},
        pv: {},
        dcf: {},
      };

      // 内製比率を格納
      quantitiesByPartNoYear.forEach((item) => {
        const partNo = item.partNo;

        const partData = partNoDataMap.get(item.partNo) || {
          inhouseAvgRatio: null,
        };

        const match = partNoInhouseRatios.find(item => item.partNo === partNo);
        partData.inhouseAvgRatio = match ? match.inhouseAvgRatio : new Decimal(0);
        partNoDataMap.set(item.partNo, partData);
      });

      // PartNoごとの販売通貨情報を格納
      if (quantityAndPriceTables) {
        quantityAndPriceTables.forEach(item => {
          const currencyArray = item.currencyAndRatio;

          // 既存のpartDataを取得、なければ初期化
          const partData = partNoDataMap.get(item.partNo) || {
            currencyAndRatoi: null
          };

          partData.currencyAndRatio = currencyArray;

          // Mapに保存
          partNoDataMap.set(item.partNo, partData)
        });
      }

      // -----------------------------
      // 設備
      // -----------------------------
      // 設備金額の為替変換
      // 振り返りモード: convertWithRate を渡して各設備の acceptanceMonth の実績レートで変換
      // 通常モード: 未指定で固定レート（計画レート）
      let convEquipmentData = convertEquipmentData(equipmentData, dispCurrency, currencyData, reviewDate ? convertWithRate : undefined);
      convEquipmentData = convEquipmentData.map((item, index) => {
        const decimalAmount = new Decimal(item.amount || 0);
        const convertedAmount = divideValue(decimalAmount);
        return {
          ...item,
          amount: convertedAmount.toString(), // 変換後の金額を設定
        };
      });
      setConvEquipmentAmounts(convEquipmentData);

      // -----------------------------
      // 回収期間
      // -----------------------------
      const originalProfitAndLoss = projectData.originalProfitAndLoss ? JSON.parse(projectData.originalProfitAndLoss) : [];
      const collectionPeriod = originalProfitAndLoss?.RecoverySimulationByYear?.collectionPeriod;
      if (collectionPeriod) {
        setRecoveryPeriod(collectionPeriod);
      }

      // -----------------------------
      // グラフデータ
      // -----------------------------
      // グラフデータの小数点表示
      const graphDecimalCurrency = hasDecimal(dispCurrency);
      // 販売単価と材料単価（当初計画）
      const mergedChartDataOriginal = projectData.mergedChartDataOriginal ? JSON.parse(projectData.mergedChartDataOriginal) : null;
      if (mergedChartDataOriginal) {
        const convMergedChartDataOriginal = convertChartData(mergedChartDataOriginal, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setMergedChartDataOriginal(convMergedChartDataOriginal);
      }
      // 販売単価と材料単価（ダウンサイド）
      const mergedChartDataModified = projectData.mergedChartDataModified ? JSON.parse(projectData.mergedChartDataModified) : null;
      if (mergedChartDataModified) {
        const convMergedChartDataModified = convertChartData(mergedChartDataModified, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setMergedChartDataModified(convMergedChartDataModified);
      }
      // 売上グラフデータ（当初計画）
      const salesChartData = projectData.salesChartData ? JSON.parse(projectData.salesChartData) : null;
      if (salesChartData) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(salesChartData, [
          "common.chart.salesTotal",
          "common.chart.operatingProfit",
          "common.chart.operatingProfitRate"
        ], "Original");

        setRawSalesChartData(salesChartData);  // raw版を保持（営業利益合計の合計→変換方式で使用）
        const convSalesChartData = convertChartData(salesChartData, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setSalesChartData(convSalesChartData);
      }
      // 売上グラフデータ（ダウンサイド）
      const salesChartDataModified = projectData.salesChartDataModified ? JSON.parse(projectData.salesChartDataModified) : null;
      if (salesChartDataModified) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(salesChartDataModified, [
          "common.chart.salesTotal",
          "common.chart.operatingProfit",
          "common.chart.operatingProfitRate"
        ], "Modified");

        setRawSalesChartDataModified(salesChartDataModified);
        const convSalesChartDataModified = convertChartData(salesChartDataModified, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setSalesChartDataModified(convSalesChartDataModified);
      }
      // 売上グラフデータ（プロジェクト全体投資回収）
      const salesChartDataAll = projectData.salesChartDataAll ? JSON.parse(projectData.salesChartDataAll) : null;
      if (salesChartDataAll) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(salesChartData, [
          "common.chart.salesTotal",
          "common.chart.operatingProfit",
          "common.chart.operatingProfitRate"
        ]);
        setRawSalesChartDataAll(salesChartDataAll);
        const convSalesChartDataAll = convertChartData(salesChartDataAll, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setSalesChartDataAll(convSalesChartDataAll);
      }
      // 売上グラフデータ（当初投資・回収用）
      const salesChartDataPast = projectData.salesChartDataPast ? JSON.parse(projectData.salesChartDataPast) : null;
      if (salesChartDataPast) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(salesChartData, [
          "common.chart.salesTotal",
          "common.chart.operatingProfit",
          "common.chart.operatingProfitRate"
        ]);
        setRawSalesChartDataPast(salesChartDataPast);
        const convSalesChartDataPast = convertChartData(salesChartDataPast, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setSalesChartDataPast(convSalesChartDataPast);
      }
      // 増分キャッシュフロー（当初計画）
      const combinedChartData = projectData.combinedChartData ? JSON.parse(projectData.combinedChartData) : null;
      if (combinedChartData) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(combinedChartData, ["common.chart.recoveryChartOriginal"]);
        const convCombinedChartData = convertChartData(combinedChartData, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setCombinedChartData(convCombinedChartData);
      }
      // 増分キャッシュフロー（ダウンサイド）
      const combinedChartDataModified = projectData.combinedChartDataModified ? JSON.parse(projectData.combinedChartDataModified) : null;
      if (combinedChartDataModified) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(combinedChartDataModified, ["common.chart.recoveryChartModified"]);
        const convCombinedChartDataModified = convertChartData(combinedChartDataModified, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setCombinedChartDataModified(convCombinedChartDataModified);
      }
      // 投資回収（プロジェクト全体投資回収）
      const combinedChartDataAll = projectData.combinedChartDataAll ? JSON.parse(projectData.combinedChartDataAll) : null;
      if (combinedChartDataAll) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(combinedChartDataAll, ["common.chart.recoveryChart"]);
        const convCombinedChartDataAll = convertChartData(combinedChartDataAll, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setCombinedChartDataAll(convCombinedChartDataAll);
      }
      // 投資回収（当初投資・回収用）
      const combinedChartDataPast = projectData.combinedChartDataPast ? JSON.parse(projectData.combinedChartDataPast) : null;
      if (combinedChartDataPast) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(combinedChartDataPast, ["common.chart.recoveryChart"]);
        const convCombinedChartDataPast = convertChartData(combinedChartDataPast, localCurrency, dispCurrency, currencyData, graphDecimalCurrency);
        setCombinedChartDataPast(convCombinedChartDataPast);
      }

      // 保存済み設定グラフデータの通貨換算
      const savedConfigSalesChartData = projectData.salesChartDataForSavedConfig ? JSON.parse(projectData.salesChartDataForSavedConfig) : [];
      const savedConfigCombinedChartData = projectData.combinedChartDataForSavedConfig ? JSON.parse(projectData.combinedChartDataForSavedConfig) : [];
      const savedConfigMergedChartData = projectData.mergedChartDataForSavedConfig ? JSON.parse(projectData.mergedChartDataForSavedConfig) : [];

      const convertSavedConfigChartData = (configArray: { configId: string; data: any }[]) => {
        return configArray.map(item => ({
          ...item,
          data: convertChartData(item.data, localCurrency, dispCurrency, currencyData, graphDecimalCurrency)
        }));
      };

      if (savedConfigSalesChartData.length > 0) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(savedConfigSalesChartData,
          [
            "common.chart.salesTotal",
            "common.chart.operatingProfit",
            "common.chart.operatingProfitRate"
          ]);
        const converted = convertSavedConfigChartData(savedConfigSalesChartData);
        setSalesChartDataForSavedConfig(prev =>
          prev.map(item => {
            const convertedItem = converted.find(c => c.configId === item.configId);
            return convertedItem ? { ...item, data: convertedItem.data } : item;
          })
        );
      }

      if (savedConfigCombinedChartData.length > 0) {
        // チャートデータラベル多言語対応
        updateSimulationChartLabels(savedConfigCombinedChartData, ["common.chart.recoveryChartModified"]);
        const converted = convertSavedConfigChartData(savedConfigCombinedChartData);
        setCombinedChartDataForSavedConfig(prev =>
          prev.map(item => {
            const convertedItem = converted.find(c => c.configId === item.configId);
            return convertedItem ? { ...item, data: convertedItem.data } : item;
          })
        );
      }

      if (savedConfigMergedChartData.length > 0) {
        const converted = convertSavedConfigChartData(savedConfigMergedChartData);
        setMergedChartDataForSavedConfig(prev =>
          prev.map(item => {
            const convertedItem = converted.find(c => c.configId === item.configId);
            return convertedItem ? { ...item, data: convertedItem.data } : item;
          })
        );
      }

      setPartNoDataMap(partNoDataMap);
      setTotalYearlyData(totalYearlyData);
    };
    calculatePartNoData(projectData, partNoData, equipmentAmounts);

  }, [projectData, partNoData, equipmentAmounts, selectedCurrency, quantityAndPriceTables]);

  // 関数宣言
  const adjustArrayLength = (array: Decimal[], max: number, defaultValue: Decimal) => {
    const result = [...array];
    // 配列の長さを調整
    while (result.length < max) {
      result.push(new Decimal(defaultValue));
    }
    return result;
  };

  // /** 回収期間の計算 */
  // const calculateRecoveryPeriod = (
  //   startDate: string,
  // ): void => {
  //   let recoveryYear = 0;
  //   let recoveryMonth = 0;
  //   let recoveryPeriod = "";

  //   const numMonthOfRecoveryOriginals = projectData?.monthOfRecoveryOriginal
  //     ?.split(",")
  //     .map((str) => Number(str.trim()))
  //     .filter((num) => !isNaN(num)) || [];

  //   if (numMonthOfRecoveryOriginals.length > 0) {
  //     numMonthOfRecoveryOriginals.forEach((month, i) => {
  //       const startDateObj = dayjs(startDate);
  //       const recoveryPeriodObj = startDateObj.add(month, 'month');
  //       recoveryYear = recoveryPeriodObj.diff(startDateObj, 'year');
  //       recoveryMonth = recoveryPeriodObj.diff(startDateObj.add(recoveryYear, 'year'), 'month');
  //       // 回収期間を設定
  //       if (recoveryYear >= 0 && recoveryMonth > 0) {
  //         recoveryPeriod = i > 0 ? recoveryPeriod + ", " : "";
  //         recoveryPeriod = recoveryPeriod + `${recoveryYear}年${recoveryMonth.toString().padStart(2, '0')}ヵ月`;
  //       } else if (recoveryYear >= 0 && recoveryMonth === 0) {
  //         recoveryPeriod = i > 0 ? recoveryPeriod + ", " : "";
  //         recoveryPeriod = recoveryPeriod + `${recoveryYear - 1}年12ヵ月`; // その年の12月に回収できた場合
  //       }
  //     });
  //   }
  //   setRecoveryPeriod(recoveryPeriod);
  // };

  // --------- Simulation関連 ---------
  /**
   * BOMデータ取得
   * @param projectId プロジェクトID
   */
  const getBomByProjectId = async (projectId: string): Promise<BomData[]> => {
    const functionName = "getBomByProjectId";
    try {
      const response = await WebAPI.request({
        query: `
          query getBomByProjectId($projectId: String, $reviewDate: String) {
            getBomByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              partNo
              seq
              level
              isCustomerSpecifiedParts
              purchaseCurrencyId
              amountMonthly
              cycleTimeMonthly
              yieldMonthly
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });
      if (!response?.data[functionName]) {
        console.log(`${functionName} データなし`);
        return [];
      }
      return response?.data[functionName];
    } catch (error) {
      console.error(`${functionName} エラー:`, error);
      return [];
    }
  };

  /**
   * タイプがlineのものを表示する。
   * @param chart チャート
   * @returns ラベル
   */
  const generateLabels = useCallback((chart: any): LegendItem[] => {
    return chart.data.datasets.filter((dataset: any) => dataset.type === "line").map((dataset: any, index: number) => {
      return {
        text: dataset.label,
        fillStyle: dataset.backgroundColor,
        strokeStyle: dataset.borderColor,
        hidden: !chart.isDatasetVisible(index),
        datasetIndex: index
      }
    });
  }, []);

  /**
   * スケール算出
   * @param data1 当初計画データ
   * @param data2 ダウンサイドデータ
   * @param isMinZero true：最小値を0とする。false：data1とdata2の最小値
   * @returns
   */
  const computeCommonScale = useCallback((data1: any, data2: any, isMinZero: boolean) => {
    // 損益非表示モード等でチャートデータが未生成の場合、datasets/labelsが存在しないことがあるため防御的に補完する
    data1 = Array.isArray(data1?.datasets) ? data1 : { ...data1, datasets: [] };
    data2 = Array.isArray(data2?.datasets) ? data2 : { ...data2, datasets: [] };
    const allData = [
      ...data1.datasets.flatMap((ds: any) => ds.data.filter((v: any) => v !== null)),
      ...data2.datasets.flatMap((ds: any) => ds.data.filter((v: any) => v !== null))
    ];
    const label1: any[] = Array.isArray(data1.labels) ? data1.labels : [];
    const label2: any[] = Array.isArray(data2.labels) ? data2.labels : [];
    const commonLabels = label1.length >= label2.length ? label1 : label2;
    const { yMin, yMax } = calcYAxisRange(allData, isMinZero);
    return { yMin, yMax, commonLabels };
  }, []);

  /**
   * スケール算出
   * @param data1 当初計画データ
   * @param data2 ダウンサイドデータ
   * @param isMinZero true：最小値を0とする。false：data1とdata2の最小値
   * @returns
   */
  const computeCommonScaleSalesChart = (data1: any, data2: any, isMinZero: boolean) => {
    // 損益非表示モード等でチャートデータが未生成の場合、datasets/labelsが存在しないことがあるため防御的に補完する
    data1 = Array.isArray(data1?.datasets) ? data1 : { ...data1, datasets: [] };
    data2 = Array.isArray(data2?.datasets) ? data2 : { ...data2, datasets: [] };
    const allData = [
      ...data1.datasets.flatMap((ds: any) => ds.data.filter((v: any) => v !== null)),
      ...data2.datasets.flatMap((ds: any) => ds.data.filter((v: any) => v !== null))
    ];
    const label1: any[] = Array.isArray(data1.labels) ? data1.labels : [];
    const label2: any[] = Array.isArray(data2.labels) ? data2.labels : [];
    const commonLabels = label1.length >= label2.length ? label1 : label2;
    const { yMin, yMax } = calcYAxisRange(allData, isMinZero);
    return { yMin, yMax, commonLabels };
  };

  // ===== 投資回収チャート options のメモ化（セレクタ切替時の Chart.update 抑制）=====
  // 元 JSX 行 9571 / 9636 / 9748 / 9831 の IIFE 内で毎レンダー新規生成されていた options を
  // コンポーネントトップレベルの useMemo に集約。データ未取得時は null を返す。
  // 依存に含めるリアクティブ値はすべて列挙する（漏れると古い値で固定化される）。

  /** chart1/2 用 yMin/yMax（プロジェクト全体投資回収・当初投資・回収の共通スケール） */
  const recoveryAllPastScale = useMemo(() => {
    if (isProfitLossHiddenMode || !combinedChartDataAll || !combinedChartDataPast) return null;
    return computeCommonScale(combinedChartDataAll, combinedChartDataPast, false);
  }, [isProfitLossHiddenMode, combinedChartDataAll, combinedChartDataPast, computeCommonScale]);

  /** chart1 用 annotations（プロジェクト全体投資回収） */
  const recoveryAllAnnotations = useMemo(() => {
    if (!combinedChartDataAll) return {};
    return generateRecoveryPointAnnotationOptions(projectData?.monthOfRecoveryAll, combinedChartDataAll.labels);
  }, [combinedChartDataAll, projectData?.monthOfRecoveryAll]);

  /** chart2 用 annotations（当初投資・回収） */
  const recoveryPastAnnotations = useMemo(() => {
    if (!combinedChartDataPast) return {};
    return generateRecoveryPointAnnotationOptions(projectData?.monthOfRecoveryPast, combinedChartDataPast.labels);
  }, [combinedChartDataPast, projectData?.monthOfRecoveryPast]);

  /** chart1 用 options（プロジェクト全体投資回収・datasets 224本級） */
  const recoveryAllOptions = useMemo<any>(() => {
    if (!combinedChartDataAll || !recoveryAllPastScale) return null;
    const { yMin, yMax } = recoveryAllPastScale;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: { enabled: true },
        legend: { labels: { generateLabels: generateLabels } },
        annotation: {
          annotations: {
            zeroLine: { type: "line", yMin: 0, yMax: 0, borderColor: "red", borderDash: [4, 4], borderWidth: 2 },
            ...recoveryAllAnnotations,
            ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose, Object.entries(recoveryAllAnnotations).length),
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: { offset: true, ticks: { callback: (index: any) => formatYearMonthByLocale(combinedChartDataAll.labels[index]) } },
        y: { min: yMin, max: yMax, ticks: { format: { notation: "standard" } } },
      },
    };
  }, [combinedChartDataAll, recoveryAllPastScale, recoveryAllAnnotations, investmentStartYearMonth, projectData?.purpose, generateLabels]);

  /** chart2 用 options（当初投資・回収・datasets 112本級） */
  const recoveryPastOptions = useMemo<any>(() => {
    if (!combinedChartDataPast || !recoveryAllPastScale) return null;
    const { yMin, yMax } = recoveryAllPastScale;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: { enabled: true },
        legend: { labels: { generateLabels: generateLabels } },
        annotation: {
          annotations: {
            zeroLine: { type: "line", yMin: 0, yMax: 0, borderColor: "red", borderDash: [4, 4], borderWidth: 2 },
            ...recoveryPastAnnotations,
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: { offset: true, ticks: { callback: (index: any) => formatYearMonthByLocale(combinedChartDataPast.labels[index]) } },
        y: { min: yMin, max: yMax, ticks: { format: { notation: "standard" } } },
      },
    };
  }, [combinedChartDataPast, recoveryAllPastScale, recoveryPastAnnotations, generateLabels]);

  /** chart3/4 用 yMin/yMax/commonLabels（増産投資分回収 当初計画・ダウンサイドの共通スケール） */
  const investmentRecoveryScale = useMemo(() => {
    if (isProfitLossHiddenMode || !combinedChartData || !combinedChartDataModified) return null;
    return computeCommonScale(combinedChartData, combinedChartDataModified, false);
  }, [isProfitLossHiddenMode, combinedChartData, combinedChartDataModified, computeCommonScale]);

  /** chart3 用 annotations（当初計画） */
  const investmentRecoveryOriginalAnnotations = useMemo(() => {
    if (!investmentRecoveryScale) return {};
    return generateRecoveryPointAnnotationOptions(projectData?.monthOfRecoveryOriginal, investmentRecoveryScale.commonLabels);
  }, [investmentRecoveryScale, projectData?.monthOfRecoveryOriginal]);

  /** chart4 用 annotations（ダウンサイド） */
  const investmentRecoveryModifiedAnnotations = useMemo(() => {
    if (!investmentRecoveryScale) return {};
    return generateRecoveryPointAnnotationOptions(projectData?.monthOfRecoveryModified, investmentRecoveryScale.commonLabels);
  }, [investmentRecoveryScale, projectData?.monthOfRecoveryModified]);

  /** chart3 用 data（spread+commonLabels 上書きの結果） */
  const investmentRecoveryOriginalData = useMemo(() => {
    if (!combinedChartData || !investmentRecoveryScale) return null;
    return { ...combinedChartData, labels: investmentRecoveryScale.commonLabels };
  }, [combinedChartData, investmentRecoveryScale]);

  /** chart4 用 data */
  const investmentRecoveryModifiedData = useMemo(() => {
    if (!combinedChartDataModified || !investmentRecoveryScale) return null;
    return { ...combinedChartDataModified, labels: investmentRecoveryScale.commonLabels };
  }, [combinedChartDataModified, investmentRecoveryScale]);

  /** chart3 用 options（増産投資分回収 当初計画・datasets 113本級） */
  const investmentRecoveryOriginalOptions = useMemo<any>(() => {
    if (!investmentRecoveryScale) return null;
    const { yMin, yMax, commonLabels } = investmentRecoveryScale;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: { enabled: true },
        legend: { labels: { generateLabels: generateLabels } },
        annotation: {
          annotations: {
            zeroLine: { type: "line", yMin: 0, yMax: 0, borderColor: "red", borderDash: [4, 4], borderWidth: 2 },
            ...investmentRecoveryOriginalAnnotations,
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: { offset: true, ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) } },
        y: { min: yMin, max: yMax, ticks: { format: { notation: "standard" } } },
      },
      layout: { padding: { bottom: 20 } },
    };
  }, [investmentRecoveryScale, investmentRecoveryOriginalAnnotations, generateLabels]);

  /** chart4 用 options（増産投資分回収 ダウンサイド・datasets 113本級） */
  const investmentRecoveryModifiedOptions = useMemo<any>(() => {
    if (!investmentRecoveryScale) return null;
    const { yMin, yMax, commonLabels } = investmentRecoveryScale;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: { enabled: true },
        legend: { labels: { generateLabels: generateLabels } },
        annotation: {
          annotations: {
            zeroLine: { type: "line", yMin: 0, yMax: 0, borderColor: "red", borderDash: [4, 4], borderWidth: 2 },
            ...investmentRecoveryModifiedAnnotations,
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: { offset: true, ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) } },
        y: { min: yMin, max: yMax, ticks: { format: { notation: "standard" } } },
      },
      layout: { padding: { bottom: 20 } },
    };
  }, [investmentRecoveryScale, investmentRecoveryModifiedAnnotations, generateLabels]);

  // --------- モーダル関連 ---------
  /**
   * 更新前後サマリーのチェック結果を計算する
   * 設備のprocessIdと更新前後サマリーのproductionLineIdが一致するかチェック
   * @param purpose 目的（引数なしの場合はステート変数を使用）
   * @param updateSummary 更新前後サマリーデータ（引数なしの場合はステート変数を使用）
   * @param equipment 設備データ（引数なしの場合はステート変数を使用）
   * @param applicationFormType 申請書様式（引数なしの場合はステート変数を使用）
   * @returns {boolean} true: 一致 false: 不一致または更新前後サマリー未入力（詳細様式のみ）
   */
  const calcUpdateSummaryMatch = (
    purpose?: string,
    updateSummary?: any[],
    equipment?: EquipmentType[],
    applicationFormType?: string | null
  ): boolean => {
    const targetPurpose = purpose ?? projectData?.purpose;
    const targetUpdateSummary = updateSummary ?? updateSummaryData;
    const targetEquipment = equipment ?? equipmentAmounts;
    const targetApplicationFormType = applicationFormType ?? projectData?.applicationFormType;

    if (targetPurpose !== PurposeType.UpdateAndOther.code) {
      return true; // 更新投資以外は常にtrue
    }
    if (targetUpdateSummary?.length && targetEquipment?.length) {
      // 全件登録必須チェック（設備の全processIdが更新前後サマリーに一致していないと通過できない仕様）は
      // 「1件以上必須チェック」のみ残す方針のためコメントアウト
      // 設備に紐づくプロセスIDを取得（ユニーク）
      // const processIds = [...new Set(targetEquipment.map((eq: any) => eq.processId).filter((id: any) => id))];
      // 更新前後サマリーのプロセスIDを取得
      // const updateSummaryProcessIds = targetUpdateSummary.map((data: any) => data.productionLineId);
      // 両方のID配列が完全一致かチェック
      // return (
      //   processIds.length === updateSummaryProcessIds.length &&
      //   processIds.every((id: any) => updateSummaryProcessIds.includes(id))
      // );
      // 上記の全件一致チェックは撤廃し、1件以上登録されていればtrueを返す
      return true;
    } else if (!targetUpdateSummary?.length) {
      // 更新投資で更新前後サマリーがない場合、簡易様式は許容・詳細様式は必須
      return isSimpleApplicationForm(targetPurpose, targetApplicationFormType);
    }
    return true;
  };

  /**
   * 必要情報をチェックし、ない場合は、トースト表示する。
   * @param projectData プロジェクト情報
   * @param capexPartNo パーツNo情報（数量・販売単価と材料費）
   * @param equipmentAmounts 設備情報
   */
  const requiredDataNotice = (projectData: ProjectWithRdsData, capexPartNo: PartNo[], equipmentAmounts: EquipmentType[], allMatch: any, updateSummaryMatch: boolean) => {
    let displayName = null;
    const isSimple = isSimpleApplicationForm(projectData.purpose, projectData.applicationFormType);
    // 損益非表示モードでは損益試算データを作成しないため、損益試算のチェックは対象外とする
    const isProfitLossHiddenForCheck = isProfitLossHidden(projectData.purpose, projectData.applicationFormType, projectData.profitLossDisplayType);

    if (!projectData.companyPlantId) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationBasicInfo');
    } else if (!isSimple && (capexPartNo.some((p: PartNo) => !p.quantityAndPrice) || !projectData.currencyId)) {
      displayName = labelQuantityAndPrice;
    } else if (!projectData.customerNationality) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationCustomerAgreement');
    } else if (!projectData.customerSchedule) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationSchedule');
    } else if (!isSimple && capexPartNo.some((p: PartNo) => !p.materialCost || !p.materialCostWithInhouseProfit)) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationManufacturing');
    } else if (!equipmentAmounts || equipmentAmounts.length === 0) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationEquipment');
    } else if (!isProfitLossHiddenForCheck && !projectData.originalProfitAndLoss) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationProfitAndLoss');
    } else if (!isSimple && !projectData.quantitiesChartDataOriginal) {
      // 目的が「新製品」の場合は「Simulation（新製品製造）」、それ以外は「Simulation（生産性向上）」
      displayName = projectData.purpose === PurposeType.NewProduct.code
        ? t('features.InvestmentScreening.ReportAndDraft.message.validationSimNewProduct')
        : t('features.InvestmentScreening.ReportAndDraft.message.validationSimProductivity');
    } else if (projectData.purpose === PurposeType.IncreasedProduction.code && !allMatch) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationBenchmark');
    } else if (!updateSummaryMatch) {
      displayName = t('features.InvestmentScreening.ReportAndDraft.message.validationUpdateSummary');
    }
    if (displayName) {
      toast({
        title: TITLE,
        description: `${displayName}${REQUIRED_MESSAGE}`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    return true;
  }


  /** 申請書の確認モーダル表示 */
  const handleModalOpen: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    event.currentTarget.blur();

    // どの部署からの確認かを保持
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);

    if (!projectData) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ReportAndDraft.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    // 画面間入力チェック
    if (!requiredDataNotice(projectData, partNoData, equipmentAmounts, true, calcUpdateSummaryMatch())) {
      return;
    }

    // 確認モーダル表示
    onOpenConfirmModal();
  }

  /**
   * 確認モーダルで「確認」ボタン押下
   */
  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // フォーカスを外す
    e.currentTarget.blur();

    try {
      // ワークフローテーブルを更新
      await updateConfirm(
        WebAPI,
        projectId,
        WorkflowId.ReportAndDraft,
        employeeId,
        confirmSource,
        "true",
        reviewDate,
      );
      // 確認担当者JSONを取得
      const confirmor = await getConfirmor(
        WebAPI,
        projectId,
        reviewDate,
      );
      setConfirmor(confirmor);
      toast({
        title: t('common.label.success'),
        description: t('features.InvestmentScreening.ReportAndDraft.message.confirmSet'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      // TODO 処理失敗時は暫定でスルーする
    } finally {
      onCloseConfirmModal();
    }
  }

  /**
   * 承認依頼モーダルで「承認依頼」ボタン押下
   */
  const handleApprovalReq = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // フォーカスを外す
    e.currentTarget.blur();
    console.log("handleApprovalReq:start");
    onCloseApprovalReqModal();
    WebAPI.setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 100));

    try {

      // ---- BOMスナップショット保存 ----
      const partNoList = partNoData.map(p => p.partNo).filter(Boolean) as string[];
      if (partNoList.length > 0) {
        console.log("handleApprovalReq: BOM snapshot start");

        // Google SpreadsheetをExcelとしてエクスポート
        // 振り返りモード時は review_{reviewDate}/BomSnapshot 配下に保存
        // 年月列はプロジェクト期間（startDate〜endDate）ベースでBomInfoListの表示と完全一致
        const snapshotFiles = await exportBomSnapshotFiles(
          projectId,
          partNoList,
          WebAPI.request,
          projectData?.projectStartDate ?? "",
          projectData?.projectEndDate ?? "",
          reviewDate ?? undefined,
          partNoData,
        );

        // 既存のBomSnapshotファイルのキーを取得（再申請時の古いファイル削除用）
        // 振り返りモード時は review_{reviewDate}/BomSnapshot 配下のみを対象とする（過去世代は誤削除しない）
        const bomSnapshotFolder = reviewDate
          ? `${projectId}/review_${reviewDate}/BomSnapshot`
          : `${projectId}/BomSnapshot`;
        let existingSnapshotKeys: string[] = [];
        try {
          const existingResult = await WebAPI.request({
            query: `
              query getFileInfoFromS3($folderName: String, $withUrl: Boolean, $reviewDate: String) {
                getFileInfoFromS3(folderName: $folderName, withUrl: $withUrl, reviewDate: $reviewDate) {
                  folderName
                  files { key }
                }
              }
            `,
            variables: { folderName: bomSnapshotFolder, withUrl: false, ...(reviewDate && { reviewDate }) },
          });
          const folders = existingResult?.data?.getFileInfoFromS3 || [];
          existingSnapshotKeys = folders.flatMap((f: any) => f.files.map((file: any) => file.key));
        } catch (e) {
          console.warn("handleApprovalReq: Failed to fetch existing BomSnapshot keys", e);
        }

        // S3アップロード（既存ファイルを全削除してから新規アップロード）
        if (snapshotFiles.length > 0) {
          const uploadResult = await WebAPI.request({
            query: `
              mutation uploadMultipleFilesToS3($input: UploadMultipleFilesToS3!) {
                uploadMultipleFilesToS3(input: $input) { status, message }
              }
            `,
            variables: {
              input: {
                files: snapshotFiles,
                deletedKeys: existingSnapshotKeys,
                ...(reviewDate && { reviewDate }),
              },
            },
          });

          if (uploadResult?.errors) {
            throw new Error("Failed to save BOM snapshot.");
          }
        }
        console.log("handleApprovalReq: BOM snapshot complete");
      }

      // ---- 承認依頼処理 ----
      const convertApproverListToInput = (approverList: ApproverList[]): { employeeId: string, flowId: string, divisionId: string }[] => {
        return approverList.map(approver => ({
          employeeId: approver.employeeId || "",
          flowId: approver.flowId || "",
          divisionId: approver.organizationId || ""
        }));
      };

      // DynamoDBの承認者設定からapproverDataを構築
      let approverData: { employeeId: string, flowId: string, divisionId: string }[] = [];

      // 承認者（製品統括/事業部長/営業本部長/事業本部長）
      const categoryToFlowId: Record<string, string> = {
        productDirector: WorkflowId.ProductDirector,
        divisionManager: WorkflowId.BusinessManager,
        salesHeadquarters: WorkflowId.SalesHQManager,
        businessHeadquarters: WorkflowId.BusinessHQManager,
      };
      if (projectData?.approverAssignees) {
        try {
          const assignees = JSON.parse(projectData.approverAssignees);
          for (const category of assignees) {
            const flowId = categoryToFlowId[category.categoryId];
            if (flowId && category.employees) {
              for (const emp of category.employees) {
                approverData.push({ employeeId: emp.employeeId, flowId, divisionId: "" });
              }
            }
          }
        } catch (e) { console.warn('approverAssignees parse error:', e); }
      }

      // CC担当者（要の場合のみ）
      if (projectData?.crossCheckRequired && projectData?.crossCheckAssignees) {
        try {
          const assignees = JSON.parse(projectData.crossCheckAssignees);
          for (const category of assignees) {
            if (category.employees) {
              for (const emp of category.employees) {
                approverData.push({ employeeId: emp.employeeId, flowId: WorkflowId.CrossCheck, divisionId: category.categoryId || "" });
              }
            }
          }
        } catch (e) { console.warn('crossCheckAssignees parse error:', e); }
      }

      // 資材担当（要の場合のみ）
      if (projectData?.materialOfficerRequired && projectData?.materialOfficerAssignees) {
        try {
          const assignees = JSON.parse(projectData.materialOfficerAssignees);
          for (const category of assignees) {
            if (category.employees) {
              for (const emp of category.employees) {
                approverData.push({ employeeId: emp.employeeId, flowId: WorkflowId.MaterialOfficer, divisionId: category.categoryId || "" });
              }
            }
          }
        } catch (e) { console.warn('materialOfficerAssignees parse error:', e); }
      }

      // 承認者が未設定の場合は基本情報画面での登録を促す
      if (approverData.length === 0) {
        toast({
          title: t('features.InvestmentScreening.ReportAndDraft.toast.approvalRequest'),
          description: t('features.InvestmentScreening.ReportAndDraft.message.setApproverInBasicInfo'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // GraphQLクエリ定義
      const query = `
        mutation putApprover ($input: ApproverInput!) {
          putApprover(input: $input) {status, failedAddresses, sentCount}
        }
      `;

      // APIリクエスト実行
      const result = await WebAPI.request({
        query: query,
        variables: {
          input: {
            projectId: projectId,
            flowId: WorkflowId.ProductDirector,
            approverData: approverData,
            materialOfficerRequired: projectData?.materialOfficerRequired || false,
            crossCheckRequired: projectData?.crossCheckRequired || false,
            registrantOrganizationId: projectData?.registrantDivisionId || "",
            // 画面閲覧制限で営業に申請書を見せない案件は、営業の申請書確認を未確認チェックの対象外にする
            validFlowDivisionPairs: getValidFlowDivisionPairs(projectData?.purpose, !!reviewDate)
              .filter(p => !(hasSalesRestriction(parseViewRestriction(projectData?.screenViewRestriction))
                && p.flowId === WorkflowId.ReportAndDraft
                && p.divisionId === ComfirmDivisions.salesDivision)),
            applicationMonth: projectData?.applicationMonth || null,
            ...(reviewDate && { reviewDate }),
          }
        }
      });
      if (result?.data?.putApprover.status === 'success') {
        toast({
          title: t('features.InvestmentScreening.ReportAndDraft.toast.approvalMailSent'),
          description: t('features.InvestmentScreening.ReportAndDraft.message.approvalMailSent'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // ホーム画面に遷移
        navigate("/");
      } else if (result?.data?.putApprover.status === 'unConfirmed') {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ReportAndDraft.message.unconfirmedScreenExists'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });

      } else if (result?.data?.putApprover.status === 'mailSendFailed') {
        const failedAddresses = result?.data?.putApprover.failedAddresses;
        const sentCount = result?.data?.putApprover.sentCount;
        const sentPart = sentCount > 0 ? t('features.InvestmentScreening.ReportAndDraft.mailStatus.sentComplete', { count: sentCount }) : '';
        const failedDesc = failedAddresses?.length
          ? t('features.InvestmentScreening.ReportAndDraft.mailStatus.sendFailed', { sentPart, addresses: failedAddresses.join(', ') })
          : t('features.InvestmentScreening.ReportAndDraft.message.fetchMailFailed');
        toast({
          title: t('features.InvestmentScreening.ReportAndDraft.toast.approvalMailFailed'),
          description: failedDesc,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // 起案申請自体は成功しているのでホーム画面に遷移
        navigate("/");
      } else {
        throw new Error(result);
      }
    } catch (error) {
      console.error('handleApprovalReq:', error);
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      WebAPI.setLoading(false);
    }
  }

  /** 承認依頼モーダル表示 */
  const handleDraftingModalOpen: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    event.currentTarget.blur();

    if (!projectData) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ReportAndDraft.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    // 画面間入力チェック
    if (!requiredDataNotice(projectData, partNoData, equipmentAmounts, true, calcUpdateSummaryMatch())) {
      return;
    }

    // 起案申請前確認モーダル表示（クロスチェック・資材担当役員確認の状態を確認）
    onOpenPreCheckModal();
  }

  /**
   * 起案申請前確認モーダルで「確認済み」を押した後、承認依頼モーダルを表示
   */
  const handlePreCheckConfirmed = () => {
    onClosePreCheckModal();
    onOpenApprovalReqModal();
  }

  /**
   * 申請金額を返す
   * @param validEquipmentAmounts 設備投資金額
   * @param currencyName 通貨名
   * @returns 申請金額（設備投資額と設備投資総額）
   */
  const getRequestedAmount = (validEquipmentAmounts: { amount: string; applicationPlannedMonth?: string }[], currencyName: string) => {
    // 申請月度と申請予定月が一致する設備をフィルタリング
    const matchingEquipments = validEquipmentAmounts.filter(
      (item) => item.applicationPlannedMonth && item.applicationPlannedMonth === projectData?.applicationMonth
    );
    // 設備投資額（申請月度と一致する設備の合計）
    const investmentAmount = calculateTotalAmount(matchingEquipments as { amount: string }[]);
    // 設備投資総額（全設備の合計）
    const totalAmount = calculateTotalAmount(validEquipmentAmounts as { amount: string }[]);

    return `${formatNumber(investmentAmount, "WithDecimal")} ${divisorStr}${currencyName}${t('features.InvestmentScreening.ReportAndDraft.label.investmentTotalSuffix', { amount: formatNumber(totalAmount, "WithDecimal"), unit: divisorStr, currency: currencyName })}`;
  }

  // -----------------------------
  // PDFダウンロード
  // -----------------------------

  /**
   * PDF出力専用：振り返り3画面コンテンツ（pdfReviewRef配下）をブロック単位でキャプチャしPDFに追記する。
   * data-pdf-block 属性で区切られた各ブロックを個別に html-to-image し、addReviewBlockFlow で
   * 「詰め改ページ」方式で追加する（現ページに入るなら同ページ、入らないなら改ページ）。
   * state切替・PartNo順送りは不要（PdfReviewContent側で重複なし縦並べ済み）。
   */
  const capturePdfReview = async (pdf: jsPDF): Promise<void> => {
    if (!pdfReviewRef.current) return;
    // Chart.jsの描画完了待ち（animation:false 前提・rAF×2 + 短い setTimeout）
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    await new Promise<void>(resolve => setTimeout(resolve, 200));

    // Simulationと同様にChart.jsインスタンスをresize()して再描画を強制する。
    // html-to-image (toJpeg) はCanvasの内容をキャプチャする際に再描画済みの状態が必要なため。
    pdfReviewRef.current.querySelectorAll<HTMLCanvasElement>('canvas').forEach(canvas => {
      const chartInstance = ChartJS.getChart(canvas);
      if (chartInstance) {
        chartInstance.resize();
      }
    });
    // resize()による再描画の完了を待つ
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );

    const blocks = pdfReviewRef.current.querySelectorAll<HTMLElement>('[data-pdf-block]');
    const blockArr = Array.from(blocks);
    if (blockArr.length === 0) return;

    // 振り返り3画面の入口は1度だけ改ページしてカーソルを margin にリセット
    pdf.addPage();
    const cursor: ReviewFlowCursor = { y: 10 };

    // 「次のブロックの前で強制改ページ」フラグ（前ブロックの data-pdf-page-break="after" から伝播）
    let pendingPageBreak = false;
    for (const block of blockArr) {
      const dataUrl = await convertHtmlToImageJpeg(block, 1.5, 1466);
      if (!dataUrl) continue;
      const breakAttr = block.getAttribute('data-pdf-page-break');
      const breakBefore = pendingPageBreak || breakAttr === 'before' || breakAttr === 'both';
      addReviewBlockFlow(pdf, dataUrl, cursor, { pageBreakBefore: breakBefore });
      pendingPageBreak = breakAttr === 'after' || breakAttr === 'both';
    }
  };

  /**
   * PDFダウンロード実行
   */
  const handlePdfDownload = async () => {
    // ScheduleTable等のキャプチャ対象コンポーネントを軽量モード（Tooltip無し）で再描画してから
    // PDFキャプチャを実行する。再レンダリングをブラウザに反映させるため次のフレームまで待機。
    setIsPdfRendering(true);
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

    const isReviewMode = !!reviewDate;

    try {
      const pdf = await generatePDF(
        {
          project: {
            data: projectData,
            currencyData,
            selectedCurrency,
            rateType,
            ratePeriod,
            customerNationalityName,
            uploadedImageFileContent,
            productionLineData,
            monthList,
            scheduleItems,
            facilityItems,
            reviewItems,
            isSimpleMode,
            isProfitLossHiddenMode,
          },
          sales: {
            partNoData,
            partNoDataMap,
            quantityAndPriceTables,
            convQuantityAndPriceTables,
            moldTables,
            developmentCostTables,
            equipmentCostTables,
            pricingAgreementSentence,
          },
          equipment: {
            amounts: equipmentAmounts,
            convAmounts: convEquipmentAmounts,
            supplierData,
            processItemNameMap,
            processItemNoMap,
            processSeqMap,
          },
          profitAndLoss: {
            display: dispProfitAndLoss,
            totalYearlyData,
            maxYears,
            moldSalesByProjectYear,
            devSalesByProjectYear,
            equipmentSalesByProjectYear,
            updateData: displayUpdateInvestmentProfitLossData,
            rationalizationData: displayRationalizationInvestmentProfitLossData,
            updateSummaryData,
          },
          simulation: {
            productionCapacityProcessChartDataList,
            rationalizationSummaryChartDataList,
          },
          approval: {
            approverList,
            executiveManager,
            confirmedPersonsList,
          },
          checkList: {
            estimateData: estimateCheckListData,
            dataList: checkListDataList,
          },
        },
        {
          pdfRef,
          scheduleRef,
          equipmentFlowRef,
          reactFlowRef,
          benchmarkRefs,
          estimateCheckListRefs,
          checkListRefs,
          simulationRef,
          simulationRef2,
          simulationRef3,
        },
        {
          toast,
          uploadedFilesByFolder,
          fetchAllBase64FromS3,
        },
        // 振り返りモード時のみhooksを渡す。見積チェックリストの直前で振り返り3画面（PdfReviewContent）を挿入する。
        isReviewMode
          ? {
            beforeEstimateCheckList: async (pdf) => {
              // captureJobs 完了後に PdfReviewContent をマウントしてからキャプチャする。
              // isPdfReviewRendering を captureJobs 完了後に true にすることで、
              // captureJobs の DOM クローン対象に PdfReviewContent が含まれないようにする。
              setIsPdfReviewRendering(true);
              await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
              await capturePdfReview(pdf);
            },
          }
          : undefined,
      );
      // generatePDFは組み立て済みpdfインスタンスを返し、saveは呼び出し元で実行する。
      if (pdf) {
        pdf.save(`${projectData?.projectId}.pdf`);
      }
    } finally {
      setIsPdfRendering(false);
      setIsPdfReviewRendering(false);
    }
  };

  // 固定選択肢のコードと多言語キーの対応
  const QUANTITY_UNIT_KEYS = [
    { code: "unit", key: "features.InvestmentScreening.Equipment.options.quantityUnit.unit" },
    { code: "set",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.set" },
    { code: "pcs",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.pcs" },
  ] as const;

  /**
   * 【出力用】DB保存値（unit/set/pcs または自由入力）をスプレッドシート表示名に変換
   */
  const toSpreadsheetUnitLabel = (code?: string | null): string => {
    const targetCode = code || "unit"; // 初期値
    const matched = QUANTITY_UNIT_KEYS.find(item => item.code === targetCode);
    // 固定選択肢に一致すれば翻訳テキスト（例: "台"）、なければ自由入力値をそのまま返す
    return matched ? t(matched.key) : targetCode;
  };


  // -----------------------------
  // 画面部品
  // -----------------------------
  const priceAgreementWidth = "250px";
  /** 顧客との合意情報 */
  const renderPriceAgreement = () => {
    if (!pricingAgreementSentence) return null;

    const files = uploadedFilesByFolder.find(f => f.folderName === "PricingAgreementForm")?.files;

    return (
      <Box mt={6}>
        {/* 単価の取り決め */}
        {pricingAgreementSentence?.pricing && (
          <Flex align="center">
            <Text width={priceAgreementWidth}>{PricingAgreementCategory.pricing}</Text>
            <Text> : </Text>
            <Text ml={2}>{pricingAgreementSentence.pricing}</Text>
          </Flex>
        )}
        {/* 単価の取り決め 自由記入欄 */}
        {pricingAgreementSentence?.pricingComment && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.pricing} {t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.freeText')}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.pricingComment}</Text>
            </Box>
          </Flex>
        )}
        {/* 為替変動の取り決め */}
        {pricingAgreementSentence?.exchange && (
          <Flex align="center">
            <Text width={priceAgreementWidth}>{PricingAgreementCategory.exchange}</Text>
            <Text> : </Text>
            <Text ml={2}>{pricingAgreementSentence.exchange}</Text>
          </Flex>
        )}
        {/* 為替変動の取り決め 自由記入欄 */}
        {pricingAgreementSentence?.exchangeComment && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.exchange} {t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.freeText')}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.exchangeComment}</Text>
            </Box>
          </Flex>
        )}
        {/* 材料費変動の取り決め */}
        {(pricingAgreementSentence?.material || pricingAgreementSentence?.selectMaterial) && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.material}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.material}{pricingAgreementSentence.selectMaterial}</Text>
            </Box>
          </Flex>
        )}
        {/* 材料費変動の取り決め 自由記入欄 */}
        {pricingAgreementSentence?.materialComment && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.material} {t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.freeText')}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.materialComment}</Text>
            </Box>
          </Flex>
        )}
        {/* 数量の取り決め */}
        {pricingAgreementSentence?.quantity && (
          <Flex align="center">
            <Text width={priceAgreementWidth}>{PricingAgreementCategory.quantity}</Text>
            <Text> : </Text>
            <Text ml={2}>{pricingAgreementSentence.quantity}</Text>
          </Flex>
        )}
        {/* 数量未達時の取り決め */}
        {pricingAgreementSentence?.notReachedComment && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.quantityNotReached}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.notReachedComment}</Text>
            </Box>
          </Flex>
        )}
        {/* 数量の取り決め 自由記入欄 */}
        {pricingAgreementSentence?.quantityComment && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.quantity} {t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.freeText')}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.quantityComment}</Text>
            </Box>
          </Flex>
        )}
        {/* 競合の有無 */}
        {pricingAgreementSentence?.competitor && (
          <Flex align="center">
            <Text width={priceAgreementWidth}>{PricingAgreementCategory.competitor}</Text>
            <Text> : </Text>
            <Text ml={2}>{pricingAgreementSentence.competitor}</Text>
          </Flex>
        )}
        {/* 競合状態 */}
        {pricingAgreementSentence?.competitiorCondition && (
          <Flex align="center">
            <Text width={priceAgreementWidth}>{PricingAgreementCategory.competitiorCondition}</Text>
            <Text> : </Text>
            <Text ml={2}>{pricingAgreementSentence.competitiorCondition}</Text>
          </Flex>
        )}
        {/* 競合会社選択 */}
        {pricingAgreementSentence?.selectCompetitor && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.selectedCompetitors}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.selectCompetitor}</Text>
            </Box>
          </Flex>
        )}
        {/* 当社シェア */}
        {pricingAgreementSentence?.sharePercentage != null && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.companyShare')}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.sharePercentage}%</Text>
            </Box>
          </Flex>
        )}
        {/* 当社及び競合のシェア */}
        {pricingAgreementSentence?.shareOfCompetitorAndCompany && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.shareOfCompetitorAndCompany}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{pricingAgreementSentence.shareOfCompetitorAndCompany}</Text>
            </Box>
          </Flex>
        )}
        {/* その他の取り決め */}
        {pricingAgreementSentence?.otherAgreements && pricingAgreementSentence.otherAgreements.length > 0 && (
          pricingAgreementSentence.otherAgreements.map((item: { title: string; content: string }, index: number) => (
            (item.title || item.content) && (
              <Flex key={index} alignItems="flex-start">
                <Text flexShrink={0} width={priceAgreementWidth}>{item.title || t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.noTitle')}</Text>
                <Text> : </Text>
                <Box ml={2} mt={0} whiteSpace="pre-wrap">
                  <Text>{item.content}</Text>
                </Box>
              </Flex>
            )
          ))
        )}
        {/* 顧客国籍 */}
        {customerNationalityName && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{t('features.InvestmentScreening.ReportAndDraft.pricingAgreement.customerNationality')}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <Text>{customerNationalityName}</Text>
            </Box>
          </Flex>
        )}
        {/* アップロード済みファイル */}
        {files && files.length > 0 && (
          <Flex alignItems="flex-start">
            <Text flexShrink={0} width={priceAgreementWidth}>{PricingAgreementCategory.uploadedFile}</Text>
            <Text> : </Text>
            <Box ml={2} mt={0} whiteSpace="pre-wrap">
              <VStack spacing={0} w="100%">
                {files!.map((file) => {
                  // マルウェア検知時はバックエンドがURLを発行しないため、リンクではなくファイル名のみ表示する
                  const downloadable = !!file.url && !isScanBlocked(file.scanStatus);
                  return (
                    <HStack key={file.key} w="100%" justify="space-between">
                      <HStack spacing={2}>
                        {downloadable ? (
                          <Link
                            href={file.url!}
                            download={file.fileName}
                            isExternal
                            color="blue.500"
                            textDecoration="underline"
                            cursor="pointer"
                          >
                            {file.fileName}
                          </Link>
                        ) : (
                          <Text color="gray.500">{file.fileName}</Text>
                        )}
                        {/* マルウェアスキャン状態（検知・スキャン中等のみ表示） */}
                        <ScanStatusBadge scanStatus={file.scanStatus} />
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          </Flex>
        )}
      </Box>
    );
  };

  // 設備投資一覧表のデータ取得と表示
  const EquipmentTable: React.FC<EquipmentTableProps> = ({ equipmentAmounts, supplierData, selectedCurrency, processItemNameMap, processItemNoMap, processSeqMap }) => {
    const validEquipmentAmounts = equipmentAmounts.filter((item): item is { amount: string } => item.amount !== undefined);
    const totalAmount = calculateTotalAmount(validEquipmentAmounts);

    const getSupplierName = (equipmentId: string) => {
      const supplier = supplierData.find((s) => s.equipmentId === equipmentId);
      return supplier ? getMultilingualDisplayName(supplier.equipmentSupplierName) : '-';
    };

    // seq降順 → displayOrder昇順で並び替え
    const sortedEquipmentAmounts = useMemo(() => {
      if (!processSeqMap) return equipmentAmounts;
      return [...equipmentAmounts].sort((a, b) => {
        const aSeq = processSeqMap[a.processId || ''] ?? 9999;
        const bSeq = processSeqMap[b.processId || ''] ?? 9999;
        if (aSeq !== bSeq) return bSeq - aSeq;
        // 同一工程内はdisplayOrder昇順（nullは最後）
        const orderA = a.displayOrder ?? Infinity;
        const orderB = b.displayOrder ?? Infinity;
        return orderA - orderB;
      });
    }, [equipmentAmounts, processSeqMap]);

    return (
      <TableContainer>
        <Heading size="sm" mt={4}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.title')}</Heading>
        <Text>unit: {divisorStr}{selectedCurrency.name}</Text>
        <Table variant="simple" size="sm" className="mm-ceas-table">
          <Thead>
            <Tr>
              <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.processName')}</Th>
              <Th textAlign="center">ItemNo</Th>
              <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.equipmentNameLabel')}</Th>
              <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.quantity')}</Th>
              <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.quantityUnit')}</Th>
              <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.label.applicationAmount')}</Th>
              <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.supplier')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedEquipmentAmounts.map((equipment: any, index: number) => {
              const amount = new Decimal(equipment.amount || 0);
              //const dividedAmount = divideValue(amount).toDecimalPlaces(2, Decimal.ROUND_DOWN);
              return (
                <Tr key={`equipment-${index}`}>
                  <Td>{processItemNameMap?.[equipment.processId] || '-'}</Td>
                  <Td>{processItemNoMap?.[equipment.processId] || '-'}</Td>
                  <Td>{equipment.equipmentName}</Td>
                  <Td textAlign="right">{equipment.quantity || '1'}</Td>
                  <Td>{toSpreadsheetUnitLabel(equipment.quantityUnit)}</Td>
                  <Td textAlign="right">{formatNumber(amount, "WithDecimal")}</Td>
                  <Td>{getSupplierName(equipment.equipmentId)}</Td>
                </Tr>
              );
            })}
            <Tr>
              <Td colSpan={5} className={`border-top-bold`}>{t('common.label.total')}</Td>
              <Td textAlign="right" className={`border-top-bold`}>{formatNumber(totalAmount, "WithDecimal")}</Td>
              <Td className={`border-top-bold`}></Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  // 設備投資 人数
  const EquipmentDataTable: React.FC<EquipmentTableProps> = ({ equipmentAmounts, processItemNameMap, processItemNoMap, processSeqMap }) => {
    const unskilledWorker = equipmentAmounts.filter((item): item is { unskilledWorker: number } => item.amount !== undefined);
    const multiSkilledWorker = equipmentAmounts.filter((item): item is { multiSkilledWorker: number } => item.amount !== undefined);
    const technician = equipmentAmounts.filter((item): item is { technician: number } => item.amount !== undefined);
    const SimpleLabor = totalNumberOfSimpleLabor(unskilledWorker);
    const MultiSkilledWorkerse = totalNumberOfMultiSkilledWorkerse(multiSkilledWorker);
    const Technicians = totalNumberOfTechnicians(technician);

    // seq降順 → displayOrder昇順で並び替え
    const sortedEquipmentAmounts = useMemo(() => {
      if (!processSeqMap) return equipmentAmounts;
      return [...equipmentAmounts].sort((a, b) => {
        const aSeq = processSeqMap[a.processId || ''] ?? 9999;
        const bSeq = processSeqMap[b.processId || ''] ?? 9999;
        if (aSeq !== bSeq) return bSeq - aSeq;
        // 同一工程内はdisplayOrder昇順（nullは最後）
        const orderA = a.displayOrder ?? Infinity;
        const orderB = b.displayOrder ?? Infinity;
        return orderA - orderB;
      });
    }, [equipmentAmounts, processSeqMap]);

    return (
      <TableContainer>
        <Heading size="sm" mt={4}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.personnelTitle')}</Heading>
        <Text mt={2}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.personnelSummary', { simple: SimpleLabor, multiSkilled: MultiSkilledWorkerse, technician: Technicians, total: SimpleLabor + MultiSkilledWorkerse + Technicians })}</Text>
        <Table variant="simple" size="sm" mt="5" className="mm-ceas-table">
          <Thead>
            <Tr>
              <Th style={{ width: "15%", textAlign: "center" }}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.processName')}</Th>
              <Th style={{ width: "15%", textAlign: "center" }}>ItemNo</Th>
              <Th style={{ width: "15%", textAlign: "center" }}>{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.equipmentNameLabel')}</Th>
              <Th style={{ width: "14%", textAlign: "center" }}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.simpleWorkerPerShift')}</Th>
              <Th style={{ width: "14%", textAlign: "center" }}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.multiSkilledWorkerPerShift')}</Th>
              <Th style={{ width: "14%", textAlign: "center" }}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.technicianPerShift')}</Th>
              <Th style={{ width: "13%", textAlign: "center" }}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.totalPerShift')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedEquipmentAmounts.map((equipment: any, index: number) => {
              return (
                <Tr key={`equipment-${index}`}>
                  <Td>{processItemNameMap?.[equipment.processId] || '-'}</Td>
                  <Td>{processItemNoMap?.[equipment.processId] || '-'}</Td>
                  <Td>{equipment.equipmentName}</Td>
                  <Td textAlign="right">{equipment.unskilledWorker}</Td>
                  <Td textAlign="right">{equipment.multiSkilledWorker}</Td>
                  <Td textAlign="right">{equipment.technician}</Td>
                  <Td textAlign="right">{equipment.numberOfPeopleRequiredForEquipment}</Td>
                </Tr>
              );
            })}
            <Tr>
              <Td className={`border-top-bold`} colSpan={3}>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.grandTotal')}</Td>
              <Td textAlign="right" className={`border-top-bold`}>{SimpleLabor}</Td>
              <Td textAlign="right" className={`border-top-bold`}>{MultiSkilledWorkerse}</Td>
              <Td textAlign="right" className={`border-top-bold`}>{Technicians}</Td>
              <Td textAlign="right" className={`border-top-bold`}>{SimpleLabor + MultiSkilledWorkerse + Technicians}</Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  const targetIds = equipmentAmounts.map(target => target.equipmentId)
  const filterBenchmark = benchmarkData.filter(eq => targetIds.includes(eq.equipmentId))

  const ProductionCapacityBenchmark = ({ equipmentAmounts }: any) => {

    // 差異計算
    const getDifference = (investmentPlan?: number, actualBest?: number, theoretical?: number) => {
      const diff = Number(investmentPlan) - Math.max(Number(actualBest), Number(theoretical));
      return isNaN(diff) ? 0 : diff;
    }
    // 差異計算(C/T)
    const getCicleTimeDifference = (investmentPlan?: number, actualBest?: number, theoretical?: number) => {
      const diff = Number(investmentPlan) - Math.min(Number(actualBest), Number(theoretical));
      return isNaN(diff) ? 0 : diff;
    }
    const formatNumber = (value: number | undefined): string => {
      if (value === undefined || value === null) return "";
      return value.toLocaleString();
    };
    // 設備ID一覧を作成
    const targetIds = equipmentAmounts.map((target: any) => target.equipmentId)
    // 設備一覧でベンチマーク一覧をフィルター（ラインの場合はフィルターなし）
    const typeBenchmark = (benchmarkData: any) => {
      if (benchmarkData[0]?.inputType === "equipment") {
        const filterBenchmark = benchmarkData.filter((eq: any) => targetIds.includes(eq.equipmentId))
        return filterBenchmark
      } else if (benchmarkData[0]?.inputType === "line") {
        // 配列・単体オブジェクトのどちらで渡ってきても安全に名称を取り出す
        const currentLineName = Array.isArray(productionLineData)
          ? productionLineData[0]?.productionLineName
          : productionLineData?.productionLineName;

        return benchmarkData.map((item: any) => ({
          ...item,
          productionLineName: currentLineName || "",
        }));
      }
      return benchmarkData
    }



    return (
      <TableContainer>
        <Heading size="sm" mt={4}>{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.title')}</Heading>
        <Table variant="simple" size="sm" mt="5" className="mm-ceas-table">

          <Tbody>
            {typeBenchmark(benchmarkData).map((benchmarkData: any, index: any) => {

              return (
                <Box key={index} ref={(el) => (benchmarkRefs.current[index] = el)}>
                  <Box>
                    <Text fontWeight="bold" mb={3} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.basicInfo')}</Text>
                    <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200">
                      <Thead bg="gray.100">

                      </Thead>
                      <Tr>
                        <Td style={{ width: "20%" }} borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{benchmarkData.equipmentId ? t('features.InvestmentScreening.ReportAndDraft.benchmarkList.equipmentNameLabel') : t('features.InvestmentScreening.ReportAndDraft.benchmarkList.lineNameLabel')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <Text>
                            {benchmarkData.equipmentId ? benchmarkData.equipmentName : benchmarkData.productionLineName}
                          </Text>
                        </Td>
                      </Tr>
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.fixedAssetNumber')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <Text>
                            {benchmarkData.fixedAssetNumber}
                          </Text>
                        </Td>
                      </Tr>
                      {benchmarkData.equipmentId === `ライン` &&
                        <Tr>
                          <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.bomProcessCode')}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            <Text>
                              {benchmarkData.processCode}
                            </Text>
                          </Td>
                        </Tr>
                      }
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.pastBestMonth')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <Text>
                            {benchmarkData.benchmarkMonth}
                          </Text>
                        </Td>
                      </Tr>
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.operatingDaysIncHoliday')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <Text>
                            {benchmarkData.operatingDays}
                          </Text>
                        </Td>
                      </Tr>
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.standardWorkHours')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <Text>
                            {benchmarkData.workTime}
                          </Text>
                        </Td>
                      </Tr>
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.shiftSystem')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <Text>
                            {Object.values(ShiftSystem).find(s => s.code === benchmarkData.shiftSystem)?.name || benchmarkData.shiftSystem}
                          </Text>
                        </Td>
                      </Tr>
                    </Table>
                  </Box>
                  {/* ベンチマークデータテーブル（PartNoループ展開対応） */}
                  {benchmarkData.benchmarkByPartNo
                    ? Object.entries(benchmarkData.benchmarkByPartNo).map(([partNo, benchmarkDetail]: [string, any], partNoIndex: number) => (
                      <Box key={partNo}>
                        {partNoIndex === 0 && <Text fontWeight="bold" mb={3} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.benchmarkData')}</Text>}
                        <Text fontSize="sm" mb={2} mt={partNoIndex === 0 ? 1 : 3}>PartNo: {resolveDisplayPartNoByKey(partNo, partNoData)}</Text>
                        <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200">
                          <Thead bg="gray.100">
                            <Tr>
                              <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200" w="200px">{t('common.label.item')}</Th>
                              <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.current')}</Th>
                              <Th colSpan={1} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productionIncrease')}</Th>
                              <Th colSpan={1} textAlign="center"></Th>
                            </Tr>
                            <Tr>
                              <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.actualPastBest')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.theoreticalValue')}</Th>
                              <Th textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.estimate')}</Th>
                              <Th textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.difference')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.yieldPct')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.actualBest?.yield}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.theoretical?.yield}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.investmentPlan?.yield}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getDifference(benchmarkDetail.investmentPlan?.yield, benchmarkDetail.actualBest?.yield, benchmarkDetail.theoretical?.yield))}</Text></Box></Td>
                            </Tr>
                            <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.operatingRatePct')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.actualBest?.operatingRate}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.theoretical?.operatingRate}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.investmentPlan?.operatingRate}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getDifference(benchmarkDetail.investmentPlan?.operatingRate, benchmarkDetail.actualBest?.operatingRate, benchmarkDetail.theoretical?.operatingRate))}</Text></Box></Td>
                            </Tr>
                            <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.cycleTimeSeconds')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.actualBest?.cycleTime}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.theoretical?.cycleTime}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.investmentPlan?.cycleTime}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getCicleTimeDifference(benchmarkDetail.investmentPlan?.cycleTime, benchmarkDetail.actualBest?.cycleTime, benchmarkDetail.theoretical?.cycleTime))}</Text></Box></Td>
                            </Tr>
                            <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.personnelPerDay')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.actualBest?.personnelPerDay}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.theoretical?.personnelPerDay}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkDetail.investmentPlan?.personnelPerDay}</Text></Box></Td>
                              <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getDifference(benchmarkDetail.investmentPlan?.personnelPerDay, benchmarkDetail.actualBest?.personnelPerDay, benchmarkDetail.theoretical?.personnelPerDay))}</Text></Box></Td>
                            </Tr>
                            <Tr bg="gray.50"><Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productionCount')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkDetail.actualBest?.productionVolume)}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkDetail.theoretical?.productionVolume)}</Td>
                              <Td textAlign="right">{formatNumber(benchmarkDetail.investmentPlan?.productionVolume)}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(getDifference(benchmarkDetail.investmentPlan?.productionVolume, benchmarkDetail.actualBest?.productionVolume, benchmarkDetail.theoretical?.productionVolume))}</Td>
                            </Tr>
                            <Tr bg="gray.50"><Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityPerEquipment')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkDetail.actualBest?.productionPerEquipment)}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkDetail.theoretical?.productionPerEquipment)}</Td>
                              <Td textAlign="right">{formatNumber(benchmarkDetail.investmentPlan?.productionPerEquipment)}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(getDifference(benchmarkDetail.investmentPlan?.productionPerEquipment, benchmarkDetail.actualBest?.productionPerEquipment, benchmarkDetail.theoretical?.productionPerEquipment))}</Td>
                            </Tr>
                            <Tr bg="gray.50"><Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityPerPerson')}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkDetail.actualBest?.productionPerPerson)}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkDetail.theoretical?.productionPerPerson)}</Td>
                              <Td textAlign="right">{formatNumber(benchmarkDetail.investmentPlan?.productionPerPerson)}</Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(getDifference(benchmarkDetail.investmentPlan?.productionPerPerson, benchmarkDetail.actualBest?.productionPerPerson, benchmarkDetail.theoretical?.productionPerPerson))}</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Box>
                    ))
                    : /* 後方互換: benchmarkByPartNo がない場合は従来通り */
                    <Box>
                      <Text fontWeight="bold" mb={3} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.benchmarkData')}</Text>
                      <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200">
                        <Thead bg="gray.100">
                          <Tr>
                            <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200" w="200px">{t('common.label.item')}</Th>
                            <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.current')}</Th>
                            <Th colSpan={1} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productionIncrease')}</Th>
                            <Th colSpan={1} textAlign="center"></Th>
                          </Tr>
                          <Tr>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.actualPastBest')}</Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.theoreticalValue')}</Th>
                            <Th textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.estimate')}</Th>
                            <Th textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.difference')}</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.yieldPct')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.actualBest?.yield}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.theoretical?.yield}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.investmentPlan?.yield}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getDifference(benchmarkData.investmentPlan?.yield, benchmarkData.actualBest?.yield, benchmarkData.theoretical?.yield))}</Text></Box></Td>
                          </Tr>
                          <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.operatingRatePct')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.actualBest?.operatingRate}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.theoretical?.operatingRate}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.investmentPlan?.operatingRate}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getDifference(benchmarkData.investmentPlan?.operatingRate, benchmarkData.actualBest?.operatingRate, benchmarkData.theoretical?.operatingRate))}</Text></Box></Td>
                          </Tr>
                          <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.cycleTimeSeconds')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.actualBest?.cycleTime}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.theoretical?.cycleTime}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.investmentPlan?.cycleTime}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getCicleTimeDifference(benchmarkData.investmentPlan?.cycleTime, benchmarkData.actualBest?.cycleTime, benchmarkData.theoretical?.cycleTime))}</Text></Box></Td>
                          </Tr>
                          <Tr><Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.personnelPerDay')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.actualBest?.personnelPerDay}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.theoretical?.personnelPerDay}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{benchmarkData.investmentPlan?.personnelPerDay}</Text></Box></Td>
                            <Td borderRight="1px solid" borderColor="gray.200"><Box display="flex" justifyContent="center"><Text>{formatNumber(getDifference(benchmarkData.investmentPlan?.personnelPerDay, benchmarkData.actualBest?.personnelPerDay, benchmarkData.theoretical?.personnelPerDay))}</Text></Box></Td>
                          </Tr>
                          <Tr bg="gray.50"><Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productionCount')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkData.actualBest?.productionVolume)}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkData.theoretical?.productionVolume)}</Td>
                            <Td textAlign="right">{formatNumber(benchmarkData.investmentPlan?.productionVolume)}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(getDifference(benchmarkData.investmentPlan?.productionVolume, benchmarkData.actualBest?.productionVolume, benchmarkData.theoretical?.productionVolume))}</Td>
                          </Tr>
                          <Tr bg="gray.50"><Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityPerEquipment')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkData.actualBest?.productionPerEquipment)}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkData.theoretical?.productionPerEquipment)}</Td>
                            <Td textAlign="right">{formatNumber(benchmarkData.investmentPlan?.productionPerEquipment)}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(getDifference(benchmarkData.investmentPlan?.productionPerEquipment, benchmarkData.actualBest?.productionPerEquipment, benchmarkData.theoretical?.productionPerEquipment))}</Td>
                          </Tr>
                          <Tr bg="gray.50"><Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityPerPerson')}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkData.actualBest?.productionPerPerson)}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(benchmarkData.theoretical?.productionPerPerson)}</Td>
                            <Td textAlign="right">{formatNumber(benchmarkData.investmentPlan?.productionPerPerson)}</Td>
                            <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">{formatNumber(getDifference(benchmarkData.investmentPlan?.productionPerPerson, benchmarkData.actualBest?.productionPerPerson, benchmarkData.theoretical?.productionPerPerson))}</Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  }
                  {/* 注意書き（*、**） */}
                  <Box mt={2} bg="gray.50" p={2} borderRadius="md" fontSize="xs" color="gray.600">
                    <Text textAlign="left">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.theoreticalNote')}</Text>
                    <Text textAlign="left">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityNote')}</Text>
                  </Box>

                  {/* フリー入力フィールド */}
                  <Box>
                    {/* ギャップ分析 */}
                    <Text fontWeight="bold" mb={2} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.analysisAndMeasures')}</Text>
                    <VStack ml={2} mb={4} align="stretch" spacing={2}>
                      <Text size="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.gapAnalysis')}</Text>
                      <Text whiteSpace="normal">
                        {benchmarkData.freeInputFields?.gapAnalysis || ""}
                      </Text>
                    </VStack>
                    {/* 設備あたり生産性向上対策 */}
                    <VStack ml={2} mb={4} align="stretch" spacing={2}>
                      <Text size="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityImprovementLabel')}</Text>
                      <Text whiteSpace="normal" >
                        {benchmarkData.freeInputFields?.productionPerEquipment || ""}
                      </Text>
                    </VStack>
                    {/* 1人あたり生産性向上対策 */}
                    <VStack ml={2} mb={4} align="stretch" spacing={2}>
                      <Text size="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.productivityPerPersonImprovementLabel')}</Text>
                      <Text whiteSpace="normal" >
                        {benchmarkData.freeInputFields?.productionPerPerson || ""}
                      </Text>
                    </VStack>
                    {/* 非稼働対策 */}
                    <VStack ml={2} mb={4} align="stretch" spacing={2}>
                      <Text size="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.nonWorkingTimeMeasuresLabel')}</Text>
                      <Text whiteSpace="normal">
                        {benchmarkData.freeInputFields?.nonOperationMeasures || ""}
                      </Text>
                    </VStack>
                    {/* 歩留向上対策 */}
                    <VStack ml={2} mb={4} align="stretch" spacing={2}>
                      <Text size="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.yieldImprovementMeasuresLabel')}</Text>
                      <Text whiteSpace="normal">
                        {benchmarkData.freeInputFields?.yieldMeasures || ""}
                      </Text>
                    </VStack>
                    {/* 注意書き（***） */}
                    <Box mt={2} mb={3} bg="gray.50" p={2} borderRadius="md" fontSize="xs" color="gray.600">
                      <Text textAlign="left">{t('features.InvestmentScreening.ReportAndDraft.benchmarkList.gapAnalysisNote')}</Text>
                    </Box>

                  </Box>
                </Box>
              );
            })}

          </Tbody>
        </Table>
      </TableContainer>
    )
  }

  // アップロードファイルのリンク表示（画像の場合は画像表示）
  const UploadedFiles: React.FC<{ title?: String, folderName: String, isImage?: boolean }> =
    ({ title, folderName, isImage = false }) => {

      const folder = uploadedFilesByFolder.find(f => f.folderName === folderName);
      if (!folder) return null;

      return (
        <Box>
          <Heading size="sm" mt={4}>{title}</Heading>
          {folder.files.map((file) => {
            // マルウェア検知時はバックエンドがURLを発行しないため、リンクではなくファイル名のみ表示する
            const downloadable = !!file.url && !isScanBlocked(file.scanStatus);
            return (
              <Box key={file.fileName} mt={2}>
                <HStack key={file.key} w="100%" justify="space-between">
                  <HStack spacing={2}>
                    {downloadable ? (
                      <Link
                        href={file.url!}
                        download={file.fileName}
                        isExternal
                        color="blue.500"
                        textDecoration="underline"
                        cursor="pointer"
                      >
                        {file.fileName}
                      </Link>
                    ) : (
                      <Text color="gray.500">{file.fileName}</Text>
                    )}
                    {/* マルウェアスキャン状態（検知・スキャン中等のみ表示） */}
                    <ScanStatusBadge scanStatus={file.scanStatus} />
                  </HStack>
                </HStack>
                <HStack spacing={4}>
                  {
                    file.url && isImage && downloadable ? (
                      <ChakraImage
                        key={file.fileName}
                        src={file.url}
                        alt={file.fileName}
                        objectFit="cover"
                      />
                    ) : null
                  }
                </HStack>
              </Box>
            );
          })}
        </Box>
      );
    };

  /**
   * 更新前後サマリーデータ一覧コンポーネント（更新投資用）
   * 更新前後サマリーは工程（ライン）単位で入力されるため、現在存在する工程のみを表示
   */
  const UpdateSummaryDataList = () => {

    // 現在存在する設備の工程ID（processId = uniqueProcessId）のセットを作成
    const existingProcessIds = useMemo(() => {
      return new Set(equipmentAmounts.map(e => e.processId).filter(Boolean));
    }, [equipmentAmounts]);

    // 削除された工程に関連する更新前後サマリーをフィルタリング
    const filteredUpdateSummaryData = useMemo(() => {
      return updateSummaryData.filter((summary: UpdateSummaryInputData) => {
        // productionLineId（= uniqueProcessId）が現在の設備の工程に存在するか
        return existingProcessIds.has(summary.productionLineId || "");
      });
    }, [updateSummaryData, existingProcessIds]);

    // 差異計算
    const getDifference = (investmentPlan?: number, recentActual?: number, theoretical?: number) => {
      const diff = Number(investmentPlan) - Math.max(Number(recentActual), Number(theoretical));
      return isNaN(diff) ? 0 : diff;
    }
    // 差異計算(C/T) - サイクルタイムは小さいほうが良いため逆
    const getCicleTimeDifference = (investmentPlan?: number, recentActual?: number, theoretical?: number) => {
      const diff = Number(investmentPlan) - Math.min(Number(recentActual), Number(theoretical));
      return isNaN(diff) ? 0 : diff;
    }
    const formatNumberLocal = (value: number | undefined): string => {
      if (value === undefined || value === null) return "";
      return value.toLocaleString();
    };

    // フィルタリング後のデータが0件の場合は表示しない
    if (filteredUpdateSummaryData.length === 0) {
      return null;
    }

    return (
      <Box>
        <Heading size="sm" mt={4}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.title')}</Heading>
        {filteredUpdateSummaryData.map((summary: UpdateSummaryInputData, index: number) => {
          return (
            <Box key={index} id={`${UPDATE_SUMMARY_ITEM_PREFIX}${index}`} mt={5} pb={6}>
              <Box>
                <Text fontWeight="bold" mb={3} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.basicInfo')}</Text>
                <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200">
                  <Thead bg="gray.100"></Thead>
                  <tbody>
                    <Tr>
                      <Td style={{ width: "20%" }} borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">
                        {t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.processName')}
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>
                          {summary.productionLineName}
                        </Text>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.fixedAssetNumber')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>{summary.fixedAssetNumber}</Text>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.operatingDaysIncHoliday')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>{summary.operatingDays} {t('common.label.days')}</Text>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.workTime')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>{summary.workTime}</Text>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.shiftSystem')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>{Object.values(ShiftSystem).find(s => s.code === summary.shiftSystem)?.name || summary.shiftSystem}</Text>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.partsPerProduct')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>{formatNumberLocal(summary.partsCount ?? 1)} pcs</Text>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.cumulativeCost')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <Text>{formatNumberLocal(summary.cumulativeCost)} JPY</Text>
                      </Td>
                    </Tr>
                  </tbody>
                </Table>
                <Text fontSize="xs" color="gray.600" mt={2}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.recentMonthNote')}</Text>
              </Box>

              {/* 更新前後サマリーデータテーブル（PartNoごと） */}
              {Object.entries(summary.productionCapacityByPartNo || {}).map(([partNo, capacityDetail], partNoIndex) => (
                <Box key={partNo}>
                  {partNoIndex === 0 && <Text fontWeight="bold" mb={1} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productionCapacity')}</Text>}
                  <Text fontSize="sm" mb={3} mt={partNoIndex === 0 ? 1 : 3}>PartNo: {resolveDisplayPartNoByKey(partNo, partNoData)}</Text>
                  <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200" sx={{ "& tbody td": { minHeight: "56px", height: "56px", verticalAlign: "middle", py: 1 } }}>
                    <Thead bg="gray.100">
                      <Tr>
                        <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200" w="200px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.processName').replace('工程名', '項目')}</Th>
                        <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.current')}</Th>
                        <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.updateInvestment')}</Th>
                        <Th colSpan={1} textAlign="center" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.difference')}</Th>
                      </Tr>
                      <Tr>
                        <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.recentActual')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.theoretical')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.estimate')}</Th>
                        <Th textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.actualVsEstimate')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {/* 歩留 */}
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.yield')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.yield ?? 0).toFixed(2)} %
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.yield ?? 0).toFixed(2)} %
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.yield ?? 0).toFixed(2)} %
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getDifference(capacityDetail.investmentPlan?.yield, capacityDetail.recentActual?.yield, capacityDetail.theoretical?.yield) ?? 0).toFixed(2)} %
                        </Td>
                      </Tr>
                      {/* 非稼働時間 */}
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.nonWorkingTime')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.nonWorkingTime ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.nonWorkingTime ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.nonWorkingTime ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getCicleTimeDifference(capacityDetail.investmentPlan?.nonWorkingTime, capacityDetail.recentActual?.nonWorkingTime, capacityDetail.theoretical?.nonWorkingTime) ?? 0).toFixed(2)} hr/24hr
                        </Td>
                      </Tr>
                      {/* 内）機械トラブル */}
                      <Tr bg="gray.50">
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.machineTrouble')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.machineTrouble ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.machineTrouble ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.machineTrouble ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getCicleTimeDifference(capacityDetail.investmentPlan?.machineTrouble, capacityDetail.recentActual?.machineTrouble, capacityDetail.theoretical?.machineTrouble) ?? 0).toFixed(2)} hr/24hr
                        </Td>
                      </Tr>
                      {/* 内）保守・メンテナンス */}
                      <Tr bg="gray.50">
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.maintenance')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.maintenance ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.maintenance ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.maintenance ?? 0).toFixed(2)} hr/24hr
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getCicleTimeDifference(capacityDetail.investmentPlan?.maintenance, capacityDetail.recentActual?.maintenance, capacityDetail.theoretical?.maintenance) ?? 0).toFixed(2)} hr/24hr
                        </Td>
                      </Tr>
                      {/* 稼働率 */}
                      <Tr bg="gray.50">
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">
                          <Text>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.operatingRate')}</Text>
                          <Text fontSize="xs" color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.operatingRateFormula')}</Text>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.operatingRate ?? 0).toFixed(2)} %
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.operatingRate ?? 0).toFixed(2)} %
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.operatingRate ?? 0).toFixed(2)} %
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getDifference(capacityDetail.investmentPlan?.operatingRate, capacityDetail.recentActual?.operatingRate, capacityDetail.theoretical?.operatingRate) ?? 0).toFixed(2)} %
                        </Td>
                      </Tr>
                      {/* サイクルタイム */}
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.cycleTime')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.cycleTime ?? 0).toFixed(1)} sec
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.cycleTime ?? 0).toFixed(1)} sec
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.cycleTime ?? 0).toFixed(1)} sec
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getCicleTimeDifference(capacityDetail.investmentPlan?.cycleTime, capacityDetail.recentActual?.cycleTime, capacityDetail.theoretical?.cycleTime) ?? 0).toFixed(1)} sec
                        </Td>
                      </Tr>
                      {/* 人数 */}
                      <Tr>
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.personnel')}</Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.personnelPerDay ?? 0).toFixed(1)} pax
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.personnelPerDay ?? 0).toFixed(1)} pax
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.personnelPerDay ?? 0).toFixed(1)} pax
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getCicleTimeDifference(capacityDetail.investmentPlan?.personnelPerDay, capacityDetail.recentActual?.personnelPerDay, capacityDetail.theoretical?.personnelPerDay) ?? 0).toFixed(1)} pax
                        </Td>
                      </Tr>
                      {/* 生産能力 */}
                      <Tr bg="gray.50">
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">
                          <Text>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productionCapacity')}</Text>
                          <Text fontSize="xs" color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productionCapacityFormula')}</Text>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {formatNumberLocal(capacityDetail.recentActual?.productionVolume)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {formatNumberLocal(capacityDetail.theoretical?.productionVolume)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {formatNumberLocal(capacityDetail.investmentPlan?.productionVolume)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {formatNumberLocal(getDifference(capacityDetail.investmentPlan?.productionVolume, capacityDetail.recentActual?.productionVolume, capacityDetail.theoretical?.productionVolume))} pcs
                        </Td>
                      </Tr>
                      {/* 設備あたり生産性 */}
                      <Tr bg="gray.50">
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">
                          <Text>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityPerEquipment')}</Text>
                          <Text fontSize="xs" color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityPerEquipmentFormula')}</Text>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.productionPerEquipment ?? 0).toFixed(2)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.productionPerEquipment ?? 0).toFixed(2)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.productionPerEquipment ?? 0).toFixed(2)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getDifference(capacityDetail.investmentPlan?.productionPerEquipment, capacityDetail.recentActual?.productionPerEquipment, capacityDetail.theoretical?.productionPerEquipment) ?? 0).toFixed(2)} pcs
                        </Td>
                      </Tr>
                      {/* 1人あたり生産性 */}
                      <Tr bg="gray.50">
                        <Td borderRight="1px solid" borderColor="gray.200" fontWeight="bold">
                          <Text>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityPerPerson')}</Text>
                          <Text fontSize="xs" color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityPerPersonFormula')}</Text>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.recentActual?.productionPerPerson ?? 0).toFixed(2)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.theoretical?.productionPerPerson ?? 0).toFixed(2)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(capacityDetail.investmentPlan?.productionPerPerson ?? 0).toFixed(2)} pcs
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          {(getDifference(capacityDetail.investmentPlan?.productionPerPerson, capacityDetail.recentActual?.productionPerPerson, capacityDetail.theoretical?.productionPerPerson) ?? 0).toFixed(2)} pcs
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                  <Text fontSize="xs" color="gray.600" mt={2}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityNote')}</Text>
                </Box>
              ))}

              {/* フリー入力フィールド */}
              <Box>
                <Text fontWeight="bold" mb={2} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.analysis')}</Text>
                {/* 更新をする理由 */}
                <VStack ml={2} mb={4} align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.updateReason')}</Text>
                  <Text fontSize="xs" whiteSpace="normal">{summary.updateReason || ""}</Text>
                </VStack>
                {/* 設備あたり生産性 */}
                <VStack ml={2} mb={4} align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityPerEquipmentAnalysis')}</Text>
                  <Text fontSize="xs" whiteSpace="normal">{summary.productionPerEquipment || ""}</Text>
                </VStack>
                {/* 1人あたり生産性 */}
                <VStack ml={2} mb={4} align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productivityPerPersonAnalysis')}</Text>
                  <Text fontSize="xs" whiteSpace="normal">{summary.productionPerPerson || ""}</Text>
                </VStack>
                {/* 非稼働対策 */}
                <VStack ml={2} mb={4} align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.nonWorkingTimeMeasures')}</Text>
                  <Text fontSize="xs" whiteSpace="normal">{summary.nonOperationMeasures || ""}</Text>
                </VStack>
                {/* 非加工対策 */}
                <VStack ml={2} mb={4} align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.nonProcessingMeasures')}</Text>
                  <Text fontSize="xs" whiteSpace="normal">{summary.nonProcessingMeasures || ""}</Text>
                </VStack>
                {/* 現行設備の処置方法 */}
                <VStack ml={2} mb={4} align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.currentEquipmentHandling')}</Text>
                  <Text fontSize="xs" whiteSpace="normal">{summary.currentEquipmentHandling || ""}</Text>
                </VStack>
              </Box>

              {/* 損益 - 簡易モード時は非表示（profitLossデータは詳細モードのみ入力） */}
              {!isSimpleMode && <Box>
                <Text fontWeight="bold" mb={3} mt={3}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.profitLossTitle')}</Text>
                <Table variant="simple" size="sm" border="1px solid" borderColor="gray.200" sx={{ "& tbody td": { minHeight: "56px", height: "56px", verticalAlign: "middle", py: 1 } }}>
                  <Thead bg="gray.100">
                    <Tr>
                      <Th rowSpan={2} borderRight="1px solid" borderColor="gray.200" w="200px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.processName').replace('工程名', '項目')}</Th>
                      <Th colSpan={2} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.current')}</Th>
                      <Th colSpan={1} textAlign="center" borderRight="1px solid" borderColor="gray.200" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.updateInvestment')}</Th>
                      <Th colSpan={1} textAlign="center" borderBottom="1px solid" borderBottomColor="gray.200">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.difference')}</Th>
                    </Tr>
                    <Tr>
                      <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.recentActual')}</Th>
                      <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.initialEstimate')}</Th>
                      <Th borderRight="1px solid" borderColor="gray.200" textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.estimate')}</Th>
                      <Th textAlign="center" w="150px">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.actualVsEstimate')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* 生産数 */}
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.productionQuantity')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.productionQuantity ?? 0)} pcs
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.productionQuantity ?? 0)} pcs
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.productionQuantity ?? 0)} pcs
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        0 pcs
                      </Td>
                    </Tr>
                    {/* スクラップ率 */}
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">
                        <Text>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.scrapRate')}</Text>
                        <Text fontSize="xs" color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.scrapRateFormula')}</Text>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {(summary.profitLoss?.recentActual?.scrapRate ?? 0).toFixed(2)} %
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {(summary.profitLoss?.theoretical?.scrapRate ?? 0).toFixed(2)} %
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {(summary.profitLoss?.investmentPlan?.scrapRate ?? 0).toFixed(2)} %
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {(getCicleTimeDifference(summary.profitLoss?.investmentPlan?.scrapRate, summary.profitLoss?.recentActual?.scrapRate, summary.profitLoss?.theoretical?.scrapRate) ?? 0).toFixed(2)} %
                      </Td>
                    </Tr>
                    {/* スクラップ費用 */}
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">
                        <Text>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.scrapCost')}</Text>
                        <Text fontSize="xs" color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.scrapCostFormula')}</Text>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.recentActual?.scrapCost)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.theoretical?.scrapCost)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.investmentPlan?.scrapCost)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(getCicleTimeDifference(summary.profitLoss?.investmentPlan?.scrapCost, summary.profitLoss?.recentActual?.scrapCost, summary.profitLoss?.theoretical?.scrapCost))} JPY
                      </Td>
                    </Tr>
                    {/* 修繕費 */}
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.repairCost')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.recentActual?.repairCost ?? undefined)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.theoretical?.repairCost ?? undefined)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.investmentPlan?.repairCost ?? undefined)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(getCicleTimeDifference(summary.profitLoss?.investmentPlan?.repairCost ?? undefined, summary.profitLoss?.recentActual?.repairCost ?? undefined, summary.profitLoss?.theoretical?.repairCost ?? undefined))} JPY
                      </Td>
                    </Tr>
                    {/* 消耗品費 */}
                    <Tr>
                      <Td borderRight="1px solid" borderColor="gray.200" fontWeight="semibold">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.consumablesCost')}</Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.recentActual?.consumablesCost ?? undefined)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.theoretical?.consumablesCost ?? undefined)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(summary.profitLoss?.investmentPlan?.consumablesCost ?? undefined)} JPY
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                        {formatNumberLocal(getCicleTimeDifference(summary.profitLoss?.investmentPlan?.consumablesCost ?? undefined, summary.profitLoss?.recentActual?.consumablesCost ?? undefined, summary.profitLoss?.theoretical?.consumablesCost ?? undefined))} JPY
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
                <Text fontSize="xs" color="gray.600" mt={2} pb={4}>{t('features.InvestmentScreening.ReportAndDraft.updateSummaryList.profitLossAssumptionNote')}</Text>
              </Box>}
            </Box>
          );
        })}
      </Box>
    )
  }

  /**
   * 目的に応じたラベルを返す
   * @param labelInfo ラベル情報候補　例：{[PurposeType.NewProduct.code]:"売上/損益", ["*"]:"売上/営業利益/営業利益率"}　※アスタリスク指定でそれ以外
   * @returns
   */
  const getLabelsByPurpose = (labelInfo: { [purposeCode: string]: string }) => {
    if (projectData?.purpose) {
      if (labelInfo.hasOwnProperty(projectData?.purpose)) {
        // 目的に合ったラベル
        return labelInfo[projectData?.purpose];
      } else {
        // 指定したもの以外
        return labelInfo["*"];
      }
    }

    // 該当ラベルなし
    return "";
  }

  // -----------------------------
  // リストボックス
  // -----------------------------
  // 通貨リスト
  //  const currencyOptions = currencyData.map((currencyItem) => ({
  //    value: currencyItem.currencyId,
  //    label: currencyItem.currencyName,
  //  }));

  // 通貨リスト(ローカル通貨、JPY設定)
  const currencyOptions = currencyData.filter(
    (currencyItem) =>
      currencyItem.currencyId === localCurrencyId || currencyItem.currencyName === CurrencyNameJPY
  )
    .map((currencyItem) => ({
      value: currencyItem.currencyId,
      label: currencyItem.currencyName,
    }));

  // -----------------------------
  // 通貨フィルター
  // -----------------------------
  /**
   * 通貨名フィルター
   * @param currencyData T_CEAS_CURRENCYのデータ
   * @param targetData 　プロジェクトデータ、またはPartNoごとデータ
   * @returns 通貨名
   */
  const currencyNameFilter = ((currencyData: any, targetData: any) => {
    if (targetData && currencyData) {
      // プロジェクトデータの場合は会社/工場スナップショットから通貨を取得
      const currencyId = targetData.companyPlantSnapshot
        ? getLocalCurrencyId(targetData.companyPlantSnapshot)
        : targetData.id;
      const found = currencyData?.find((c: any) => c.currencyId === currencyId);
      return found?.currencyName ?? "--"
    } return "--"
  })
  /**
  * 通貨レートフィルター
  * @param currencyData T_CEAS_CURRENCYのデータ
  * @param targetData 　プロジェクトデータ、またはPartNoごとデータ
  * @returns 通貨レート
  */
  const currencyRateFilter = ((currencyData: any, targetData: any) => {
    if (targetData && currencyData) {
      // プロジェクトデータの場合は会社/工場スナップショットから通貨を取得
      const currencyId = targetData.companyPlantSnapshot
        ? getLocalCurrencyId(targetData.companyPlantSnapshot)
        : targetData.id;
      const found = currencyData?.find((c: any) => c.currencyId === currencyId);
      return found?.exchangeRate ?? null
    } return null
  })
  /**
   * 通貨レート変換
   * @param rate 通貨レート
   * @returns 小数点第二位以下は０埋め、以上はそのまま表示
   */
  const formatExchangeRate = (rate: number | undefined) => {
    if (rate === undefined || rate === null) return '--';

    const str = String(rate);
    const decimalIndex = str.indexOf('.');

    // 小数点がない場合は2桁で0埋め
    if (decimalIndex === -1) {
      return rate.toFixed(2);
    }

    // 小数点以下の桁数を取得
    const decimalPlaces = str.length - decimalIndex - 1;

    // 2桁未満の場合は2桁に0埋め
    if (decimalPlaces < 2) {
      return rate.toFixed(2);
    }

    // 2桁以上の場合はそのまま表示
    return str;
  };

  // -----------------------------
  // 画面
  // -----------------------------
  // 年・月ごとにまとめる
  const grouped = groupByYear(monthList);
  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
      ref={pdfRef}
    >
      <Container maxW="container.2xl" p={4}>
        <Box bg="white" zIndex="1000" boxShadow="md" position="sticky" top="-2rem" p={4}>
          {/* Row 1: Currency Selector */}
          <Flex align="center" mb={2}>
            <Text alignItems="center" mr={4}>{t('features.InvestmentScreening.ReportAndDraft.label.displayCurrency')}</Text>
            <Select
              value={selectedCurrency.id}
              onChange={(e) => {
                const selectedOption = currencyOptions.find(option => option.value === e.target.value);
                if (selectedOption && selectedOption.value && selectedOption.label) {
                  setSelectedCurrency({ id: selectedOption.value, name: selectedOption.label });
                }
              }}
              width="250px"
            >
              {currencyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Flex>

          {/* Row 2: Navigation Buttons */}
          <Flex wrap="wrap">
            <Button size="sm" onClick={() => scrollToSection('section-0')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.coverPage')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-1')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.applicationSummary')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-2')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.overview')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-3')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.customerAgreement')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-4')} mr={1}>{labelQuantityAndPrice}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-5')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.pl')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-7')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.schedule')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-8')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.equipmentInvestment')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-9')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.cost')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-10')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.simulation')}</Button>
            {reviewDate && (
              <Button size="sm" onClick={() => scrollToSection('section-review')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.review')}</Button>
            )}
            <Button size="sm" onClick={() => scrollToSection('section-11')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.estimate')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-12')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.tenRules')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-13')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.attachments')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-14')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.confirmedPersons')}</Button>
            <Button size="sm" onClick={() => scrollToSection('section-15')} mr={1}>{t('features.InvestmentScreening.ReportAndDraft.button.approvalHistory')}</Button>
          </Flex>
        </Box>
        <Box id="section-0" mb={4} className="pdf-content section">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Box textAlign="center" mt={40} width="100%" display="flex" justifyContent="center" flexDirection="column">
              <Heading as="h1" size="lg" mb={2}>
                {projectData && projectData.applicationMonth ? dayjs(projectData.applicationMonth).format(t('common.dateFormat.dayjsYearMonthDegree')) : ""}
              </Heading>
              <Heading as="h1" size="lg" mb={10}>
                {projectData?.projectName}
              </Heading>
            </Box>

            <Box
              mt={80}
              ml={10}
              display="grid"
              // 1列目(ラベル): 自動幅, 2列目(コロン): 自動幅, 3列目(値): 残りの幅
              gridTemplateColumns="max-content min-content 1fr"
              rowGap={0.2}     // 行間の余白
              columnGap={2}    // ラベル、コロン、値の間の横の隙間
              alignItems="center"
            >
              {/* 申請部門 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.applicantDivision')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{getDivisionHierarchyDisplay(projectData) || projectData?.divisionName}</Text>

              {/* 起案者 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.drafter')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{projectData?.registrantEmployeeName}</Text>

              {/* 申請者 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.applicant')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{executiveManager[0]?.employeeName}</Text>

              {/* 設備受付日 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.equipmentAcceptanceDate')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{projectData && projectData.projectStartDate ? dayjs(projectData.projectStartDate).format(t('common.dateFormat.dayjsFullDate')) : ""}</Text>

              {/* 申請番号 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.applicationNumber')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{projectData?.applicationNumber || ''}</Text>

              {/* 通帳通貨 */}
              {!reviewDate && (
                <>
                  <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.bookCurrency')}</Text>
                  <Text as="span">:</Text>
                  <Text as="span">
                    {t('features.InvestmentScreening.ReportAndDraft.label.currencyName')} : {currencyNameFilter(currencyData, projectData)} / {t('features.InvestmentScreening.ReportAndDraft.label.exchangeRate')} : {formatExchangeRate(currencyRateFilter(currencyData, projectData))}{rateType && ratePeriod ? ` (${rateType === 'actual' ? t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRateActual') : t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRatePlan')} ${rateType === 'actual' ? t('common.dateFormat.yearMonth', { year: ratePeriod.substring(0, 4), month: ratePeriod.substring(5, 7) }) : t('common.dateFormat.fiscalYearStr', { year: ratePeriod })})` : ''}
                  </Text>
                </>
              )}

              {/* 申請金額 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.applicationAmount')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{getRequestedAmount(convEquipmentAmounts.filter((equipment): equipment is { amount: string } => equipment.amount !== undefined), selectedCurrency.name)}</Text>

              {/* 目的区分 */}
              <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.label.purposeType')}</Text>
              <Text as="span">:</Text>
              <Text as="span">{projectData?.purpose ? getPurposeTypeNameByCode(projectData.purpose) : ''}</Text>

              {/* 会社/工場（複数選択対応：縦並び表示） */}
              {(() => {
                const snapshot = parseCompanyPlantSnapshot(projectData?.companyPlantSnapshot);
                if (!snapshot || snapshot.items.length === 0) {
                  return (
                    <>
                      <Text as="span">{t('features.InvestmentScreening.ReportAndDraft.field.companyPlant')}</Text>
                      <Text as="span">:</Text>
                      <Text as="span"></Text>
                    </>
                  );
                }
                return snapshot.items.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <Text as="span">
                      {idx === 0 ? t('features.InvestmentScreening.ReportAndDraft.field.companyPlant') : ''}
                    </Text>
                    <Text as="span">:</Text>
                    <Text as="span">
                      {getBilingualDisplayName(item, 'companyName', 'companyNameEn')}
                      {item.plantName ? ` / ${getBilingualDisplayName(item, 'plantName', 'plantNameEn')}` : ''}
                    </Text>
                  </React.Fragment>
                ));
              })()}
            </Box>
          </Box>
        </Box>
        <Box id="section-1" mb={4} className="pdf-content section">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.heading.investmentApplication')}
            </Heading>
            <Table variant="simple" className="mm-ceas-table">
              <Tbody>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.opportunityName')}</Td>
                  <Td>
                    {projectData?.revisedOpportunityNameByRevision ? projectData.revisedOpportunityNameByRevision : projectData?.opportunityName}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.division')}</Td>
                  <Td>
                    {getDivisionHierarchyDisplay(projectData) || projectData?.divisionName}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.productName')}</Td>
                  <Td>
                    {projectData?.customerProductName}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.customer')}</Td>
                  <Td>
                    {splitEscapedList(projectData?.endUserName).join(" / ")}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.companyPlant')}</Td>
                  <Td>
                    {/* 会社/工場（複数選択対応：縦並び表示） */}
                    {(() => {
                      const snapshot = parseCompanyPlantSnapshot(projectData?.companyPlantSnapshot);
                      if (!snapshot || snapshot.items.length === 0) return '';
                      return snapshot.items.map((item, idx) => (
                        <Text key={idx}>
                          {getBilingualDisplayName(item, 'companyName', 'companyNameEn')}
                          {item.plantName ? ` / ${getBilingualDisplayName(item, 'plantName', 'plantNameEn')}` : ''}
                        </Text>
                      ));
                    })()}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.baseInterestRate')}</Td>
                  <Td>
                    {projectData?.baseInterestRate != null ? `${projectData.baseInterestRate}%` : ''}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.productionLineName')}</Td>
                  <Td>
                    {productionLineData?.productionLineName}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.equipmentPlanNumber')}</Td>
                  <Td>
                    {Array.isArray(projectData?.equipmentPlanNumber)
                      ? projectData.equipmentPlanNumber.join(", ")
                      : projectData?.equipmentPlanNumber}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.plannedBudget')}</Td>
                  <Td>
                    {projectData?.budgetAmount
                      ? `${projectData.budgetAmount.toLocaleString()} JPY（${getPlannedBudgetTypeNameByCode(projectData.plannedBudget ?? "")}）`
                      : getPlannedBudgetTypeNameByCode(projectData?.plannedBudget ?? "")}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.equipmentCategory')}</Td>
                  <Td>
                    {projectData?.equipmentCategory ? getEquipmentCategoryNamesByCodes(projectData.equipmentCategory) : ''}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.purpose')}</Td>
                  <Td>
                    {projectData?.purpose ? getPurposeTypeNameByCode(projectData.purpose) : ''}
                  </Td>
                </Tr>
                <Tr>
                  <Td width="200px">{t('features.InvestmentScreening.ReportAndDraft.field.supplier')}</Td>
                  <Td>{uniqueSupplierNames}</Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.investmentApplicationAmount')}</Td>
                  <Td>
                    {getRequestedAmount(convEquipmentAmounts.filter((equipment): equipment is { amount: string } => equipment.amount !== undefined), selectedCurrency.name)}
                  </Td>
                </Tr>
                <Tr>
                  {/* EOL予定日（設備の実際のEOL予定日。未設定の既存案件は計上期間終了日(projectEndDate)をフォールバック表示） */}
                  <Td>{t('features.InvestmentScreening.BasicInfo.field.eolDate')}</Td>
                  <Td>
                    {(() => {
                      const eolDateValue = projectData?.eolDate ?? projectData?.projectEndDate;
                      return eolDateValue ? dayjs(eolDateValue).format(t('common.dateFormat.dayjsFullDate')) : "";
                    })()}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.equipmentAcceptanceDate')}</Td>
                  <Td>
                    {projectData && projectData.projectStartDate ? dayjs(projectData.projectStartDate).format(t('common.dateFormat.dayjsFullDate')) : ""}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.paybackPeriod')}</Td>
                  <Td>
                    {dispProfitAndLoss.paybackPeriodByYear?.paybackPeriod ? `${dispProfitAndLoss.paybackPeriodByYear.paybackPeriod}` : ""}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('features.InvestmentScreening.ReportAndDraft.field.downsideRisk')}</Td>
                  <Td whiteSpace="pre-wrap">
                    {projectData?.downsideRisk}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </Box>
        <Box id="section-2" mb={4} className="pdf-content section">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.overview')}
            </Heading>
            <Flex mb={4}>

              {uploadedImageFileContent ? (
                <ChakraImage
                  src={uploadedImageFileContent}
                  alt={t('features.InvestmentScreening.ReportAndDraft.productInfo.productImageAlt')}
                  boxSize="300px"
                  objectFit="contain"
                  mr={10}
                  ml={10}
                />
              ) : (<Text display="Flex" alignItems="center" justifyContent="center" w="100%" h="100%" fontWeight="bold">No Image</Text>)}

              <Table variant="simple" size="sm">
                <Tbody>
                  <Tr>
                    <Td>{t('features.InvestmentScreening.ReportAndDraft.field.productName')}</Td>
                    <Td>
                      {projectData?.customerProductName}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.finalCustomer')}</Td>
                    <Td>
                      {splitEscapedList(projectData?.endUserName).join(" / ")}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.customerIndustry')}</Td>
                    <Td>
                      {projectData?.customerIndustryHierarchy}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.application')}</Td>
                    <Td>
                      {projectData?.applicationCategory}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.mountedModel')}</Td>
                    <Td>
                      {projectData?.carModel}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.specAndPerformance')}</Td>
                    <Td>
                      {projectData?.specificationAndPerformance}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Flex>
            <Heading as="h3" size="md" mb={2}>
              {t('features.InvestmentScreening.ReportAndDraft.heading.investmentPurpose')}
            </Heading>
            <Text mb={2} style={{ whiteSpace: 'pre-wrap' }}>
              {projectData?.purposeOfInvestment}
            </Text>
          </Box>
        </Box>
        <Box id="section-3" mb={4} className="pdf-content section">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.heading.pricingAgreement')}
            </Heading>

            {renderPriceAgreement()}

          </Box>
        </Box>
        {/* セクション：販売数量・販売単価 */}
        <Box id="section-4" mb={4} className="section">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md" className="pdf-content">
            <Heading as="h2" size="md" mb={4}>
              {labelQuantityAndPrice}
            </Heading>
            {/* 簡易モード時はメッセージのみ表示 */}
            {isSimpleMode && (
              <Text color="gray.500" fontSize="sm">
                {t('features.InvestmentScreening.ReportAndDraft.message.simpleModeSkipped')}
              </Text>
            )}
            {!isSimpleMode && <>
              <Heading as="h3" size="md" mb={2}>
                {t('features.InvestmentScreening.ReportAndDraft.heading.salesRevenueEstimate')}
              </Heading>
              {/* 振り返りモード時は月別レートで変換されるため、単一レート表示は誤解を招くので非表示 */}
              {!reviewDate && (
                <Heading size="sm" mb={2}>
                  {t('features.InvestmentScreening.ReportAndDraft.label.currency')}：{selectedCurrency.name || "--"} / {t('features.InvestmentScreening.ReportAndDraft.label.exchangeRate')}：{formatExchangeRate(currencyData.find(c => c.currencyId === selectedCurrency.id)?.exchangeRate)}
                </Heading>
              )}

              {partNoDataMap && Array.from(partNoDataMap.entries()).map(([partNo, data], partIndex) => {
                // 数量配列の取得
                const partQuantitiesData = quantitiesByPartNo.find(item => item.partNo === partNo);
                let quantities: Decimal[] = partQuantitiesData ? partQuantitiesData.quantities : [];
                quantities = adjustArrayLength(quantities, maxYears.length, new Decimal(0));

                // 単価配列の取得
                const partPricesData = pricesByPartNo.find(item => item.partNo === partNo);
                let prices: Decimal[] = partPricesData ? partPricesData.prices : [];
                prices = adjustArrayLength(prices, maxYears.length, new Decimal(0));
                // 売上金額配列の取得
                const partTotalSalesData = totalSalesByPartNo.find(item => item.partNo === partNo);
                let totalSales: Decimal[] = partTotalSalesData ? partTotalSalesData.totalSales : [];
                totalSales = adjustArrayLength(totalSales, maxYears.length, new Decimal(0));
                // PartNo金型配列の取得
                const partMoldsData = moldSalesPartNoYear.find(item => item.partNo === partNo);
                let moldPrices: Decimal[] = partMoldsData ? partMoldsData.totalSales : [];
                moldPrices = adjustArrayLength(moldPrices, maxYears.length, new Decimal(0));
                // PartNo開発費配列の取得
                const partDevsData = devSalesPartNoYear.find(item => item.partNo === partNo);
                let devPrices: Decimal[] = partDevsData ? partDevsData.totalSales : [];
                devPrices = adjustArrayLength(devPrices, maxYears.length, new Decimal(0));

                return (
                  <Box key={`${partNo}-actualYear-${partIndex}`} mb={4}>
                    <Heading as="h4" size="sm" mb={2}>
                      PartNo: {resolveDisplayPartNoByKey(partNo, partNoData)}
                    </Heading>
                    {/* 振り返りモード時は月別レートで変換されるため、単一レート表示は誤解を招くので非表示 */}
                    {!reviewDate && (
                      <Text>{t('features.InvestmentScreening.ReportAndDraft.productInfo.salesCurrencyLabel')}: {currencyNameFilter(currencyData, data.currencyAndRatio)} / {t('features.InvestmentScreening.ReportAndDraft.productInfo.exchangeRateLabel')}: {formatExchangeRate(currencyRateFilter(currencyData, data.currencyAndRatio))}</Text>
                    )}
                    <TableContainer>
                      <Table id={`${QUANTITY_PRICE_PARTNO_TABLE_PREFIX}${partIndex}`} variant="simple" size="sm" mb={4} className="mm-ceas-table" sx={{ tableLayout: 'auto' }}>
                        <Thead>
                          <Tr>
                            <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                            <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                            {maxYears.map((year, index) => (
                              <Th key={`price-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                            ))}
                            <Th className="fixed-width" rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                          </Tr>
                          <Tr>
                            {maxYears.map((year, index) => (
                              <Th key={`${partNo}-actualYear-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td className="fixed-width">{labelQuantity}</Td>
                            <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.unitPcs')}</Td>
                            {quantities.map((quantity, idx) => (
                              <Td key={idx} textAlign="right">
                                {formatNumber(quantity, "")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {formatNumber(
                                calculateTotal(
                                  quantities.map((quantity) => { return quantity; })
                                ),
                                ""
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            <Td className="fixed-width">{labelPrice}{t('features.InvestmentScreening.ReportAndDraft.plTable.weightedAvgSuffix')}</Td>
                            <Td>{selectedCurrency.name}</Td>
                            {prices.map((prices, idx) => (
                              <Td key={idx} textAlign="right">
                                {formatNumber(prices, "WithDecimal", 4)}
                              </Td>
                            ))}
                            <Td textAlign="right">-</Td>
                          </Tr>
                          <Tr>
                            <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.productRevenue')}</Td>
                            <Td>{divisorStr}{selectedCurrency.name}</Td>
                            {totalSales.map((sales, idx) => (
                              <Td key={idx} textAlign="right">
                                {formatNumber(sales, "WithDecimal")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {
                                formatNumber(
                                  calculateTotal(
                                    totalSales.map((sales) => { return sales; })
                                  ),
                                  "WithDecimal"
                                )
                              }
                            </Td>
                          </Tr>
                          {moldPrices && moldPrices.length > 0 && (
                            <Tr>
                              <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.mold')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {moldPrices.map((mold, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(mold, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {
                                  formatNumber(
                                    calculateTotal(
                                      moldPrices.map((mold) => { return mold; })
                                    ),
                                    "WithDecimal"
                                  )
                                }
                              </Td>
                            </Tr>
                          )}
                          {devPrices && devPrices.length > 0 && (
                            <Tr>
                              <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.devCost')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {devPrices.map((dev, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(dev, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {
                                  formatNumber(
                                    calculateTotal(
                                      devPrices.map((dev) => { return dev; })
                                    ),
                                    "WithDecimal"
                                  )
                                }
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )
              })}

              {moldSalesByProjectYear && moldSalesByProjectYear.length > 0 && (
                (() => {
                  const moldSales = adjustArrayLength(moldSalesByProjectYear, maxYears.length, new Decimal(0));

                  return (
                    <>
                      <Heading as="h4" size="sm" mb={2}>
                        {t('features.InvestmentScreening.ReportAndDraft.plTable.moldProjectUnit')}
                      </Heading>
                      <TableContainer>
                        <Table id={`${QUANTITY_PRICE_MOLD_TABLE_PREFIX}`} variant="simple" size="sm" mb={4} className="mm-ceas-table" sx={{ tableLayout: 'auto' }}>
                          <Thead>
                            <Tr>
                              <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                              <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                              {maxYears.map((year, index) => (
                                <Th key={`projectmold-first-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                              ))}
                              <Th className="fixed-width" rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                            </Tr>
                            <Tr>
                              {maxYears.map((year, index) => (
                                <Th key={`projectmold-first-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.mold')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {moldSales.map((moldByProject, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(moldByProject, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(
                                  calculateTotal(moldSales)
                                  , "WithDecimal")}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()
              )}

              {devSalesByProjectYear && devSalesByProjectYear.length > 0 && (
                (() => {
                  const devSales = adjustArrayLength(devSalesByProjectYear, maxYears.length, new Decimal(0));

                  return (
                    <>
                      <Heading as="h4" size="sm" mb={2}>
                        {t('features.InvestmentScreening.ReportAndDraft.plTable.devCostProjectUnit')}
                      </Heading>
                      <TableContainer>
                        <Table id={`${QUANTITY_PRICE_DEV_TABLE_PREFIX}`} variant="simple" size="sm" mb={4} className="mm-ceas-table" sx={{ tableLayout: 'auto' }}>
                          <Thead>
                            <Tr>
                              <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                              <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                              {maxYears.map((year, index) => (
                                <Th key={`dev-year-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                              ))}
                              <Th className="fixed-width" rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                            </Tr>
                            <Tr>
                              {maxYears.map((year, index) => (
                                <Th key={`projectdev-first-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.devCost')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {devSales.map((devByProject, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(devByProject, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(
                                  calculateTotal(devSales)
                                  , "WithDecimal")}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()
              )}

              {equipmentSalesByProjectYear && equipmentSalesByProjectYear.length > 0 && (
                (() => {
                  const equipmentSales = adjustArrayLength(equipmentSalesByProjectYear, maxYears.length, new Decimal(0));

                  return (
                    <>
                      <Heading as="h4" size="sm" mb={2}>
                        {t('features.InvestmentScreening.ReportAndDraft.plTable.equipmentCostProjectUnit')}
                      </Heading>
                      <TableContainer>
                        <Table id={`${QUANTITY_PRICE_EQUIPMENT_TABLE_PREFIX}`} variant="simple" size="sm" mb={4} className="mm-ceas-table" sx={{ tableLayout: 'auto' }}>
                          <Thead>
                            <Tr>
                              <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                              <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                              {maxYears.map((year, index) => (
                                <Th key={`equipment-year-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                              ))}
                              <Th className="fixed-width" rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                            </Tr>
                            <Tr>
                              {maxYears.map((year, index) => (
                                <Th key={`projectequipment-first-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.equipmentCost')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {equipmentSales.map((equipmentByProject, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(equipmentByProject, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(
                                  calculateTotal(equipmentSales)
                                  , "WithDecimal")}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()
              )}

              <Heading as="h4" size="sm" mb={2}>
                {t('features.InvestmentScreening.ReportAndDraft.plTable.projectTotal')}
              </Heading>
              <TableContainer>
                <Table
                  id={`${QUANTITY_PRICE_PROJECT_TOTAL_TABLE_PREFIX}`}
                  variant="simple"
                  size="sm"
                  mb={2}
                  className="mm-ceas-table"
                  sx={{ tableLayout: "auto" }}
                >
                  <Thead>
                    <Tr>
                      <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                      <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                      {maxYears.map((year, index) => (
                        <Th key={`project-year-${index}`} textAlign="center">
                          {t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}
                        </Th>
                      ))}
                      <Th className="fixed-width" rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                    </Tr>
                    <Tr>
                      {maxYears.map((year, index) => (
                        <Th key={`projectTotal-act-${year}-${index}`} colSpan={1} textAlign="center">
                          {baseStartYear + year - 1}{YEARS_UNIT}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(() => {
                      // 各データ配列をadjustArrayLengthで正規化
                      const normalizedTotalSales = adjustArrayLength(projectTotalSalesByYear, maxYears.length, new Decimal(0));
                      const normalizedMoldSales = adjustArrayLength(projectTotalMoldSalesByYear, maxYears.length, new Decimal(0));
                      const normalizedDevSales = adjustArrayLength(projectTotalDevSalesByYear, maxYears.length, new Decimal(0));
                      const normalizedGrandTotal = adjustArrayLength(projectGrandTotalByYear, maxYears.length, new Decimal(0));

                      return (
                        <>
                          <Tr>
                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.productRevenue')}</Td>
                            <Td>{divisorStr}{selectedCurrency.name}</Td>
                            {normalizedTotalSales.map((projectSalesTotal, index) => (
                              <Td key={`totalSales-${index}`} textAlign="right">
                                {formatNumber(projectSalesTotal, "WithDecimal")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {formatNumber(
                                calculateTotal(normalizedTotalSales),
                                "WithDecimal"
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.mold')}</Td>
                            <Td>{divisorStr}{selectedCurrency.name}</Td>
                            {normalizedMoldSales.map((projectMoldTotal, index) => (
                              <Td key={`moldSales-${index}`} textAlign="right">
                                {formatNumber(projectMoldTotal, "WithDecimal")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {formatNumber(
                                calculateTotal(normalizedMoldSales),
                                "WithDecimal"
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.devCost')}</Td>
                            <Td>{divisorStr}{selectedCurrency.name}</Td>
                            {normalizedDevSales.map((projectDevTotal, index) => (
                              <Td key={`devSales-${index}`} textAlign="right">
                                {formatNumber(projectDevTotal, "WithDecimal")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {formatNumber(
                                calculateTotal(normalizedDevSales),
                                "WithDecimal"
                              )}
                            </Td>
                          </Tr>
                          <Tr>
                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.grandTotal')}</Td>
                            <Td>{divisorStr}{selectedCurrency.name}</Td>
                            {normalizedGrandTotal.map((projectGrandTotal, index) => (
                              <Td key={`grandTotal-${index}`} textAlign="right">
                                {formatNumber(projectGrandTotal, "WithDecimal")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {formatNumber(
                                calculateTotal(normalizedGrandTotal),
                                "WithDecimal"
                              )}
                            </Td>
                          </Tr>
                        </>
                      );
                    })()}
                  </Tbody>
                </Table>
              </TableContainer>
              {projectData?.quantityPriceFreeText &&
                <>
                  <Heading as="h3" size="md" mt={2} mb={2}>
                    {t('features.InvestmentScreening.ReportAndDraft.plTable.freeTextTitle')}
                  </Heading>
                  <Text style={ecTdStyleTextArea} mb={4}>{projectData?.quantityPriceFreeText}</Text>
                </>
              }
              <Heading as="h3" size="md" mt={2} mb={2}>
                {t('features.InvestmentScreening.ReportAndDraft.plTable.plannedQuantityEOL')}
              </Heading>
              <Box mb={2}>
                {partNoDataMap && Array.from(partNoDataMap.entries()).map(([partNo, data], partIndex) => {
                  // 数量配列の取得
                  const partQuantitiesData = quantitiesByPartNo.find((item) => item.partNo === partNo);
                  let quantities: Decimal[] = partQuantitiesData ? partQuantitiesData.quantities : [];
                  quantities = adjustArrayLength(quantities, maxYears.length, new Decimal(0));

                  return (
                    <div key={partNo}>
                      {resolveDisplayPartNoByKey(partNo, partNoData)}: {formatNumber(
                        calculateTotal(quantities.map((quantity) => { return quantity; })),
                        ""
                      )}
                    </div>
                  );
                })}
              </Box>
            </>}
          </Box>
        </Box>

        {/* 損益非表示モード時の案内（P&L）: 見出しは残して本文をメッセージに差し替える */}
        {isProfitLossHiddenMode && (
          <Box mb={4} id="section-5" className="pdf-content section">
            <Box p={4} bg="white" boxShadow="md" borderRadius="md">
              <Heading as="h2" size="md" mb={4}>
                P&L
              </Heading>
              <Text color="gray.500" fontSize="sm">
                {t('common.message.profitLossHiddenSectionNote')}
              </Text>
            </Box>
          </Box>
        )}

        {/* 損益試算（P&L）: 損益非表示モード時はセクションごと非表示 */}
        {!isProfitLossHiddenMode && (
          <Box mb={4} id="section-5" className="pdf-content section">
            <Box p={4} bg="white" boxShadow="md" borderRadius="md">
              <Heading as="h2" size="md" mb={4}>
                P&L
              </Heading>
              {/* PartNo別テーブル：簡易モード時は非表示 */}
              {!isSimpleMode && <>
                {/* PartNoごとの年次テーブル */}
                {partNoDataMap && Array.from(partNoDataMap.entries()).map(([partNo, data], partIndex) => {

                  const adjustArrayLength = (array: Decimal[], max: number, defaultValue: Decimal) => {
                    const result = [...array];
                    // 配列の長さを調整
                    while (result.length < max) {
                      result.push(new Decimal(defaultValue));
                    }
                    return result;
                  };
                  // 数量配列の取得
                  const partQuantitiesData = quantitiesByPartNo.find(item => item.partNo === partNo);
                  let quantities: Decimal[] = partQuantitiesData ? partQuantitiesData.quantities : [];
                  const curNumberOfYear: number = quantities.length; // PartNoの年数を取得 材料単価配列の取得で使用
                  quantities = adjustArrayLength(quantities, maxYears.length, new Decimal(0));

                  // 単価配列の取得
                  const partPricesData = pricesByPartNo.find(item => item.partNo === partNo);
                  let prices: Decimal[] = partPricesData ? partPricesData.prices : [];
                  prices = adjustArrayLength(prices, maxYears.length, new Decimal(0));
                  // 売上金額配列の取得
                  const partTotalSalesData = totalSalesByPartNo.find(item => item.partNo === partNo);
                  let totalSales: Decimal[] = partTotalSalesData ? partTotalSalesData.totalSales : [];
                  totalSales = adjustArrayLength(totalSales, maxYears.length, new Decimal(0));
                  // PartNo金型配列の取得
                  const partMoldsData = moldSalesPartNoYear.find(item => item.partNo === partNo);
                  let moldPrices: Decimal[] = partMoldsData ? partMoldsData.totalSales : [];
                  moldPrices = adjustArrayLength(moldPrices, maxYears.length, new Decimal(0));
                  // PartNo開発費配列の取得
                  const partDevsData = devSalesPartNoYear.find(item => item.partNo === partNo);
                  let devPrices: Decimal[] = partDevsData ? partDevsData.totalSales : [];
                  devPrices = adjustArrayLength(devPrices, maxYears.length, new Decimal(0));
                  // PartNo製品売上高、金型、開発費合計
                  const partMergesData = mergePartNoSalesByYear.find(item => item.partNo === partNo);
                  let mergePrices: Decimal[] = partMergesData ? partMergesData.totalSales : [];
                  mergePrices = adjustArrayLength(mergePrices, maxYears.length, new Decimal(0));
                  // PartNo材料単価（年度別加重平均）
                  const partUnitMatCostData = materialCostByPartNo.find(item => item.partNo === partNo);
                  let unitMatCosts: Decimal[] = partUnitMatCostData ? partUnitMatCostData.materialCosts : [];
                  unitMatCosts = adjustArrayLength(unitMatCosts, maxYears.length, new Decimal(0));

                  // PartNo材料費
                  const partMatCostData = totalMaterialCostsByPartNo.find(item => item.partNo === partNo);
                  let materialCosts: Decimal[] = partMatCostData ? partMatCostData.totalMaterialCosts : [];
                  materialCosts = adjustArrayLength(materialCosts, maxYears.length, new Decimal(0));
                  // スループット合計
                  const partThroughputsData = throughputsByPartNo.find(item => item.partNo === partNo);
                  let throughputs: Decimal[] = partThroughputsData ? partThroughputsData.throughputs : [];
                  throughputs = adjustArrayLength(throughputs, maxYears.length, new Decimal(0));
                  // スループット率
                  const partThroughputRatesData = throughputRatesByPartNo.find(item => item.partNo === partNo);
                  let throughputRates: Decimal[] = partThroughputRatesData ? partThroughputRatesData.throughputRate : [];
                  throughputRates = adjustArrayLength(throughputRates, maxYears.length, new Decimal(0));

                  // 外注のみ材料単価（年度別加重平均）
                  const outsourcingUnitMatCostData = outsourcingUnitMaterialCostByPartNo.find(item => item.partNo === partNo);
                  let outsourcingUnitMatCosts: Decimal[] = outsourcingUnitMatCostData ? outsourcingUnitMatCostData.materialCosts : [];
                  outsourcingUnitMatCosts = adjustArrayLength(outsourcingUnitMatCosts, maxYears.length, new Decimal(0));
                  // 外注のみ材料費
                  const outsourcingMatCostData = outsourcingMaterialCostsByPartNo.find(item => item.partNo === partNo);
                  let outsourcingMaterialCosts: Decimal[] = outsourcingMatCostData ? outsourcingMatCostData.totalMaterialCosts : [];
                  outsourcingMaterialCosts = adjustArrayLength(outsourcingMaterialCosts, maxYears.length, new Decimal(0));
                  // 外注のみスループット
                  const outsourcingMatThroughputs = outsourcingThroughputsByPartNo.find(item => item.partNo === partNo);
                  let outsourcingMaterialThroughputs: Decimal[] = outsourcingMatThroughputs ? outsourcingMatThroughputs.throughputs : [];
                  outsourcingMaterialThroughputs = adjustArrayLength(outsourcingMaterialThroughputs, maxYears.length, new Decimal(0));
                  // 外注のみスループット率
                  const partOutsourcingThroughputRatesData = outsourcingThroughputRatesByPartNo.find(item => item.partNo === partNo);
                  let outsourcingThroughputRates: Decimal[] = partOutsourcingThroughputRatesData ? partOutsourcingThroughputRatesData.throughputRate : [];
                  outsourcingThroughputRates = adjustArrayLength(outsourcingThroughputRates, maxYears.length, new Decimal(0));

                  // 内製のみ材料単価（年度別加重平均）
                  const inhouseUnitMatCostData = inhouseUnitMaterialCostByPartNo.find(item => item.partNo === partNo);
                  let inhouseUnitMatCosts: Decimal[] = inhouseUnitMatCostData ? inhouseUnitMatCostData.materialCosts : [];
                  inhouseUnitMatCosts = adjustArrayLength(inhouseUnitMatCosts, maxYears.length, new Decimal(0));

                  // 内製のみ材料費
                  const inhouseMatCostData = inhouseMaterialCostsByPartNo.find(item => item.partNo === partNo);
                  let inhouseMaterialCosts: Decimal[] = inhouseMatCostData ? inhouseMatCostData.totalMaterialCosts : [];
                  inhouseMaterialCosts = adjustArrayLength(inhouseMaterialCosts, maxYears.length, new Decimal(0));
                  // 内製のみスループット
                  const inhouseMatThroughputs = inhouseThroughputsByPartNo.find(item => item.partNo === partNo);
                  let inhouseMaterialThroughputs: Decimal[] = inhouseMatThroughputs ? inhouseMatThroughputs.throughputs : [];
                  inhouseMaterialThroughputs = adjustArrayLength(inhouseMaterialThroughputs, maxYears.length, new Decimal(0));
                  // 内製のみスループット率
                  const partInhouseThroughputRatesData = inhouseThroughputRatesByPartNo.find(item => item.partNo === partNo);
                  let inhouseThroughputRates: Decimal[] = partInhouseThroughputRatesData ? partInhouseThroughputRatesData.throughputRate : [];
                  inhouseThroughputRates = adjustArrayLength(inhouseThroughputRates, maxYears.length, new Decimal(0));

                  return (
                    <Box key={partNo} mt={6}>
                      <Text fontSize="lg" fontWeight="bold" textAlign="left" mb={2}>
                        PartNo: {resolveDisplayPartNoByKey(partNo, partNoData)}
                      </Text>
                      {/* 振り返りモード時は月別レートで変換されるため、単一レート表示は誤解を招くので非表示 */}
                      {!reviewDate && (
                        <Text>{t('features.InvestmentScreening.ReportAndDraft.productInfo.salesCurrencyLabel')}: {currencyNameFilter(currencyData, data.currencyAndRatio)} / {t('features.InvestmentScreening.ReportAndDraft.productInfo.exchangeRateLabel')}: {formatExchangeRate(currencyRateFilter(currencyData, data.currencyAndRatio))}</Text>
                      )}
                      <TableContainer>
                        <Table
                          id={`${PROFIT_AND_LOSS_PARTNO_TABLE_PREFIX}${partIndex}`}
                          className="mm-ceas-table"
                          size="sm"
                          variant="simple"
                          sx={{ tableLayout: 'auto' }}
                        >
                          <Thead>
                            <Tr>
                              <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                              <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                              {maxYears.map((year, index) => (
                                <Th key={`${partNo}-year-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                              ))}
                              <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                            </Tr>
                            <Tr>
                              {maxYears.map((year, index) => (
                                <Th key={`${partNo}-actualYear-${year}`} textAlign="center">
                                  {baseStartYear + year - 1}{YEARS_UNIT}
                                </Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td>{labelQuantity}</Td>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.unitPcs')}</Td>
                              {quantities.map((quantity, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(quantity, "")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(quantities.reduce((sum, v) => sum.plus(v), new Decimal(0)), "")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td className={`border-bottom-bold`}>{labelPrice}{t('features.InvestmentScreening.ReportAndDraft.plTable.weightedAvgSuffix')}</Td>
                              <Td className={`border-bottom-bold`}>{selectedCurrency.name}</Td>
                              {prices.map((price, idx) => (
                                <Td key={idx} textAlign="right" className={`border-bottom-bold`}>
                                  {formatNumber(price, "WithDecimal", 4)}
                                </Td>
                              ))}
                              <Td textAlign="right" className={`border-bottom-bold`}>-</Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.productSales')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {totalSales.map((sales, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(sales, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(totalSales.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.moldSales')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {moldPrices.map((price, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(price, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(moldPrices.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td className={`border-bottom-bold`}>{t('features.InvestmentScreening.ReportAndDraft.plTable.devCostSales')}</Td>
                              <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                              {devPrices.map((price, idx) => (
                                <Td key={idx} textAlign="right" className={`border-bottom-bold`}>
                                  {formatNumber(price, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right" className={`border-bottom-bold`}>
                                {formatNumber(devPrices.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.totalSales')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {mergePrices.map((price, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(price, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(mergePrices.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.materialUnitCostWeighted')}</Td>
                              <Td>{selectedCurrency.name}</Td>
                              {unitMatCosts.map((unitMatCost, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(unitMatCost, "WithDecimal", 4)}
                                </Td>
                              ))}
                              <Td textAlign="right">-</Td>
                            </Tr>
                            <Tr>
                              <Td className={`border-bottom-bold`}>{t('features.InvestmentScreening.ReportAndDraft.plTable.materialCostWithRatio', { ratio: data.inhouseAvgRatio.toFixed(1) })}</Td>
                              <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                              {materialCosts.map((cost, idx) => (
                                <Td key={idx} textAlign="right" className={`border-bottom-bold`}>
                                  {formatNumber(cost, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right" className={`border-bottom-bold`}>
                                {formatNumber(materialCosts.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.throughputAmount')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {throughputs.map((throughput, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(throughput, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(throughputs.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.throughputRate')}</Td>
                              <Td>%</Td>
                              {throughputRates.map((throughputRate, idx) => (
                                <Td key={idx} textAlign="right">
                                  {throughputRate?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                </Td>
                              ))}
                              <Td textAlign="right">-</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>

                      <Text mt={4}>{t('features.InvestmentScreening.ReportAndDraft.plTable.outsourcingOnly')}</Text>
                      <TableContainer>
                        <Table
                          id={`${PROFIT_AND_LOSS_PARTNO_OUTSOURC_TABLE_PREFIX}${partIndex}`}
                          className="mm-ceas-table"
                          size="sm"
                          variant="simple"
                          sx={{ tableLayout: 'auto' }}
                        >
                          <Thead>
                            <Tr>
                              <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                              <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                              {maxYears.map((year, index) => (
                                <Th key={`${partNo}-year-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                              ))}
                              <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                            </Tr>
                            <Tr>
                              {maxYears.map((year, index) => (
                                <Th key={`${partNo}-actualYear-${year}`} textAlign="center">
                                  {baseStartYear + year - 1}{YEARS_UNIT}
                                </Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td>{labelQuantity}</Td>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.unitPcs')}</Td>
                              {quantities.map((quantity, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(quantity, "")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(quantities.reduce((sum, v) => sum.plus(v), new Decimal(0)), "")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.totalSales')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {mergePrices.map((price, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(price, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(mergePrices.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.materialUnitCostWeighted')}</Td>
                              <Td>{selectedCurrency.name}</Td>
                              {outsourcingUnitMatCosts.map((outsourcingUnitMatCost, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(outsourcingUnitMatCost, "WithDecimal", 4)}
                                </Td>
                              ))}
                              <Td textAlign="right">-</Td>
                            </Tr>
                            <Tr>
                              <Td className={`border-bottom-bold`}>{t('features.InvestmentScreening.ReportAndDraft.plTable.materialCostOutsourceOnly')}</Td>
                              <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                              {outsourcingMaterialCosts.map((cost, idx) => (
                                <Td key={idx} textAlign="right" className={`border-bottom-bold`}>
                                  {formatNumber(cost, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right" className={`border-bottom-bold`}>
                                {formatNumber(outsourcingMaterialCosts.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.throughputAmount')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {outsourcingMaterialThroughputs.map((throughput, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(throughput, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(outsourcingMaterialThroughputs.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.throughputRate')}</Td>
                              <Td>%</Td>
                              {outsourcingThroughputRates.map((throughputRate, idx) => (
                                <Td key={idx} textAlign="right">
                                  {throughputRate?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                </Td>
                              ))}
                              <Td textAlign="right">-</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>

                      <Text mt={4}>{t('features.InvestmentScreening.ReportAndDraft.plTable.inhouseOnly')}</Text>
                      <TableContainer>
                        <Table
                          id={`${PROFIT_AND_LOSS_PARTNO_INHOUSE_TABLE_PREFIX}${partIndex}`}
                          className="mm-ceas-table"
                          size="sm"
                          variant="simple"
                          sx={{ tableLayout: 'auto' }}
                        >
                          <Thead>
                            <Tr>
                              <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                              <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                              {maxYears.map((year, index) => (
                                <Th key={`${partNo}-year-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                              ))}
                              <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                            </Tr>
                            <Tr>
                              {maxYears.map((year, index) => (
                                <Th key={`${partNo}-actualYear-${year}`} textAlign="center">
                                  {baseStartYear + year - 1}{YEARS_UNIT}
                                </Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td>{labelQuantity}</Td>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.productInfo.unitPcs')}</Td>
                              {quantities.map((quantity, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(quantity, "")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(quantities.reduce((sum, v) => sum.plus(v), new Decimal(0)), "")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.totalSales')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {mergePrices.map((price, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(price, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(mergePrices.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.materialUnitCostWeighted')}</Td>
                              <Td>{selectedCurrency.name}</Td>
                              {inhouseUnitMatCosts.map((inhouseUnitMatCost, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(inhouseUnitMatCost, "WithDecimal", 4)}
                                </Td>
                              ))}
                              <Td textAlign="right">-</Td>
                            </Tr>
                            <Tr>
                              <Td className={`border-bottom-bold`}>{t('features.InvestmentScreening.ReportAndDraft.plTable.materialCostInhouseOnly')}</Td>
                              <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                              {inhouseMaterialCosts.map((cost, idx) => (
                                <Td key={idx} textAlign="right" className={`border-bottom-bold`}>
                                  {formatNumber(cost, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right" className={`border-bottom-bold`}>
                                {formatNumber(inhouseMaterialCosts.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.throughputAmount')}</Td>
                              <Td>{divisorStr}{selectedCurrency.name}</Td>
                              {inhouseMaterialThroughputs.map((throughput, idx) => (
                                <Td key={idx} textAlign="right">
                                  {formatNumber(throughput, "WithDecimal")}
                                </Td>
                              ))}
                              <Td textAlign="right">
                                {formatNumber(inhouseMaterialThroughputs.reduce((sum, v) => sum.plus(v), new Decimal(0)), "WithDecimal")}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.throughputRate')}</Td>
                              <Td>%</Td>
                              {inhouseThroughputRates.map((throughputRate, idx) => (
                                <Td key={idx} textAlign="right">
                                  {throughputRate?.toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                </Td>
                              ))}
                              <Td textAlign="right">-</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )
                })}

                {/* 金型（プロジェクト単位） */}
                {moldSalesByProjectYear && moldSalesByProjectYear.length > 0 && (
                  (() => {
                    const moldSales = adjustArrayLength(moldSalesByProjectYear, maxYears.length, new Decimal(0));

                    return (
                      <>
                        <Heading as="h4" size="sm" mt={4} mb={2}>
                          {t('features.InvestmentScreening.ReportAndDraft.plTable.moldProjectUnit')}
                        </Heading>
                        <TableContainer>
                          <Table
                            id={`${PROFIT_AND_LOSS_MOLD_TABLE_PREFIX}`}
                            className="mm-ceas-table"
                            variant="simple"
                            size="sm" mb={4}
                            sx={{ tableLayout: 'auto' }}
                          >
                            <Thead>
                              <Tr>
                                <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                                <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                                {maxYears.map((year, index) => (
                                  <Th key={`projectmold-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                                ))}
                              </Tr>
                              <Tr>
                                {maxYears.map((year, index) => (
                                  <Th key={`projectmold-second-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              <Tr>
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.mold')}</Td>
                                <Td>{divisorStr}{selectedCurrency.name}</Td>
                                {moldSales.map((moldByProject, idx) => (
                                  <Td key={idx} textAlign="right">
                                    {formatNumber(moldByProject, "WithDecimal")}
                                  </Td>
                                ))}
                              </Tr>
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </>
                    );
                  })()
                )}

                {/* 開発費（プロジェクト単位） */}
                {devSalesByProjectYear && devSalesByProjectYear.length > 0 && (
                  (() => {
                    const devSales = adjustArrayLength(devSalesByProjectYear, maxYears.length, new Decimal(0));

                    return (
                      <>
                        <Heading as="h4" size="sm" mt={4} mb={2}>
                          {t('features.InvestmentScreening.ReportAndDraft.plTable.devCostProjectUnit')}
                        </Heading>
                        <TableContainer>
                          <Table
                            id={`${PROFIT_AND_LOSS_DEV_TABLE_PREFIX}`}
                            className="mm-ceas-table"
                            variant="simple"
                            size="sm"
                            mb={4}
                            sx={{ tableLayout: 'auto' }}
                          >
                            <Thead>
                              <Tr>
                                <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                                <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                                {maxYears.map((year, index) => (
                                  <Th key={`projectdev-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                                ))}
                              </Tr>
                              <Tr>
                                {maxYears.map((year, index) => (
                                  <Th key={`projectdev-second-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              <Tr>
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.devCost')}</Td>
                                <Td>{divisorStr}{selectedCurrency.name}</Td>
                                {devSales.map((devByProject, idx) => (
                                  <Td key={idx} textAlign="right">
                                    {formatNumber(devByProject, "WithDecimal")}
                                  </Td>
                                ))}
                              </Tr>
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </>
                    );
                  })()
                )}

                {/* 設備費（プロジェクト単位） */}
                {equipmentSalesByProjectYear && equipmentSalesByProjectYear.length > 0 && (
                  (() => {
                    const equipmentSales = adjustArrayLength(equipmentSalesByProjectYear, maxYears.length, new Decimal(0));

                    return (
                      <>
                        <Heading as="h4" size="sm" mt={4} mb={2}>
                          {t('features.InvestmentScreening.ReportAndDraft.plTable.equipmentCostProjectUnit')}
                        </Heading>
                        <TableContainer>
                          <Table
                            id={`${PROFIT_AND_LOSS_EQUIPMENT_TABLE_PREFIX}`}
                            className="mm-ceas-table"
                            variant="simple"
                            size="sm"
                            mb={4}
                            sx={{ tableLayout: 'auto' }}
                          >
                            <Thead>
                              <Tr>
                                <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                                <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                                {maxYears.map((year, index) => (
                                  <Th key={`projectequipment-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                                ))}
                              </Tr>
                              <Tr>
                                {maxYears.map((year, index) => (
                                  <Th key={`projectequipment-second-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              <Tr>
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.plTable.equipmentCost')}</Td>
                                <Td>{divisorStr}{selectedCurrency.name}</Td>
                                {equipmentSales.map((equipmentByProject, idx) => (
                                  <Td key={idx} textAlign="right">
                                    {formatNumber(equipmentByProject, "WithDecimal")}
                                  </Td>
                                ))}
                              </Tr>
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </>
                    );
                  })()
                )}
              </>}

              {/* 年度別損益（更新投資の場合：更新前/更新後比較形式）: 簡易モード時は非表示 */}
              {!isSimpleMode && maxYears.length !== 0 && totalYearlyData && projectData?.purpose === PurposeType.UpdateAndOther.code && updateInvestmentProfitLossData && currencyData && currencyData.length > 0 && selectedCurrency?.name && (
                <Box mt={6}>
                  <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.plTable.annualPLBeforeAfterUpdate')}</Heading>
                  <TableContainer>
                    <Table
                      id={`${PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX}`}
                      className="mm-ceas-table"
                      size="sm"
                      variant="simple"
                      sx={{ tableLayout: 'auto' }}
                    >
                      <Thead>
                        <Tr>
                          <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                          <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                          <Th className="unit-width" rowSpan={2}>{t('common.label.category')}</Th>
                          {maxYears.map((year, index) => (
                            <Th key={`year-total-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                          ))}
                          <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                        </Tr>
                        <Tr>
                          {maxYears.map((year, index) => (
                            <Th key={`year-total-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(() => {
                          // 選択通貨がJPYの場合はJPY版を使用（二重変換回避）
                          const useDisplayVersion = displayUpdateInvestmentProfitLossData && selectedCurrency.name === CurrencyNameJPY;
                          const beforeData = useDisplayVersion
                            ? displayUpdateInvestmentProfitLossData.beforeUpdateProfitAndLossByYear
                            : updateInvestmentProfitLossData.beforeUpdateProfitAndLossByYear;
                          const afterData = dispProfitAndLoss.profitAndLossByYear;

                          // 更新前データの通貨変換用（元通貨から表示通貨に変換し、千単位に変換）
                          // JPY版使用時は変換不要
                          const localCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
                          const convertBeforeValue = (value: string): string => {
                            if (!value) return "0";
                            let decimalValue = new Decimal(value);
                            // JPY版使用時は変換済みのため、千単位変換のみ
                            if (!useDisplayVersion && localCurrency && selectedCurrency.id !== localCurrencyId) {
                              decimalValue = convertCurrency(decimalValue, localCurrency.currencyName!, selectedCurrency.name, currencyData);
                            }
                            return divideValue(decimalValue).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed();
                          };

                          // 行ペアを生成するヘルパー関数
                          const renderRowPair = (
                            label: string,
                            beforeValues: string[],
                            afterValues: string[],
                            unit: string,
                            indent: number = 0,
                            isRate: boolean = false,
                            borderClass: string = "",
                            overrideBeforeTotal?: string,
                            overrideAfterTotal?: string,
                            uniqueKey?: string
                          ) => {
                            const pl = indent > 0 ? `${indent}px` : undefined;
                            // 更新前の値を変換（レートの場合は変換不要）
                            const convertedBeforeValues = isRate ? beforeValues : beforeValues.map(v => convertBeforeValue(v));
                            const beforeTotal = overrideBeforeTotal !== undefined
                              ? new Decimal(overrideBeforeTotal)
                              : isRate
                                ? (() => {
                                  // レートの場合は合計の利益率を計算（変換後の値で計算）
                                  const totalProfit = beforeData.profit.reduce((sum, v) => sum.plus(new Decimal(convertBeforeValue(v) || "0")), new Decimal(0));
                                  const totalSales = beforeData.totalSales.reduce((sum, v) => sum.plus(new Decimal(convertBeforeValue(v) || "0")), new Decimal(0));
                                  return calcProfitRate(totalSales, totalProfit);
                                })()
                                : convertedBeforeValues.reduce((sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0));
                            const afterTotal = overrideAfterTotal !== undefined
                              ? new Decimal(overrideAfterTotal)
                              : isRate
                                ? (() => {
                                  const totalProfit = afterData.profit.reduce((sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0));
                                  const totalSales = afterData.totalSales.reduce((sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0));
                                  return calcProfitRate(totalSales, totalProfit);
                                })()
                                : afterValues.reduce((sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0));

                            return (
                              <React.Fragment key={uniqueKey ?? label}>
                                <Tr>
                                  <Td rowSpan={2} pl={pl} className={borderClass}>{label}</Td>
                                  <Td rowSpan={2} className={borderClass}>{unit}</Td>
                                  <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.beforeUpdate')}</Td>
                                  {maxYears.map((year, index) => (
                                    <Td key={`before-${label}-${year}-${index}`} textAlign="right">
                                      {isRate
                                        ? (new Decimal(beforeValues[index] || "0")).toFixed(1)
                                        : formatNumber(new Decimal(convertedBeforeValues[index] || "0"), "WithDecimal")}
                                    </Td>
                                  ))}
                                  <Td textAlign="right">
                                    {isRate ? beforeTotal.toFixed(1) : formatNumber(beforeTotal, "WithDecimal")}
                                  </Td>
                                </Tr>
                                <Tr>
                                  <Td className={borderClass}>{t('features.InvestmentScreening.ReportAndDraft.plTable.afterUpdate')}</Td>
                                  {maxYears.map((year, index) => (
                                    <Td key={`after-${label}-${year}-${index}`} textAlign="right" className={borderClass}>
                                      {isRate
                                        ? (new Decimal(afterValues[index] || "0")).toFixed(1)
                                        : formatNumber(new Decimal(afterValues[index] || "0"), "WithDecimal")}
                                    </Td>
                                  ))}
                                  <Td textAlign="right" className={borderClass}>
                                    {isRate ? afterTotal.toFixed(1) : formatNumber(afterTotal, "WithDecimal")}
                                  </Td>
                                </Tr>
                              </React.Fragment>
                            );
                          };

                          return (
                            <>
                              {renderRowPair(ProfitAndLossItems.totalSales.name, beforeData.totalSales, afterData.totalSales, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.totalSales.level))}
                              {renderRowPair(ProfitAndLossItems.productSales.name, beforeData.productSales, afterData.productSales, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.productSales.level))}
                              {renderRowPair(ProfitAndLossItems.moldRecovery.name, beforeData.moldSales, afterData.moldSales, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.moldRecovery.level))}
                              {renderRowPair(ProfitAndLossItems.devRecovery.name, beforeData.devSales, afterData.devSales, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.devRecovery.level))}
                              {renderRowPair(ProfitAndLossItems.equipmentRecovery.name, beforeData.equipmentSales, afterData.equipmentSales, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.equipmentRecovery.level), false, "border-bottom-bold")}
                              {renderRowPair(ProfitAndLossItems.salesCost.name, beforeData.salesCost, afterData.salesCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.salesCost.level))}
                              {renderRowPair(ProfitAndLossItems.materialCost.name, beforeData.totalMaterialCost, afterData.totalMaterialCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.materialCost.level))}
                              {renderRowPair(ProfitAndLossItems.outsideCost.name, beforeData.outsideCost, afterData.outsideCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.outsideCost.level))}
                              {renderRowPair(ProfitAndLossItems.laborCost.name, beforeData.laborCost, afterData.laborCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.laborCost.level))}
                              {renderRowPair(ProfitAndLossItems.domesticLaborCost.name, beforeData.domesticLaborCost || maxYears.map(() => "0"), afterData.domesticLaborCost || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticLaborCost.level))}
                              {renderRowPair(ProfitAndLossItems.factoryExpenses.name, beforeData.factoryExpenses, afterData.factoryExpenses, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.factoryExpenses.level))}
                              {renderRowPair(ProfitAndLossItems.overHeadCost.name, beforeData.overHeadCost, afterData.overHeadCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.overHeadCost.level))}
                              {renderRowPair(ProfitAndLossItems.depreciation.name, beforeData.depreciation, afterData.depreciation, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.depreciation.level))}
                              {renderRowPair(ProfitAndLossItems.equipmentDepreciation.name, beforeData.equipmentDepreciation, afterData.equipmentDepreciation, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.equipmentDepreciation.level))}
                              {renderRowPair(ProfitAndLossItems.factoryCost.name, beforeData.factoryCost, afterData.factoryCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.factoryCost.level))}
                              {projectData?.pastProjectId && renderRowPair(ProfitAndLossItems.pastEquipmentCost.name, beforeData.pastEquipmentCost || maxYears.map(() => "0"), afterData.pastEquipmentCost || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.pastEquipmentCost.level))}
                              {renderRowPair(ProfitAndLossItems.otherEquipmentCost.name, beforeData.otherEquipmentCost, afterData.otherEquipmentCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.otherEquipmentCost.level))}
                              {renderRowPair(ProfitAndLossItems.domesticFactoryExpenses.name, beforeData.domesticFactoryExpenses || maxYears.map(() => "0"), afterData.domesticFactoryExpenses || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticFactoryExpenses.level))}
                              {renderRowPair(ProfitAndLossItems.domesticOverHeadCost.name, beforeData.domesticOverHeadCost || maxYears.map(() => "0"), afterData.domesticOverHeadCost || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticOverHeadCost.level), false, "", undefined, undefined, "domesticOverHeadCost")}
                              {renderRowPair(ProfitAndLossItems.domesticDepreciation.name, beforeData.domesticDepreciation || maxYears.map(() => "0"), afterData.domesticDepreciation || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticDepreciation.level), false, "", undefined, undefined, "domesticDepreciation")}
                              {renderRowPair(ProfitAndLossItems.domesticInvestmentDepreciation.name, beforeData.domesticInvestmentDepreciation || maxYears.map(() => "0"), afterData.domesticInvestmentDepreciation || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticInvestmentDepreciation.level), false, "", undefined, undefined, "domesticInvestmentDepreciation")}
                              {renderRowPair(ProfitAndLossItems.domesticFactoryBuildingDepreciation.name, beforeData.domesticFactoryBuildingDepreciation || maxYears.map(() => "0"), afterData.domesticFactoryBuildingDepreciation || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticFactoryBuildingDepreciation.level), false, "", undefined, undefined, "domesticFactoryBuildingDepreciation")}
                              {renderRowPair(ProfitAndLossItems.domesticOtherDepreciation.name, beforeData.domesticOtherDepreciation || maxYears.map(() => "0"), afterData.domesticOtherDepreciation || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticOtherDepreciation.level), false, "", undefined, undefined, "domesticOtherDepreciation")}
                              {renderRowPair(ProfitAndLossItems.scrapCost.name, beforeData.scrapCost || maxYears.map(() => "0"), afterData.scrapCost || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.scrapCost.level), false, "border-bottom-bold")}
                              {renderRowPair(ProfitAndLossItems.sgaExpenses.name, beforeData.sGAExpenses, afterData.sGAExpenses, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.sgaExpenses.level))}
                              {renderRowPair(ProfitAndLossItems.appliedLaborCost.name, beforeData.appliedLaborCost, afterData.appliedLaborCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.appliedLaborCost.level))}
                              {renderRowPair(ProfitAndLossItems.domesticSgaCost.name, beforeData.domesticSgaCost || maxYears.map(() => "0"), afterData.domesticSgaCost || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.domesticSgaCost.level))}
                              {renderRowPair(ProfitAndLossItems.salesManagementCost.name, beforeData.salesManagementCost, afterData.salesManagementCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.salesManagementCost.level), false, "border-bottom-bold")}
                              {renderRowPair(ProfitAndLossItems.devCost.name, beforeData.devCost, afterData.devCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.devCost.level))}
                              {renderRowPair(ProfitAndLossItems.royalty.name, beforeData.royalty || maxYears.map(() => "0"), afterData.royalty || maxYears.map(() => "0"), `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.royalty.level))}
                              {renderRowPair(ProfitAndLossItems.otherCost.name, beforeData.otherCost, afterData.otherCost, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.otherCost.level))}
                              {renderRowPair(ProfitAndLossItems.operatingProfit.name, beforeData.profit, afterData.profit, `${divisorStr}${selectedCurrency.name}`, getLevelIndent(ProfitAndLossItems.operatingProfit.level))}
                              {renderRowPair(ProfitAndLossItems.operatingProfitRate.name, beforeData.profitRate, afterData.profitRate, "%", getLevelIndent(ProfitAndLossItems.operatingProfitRate.level), true)}
                              {/* 実質的営業利益率は値がある場合のみ表示（客先指定部品が存在する場合のみ） */}
                              {(afterData.realOperatingProfitRate?.some((v: string) => v && v !== "0" && new Decimal(v).greaterThan(0))) &&
                                renderRowPair(
                                  ProfitAndLossItems.realOperatingProfitRate.name,
                                  beforeData.realOperatingProfitRate || maxYears.map(() => "0"),
                                  afterData.realOperatingProfitRate || maxYears.map(() => "0"),
                                  "%",
                                  getLevelIndent(ProfitAndLossItems.realOperatingProfitRate.level),
                                  true,
                                  "",
                                  beforeData.totalRealOperatingProfitRate || "0",
                                  afterData.totalRealOperatingProfitRate || "0"
                                )}
                            </>
                          );
                        })()}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* 年度別損益（更新投資・合理化投資以外の場合：従来形式） */}
              {maxYears.length !== 0 && totalYearlyData && projectData?.purpose !== PurposeType.UpdateAndOther.code && projectData?.purpose !== PurposeType.Rationalization.code && (
                <Box mt={6}>
                  <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.plTable.annualPL')}</Heading>
                  <TableContainer>
                    <Table
                      id={`${PROFIT_AND_LOSS_GRAND_TOTAL_TABLE_PREFIX}`}
                      className="mm-ceas-table"
                      size="sm"
                      variant="simple"
                      sx={{ tableLayout: 'auto' }}
                    >
                      <Thead>
                        <Tr>
                          <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                          <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                          {maxYears.map((year, index) => (
                            <Th key={`year-total-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                          ))}
                          <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                        </Tr>
                        <Tr>
                          {maxYears.map((year, index) => (
                            <Th key={`year-total-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.totalSales.level)}px`}>{ProfitAndLossItems.totalSales.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalSales-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.totalSales[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.totalSales.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.productSales.level)}px`}>{ProfitAndLossItems.productSales.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.productSales[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.productSales.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.moldRecovery.level)}px`}>{ProfitAndLossItems.moldRecovery.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.moldSales[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.moldSales.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.devRecovery.level)}px`}>{ProfitAndLossItems.devRecovery.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.devSales[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.devSales.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td className={`border-bottom-bold`} pl={`${getLevelIndent(ProfitAndLossItems.equipmentRecovery.level)}px`}>{ProfitAndLossItems.equipmentRecovery.name}</Td>
                          <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} className={`border-bottom-bold`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.equipmentSales[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td className={`border-bottom-bold`} textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.equipmentSales.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.salesCost.level)}px`}>{ProfitAndLossItems.salesCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.salesCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.salesCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.materialCost.level)}px`}>{ProfitAndLossItems.materialCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.totalMaterialCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.totalMaterialCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.outsideCost.level)}px`}>{ProfitAndLossItems.outsideCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-appliedOutsideToSalesRatio-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.outsideCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.outsideCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.laborCost.level)}px`}>{ProfitAndLossItems.laborCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-appliedLaborToSalesRatio-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.laborCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.laborCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticLaborCost.level)}px`}>{ProfitAndLossItems.domesticLaborCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticLaborCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticLaborCost?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticLaborCost || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.factoryExpenses.level)}px`}>{ProfitAndLossItems.factoryExpenses.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-appliedOverheadToSalesRatio-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.factoryExpenses[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.factoryExpenses.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.overHeadCost.level)}px`}>{ProfitAndLossItems.overHeadCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.overHeadCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.overHeadCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.depreciation.level)}px`}>{ProfitAndLossItems.depreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.depreciation[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.depreciation.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.equipmentDepreciation.level)}px`}>{ProfitAndLossItems.equipmentDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.equipmentDepreciation[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.equipmentDepreciation.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.factoryCost.level)}px`}>{ProfitAndLossItems.factoryCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.factoryCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.factoryCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        {projectData?.pastProjectId && (
                          <Tr>
                            <Td pl={`${getLevelIndent(ProfitAndLossItems.pastEquipmentCost.level)}px`}>{ProfitAndLossItems.pastEquipmentCost.name}</Td>
                            <Td>{divisorStr}{selectedCurrency.name}</Td>
                            {maxYears.map((year, index) => (
                              <Td key={`yeartotal-pastEquipmentCost-${year}-${index}`} textAlign="right">
                                {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.pastEquipmentCost?.[index] || "0")), "WithDecimal")}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {formatNumber(
                                (dispProfitAndLoss.profitAndLossByYear.pastEquipmentCost || []).reduce((sum: Decimal, value: string) =>
                                  sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                              )}
                            </Td>
                          </Tr>
                        )}
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.otherEquipmentCost.level)}px`}>{ProfitAndLossItems.otherEquipmentCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.otherEquipmentCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.otherEquipmentCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticFactoryExpenses.level)}px`}>{ProfitAndLossItems.domesticFactoryExpenses.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticFactoryExpenses-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticFactoryExpenses?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticFactoryExpenses || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticOverHeadCost.level)}px`}>{ProfitAndLossItems.domesticOverHeadCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticOverHeadCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticOverHeadCost?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticOverHeadCost || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticDepreciation.level)}px`}>{ProfitAndLossItems.domesticDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticDepreciation-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticDepreciation?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticDepreciation || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticInvestmentDepreciation.level)}px`}>{ProfitAndLossItems.domesticInvestmentDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticInvestmentDepreciation-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticInvestmentDepreciation?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticInvestmentDepreciation || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticFactoryBuildingDepreciation.level)}px`}>{ProfitAndLossItems.domesticFactoryBuildingDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticFactoryBuildingDepreciation-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticFactoryBuildingDepreciation?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticFactoryBuildingDepreciation || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticOtherDepreciation.level)}px`}>{ProfitAndLossItems.domesticOtherDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticOtherDepreciation-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticOtherDepreciation?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticOtherDepreciation || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.scrapCost.level)}px`} className={`border-bottom-bold`}>{ProfitAndLossItems.scrapCost.name}</Td>
                          <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-scrapCost-${year}-${index}`} className={`border-bottom-bold`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.scrapCost?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td className={`border-bottom-bold`} textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.scrapCost || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.sgaExpenses.level)}px`}>{ProfitAndLossItems.sgaExpenses.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.sGAExpenses[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.sGAExpenses.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.appliedLaborCost.level)}px`}>{ProfitAndLossItems.appliedLaborCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.appliedLaborCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.appliedLaborCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.domesticSgaCost.level)}px`}>{ProfitAndLossItems.domesticSgaCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-domesticSgaCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.domesticSgaCost?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.domesticSgaCost || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.salesManagementCost.level)}px`} className={`border-bottom-bold`} >{ProfitAndLossItems.salesManagementCost.name}</Td>
                          <Td className={`border-bottom-bold`}>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} className={`border-bottom-bold`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.salesManagementCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td className={`border-bottom-bold`} textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.salesManagementCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.devCost.level)}px`}>{ProfitAndLossItems.devCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.devCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.devCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.royalty.level)}px`}>{ProfitAndLossItems.royalty.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-royalty-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.royalty?.[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.royalty || []).reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value || "0")), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.otherCost.level)}px`}>{ProfitAndLossItems.otherCost.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.otherCost[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.otherCost.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.operatingProfit.level)}px`}>{ProfitAndLossItems.operatingProfit.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.profit[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              dispProfitAndLoss.profitAndLossByYear.profit.reduce((sum: Decimal, value: string) =>
                                sum.plus(new Decimal(value)), new Decimal(0)), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl={`${getLevelIndent(ProfitAndLossItems.operatingProfitRate.level)}px`}>{ProfitAndLossItems.operatingProfitRate.name}</Td>
                          <Td>%</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {(new Decimal(dispProfitAndLoss.profitAndLossByYear.profitRate[index] || "0")).toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {(() => {
                              const totalProfit = dispProfitAndLoss.profitAndLossByYear.profit.reduce((sum, value) => sum.plus(new Decimal(value)), new Decimal(0));
                              const totalSales = dispProfitAndLoss.profitAndLossByYear.totalSales.reduce((sum, value) => sum.plus(new Decimal(value)), new Decimal(0));
                              const totalProfitRate = calcProfitRate(totalSales, totalProfit);
                              return `${totalProfitRate?.toFixed(1)}`;
                            })()}
                          </Td>
                        </Tr>
                        {/* 実質的営業利益率は値がある場合のみ表示（客先指定部品が存在する場合のみ） */}
                        {dispProfitAndLoss.profitAndLossByYear.realOperatingProfitRate?.some((v: string) => v && v !== "0" && new Decimal(v).greaterThan(0)) && (
                          <Tr>
                            <Td pl={`${getLevelIndent(ProfitAndLossItems.realOperatingProfitRate.level)}px`}>{ProfitAndLossItems.realOperatingProfitRate.name}</Td>
                            <Td>%</Td>
                            {maxYears.map((year, index) => (
                              <Td key={`yeartotal-realOperatingProfitRate-${year}-${index}`} textAlign="right">
                                {(new Decimal(dispProfitAndLoss.profitAndLossByYear.realOperatingProfitRate?.[index] || "0")).toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              </Td>
                            ))}
                            <Td textAlign="right">
                              {(() => {
                                // 率は通貨に依存しないためoriginalProfitAndLossから直接取得
                                const totalRate = originalProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate;
                                if (totalRate) {
                                  return new Decimal(totalRate).toFixed(1);
                                }
                                return "0.0";
                              })()}
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* 更新による改善効果（更新投資の場合のみ表示） */}
              {projectData?.purpose === PurposeType.UpdateAndOther.code && updateInvestmentProfitLossData && currencyData && currencyData.length > 0 && selectedCurrency?.name && (() => {
                // 工場通貨から表示通貨への変換用関数
                const factoryCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
                const factoryCurrencyName = factoryCurrency?.currencyName || "JPY";

                // JPY表示 + display版データあり = backend で月別レート換算済みの値を使用（フロント側の二重変換を回避）
                const useDisplayVersionForRat = !!displayUpdateInvestmentProfitLossData && selectedCurrency.name === CurrencyNameJPY;
                const updateSrc = useDisplayVersionForRat
                  ? displayUpdateInvestmentProfitLossData!
                  : updateInvestmentProfitLossData;

                // 金額を通貨変換し、千単位でフォーマットするヘルパー関数
                const formatCostValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  let decimalValue = new Decimal(numValue);
                  // 通貨変換（工場通貨から表示通貨に変換）— JPY版は backend 換算済みなのでスキップ
                  if (!useDisplayVersionForRat && selectedCurrency.name !== factoryCurrencyName) {
                    decimalValue = convertCurrency(decimalValue, factoryCurrencyName, selectedCurrency.name, currencyData);
                  }
                  // 千単位に変換
                  const dividedValue = decimalValue.dividedBy(1000);
                  return formatNumber(dividedValue, selectedCurrency.name);
                };

                // 歩留用フォーマット（小数点以下2桁）
                const formatYieldValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  return numValue.toFixed(2);
                };
                // 数量フォーマット（通貨変換なし）
                const formatQuantityValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  return formatNumber(new Decimal(numValue), "pcs");
                };
                // 簡易モード用：maxYearsから年ラベルを生成
                const updateSimpleYears = maxYears.map(year => `${baseStartYear + year - 1}${YEARS_UNIT}`);

                return (
                  <Box mt={6} id={`${UPDATE_IMPROVEMENT_EFFECT_TABLE_PREFIX}`}>
                    <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.updateTitle')}</Heading>

                    {isSimpleMode ? (
                      // 簡易モード：合理化と同様の指標・費用テーブルを表示
                      <>
                        {/* 更新による改善効果（指標）- 工程別 */}
                        {updateSrc?.improvementEffectIndicatorByProcess?.length > 0 && (
                          <Box mb={4}>
                            <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators')}</Text>
                            {updateSrc.improvementEffectIndicatorByProcess.map((process, processIndex) => (
                              <Box key={processIndex} mb={4}>
                                <Text fontWeight="bold" mb={2}>{process.processName}</Text>
                                <TableContainer>
                                  <Table
                                    id={`${UPDATE_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${processIndex}`}
                                    className="mm-ceas-table"
                                    size="sm"
                                    variant="simple"
                                    sx={{ tableLayout: 'auto' }}
                                  >
                                    <Thead>
                                      <Tr>
                                        <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                        <Th className="unit-width">{t('common.label.unit')}</Th>
                                        <Th className="unit-width" textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.category')}</Th>
                                        {updateSimpleYears.map((year, idx) => (
                                          <Th key={idx} textAlign="center">{year}</Th>
                                        ))}
                                        <Th textAlign="center">{t('common.label.total')}</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {process.items.map((item, itemIndex) => (
                                        <React.Fragment key={itemIndex}>
                                          <Tr>
                                            <Td rowSpan={2}>{item.itemName}</Td>
                                            <Td rowSpan={2} className="unit-width">pcs</Td>
                                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.beforeUpdate')}</Td>
                                            {item.beforeByYear.map((val, idx) => (
                                              <Td key={idx} textAlign="right">{formatQuantityValue(val)}</Td>
                                            ))}
                                            <Td textAlign="right">{formatQuantityValue(item.beforeTotal)}</Td>
                                          </Tr>
                                          <Tr>
                                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.afterUpdate')}</Td>
                                            {item.afterByYear.map((val, idx) => (
                                              <Td key={idx} textAlign="right">{formatQuantityValue(val)}</Td>
                                            ))}
                                            <Td textAlign="right">{formatQuantityValue(item.afterTotal)}</Td>
                                          </Tr>
                                        </React.Fragment>
                                      ))}
                                    </Tbody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* 更新による改善効果（費用）- 工程別 */}
                        {updateSrc?.improvementEffectCostByProcess?.length > 0 && (
                          <Box mb={4}>
                            <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.costs')}</Text>
                            {updateSrc.improvementEffectCostByProcess.map((process, processIndex) => (
                              <Box key={processIndex} mb={4}>
                                <Text fontWeight="bold" mb={2}>{process.processName}</Text>
                                <TableContainer>
                                  <Table
                                    id={`${UPDATE_IMPROVEMENT_COST_PROCESS_PREFIX}${processIndex}`}
                                    className="mm-ceas-table"
                                    size="sm"
                                    variant="simple"
                                    sx={{ tableLayout: 'auto' }}
                                  >
                                    <Thead>
                                      <Tr>
                                        <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                        <Th className="unit-width">{t('common.label.unit')}</Th>
                                        <Th className="unit-width" textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.category')}</Th>
                                        {updateSimpleYears.map((year, idx) => (
                                          <Th key={idx} textAlign="center">{year}</Th>
                                        ))}
                                        <Th textAlign="center">{t('common.label.total')}</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {process.items.map((item, itemIndex) => (
                                        <React.Fragment key={itemIndex}>
                                          <Tr>
                                            <Td rowSpan={2}>{item.itemName}</Td>
                                            <Td rowSpan={2} className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.beforeUpdate')}</Td>
                                            {item.beforeByYear.map((val, idx) => (
                                              <Td key={idx} textAlign="right">{formatCostValue(val)}</Td>
                                            ))}
                                            <Td textAlign="right">{formatCostValue(item.beforeTotal)}</Td>
                                          </Tr>
                                          <Tr>
                                            <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.afterUpdate')}</Td>
                                            {item.afterByYear.map((val, idx) => (
                                              <Td key={idx} textAlign="right">{formatCostValue(val)}</Td>
                                            ))}
                                            <Td textAlign="right">{formatCostValue(item.afterTotal)}</Td>
                                          </Tr>
                                        </React.Fragment>
                                      ))}
                                    </Tbody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            ))}
                          </Box>
                        )}

                        <Box id={UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID}>
                          {/* 改善効果の合計（指標）*/}
                          {updateSrc?.improvementTotalIndicator?.length > 0 && (
                            <Box mb={4}>
                              <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicatorImprovementTotal')}</Text>
                              <TableContainer>
                                <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                                  <Thead>
                                    <Tr>
                                      <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                      <Th className="unit-width">{t('common.label.unit')}</Th>
                                      {updateSimpleYears.map((year, idx) => (
                                        <Th key={idx} textAlign="center">{year}</Th>
                                      ))}
                                      <Th textAlign="center">{t('common.label.total')}</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {updateSrc.improvementTotalIndicator.map((item, itemIndex) => (
                                      <Tr key={itemIndex}>
                                        <Td>{item.itemName}</Td>
                                        <Td className="unit-width">pcs</Td>
                                        {item.differenceByYear.map((val, idx) => (
                                          <Td key={idx} textAlign="right">{formatQuantityValue(val)}</Td>
                                        ))}
                                        <Td textAlign="right">{formatQuantityValue(item.differenceTotal)}</Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {/* 改善効果の合計（費用）*/}
                          {updateSrc?.improvementTotalCost?.length > 0 && (
                            <Box mb={4}>
                              <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.costImprovementTotal')}</Text>
                              <TableContainer>
                                <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                                  <Thead>
                                    <Tr>
                                      <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                      <Th className="unit-width">{t('common.label.unit')}</Th>
                                      {updateSimpleYears.map((year, idx) => (
                                        <Th key={idx} textAlign="center">{year}</Th>
                                      ))}
                                      <Th textAlign="center">{t('common.label.total')}</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {updateSrc.improvementTotalCost.map((item, itemIndex) => (
                                      <Tr key={itemIndex}>
                                        <Td>{item.itemName}</Td>
                                        <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                        {item.differenceByYear.map((val, idx) => (
                                          <Td key={idx} textAlign="right">{formatCostValue(val)}</Td>
                                        ))}
                                        <Td textAlign="right">{formatCostValue(item.differenceTotal)}</Td>
                                      </Tr>
                                    ))}
                                    {/* 合計（損益）行 */}
                                    {(() => {
                                      const totalByYear = updateSimpleYears.map((_, yearIdx) =>
                                        (updateSrc.improvementTotalCost || []).reduce(
                                          (sum, item) => sum + (parseFloat(item.differenceByYear[yearIdx]) || 0),
                                          0
                                        )
                                      );
                                      const grandTotal = (updateSrc.improvementTotalCost || []).reduce(
                                        (sum, item) => sum + (parseFloat(item.differenceTotal) || 0),
                                        0
                                      );
                                      return (
                                        <Tr>
                                          <Td>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.totalProfit')}</Td>
                                          <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                          {totalByYear.map((val, idx) => (
                                            <Td key={idx} textAlign="right">{formatCostValue(String(val))}</Td>
                                          ))}
                                          <Td textAlign="right">{formatCostValue(String(grandTotal))}</Td>
                                        </Tr>
                                      );
                                    })()}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                              <Text fontSize="xs" color="gray.600" mt={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.negativeNote')}</Text>
                            </Box>
                          )}
                        </Box>
                      </>
                    ) : (
                      // 標準モード：詳細テーブル（工程別 + 改善額合計（差異の合計））
                      <>
                        {/* 工程別の改善効果 */}
                        {updateInvestmentProfitLossData.improvementEffectByProcess?.map((process, processIndex) => (
                          <Box key={processIndex} mb={4} id={`${UPDATE_IMPROVEMENT_EFFECT_PROCESS_ITEM_PREFIX}${processIndex}`}>
                            <Text fontWeight="bold" mb={2}>{process.processName}</Text>
                            <TableContainer>
                              <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                                <Thead>
                                  <Tr>
                                    <Th className="fixed-width" rowSpan={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                    <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                                    <Th colSpan={2} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.current')}</Th>
                                    <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.updateInvestment')}</Th>
                                    <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.difference')}</Th>
                                  </Tr>
                                  <Tr>
                                    <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.recentActual')}</Th>
                                    <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.initialEstimate')}</Th>
                                    <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.estimate')}</Th>
                                    <Th textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.actualVsEstimate')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  <Tr>
                                    <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.yield')}</Td>
                                    <Td className="unit-width">%</Td>
                                    <Td textAlign="right">{formatYieldValue(process.yield?.actual)}</Td>
                                    <Td textAlign="right">{formatYieldValue(process.yield?.initial)}</Td>
                                    <Td textAlign="right">{formatYieldValue(process.yield?.target)}</Td>
                                    <Td textAlign="right">{formatYieldValue(process.yield?.difference)}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.scrapCost')}</Td>
                                    <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                    <Td textAlign="right">{formatCostValue(process.scrapCost?.actual)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.scrapCost?.initial)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.scrapCost?.target)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.scrapCost?.difference)}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.repairCost')}</Td>
                                    <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                    <Td textAlign="right">{formatCostValue(process.repairCost?.actual)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.repairCost?.initial)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.repairCost?.target)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.repairCost?.difference)}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.consumablesCost')}</Td>
                                    <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                    <Td textAlign="right">{formatCostValue(process.consumablesCost?.actual)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.consumablesCost?.initial)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.consumablesCost?.target)}</Td>
                                    <Td textAlign="right">{formatCostValue(process.consumablesCost?.difference)}</Td>
                                  </Tr>
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}

                        {/* 改善額合計（差異の合計）*/}
                        <Box mt={4} id={UPDATE_IMPROVEMENT_EFFECT_TOTAL_ID}>
                          <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.improvementTotal')}</Text>
                          <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }} w="auto">
                            <Thead>
                              <Tr>
                                <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                <Th className="unit-width">{t('common.label.unit')}</Th>
                                <Th textAlign="right">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.amount')}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              <Tr>
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.scrapCostImprovement')}</Td>
                                <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                <Td textAlign="right">{formatCostValue(updateInvestmentProfitLossData.improvementTotal?.scrapCost)}</Td>
                              </Tr>
                              <Tr>
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.repairCostImprovement')}</Td>
                                <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                <Td textAlign="right">{formatCostValue(updateInvestmentProfitLossData.improvementTotal?.repairCost)}</Td>
                              </Tr>
                              <Tr>
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.consumablesCostImprovement')}</Td>
                                <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                <Td textAlign="right">{formatCostValue(updateInvestmentProfitLossData.improvementTotal?.consumablesCost)}</Td>
                              </Tr>
                              <Tr fontWeight="bold" bg="gray.100">
                                <Td className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.totalImprovement')}</Td>
                                <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                <Td textAlign="right">{formatCostValue(updateInvestmentProfitLossData.improvementTotal?.total)}</Td>
                              </Tr>
                            </Tbody>
                          </Table>
                          <Text fontSize="xs" color="gray.600" mt={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.negativeNote')}</Text>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })()}

              {/* 合理化による改善効果（合理化投資の場合のみ表示） */}
              {projectData?.purpose === PurposeType.Rationalization.code && rationalizationInvestmentProfitLossData && currencyData && currencyData.length > 0 && selectedCurrency?.name && (() => {
                // 工場通貨から表示通貨への変換用関数
                const factoryCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
                const factoryCurrencyName = factoryCurrency?.currencyName || "JPY";

                // JPY表示 + display版データあり = backend で月別レート換算済みの値を使用（フロント側の二重変換を回避）
                const useDisplayVersionForRat = !!displayRationalizationInvestmentProfitLossData && selectedCurrency.name === CurrencyNameJPY;
                const ratSrc = useDisplayVersionForRat
                  ? displayRationalizationInvestmentProfitLossData!
                  : rationalizationInvestmentProfitLossData;
                const beforePL = ratSrc.beforeProfitAndLossByYear;
                const afterPL = ratSrc.afterProfitAndLossByYear;

                // 金額を通貨変換し、千単位でフォーマットするヘルパー関数
                const formatCostValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  let decimalValue = new Decimal(numValue);
                  // 通貨変換（工場通貨から表示通貨に変換）— JPY版は backend 換算済みなのでスキップ
                  if (!useDisplayVersionForRat && selectedCurrency.name !== factoryCurrencyName) {
                    decimalValue = convertCurrency(decimalValue, factoryCurrencyName, selectedCurrency.name, currencyData);
                  }
                  // 千単位に変換
                  const dividedValue = decimalValue.dividedBy(1000);
                  return formatNumber(dividedValue, selectedCurrency.name);
                };

                // 数量フォーマット（通貨変換なし）
                const formatQuantityValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  return formatNumber(new Decimal(numValue), "pcs");
                };

                // 年度別損益テーブル用の年度データ（fiscalYearsからmaxYearsに相当するものを作成）
                const ratFiscalYears = ratSrc.fiscalYears || [];
                const ratYearsCount = ratFiscalYears.length;

                // 金額フォーマット（千単位、通貨変換あり）
                const formatProfitLossValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  let decimalValue = new Decimal(numValue);
                  // 通貨変換（工場通貨から表示通貨に変換）— JPY版は backend 換算済みなのでスキップ
                  if (!useDisplayVersionForRat && selectedCurrency.name !== factoryCurrencyName) {
                    decimalValue = convertCurrency(decimalValue, factoryCurrencyName, selectedCurrency.name, currencyData);
                  }
                  // 千単位に変換
                  const dividedValue = decimalValue.dividedBy(1000);
                  return formatNumber(dividedValue, "WithDecimal");
                };

                // 率フォーマット（通貨変換なし、小数点1桁）
                const formatRateValue = (value: string | undefined): string => {
                  if (!value || value === "") return "-";
                  const numValue = parseFloat(value);
                  if (isNaN(numValue)) return "-";
                  return formatNumber(new Decimal(numValue), "Ratio");
                };

                // 年度別損益テーブル行を生成するヘルパー関数
                const renderRationalizationProfitLossRow = (
                  item: { name: string; level: number },
                  data: string[] | undefined,
                  dataSum: string | undefined,
                  isRate: boolean = false,
                  borderClass: string = "",
                  uniqueKey?: string
                ) => {
                  return (
                    <Tr key={uniqueKey ?? item.name}>
                      <Td className={borderClass} pl={`${getLevelIndent(item.level)}px`}>{item.name}</Td>
                      <Td className={borderClass}>{isRate ? "%" : `${divisorStr}${selectedCurrency.name}`}</Td>
                      {ratFiscalYears.map((_, idx) => (
                        <Td key={idx} className={borderClass} textAlign="right">
                          {isRate ? formatRateValue((data ?? [])[idx]) : formatProfitLossValue((data ?? [])[idx])}
                        </Td>
                      ))}
                      <Td className={borderClass} textAlign="right">
                        {isRate ? formatRateValue(dataSum) : formatProfitLossValue(dataSum)}
                      </Td>
                    </Tr>
                  );
                };

                return (
                  <Box mt={6}>
                    {/* 年度別損益（合理化前）: 簡易モード時は非表示 */}
                    {!isSimpleMode && beforePL && ratYearsCount > 0 && (
                      <Box mb={6} id={RATIONALIZATION_BEFORE_PROFIT_LOSS_ID}>
                        <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.plTable.annualPLBeforeRationalization')}</Heading>
                        <TableContainer>
                          <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                            <Thead>
                              <Tr>
                                <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                                <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                                {ratFiscalYears.map((_, idx) => (
                                  <Th key={idx} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: idx + 1 })}</Th>
                                ))}
                                <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                              </Tr>
                              <Tr>
                                {ratFiscalYears.map((year, idx) => (
                                  <Th key={idx} textAlign="center">{year}</Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.totalSales, beforePL.totalSales, beforePL.totalSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.productSales, beforePL.productSales, beforePL.productSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.moldRecovery, beforePL.moldSales, beforePL.moldSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.devRecovery, beforePL.devSales, beforePL.devSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.equipmentRecovery, beforePL.equipmentSales, beforePL.equipmentSalesSum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.salesCost, beforePL.salesCost, beforePL.salesCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.materialCost, beforePL.totalMaterialCost, beforePL.totalMaterialCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.outsideCost, beforePL.outsideCost, beforePL.outsideCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.laborCost, beforePL.laborCost, beforePL.laborCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticLaborCost, beforePL.domesticLaborCost, beforePL.domesticLaborCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.factoryExpenses, beforePL.factoryExpenses, beforePL.factoryExpensesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.overHeadCost, beforePL.overHeadCost, beforePL.overHeadCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.depreciation, beforePL.depreciation, beforePL.depreciationSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.equipmentDepreciation, beforePL.equipmentDepreciation, beforePL.equipmentDepreciationSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.factoryCost, beforePL.factoryCost, beforePL.factoryCostSum)}
                              {projectData?.pastProjectId && renderRationalizationProfitLossRow(ProfitAndLossItems.pastEquipmentCost, beforePL.pastEquipmentCost || [], beforePL.pastEquipmentCostSum || "0")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.otherEquipmentCost, beforePL.otherEquipmentCost, beforePL.otherEquipmentCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticFactoryExpenses, beforePL.domesticFactoryExpenses, beforePL.domesticFactoryExpensesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticOverHeadCost, beforePL.domesticOverHeadCost, beforePL.domesticOverHeadCostSum, false, "", "domesticOverHeadCost")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticDepreciation, beforePL.domesticDepreciation, beforePL.domesticDepreciationSum, false, "", "domesticDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticInvestmentDepreciation, beforePL.domesticInvestmentDepreciation, beforePL.domesticInvestmentDepreciationSum, false, "", "domesticInvestmentDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticFactoryBuildingDepreciation, beforePL.domesticFactoryBuildingDepreciation, beforePL.domesticFactoryBuildingDepreciationSum, false, "", "domesticFactoryBuildingDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticOtherDepreciation, beforePL.domesticOtherDepreciation, beforePL.domesticOtherDepreciationSum, false, "", "domesticOtherDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.scrapCost, beforePL.scrapCost, beforePL.scrapCostSum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.sgaExpenses, beforePL.sGAExpenses, beforePL.sGAExpensesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.appliedLaborCost, beforePL.appliedLaborCost, beforePL.appliedLaborCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticSgaCost, beforePL.domesticSgaCost, beforePL.domesticSgaCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.salesManagementCost, beforePL.salesManagementCost, beforePL.salesManagementCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.devCost, beforePL.devCost, beforePL.devCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.royalty, beforePL.royalty, beforePL.royaltySum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.otherCost, beforePL.otherCost, beforePL.otherCostSum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.operatingProfit, beforePL.profit, beforePL.profitSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.operatingProfitRate, beforePL.profitRate, beforePL.profitRateSum, true)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.realOperatingProfitRate, beforePL.realOperatingProfitRate, beforePL.realOperatingProfitRateSum, true)}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* 年度別損益（合理化後）: 簡易モード時は非表示 */}
                    {!isSimpleMode && afterPL && ratYearsCount > 0 && (
                      <Box mb={6} id={RATIONALIZATION_AFTER_PROFIT_LOSS_ID}>
                        <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.plTable.annualPLAfterRationalization')}</Heading>
                        <TableContainer>
                          <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                            <Thead>
                              <Tr>
                                <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                                <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                                {ratFiscalYears.map((_, idx) => (
                                  <Th key={idx} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: idx + 1 })}</Th>
                                ))}
                                <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                              </Tr>
                              <Tr>
                                {ratFiscalYears.map((year, idx) => (
                                  <Th key={idx} textAlign="center">{year}</Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.totalSales, afterPL.totalSales, afterPL.totalSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.productSales, afterPL.productSales, afterPL.productSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.moldRecovery, afterPL.moldSales, afterPL.moldSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.devRecovery, afterPL.devSales, afterPL.devSalesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.equipmentRecovery, afterPL.equipmentSales, afterPL.equipmentSalesSum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.salesCost, afterPL.salesCost, afterPL.salesCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.materialCost, afterPL.totalMaterialCost, afterPL.totalMaterialCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.outsideCost, afterPL.outsideCost, afterPL.outsideCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.laborCost, afterPL.laborCost, afterPL.laborCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticLaborCost, afterPL.domesticLaborCost, afterPL.domesticLaborCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.factoryExpenses, afterPL.factoryExpenses, afterPL.factoryExpensesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.overHeadCost, afterPL.overHeadCost, afterPL.overHeadCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.depreciation, afterPL.depreciation, afterPL.depreciationSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.equipmentDepreciation, afterPL.equipmentDepreciation, afterPL.equipmentDepreciationSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.factoryCost, afterPL.factoryCost, afterPL.factoryCostSum)}
                              {projectData?.pastProjectId && renderRationalizationProfitLossRow(ProfitAndLossItems.pastEquipmentCost, afterPL.pastEquipmentCost || [], afterPL.pastEquipmentCostSum || "0")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.otherEquipmentCost, afterPL.otherEquipmentCost, afterPL.otherEquipmentCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticFactoryExpenses, afterPL.domesticFactoryExpenses, afterPL.domesticFactoryExpensesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticOverHeadCost, afterPL.domesticOverHeadCost, afterPL.domesticOverHeadCostSum, false, "", "domesticOverHeadCost")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticDepreciation, afterPL.domesticDepreciation, afterPL.domesticDepreciationSum, false, "", "domesticDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticInvestmentDepreciation, afterPL.domesticInvestmentDepreciation, afterPL.domesticInvestmentDepreciationSum, false, "", "domesticInvestmentDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticFactoryBuildingDepreciation, afterPL.domesticFactoryBuildingDepreciation, afterPL.domesticFactoryBuildingDepreciationSum, false, "", "domesticFactoryBuildingDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticOtherDepreciation, afterPL.domesticOtherDepreciation, afterPL.domesticOtherDepreciationSum, false, "", "domesticOtherDepreciation")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.scrapCost, afterPL.scrapCost, afterPL.scrapCostSum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.sgaExpenses, afterPL.sGAExpenses, afterPL.sGAExpensesSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.appliedLaborCost, afterPL.appliedLaborCost, afterPL.appliedLaborCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.domesticSgaCost, afterPL.domesticSgaCost, afterPL.domesticSgaCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.salesManagementCost, afterPL.salesManagementCost, afterPL.salesManagementCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.devCost, afterPL.devCost, afterPL.devCostSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.royalty, afterPL.royalty, afterPL.royaltySum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.otherCost, afterPL.otherCost, afterPL.otherCostSum, false, "border-bottom-bold")}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.operatingProfit, afterPL.profit, afterPL.profitSum)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.operatingProfitRate, afterPL.profitRate, afterPL.profitRateSum, true)}
                              {renderRationalizationProfitLossRow(ProfitAndLossItems.realOperatingProfitRate, afterPL.realOperatingProfitRate, afterPL.realOperatingProfitRateSum, true)}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.rationalizationTitle')}</Heading>

                    {/* 合理化による改善効果（指標）- 工程別 */}
                    {ratSrc.improvementEffectIndicatorByProcess.length > 0 && (
                      <Box mb={4}>
                        <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators')}</Text>
                        {ratSrc.improvementEffectIndicatorByProcess.map((process, processIndex) => (
                          <Box key={processIndex} mb={4}>
                            <Text fontWeight="bold" mb={2}>{process.processName}</Text>
                            <TableContainer>
                              <Table
                                id={`${RATIONALIZATION_IMPROVEMENT_INDICATOR_PROCESS_PREFIX}${processIndex}`}
                                className="mm-ceas-table"
                                size="sm"
                                variant="simple"
                                sx={{ tableLayout: 'auto' }}
                              >
                                <Thead>
                                  <Tr>
                                    <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                    <Th className="unit-width">{t('common.label.unit')}</Th>
                                    <Th className="unit-width" textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.category')}</Th>
                                    {ratSrc.fiscalYears.map((year, idx) => (
                                      <Th key={idx} textAlign="center">{year}</Th>
                                    ))}
                                    <Th textAlign="center">{t('common.label.total')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {process.items.map((item, itemIndex) => (
                                    <React.Fragment key={itemIndex}>
                                      <Tr>
                                        <Td rowSpan={2}>{item.itemName}</Td>
                                        <Td rowSpan={2} className="unit-width">pcs</Td>
                                        <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.beforeRationalization')}</Td>
                                        {item.beforeByYear.map((val, idx) => (
                                          <Td key={idx} textAlign="right">{formatQuantityValue(val)}</Td>
                                        ))}
                                        <Td textAlign="right">{formatQuantityValue(item.beforeTotal)}</Td>
                                      </Tr>
                                      <Tr>
                                        <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.afterRationalization')}</Td>
                                        {item.afterByYear.map((val, idx) => (
                                          <Td key={idx} textAlign="right">{formatQuantityValue(val)}</Td>
                                        ))}
                                        <Td textAlign="right">{formatQuantityValue(item.afterTotal)}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* 合理化による改善効果（費用）- 工程別 */}
                    {ratSrc.improvementEffectCostByProcess.length > 0 && (
                      <Box mb={4}>
                        <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.costs')}</Text>
                        {ratSrc.improvementEffectCostByProcess.map((process, processIndex) => (
                          <Box key={processIndex} mb={4}>
                            <Text fontWeight="bold" mb={2}>{process.processName}</Text>
                            <TableContainer>
                              <Table
                                id={`${RATIONALIZATION_IMPROVEMENT_COST_PROCESS_PREFIX}${processIndex}`}
                                className="mm-ceas-table"
                                size="sm"
                                variant="simple"
                                sx={{ tableLayout: 'auto' }}
                              >
                                <Thead>
                                  <Tr>
                                    <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                    <Th className="unit-width">{t('common.label.unit')}</Th>
                                    <Th className="unit-width" textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.category')}</Th>
                                    {ratSrc.fiscalYears.map((year, idx) => (
                                      <Th key={idx} textAlign="center">{year}</Th>
                                    ))}
                                    <Th textAlign="center">{t('common.label.total')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {process.items.map((item, itemIndex) => (
                                    <React.Fragment key={itemIndex}>
                                      <Tr>
                                        <Td rowSpan={2}>{item.itemName}</Td>
                                        <Td rowSpan={2} className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                        <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.beforeRationalization')}</Td>
                                        {item.beforeByYear.map((val, idx) => (
                                          <Td key={idx} textAlign="right">{formatCostValue(val)}</Td>
                                        ))}
                                        <Td textAlign="right">{formatCostValue(item.beforeTotal)}</Td>
                                      </Tr>
                                      <Tr>
                                        <Td>{t('features.InvestmentScreening.ReportAndDraft.plTable.afterRationalization')}</Td>
                                        {item.afterByYear.map((val, idx) => (
                                          <Td key={idx} textAlign="right">{formatCostValue(val)}</Td>
                                        ))}
                                        <Td textAlign="right">{formatCostValue(item.afterTotal)}</Td>
                                      </Tr>
                                    </React.Fragment>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box id={RATIONALIZATION_IMPROVEMENT_EFFECT_TOTAL_ID}>
                      {/* 改善効果の合計（指標） */}
                      {ratSrc.improvementTotalIndicator.length > 0 && (
                        <Box mb={4}>
                          <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicatorImprovementTotal')}</Text>
                          <TableContainer>
                            <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                              <Thead>
                                <Tr>
                                  <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                  <Th className="unit-width">{t('common.label.unit')}</Th>
                                  {ratSrc.fiscalYears.map((year, idx) => (
                                    <Th key={idx} textAlign="center">{year}</Th>
                                  ))}
                                  <Th textAlign="center">{t('common.label.total')}</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {ratSrc.improvementTotalIndicator.map((item, itemIndex) => (
                                  <Tr key={itemIndex}>
                                    <Td>{item.itemName}</Td>
                                    <Td className="unit-width">pcs</Td>
                                    {item.differenceByYear.map((val, idx) => (
                                      <Td key={idx} textAlign="right">{formatQuantityValue(val)}</Td>
                                    ))}
                                    <Td textAlign="right">{formatQuantityValue(item.differenceTotal)}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}

                      {/* 改善効果の合計（費用） */}
                      {ratSrc.improvementTotalCost.length > 0 && (
                        <Box mb={4}>
                          <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.costImprovementTotal')}</Text>
                          <TableContainer>
                            <Table className="mm-ceas-table" size="sm" variant="simple" sx={{ tableLayout: 'auto' }}>
                              <Thead>
                                <Tr>
                                  <Th className="fixed-width">{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.indicators').replace('指標', '項目')}</Th>
                                  <Th className="unit-width">{t('common.label.unit')}</Th>
                                  {ratSrc.fiscalYears.map((year, idx) => (
                                    <Th key={idx} textAlign="center">{year}</Th>
                                  ))}
                                  <Th textAlign="center">{t('common.label.total')}</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {ratSrc.improvementTotalCost.map((item, itemIndex) => (
                                  <Tr key={itemIndex}>
                                    <Td>{item.itemName}</Td>
                                    <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                    {item.differenceByYear.map((val, idx) => (
                                      <Td key={idx} textAlign="right">{formatCostValue(val)}</Td>
                                    ))}
                                    <Td textAlign="right">{formatCostValue(item.differenceTotal)}</Td>
                                  </Tr>
                                ))}
                                {/* 合計（損益）行 */}
                                {(() => {
                                  const totalByYear = ratSrc.fiscalYears.map((_, yearIdx) =>
                                    ratSrc.improvementTotalCost.reduce(
                                      (sum, item) => sum + (parseFloat(item.differenceByYear[yearIdx]) || 0),
                                      0
                                    )
                                  );
                                  const grandTotal = ratSrc.improvementTotalCost.reduce(
                                    (sum, item) => sum + (parseFloat(item.differenceTotal) || 0),
                                    0
                                  );
                                  return (
                                    <Tr>
                                      <Td>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.totalProfit')}</Td>
                                      <Td className="unit-width">{divisorStr}{selectedCurrency.name}</Td>
                                      {totalByYear.map((val, idx) => (
                                        <Td key={idx} textAlign="right">{formatCostValue(String(val))}</Td>
                                      ))}
                                      <Td textAlign="right">{formatCostValue(String(grandTotal))}</Td>
                                    </Tr>
                                  );
                                })()}
                              </Tbody>
                            </Table>
                          </TableContainer>
                          <Text fontSize="xs" color="gray.600" mt={2}>{t('features.InvestmentScreening.ReportAndDraft.improvementEffect.negativeNote')}</Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })()}

              {/* 回収期間 */}
              {maxYears.length !== 0 && (
                <Box mt={6}>
                  <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.paybackAndDCF.paybackPeriodTitle')}</Heading>
                  <TableContainer>
                    <Table
                      id={`${PROFIT_AND_LOSS_PAYBACK_PERIOD_TABLE_PREFIX}`}
                      className="mm-ceas-table"
                      size="sm"
                      variant="simple"
                      sx={{ tableLayout: 'auto' }}
                    >
                      <Thead>
                        <Tr>
                          <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                          <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                          {maxYears.map((year, index) => (
                            <Th key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                          ))}
                          <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                        </Tr>
                        <Tr>
                          {maxYears.map((year, index) => (
                            <Th key={`yeartotal-ebitdaTotal-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>{PaybackPeriodItems.capexAmount.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.RecoverySimulationByYear.capexAmount[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.RecoverySimulationByYear.capexAmount || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{PaybackPeriodItems.operatingProfit.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.RecoverySimulationByYear.profit[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.RecoverySimulationByYear.profit || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{PaybackPeriodItems.interestRate.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {
                                dispProfitAndLoss.paybackPeriodByYear ?
                                  formatNumber(new Decimal((dispProfitAndLoss.paybackPeriodByYear.interestRateByYear[index]) || "0"), "WithDecimal")
                                  :
                                  ""
                              }
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {dispProfitAndLoss.paybackPeriodByYear ?
                              formatNumber(
                                (dispProfitAndLoss.paybackPeriodByYear.interestRateByYear || []).slice(0, maxYears.length).reduce(
                                  (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                                ), "WithDecimal"
                              )
                              : ""
                            }
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{PaybackPeriodItems.equipmentDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.equipmentDepreciation[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.equipmentDepreciation || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{PaybackPeriodItems.paybackPeriod.name}</Td>
                          <Td>{t('features.InvestmentScreening.ReportAndDraft.paybackAndDCF.months')}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {dispProfitAndLoss.paybackPeriodByYear?.paybackPeriod}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Text fontSize="sm" mt={2}>
                    {t('features.InvestmentScreening.ReportAndDraft.note.recoveryPeriodFormula')}
                  </Text>
                </Box>
              )}

              {/* 増分キャッシュフロー */}
              {maxYears.length !== 0 && (
                <Box mt={6}>
                  <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.paybackAndDCF.incrementalCFTitle')}</Heading>
                  <TableContainer>
                    <Table
                      id={`${PROFIT_AND_LOSS_COLLECT_SIMULATION_TABLE_PREFIX}`}
                      className="mm-ceas-table"
                      size="sm"
                      variant="simple"
                      sx={{ tableLayout: 'auto' }}
                    >
                      <Thead>
                        <Tr>
                          <Th className="fixed-width" rowSpan={2}>{t('common.label.item')}</Th>
                          <Th className="unit-width" rowSpan={2}>{t('common.label.unit')}</Th>
                          {maxYears.map((year, index) => (
                            <Th key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}</Th>
                          ))}
                          <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                        </Tr>
                        <Tr>
                          {maxYears.map((year, index) => (
                            <Th key={`yeartotal-ebitdaTotal-act-${year}-${index}`} colSpan={1} textAlign="center">{baseStartYear + year - 1}{YEARS_UNIT}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>{RecoverySimulationItems.capexAmount.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.RecoverySimulationByYear.capexAmount[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.RecoverySimulationByYear.capexAmount || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{RecoverySimulationItems.recoveredResources.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.RecoverySimulationByYear.recoveredResources[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.RecoverySimulationByYear.recoveredResources || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl="30px">{RecoverySimulationItems.operatingProfit.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.RecoverySimulationByYear.profit[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.RecoverySimulationByYear.profit || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl="30px">{RecoverySimulationItems.equipmentDepreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-totalMaterialCost-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.profitAndLossByYear.equipmentDepreciation[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.profitAndLossByYear.equipmentDepreciation || []).slice(0, maxYears.length).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{RecoverySimulationItems.uncollectedAmount.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.RecoverySimulationByYear.uncollectedAmount[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">-</Td>
                        </Tr>
                        <Tr>
                          <Td>{RecoverySimulationItems.collectionPeriod.name}</Td>
                          <Td>{t('features.InvestmentScreening.ReportAndDraft.paybackAndDCF.months')}</Td>
                          {maxYears.map((year, index) => (
                            <Td key={`yeartotal-ebitdaTotal-${year}-${index}`} textAlign="right">
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {dispProfitAndLoss.RecoverySimulationByYear.collectionPeriod}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Text fontSize="sm" mt={2}>
                    {t('features.InvestmentScreening.ReportAndDraft.paybackAndDCF.breakEvenNote')}
                  </Text>
                </Box>
              )}

              {/* DCF */}
              {maxYears.length !== 0 && (
                <Box mt={6}>
                  <Heading size="sm" mb={2}>{t('features.InvestmentScreening.ReportAndDraft.paybackAndDCF.dcfTitle')}</Heading>
                  <TableContainer>
                    <Table
                      id={`${PROFIT_AND_LOSS_DCF_TABLE_PREFIX}`}
                      className="mm-ceas-table"
                      size="sm"
                      variant="simple"
                      sx={{ tableLayout: 'auto' }}
                    >
                      <Thead>
                        <Tr>
                          {/* 0年目と年度列の追加 */}
                          <Th className="fixed-width" rowSpan={2}>
                            {t('common.label.item')}
                          </Th>
                          <Th className="unit-width" rowSpan={2}>
                            {t('common.label.unit')}
                          </Th>
                          {[0, ...maxYears].map((year, index) => (
                            <Th key={`dcf-${year}-${index}`} textAlign="center">
                              {t('features.InvestmentScreening.ReportAndDraft.plTable.yearN', { n: year })}
                            </Th>
                          ))}
                          <Th rowSpan={2} textAlign="center">{t('common.label.total')}</Th>
                        </Tr>
                        <Tr>
                          {[baseStartYear - 1, ...maxYears.map((year) => baseStartYear + year - 1)].map((year, index) => (
                            <Th key={`dcf-act-${year}-${index}`} colSpan={1} textAlign="center">
                              {`${year}${YEARS_UNIT}`}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>{DCFItems.freeCashFlow.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.dcf.freeCashFlow[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.dcf.freeCashFlow || []).slice(0, maxYears.length + 1).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl="30px">{DCFItems.operatingProfit.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.dcf.profit[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.dcf.profit || []).slice(0, maxYears.length + 1).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl="30px">{DCFItems.taxRate.name}</Td>
                          <Td>%</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {new Decimal((dispProfitAndLoss.dcf.setTaxRate[index] || "0")).toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </Td>
                          ))}
                          <Td textAlign="right">-</Td>
                        </Tr>
                        <Tr>
                          <Td pl="30px">{DCFItems.depreciation.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.dcf.depreciation[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.dcf.depreciation || []).slice(0, maxYears.length + 1).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td pl="30px">{DCFItems.capexAmount.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.dcf.capexAmount[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.dcf.capexAmount || []).slice(0, maxYears.length + 1).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{DCFItems.wacc.name}</Td>
                          <Td>%</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {new Decimal((dispProfitAndLoss.dcf.discountRate[index] || "0")).toFixed(1)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </Td>
                          ))}
                          <Td textAlign="right">-</Td>
                        </Tr>
                        <Tr>
                          <Td>{DCFItems.presentValue.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                              {formatNumber(new Decimal((dispProfitAndLoss.dcf.presentValue[index] || "0")), "WithDecimal")}
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(
                              (dispProfitAndLoss.dcf.presentValue || []).slice(0, maxYears.length + 1).reduce(
                                (sum, v) => sum.plus(new Decimal(v || "0")), new Decimal(0)
                              ), "WithDecimal"
                            )}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td>{DCFItems.netPresentValue.name}</Td>
                          <Td>{divisorStr}{selectedCurrency.name}</Td>
                          {[0, ...maxYears].map((year, index) => (
                            <Td key={`dcf-ebitda-${year}-${index}`} textAlign="right">
                            </Td>
                          ))}
                          <Td textAlign="right">
                            {formatNumber(new Decimal((dispProfitAndLoss.dcf.netPresentValue || "0")), "WithDecimal")}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </TableContainer>
                  {/*
                <Text fontSize="sm" mt={2}>
                  ※ FCF、PVは初年度のみ設備投資額を含みます。
                </Text>
                */}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box mb={4} id="section-7" className="pdf-content section">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.schedule')}
            </Heading>
            {/* スケジュール描画 */}
            <div ref={scheduleRef}>
              <ScheduleTable
                scheduleItems={scheduleItems}
                facilityItems={facilityItems}
                reviewItems={reviewItems}
                monthList={monthList}
                grouped={grouped}
                showTitle={false}
              />
            </div>
          </Box>
        </Box>

        <Box mb={4} id="section-8" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.equipmentInvestment')}
            </Heading>
            <div ref={equipmentFlowRef}>
              <ProcessFlowComponent projectId={projectId} ref={reactFlowRef} />
            </div>

            <Box mt={4} className="pdf-content">
              <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.productionLineImage')} folderName="ProductionProcess" isImage={true} />
            </Box>

            <Box mt={4} className="pdf-content">
              <EquipmentTable equipmentAmounts={convEquipmentAmounts} supplierData={supplierData} selectedCurrency={selectedCurrency} processItemNameMap={processItemNameMap} processItemNoMap={processItemNoMap} processSeqMap={processSeqMap} />
            </Box>
            <Box mt={4} className="pdf-content">
              <EquipmentDataTable equipmentAmounts={convEquipmentAmounts} supplierData={supplierData} selectedCurrency={selectedCurrency} processItemNameMap={processItemNameMap} processItemNoMap={processItemNoMap} processSeqMap={processSeqMap} />
            </Box>
            {projectData?.purpose === "2" && (<Box mt={4} className="pdf-content">
              <ProductionCapacityBenchmark equipmentAmounts={convEquipmentAmounts} supplierData={supplierData} selectedCurrency={selectedCurrency} />
            </Box>
            )}
            {projectData?.purpose === PurposeType.UpdateAndOther.code && updateSummaryData.length > 0 && (
              <Box mt={4} className="pdf-content" id={`${UPDATE_SUMMARY_DATA_LIST_PREFIX}`}>
                <UpdateSummaryDataList />
              </Box>
            )}
            <Box mt={4} className="pdf-content">
              <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.equipmentDocs')} folderName="EquipmentFiles" />
            </Box>
            <Box mt={4} className="pdf-content">
              <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.equipmentImage')} folderName="EquipmentImages" isImage={true} />
            </Box>

          </Box>
        </Box>

        <Box mb={4} id="section-9" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.cost')}
            </Heading>
            {isSimpleMode ? (
              <Text color="gray.500" fontSize="sm">
                {t('features.InvestmentScreening.ReportAndDraft.message.simpleModeSkipped')}
              </Text>
            ) : (
              <>
                <Box mt={4} className="pdf-content">
                  <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.plsFile')} folderName="ProfitAndLoss" />
                </Box>
                <Box mt={4} className="pdf-content">
                  <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.reportFile')} folderName="Report" />
                </Box>
                {(() => {
                  const fileList = uploadedFilesByFolder ?? [];
                  const hasProfitAndLossFiles = (fileList.find(f => f.folderName === "ProfitAndLoss")?.files?.length ?? 0) > 0;
                  const hasReportFiles = (fileList.find(f => f.folderName === "Report")?.files?.length ?? 0) > 0;
                  // 帳票、plsのどちらにもアップロードファイルのないときtrue
                  const hasNoFiles = !hasProfitAndLossFiles && !hasReportFiles;
                  return hasNoFiles && (<Text color="gray.500" fontSize="sm">{t('features.InvestmentScreening.ReportAndDraft.message.noAttachedFile')}</Text>);
                }
                )()}
              </>
            )}
          </Box>
        </Box>

        <Box mb={4} id="section-10" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              Simulation
            </Heading>

            {/* 損益非表示モード時の案内（Simulation） */}
            {isProfitLossHiddenMode && (
              <Text color="gray.500" fontSize="sm" mb={4}>
                {t('common.message.profitLossHiddenSectionNote')}
              </Text>
            )}

            {/* Simulationの表・グラフ（損益非表示モード時は非表示。投資判断の理由のみ残す） */}
            {!isProfitLossHiddenMode && (<>
              <Box ref={simulationRef}>
                {/* 数量（Quantities Chart）: 簡易モード時は非表示 */}
                {!isSimpleMode && quantitiesChartDataOriginal && quantitiesChartDataModified && (() => {
                  const { yMin, yMax, commonLabels } = computeCommonScale(
                    quantitiesChartDataOriginal,
                    quantitiesChartDataModified,
                    true
                  );
                  return (
                    <Box>
                      <Heading size="md" mt={2} mb={2}>
                        {labelQuantity}
                      </Heading>
                      <Text>
                        unit: pcs
                      </Text>
                      <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan')}
                          </Heading>
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            <Line
                              data={{ ...quantitiesChartDataOriginal, labels: commonLabels }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                spanGaps: false,
                                elements: { point: { radius: 2 } },
                                scales: {
                                  x: {
                                    offset: true,
                                    ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                  },
                                  y: {
                                    min: yMin,
                                    max: yMax,
                                    ticks: {
                                      format: {
                                        notation: "standard"
                                      }
                                    }
                                  },
                                },
                                plugins: {
                                  datalabels: {
                                    display: false
                                  },
                                  annotation: {
                                    annotations: {
                                      ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                    }
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {t('features.InvestmentScreening.ReportAndDraft.chart.downside')}
                          </Heading>
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            <Line
                              data={{ ...quantitiesChartDataModified, labels: commonLabels }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                spanGaps: false,
                                elements: { point: { radius: 2 } },
                                scales: {
                                  x: {
                                    offset: true,
                                    ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                  },
                                  y: {
                                    min: yMin,
                                    max: yMax,
                                    ticks: {
                                      format: {
                                        notation: "standard"
                                      }
                                    }
                                  },
                                },
                                plugins: {
                                  datalabels: {
                                    display: false
                                  },
                                  annotation: {
                                    annotations: {
                                      ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                    }
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        {/* 保存済み設定グラフ */}
                        {quantitiesChartDataForSavedConfig.map((config) => (
                          <Box key={config.configId} textAlign="center">
                            <Heading size="sm" mb={1}>
                              {config.name}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Line
                                data={{ ...config.data, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  spanGaps: false,
                                  elements: { point: { radius: 2 } },
                                  scales: {
                                    x: {
                                      offset: true,
                                      ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    y: {
                                      min: yMin,
                                      max: yMax,
                                      ticks: {
                                        format: {
                                          notation: "standard"
                                        }
                                      }
                                    },
                                  },
                                  plugins: {
                                    datalabels: {
                                      display: false
                                    },
                                    annotation: {
                                      annotations: {
                                        ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                      }
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  );
                })()}

                {/* 販売単価と材料単価（Price & Material Chart）: 簡易モード時は非表示 */}
                {!isSimpleMode && mergedChartDataOriginal && mergedChartDataModified && (() => {
                  const { yMin, yMax, commonLabels } = computeCommonScale(
                    mergedChartDataOriginal,
                    mergedChartDataModified,
                    true
                  );
                  return (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>
                        {t('features.InvestmentScreening.ReportAndDraft.label.priceAndMaterialCost', { priceLabel: labelPrice })}
                      </Heading>
                      <Text>
                        unit: {selectedCurrency?.name!}
                      </Text>
                      <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan')}
                          </Heading>
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            <Line
                              data={{ ...mergedChartDataOriginal, labels: commonLabels }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                spanGaps: false,
                                elements: { point: { radius: 2 } },
                                scales: {
                                  x: {
                                    offset: true,
                                    ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                  },
                                  y: {
                                    min: yMin,
                                    max: yMax,
                                    ticks: {
                                      format: {
                                        notation: "standard"
                                      }
                                    }
                                  },
                                },
                                plugins: {
                                  datalabels: {
                                    display: false
                                  },
                                  annotation: {
                                    annotations: {
                                      ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                    }
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {t('features.InvestmentScreening.ReportAndDraft.chart.downside')}
                          </Heading>
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            <Line
                              data={{ ...mergedChartDataModified, labels: commonLabels }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                spanGaps: false,
                                elements: { point: { radius: 2 } },
                                scales: {
                                  x: {
                                    offset: true,
                                    ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                  },
                                  y: {
                                    min: yMin,
                                    max: yMax,
                                    ticks: {
                                      format: {
                                        notation: "standard"
                                      }
                                    }
                                  },
                                },
                                plugins: {
                                  datalabels: {
                                    display: false
                                  },
                                  annotation: {
                                    annotations: {
                                      ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                    }
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        {/* 保存済み設定グラフ */}
                        {mergedChartDataForSavedConfig.map((config) => (
                          <Box key={config.configId} textAlign="center">
                            <Heading size="sm" mb={1}>
                              {config.name}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Line
                                data={{ ...config.data, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  spanGaps: false,
                                  elements: { point: { radius: 2 } },
                                  scales: {
                                    x: {
                                      offset: true,
                                      ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    y: {
                                      min: yMin,
                                      max: yMax,
                                      ticks: {
                                        format: {
                                          notation: "standard"
                                        }
                                      }
                                    },
                                  },
                                  plugins: {
                                    datalabels: {
                                      display: false
                                    },
                                    annotation: {
                                      annotations: {
                                        ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                      }
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  );
                })()}
              </Box>

              <Box ref={simulationRef2}>
                {/* 売上・利益（Sales Chart）: 簡易モード時は非表示 */}
                {/* 通貨マスタ未取得 / 表示通貨未確定の段階で描画すると convertCurrency が空引数で呼ばれ console.error が出るため、揃ってから描画する */}
                {!isSimpleMode && salesChartData && currencyData.length > 0 && selectedCurrency?.name && (
                  <Box mt={4}>
                    <Heading size="md" mb={1}>
                      {getLabelsByPurpose({
                        [PurposeType.NewProduct.code]: t('features.InvestmentScreening.ReportAndDraft.chart.salesProfit'), ["*"]: t('features.InvestmentScreening.ReportAndDraft.chart.salesProfitRate'),
                      })}
                    </Heading>
                    <Text>
                      unit: {selectedCurrency?.name!}
                    </Text>
                    {(() => {

                      // 変数宣言
                      const dataLeft = salesChartData;
                      const dataRight = salesChartDataModified || salesChartData;

                      // 営業利益合計: costBreakdownがあれば年度丸め方式、なければ合計→変換方式
                      const rawDataLeft = rawSalesChartData || dataLeft;
                      const rawDataRight = rawSalesChartDataModified || rawSalesChartData || dataRight;

                      const localCurrencyName = currencyData.find(c => c.currencyId === localCurrencyId)?.currencyName;
                      const dp = hasDecimal(selectedCurrency?.name!) ? 2 : 0;

                      // costBreakdownベースの年度丸め方式（Simulation画面と同一）
                      const calcDisplayProfit = (chartData: any): Decimal => {
                        // 振り返りモード: profit[] は月別レートで表示通貨に変換済み。
                        // calcYearlyRoundedProfitTotal はソース通貨+単一レートで再計算するため使用不可。
                        // calcTotalProfitAndRate でprofit[]を合計し、そのまま返す（既に表示通貨）。
                        if (reviewDate) {
                          return calcTotalProfitAndRate(chartData).totalProfit;
                        }
                        if (chartData?.costBreakdown && localCurrencyName && selectedCurrency?.name) {
                          const inputs = chartData.equipmentDepreciationInputs;
                          const equipInputs = inputs?.originalEquipments
                            ? { ...inputs, equipments: inputs.originalEquipments }
                            : inputs;
                          return calcYearlyRoundedProfitTotal(
                            chartData.costBreakdown,
                            localCurrencyName,
                            selectedCurrency?.name!,
                            currencyData,
                            equipInputs,
                            chartData.scrapCostInputs,
                          );
                        }
                        // フォールバック: costBreakdownがない場合（合計→変換方式）
                        const chartTotal = calcTotalProfitAndRate(chartData).totalProfit;
                        return localCurrencyName === selectedCurrency?.name
                          ? chartTotal
                          : convertCurrency(chartTotal, localCurrencyName!, selectedCurrency?.name!, currencyData).toDecimalPlaces(dp);
                      };

                      const totalProfit = calcDisplayProfit(rawDataLeft);
                      const totalProfitModified = calcDisplayProfit(rawDataRight);

                      // 営業利益率（率は無次元のため変換順序の影響なし）
                      const { profitRate } = calcTotalProfitAndRate(dataLeft);
                      const { profitRate: profitRateModified } = calcTotalProfitAndRate(dataRight);

                      // 当初計画（売上/営業利益額）データをフィルタリング
                      const leftSalesData = {
                        datasets: dataLeft.datasets.filter((ds: { yAxisID: string; }) => ds.yAxisID === "sales"),
                        labels: dataLeft.labels
                      };

                      // 当初計画（営業利益率）データをフィルタリング
                      const leftProfitData = {
                        datasets: dataLeft.datasets.filter((ds: { yAxisID: string; }) => ds.yAxisID === "profitRate"),
                        labels: dataLeft.labels
                      };

                      // ダウンサイド（売上/営業利益額）データをフィルタリング
                      const rightSalesData = {
                        datasets: dataRight.datasets.filter((ds: { yAxisID: string; }) => ds.yAxisID === "sales"),
                        labels: dataRight.labels
                      };

                      // ダウンサイド（営業利益率）データをフィルタリング
                      const rightProfitData = {
                        datasets: dataRight.datasets.filter((ds: { yAxisID: string; }) => ds.yAxisID === "profitRate"),
                        labels: dataRight.labels
                      };

                      // 売上/営業利益額のスケールを算出
                      const { yMin: yMinLeft, yMax: yMaxLeft, commonLabels: commonLabelsLeft } = computeCommonScaleSalesChart(
                        leftSalesData,
                        rightSalesData,
                        false
                      );

                      // 営業利益率のスケールを算出
                      const { yMin: yMinRight, yMax: yMaxRight, commonLabels: commonLabelsRight } = computeCommonScaleSalesChart(
                        leftProfitData,
                        rightProfitData,
                        false
                      );

                      // 年月ラベルを取得
                      const commonLabels = commonLabelsLeft || commonLabelsRight;

                      // 顧客指定部品の有無判断
                      const hasCustomerSpecifiedParts = bomData.some((c) => c.isCustomerSpecifiedParts === "true");

                      // 実質的営業利益率データセットを追加（当初計画）
                      const dataLeftWithRealProfitRate = hasCustomerSpecifiedParts // combinedChartData?.realProfitRateByMonth?.length > 0 条件をbomDataから直接顧客部品があるかを確認に変更
                        ? {
                          ...dataLeft,
                          datasets: [
                            ...dataLeft.datasets,
                            {
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate'),
                              id: "realProfitRate",
                              yAxisID: "profitRate",
                              data: combinedChartData.realProfitRateByMonth,
                              borderColor: "orange",
                              backgroundColor: "orange",
                              fill: false,
                              tension: 0.4,
                              type: "line" as const,
                            }
                          ]
                        }
                        : dataLeft;

                      // 実質的営業利益率データセットを追加（ダウンサイド）
                      const dataRightWithRealProfitRate = hasCustomerSpecifiedParts // combinedChartDataModified?.realProfitRateByMonth?.length > 0 条件をbomDataから直接顧客部品があるかを確認に変更
                        ? {
                          ...dataRight,
                          datasets: [
                            ...dataRight.datasets,
                            {
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate'),
                              id: "realProfitRate",
                              yAxisID: "profitRate",
                              data: combinedChartDataModified.realProfitRateByMonth,
                              borderColor: "orange",
                              backgroundColor: "orange",
                              fill: false,
                              tension: 0.4,
                              type: "line" as const,
                            }
                          ]
                        }
                        : dataRight;

                      return (
                        <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                          <Box textAlign="center">
                            <Heading size="sm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan')}
                            </Heading>
                            <Text fontSize={"sm"} align="right">
                              {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                                totalProfit,
                                selectedCurrency?.name!
                              )} {selectedCurrency?.name!}
                            </Text>
                            <Text fontSize={"sm"} align="right">
                              {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {profitRate} %
                            </Text>
                            {(hasCustomerSpecifiedParts && combinedChartData?.realProfitRate) ? (
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {combinedChartData.realProfitRate} %
                              </Text>
                            ) : (dispProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate && new Decimal(dispProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate).greaterThan(0) && (
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {new Decimal(dispProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate).toFixed(1)} %
                              </Text>
                            ))}
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Line
                                data={{ ...dataLeftWithRealProfitRate, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    x: {
                                      offset: true,
                                      ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    sales: {
                                      position: "left",
                                      min: yMinLeft,
                                      max: yMaxLeft,
                                      ticks: {
                                        callback: (value) =>
                                          new Intl.NumberFormat("en-US", { notation: "standard" }).format(Number(value)),
                                      },
                                    },
                                    profitRate: {
                                      position: "right",
                                      min: ProfitRateChartYAxisMin,
                                      max: ProfitRateChartYAxisMax,
                                      ticks: {
                                        callback: (value) => `${value}%`,
                                      },
                                      grid: {
                                        drawOnChartArea: false,
                                      },
                                    },
                                  },
                                  spanGaps: true,
                                  elements: { point: { radius: 2 } },
                                  plugins: {
                                    datalabels: {
                                      display: false
                                    },
                                    annotation: {
                                      annotations: {
                                        ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                      }
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                          <Box textAlign="center">
                            <Heading size="sm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.chart.downside')}
                            </Heading>
                            <Text fontSize={"sm"} align="right">
                              {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                                totalProfitModified,
                                selectedCurrency?.name!
                              )} {selectedCurrency?.name!}
                            </Text>
                            <Text fontSize={"sm"} align="right">
                              {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {profitRateModified} %
                            </Text>
                            {(hasCustomerSpecifiedParts && combinedChartDataModified?.realProfitRate) && (
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {combinedChartDataModified.realProfitRate} %
                              </Text>
                            )}
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Line
                                data={{ ...dataRightWithRealProfitRate, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    x: {
                                      offset: true,
                                      ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    sales: {
                                      position: "left",
                                      min: yMinLeft,
                                      max: yMaxLeft,
                                      ticks: {
                                        callback: (value) =>
                                          new Intl.NumberFormat("en-US", { notation: "standard" }).format(Number(value)),
                                      },
                                    },
                                    profitRate: {
                                      position: "right",
                                      min: ProfitRateChartYAxisMin,
                                      max: ProfitRateChartYAxisMax,
                                      ticks: {
                                        callback: (value) => `${value}%`,
                                      },
                                      grid: {
                                        drawOnChartArea: false,
                                      },
                                    },
                                  },
                                  spanGaps: true,
                                  elements: { point: { radius: 2 } },
                                  plugins: {
                                    datalabels: {
                                      display: false
                                    },
                                    annotation: {
                                      annotations: {
                                        ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                      }
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                          {/* 保存済み設定グラフ */}
                          {salesChartDataForSavedConfig.map((config) => {
                            const configData = config.data;

                            // 営業利益額を算出（costBreakdownがあれば年度丸め方式）
                            const localCurrencyName = currencyData.find(c => c.currencyId === localCurrencyId)?.currencyName;
                            const configTotalProfit: Decimal = (() => {
                              // 振り返りモード: profit[] は月別レートで表示通貨に変換済み。
                              // calcYearlyRoundedProfitTotal はソース通貨+単一レートで再計算するため使用不可。
                              // calcTotalProfitAndRate でprofit[]を合計し、そのまま返す（既に表示通貨）。
                              if (reviewDate) {
                                return calcTotalProfitAndRate(configData).totalProfit;
                              }
                              if (configData.costBreakdown && localCurrencyName) {
                                const inputs = configData.equipmentDepreciationInputs;
                                const equipInputs = inputs?.originalEquipments
                                  ? { ...inputs, equipments: inputs.originalEquipments }
                                  : inputs;
                                return calcYearlyRoundedProfitTotal(
                                  configData.costBreakdown,
                                  localCurrencyName,
                                  selectedCurrency?.name!,
                                  currencyData,
                                  equipInputs,
                                  configData.scrapCostInputs,
                                );
                              }
                              return configData.datasets
                                .filter((ds: { id: string }) => ds.id === "profit")
                                .flatMap((ds: { data: any }) => ds.data)
                                .reduce((sum: Decimal, value: number) => sum.plus(new Decimal(value)), new Decimal(0));
                            })();

                            // 営業利益率を算出（①③と同じcalcTotalProfitAndRateのprofitRateに統一。No.411追加対応）
                            // ※configTotalProfit（表示用合計）はcalcYearlyRoundedProfitTotal（年度丸め方式）のまま据え置き、率のみ区分を揃える
                            const configProfitRate = calcTotalProfitAndRate(configData).profitRate;

                            return (
                              <Box key={config.configId} textAlign="center">
                                <Heading size="sm" mb={1}>
                                  {config.name}
                                </Heading>
                                <Text fontSize={"sm"} align="right">
                                  {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                                    configTotalProfit,
                                    selectedCurrency?.name!
                                  )} {selectedCurrency?.name!}
                                </Text>
                                <Text fontSize={"sm"} align="right">
                                  {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {configProfitRate} %
                                </Text>
                                {(() => {
                                  // No.411追加対応: 投資回収用データ構造（combinedChartDataForSavedConfig）への間借りをやめ、
                                  // salesChartDataForSavedConfig自体に付帯したrealProfitRateを直接参照する。
                                  return (hasCustomerSpecifiedParts && configData.realProfitRate) ? (
                                    <Text fontSize={"sm"} align="right">
                                      {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {configData.realProfitRate} %
                                    </Text>
                                  ) : null;
                                })()}
                                {(() => {
                                  // 実質的営業利益率のオレンジ線データセットを追加
                                  const configDataWithRealProfitRate = hasCustomerSpecifiedParts // configData?.realProfitRateByMonth?.length > 0  を hasCustomerSpecifiedParts に変更
                                    ? {
                                      ...configData,
                                      datasets: [
                                        ...configData.datasets,
                                        {
                                          label: t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate'),
                                          id: "realProfitRate",
                                          yAxisID: "profitRate",
                                          data: configData.realProfitRateByMonth,
                                          borderColor: "orange",
                                          backgroundColor: "orange",
                                          fill: false,
                                          tension: 0.4,
                                          type: "line" as const,
                                        }
                                      ]
                                    }
                                    : configData;
                                  return (
                                    <Box width="30vw" height="calc(30vw * 0.5625)">
                                      <Line
                                        data={{ ...configDataWithRealProfitRate, labels: commonLabels }}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          scales: {
                                            x: {
                                              offset: true,
                                              ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                            },
                                            sales: {
                                              position: "left",
                                              min: yMinLeft,
                                              max: yMaxLeft,
                                              ticks: {
                                                callback: (value) =>
                                                  new Intl.NumberFormat("en-US", { notation: "standard" }).format(Number(value)),
                                              },
                                            },
                                            profitRate: {
                                              position: "right",
                                              min: ProfitRateChartYAxisMin,
                                              max: ProfitRateChartYAxisMax,
                                              ticks: {
                                                callback: (value) => `${value}%`,
                                              },
                                              grid: {
                                                drawOnChartArea: false,
                                              },
                                            },
                                          },
                                          spanGaps: true,
                                          elements: { point: { radius: 2 } },
                                          plugins: {
                                            datalabels: {
                                              display: false
                                            },
                                            annotation: {
                                              annotations: {
                                                ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                              }
                                            },
                                          },
                                        }}
                                      />
                                    </Box>
                                  );
                                })()}
                              </Box>
                            );
                          })}
                        </Flex>
                      );
                    })()}
                  </Box>
                )}

                {/* 投資回収（Combined Chart）見出し */}
                {combinedChartData && combinedChartDataModified && (
                  <Box mt={4} >
                    <Heading size="md" mb={2}>
                      {t('features.InvestmentScreening.ReportAndDraft.heading.incrementalCashFlow')}
                    </Heading>
                    <Text>
                      unit: {selectedCurrency?.name!}
                    </Text>
                  </Box>
                )}

                {/* 投資回収（Combined Chart）プロジェクト全体投資回収、当初投資・回収 */}
                {/* 通貨マスタ未取得 / 表示通貨未確定の段階で描画すると convertCurrency が空引数で呼ばれ console.error が出るため、揃ってから描画する */}
                {combinedChartDataAll && combinedChartDataPast && currencyData.length > 0 && selectedCurrency?.name && (() => {
                  // yMin/yMax/annotations は recoveryAllOptions/recoveryPastOptions（コンポーネントトップレベルで useMemo 化）内で算出
                  // 営業利益合計: costBreakdownがあれば年度丸め方式、なければ合計→変換方式
                  const rawDataAll = rawSalesChartDataAll || salesChartDataAll;
                  const rawDataPast = rawSalesChartDataPast || salesChartDataPast;

                  const localCurrencyName = currencyData.find(c => c.currencyId === localCurrencyId)?.currencyName;
                  const dp = hasDecimal(selectedCurrency?.name!) ? 2 : 0;

                  const calcDisplayProfit = (chartData: any): Decimal => {
                    // 振り返りモード: profit[] は月別レートで表示通貨に変換済み。
                    // calcYearlyRoundedProfitTotal はソース通貨+単一レートで再計算するため使用不可。
                    // calcTotalProfitAndRate でprofit[]を合計し、そのまま返す（既に表示通貨）。
                    if (reviewDate) {
                      return calcTotalProfitAndRate(chartData).totalProfit;
                    }
                    if (chartData?.costBreakdown && localCurrencyName && selectedCurrency?.name) {
                      const inputs = chartData.equipmentDepreciationInputs;
                      const equipInputs = inputs?.originalEquipments
                        ? { ...inputs, equipments: inputs.originalEquipments }
                        : inputs;
                      return calcYearlyRoundedProfitTotal(
                        chartData.costBreakdown,
                        localCurrencyName,
                        selectedCurrency?.name!,
                        currencyData,
                        equipInputs,
                        chartData.scrapCostInputs,
                      );
                    }
                    const chartTotal = calcTotalProfitAndRate(chartData).totalProfit;
                    return localCurrencyName === selectedCurrency?.name
                      ? chartTotal
                      : convertCurrency(chartTotal, localCurrencyName!, selectedCurrency?.name!, currencyData).toDecimalPlaces(dp);
                  };

                  const totalProfitAll = calcDisplayProfit(rawDataAll);
                  const totalProfitPast = calcDisplayProfit(rawDataPast);

                  // 営業利益率（率は無次元のため変換順序の影響なし）
                  const { profitRate: profitRateAll } = calcTotalProfitAndRate(salesChartDataAll);
                  const { profitRate: profitRatePast } = calcTotalProfitAndRate(salesChartDataPast);

                  return (
                    <Box mb={4}>
                      <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {t('features.InvestmentScreening.ReportAndDraft.heading.projectTotalRecovery')}
                          </Heading>
                          <Text fontSize={"sm"} align="right">
                            {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                              totalProfitAll,
                              selectedCurrency?.name!
                            )} {selectedCurrency?.name!}
                          </Text>
                          <Text fontSize={"sm"} align="right">
                            {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {profitRateAll} %
                          </Text>
                          {/* TODO: 生産性向上に合わせて一時コメントアウト（PJ全体投資回収の実質的営業利益率）
                        {dispProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate && new Decimal(dispProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate).greaterThan(0) && (
                          <Text fontSize={"sm"} align="right">
                            {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {new Decimal(dispProfitAndLoss.profitAndLossByYear.totalRealOperatingProfitRate).toFixed(1)} %
                          </Text>
                        )}
                        */}
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            {recoveryAllOptions && (
                              <Chart
                                type="bar"
                                data={combinedChartDataAll}
                                options={recoveryAllOptions}
                              />
                            )}
                          </Box>
                        </Box>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {t('features.InvestmentScreening.ReportAndDraft.heading.initialInvestmentRecovery')}
                          </Heading>
                          <Text fontSize={"sm"} align="right">
                            {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                              totalProfitPast,
                              selectedCurrency?.name!
                            )} {selectedCurrency?.name!}
                          </Text>
                          <Text fontSize={"sm"} align="right">
                            {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {profitRatePast} %
                          </Text>
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            {recoveryPastOptions && (
                              <Chart
                                type="bar"
                                data={combinedChartDataPast}
                                options={recoveryPastOptions}
                              />
                            )}
                          </Box>
                        </Box>
                      </Flex>
                    </Box>
                  );
                })()}

                {/* 投資回収（Combined Chart）or 増産投資回収 */}
                {combinedChartData && combinedChartDataModified && currencyData && currencyData.length > 0 && selectedCurrency?.name && (() => {
                  const { yMin, yMax, commonLabels } = computeCommonScale(
                    combinedChartData,
                    combinedChartDataModified,
                    false
                  );

                  // ローカル通貨
                  const localCurrency = currencyData.find(c => c.currencyId === localCurrencyId);
                  if (!localCurrency || !localCurrency.currencyName) return null;

                  const totalProfit = combinedChartData.displayTotalProfit
                    ? new Decimal(combinedChartData.displayTotalProfit)
                    : convertCurrency(new Decimal(combinedChartData.totalProfit), localCurrency.currencyName!, selectedCurrency.name, currencyData);
                  const totalProfitModified = combinedChartDataModified.displayTotalProfit
                    ? new Decimal(combinedChartDataModified.displayTotalProfit)
                    : convertCurrency(new Decimal(combinedChartDataModified.totalProfit), localCurrency.currencyName!, selectedCurrency.name, currencyData);

                  // 動的に「回収○ヶ月目」ラベル表示のアノテーションを生成
                  const annotations = generateRecoveryPointAnnotationOptions(projectData?.monthOfRecoveryOriginal, commonLabels);

                  // 動的に「回収○ヶ月目」ラベル表示のアノテーションを生成（ダウンサイド）
                  const annotationsModified = generateRecoveryPointAnnotationOptions(projectData?.monthOfRecoveryModified, commonLabels);

                  // 顧客指定部品の有無判断
                  const hasCustomerSpecifiedParts = bomData.some((c) => c.isCustomerSpecifiedParts === "true");

                  return (
                    <Box mb={4}>

                      <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {getLabelsByPurpose({
                              [PurposeType.NewProduct.code]: t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan'),
                              [PurposeType.UpdateAndOther.code]: t('features.InvestmentScreening.ReportAndDraft.chart.updateRecoveryOriginal'),
                              [PurposeType.Rationalization.code]: t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationRecoveryOriginal'),
                              ["*"]: t('features.InvestmentScreening.ReportAndDraft.chart.increaseRecoveryOriginal'),
                            })}
                          </Heading>
                          {combinedChartData.totalProfit && combinedChartData.profitRate && (
                            <>
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                                  totalProfit,
                                  selectedCurrency?.name!
                                )} {selectedCurrency?.name!}
                              </Text>
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {combinedChartData.profitRate || 0} %
                              </Text>
                              {(hasCustomerSpecifiedParts && Boolean(combinedChartData.realProfitRateCashflow ?? combinedChartData.realProfitRate)) && (
                                <Text fontSize={"sm"} align="right">
                                  {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {combinedChartData.realProfitRateCashflow ?? combinedChartData.realProfitRate} %
                                </Text>
                              )}
                            </>
                          )}
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            {investmentRecoveryOriginalData && investmentRecoveryOriginalOptions && (
                              <Chart
                                type="bar"
                                data={investmentRecoveryOriginalData}
                                options={investmentRecoveryOriginalOptions}
                              />
                            )}
                          </Box>
                        </Box>
                        <Box textAlign="center">
                          <Heading size="sm" mb={1}>
                            {getLabelsByPurpose({
                              [PurposeType.NewProduct.code]: t('features.InvestmentScreening.ReportAndDraft.chart.downside'),
                              [PurposeType.UpdateAndOther.code]: t('features.InvestmentScreening.ReportAndDraft.chart.updateRecoveryDownside'),
                              [PurposeType.Rationalization.code]: t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationRecoveryDownside'),
                              ["*"]: t('features.InvestmentScreening.ReportAndDraft.chart.increaseRecoveryDownside'),
                            })}
                          </Heading>
                          {combinedChartDataModified.totalProfit && combinedChartDataModified.profitRate && (
                            <>
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                                  totalProfitModified,
                                  selectedCurrency?.name!
                                )} {selectedCurrency?.name!}
                              </Text>
                              <Text fontSize={"sm"} align="right">
                                {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {combinedChartDataModified.profitRate || 0} %
                              </Text>
                              {(hasCustomerSpecifiedParts && Boolean(combinedChartDataModified.realProfitRateCashflow ?? combinedChartDataModified.realProfitRate)) && (
                                <Text fontSize={"sm"} align="right">
                                  {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {combinedChartDataModified.realProfitRateCashflow ?? combinedChartDataModified.realProfitRate} %
                                </Text>
                              )}
                            </>
                          )}
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            {investmentRecoveryModifiedData && investmentRecoveryModifiedOptions && (
                              <Chart
                                type="bar"
                                data={investmentRecoveryModifiedData}
                                options={investmentRecoveryModifiedOptions}
                              />
                            )}
                          </Box>
                        </Box>
                        {/* 保存済み設定グラフ */}
                        {combinedChartDataForSavedConfig.map((config) => {
                          // 保存済み設定の回収月を取得
                          const configRecoveryData = monthOfRecoveryForSavedConfig.find(item => item.configId === config.configId);
                          const configRecoveryMonths = configRecoveryData?.data || [];

                          // アノテーションを動的に生成
                          const configAnnotations = {} as Record<string, AnnotationOptions>;
                          if (configRecoveryMonths.length > 0) {
                            configRecoveryMonths.forEach((index, i) => {
                              const positions: LabelPosition[] = ["start", "center", "end"];
                              const currentPosition = positions[i % positions.length];

                              configAnnotations[`recoveryPoint${i}`] = {
                                type: "line",
                                xMin: commonLabels[index - 1],
                                xMax: commonLabels[index - 1],
                                borderColor: "orange",
                                borderWidth: 1,
                                display: true,
                                label: {
                                  content: t('features.InvestmentScreening.ReportAndDraft.chart.recoveryMonthLabel', { months: formatRecoveryMonths(index) }),
                                  display: true,
                                  position: currentPosition,
                                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                                },
                              };
                            });
                          }

                          const configTotalProfit = (config.data.totalProfit && localCurrency)
                            ? config.data.displayTotalProfit
                              ? new Decimal(config.data.displayTotalProfit)
                              : convertCurrency(new Decimal(config.data.totalProfit), localCurrency.currencyName!, selectedCurrency.name, currencyData).toDecimalPlaces(4)
                            : 0;

                          return (
                            <Box key={config.configId} textAlign="center">
                              <Heading size="sm" mb={1}>
                                {config.name}
                              </Heading>
                              {config.data.totalProfit && config.data.profitRate && (
                                <>
                                  <Text fontSize={"sm"} align="right">
                                    {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitTotal')}: {formatNumber(
                                      configTotalProfit,
                                      selectedCurrency?.name!
                                    )} {selectedCurrency?.name!}
                                  </Text>
                                  <Text fontSize={"sm"} align="right">
                                    {t('features.InvestmentScreening.ReportAndDraft.chart.operatingProfitRate')}: {config.data.profitRate || 0} %
                                  </Text>
                                  {(hasCustomerSpecifiedParts && Boolean(config.data?.realProfitRateCashflow ?? config.data?.realProfitRate)) && (
                                    <Text fontSize={"sm"} align="right">
                                      {t('features.InvestmentScreening.ReportAndDraft.chart.realOperatingProfitRate')}: {config.data.realProfitRateCashflow ?? config.data.realProfitRate} %
                                    </Text>
                                  )}
                                </>
                              )}
                              <Box width="30vw" height="calc(30vw * 0.5625)">
                                <Chart
                                  type="bar"
                                  data={{ ...config.data, labels: commonLabels }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      tooltip: { enabled: true },
                                      legend: {
                                        labels: {
                                          // 凡例のカスタマイズ
                                          generateLabels: generateLabels,
                                        }
                                      },
                                      annotation: {
                                        annotations: {
                                          zeroLine: {
                                            type: "line",
                                            yMin: 0,
                                            yMax: 0,
                                            borderColor: "red",
                                            borderDash: [4, 4],
                                            borderWidth: 2,
                                          },
                                          ...configAnnotations, // 動的に生成したアノテーションをここで展開
                                        }
                                      },
                                      datalabels: {
                                        display: false
                                      },
                                    },
                                    scales: {
                                      x: {
                                        offset: true,
                                        ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                      },
                                      y: {
                                        min: yMin,
                                        max: yMax,
                                        ticks: {
                                          format: {
                                            notation: "standard"
                                          }
                                        }
                                      },
                                    },
                                    layout: {
                                      padding: {
                                        bottom: 20,
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Flex>
                    </Box>
                  );
                })()}
              </Box>

              <Box ref={simulationRef3} style={{ padding: '4px' }}>
                {/* 顧客需要と設備キャパシティ (Demand & Capacity Chart) */}
                {gapChartDataOriginal && (
                  <Box mt={4}>
                    <Heading size="md" mb={2}>
                      {t('features.InvestmentScreening.ReportAndDraft.heading.demandAndCapacity')}
                    </Heading>
                    <Text>
                      unit: pcs
                    </Text>
                    {(() => {
                      const dataLeft = gapChartDataOriginal;
                      const dataRight = gapChartDataModified ? gapChartDataModified : gapChartDataOriginal;
                      // 保存済み設定データも含めてY軸スケールを計算
                      const allDataSets = [dataLeft, dataRight];
                      gapChartDataForSavedConfig.forEach((config) => {
                        if (config.data) allDataSets.push(config.data);
                      });
                      const allData = allDataSets.flatMap(ds =>
                        ds?.datasets?.flatMap((dataset: any) => dataset?.data?.filter((v: any) => v !== null)) || []
                      );
                      const { yMin, yMax } = calcYAxisRange(allData, false);
                      const commonLabels = dataLeft.labels.length >= dataRight.labels.length ? dataLeft.labels : dataRight.labels;
                      return (
                        <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                          <Box textAlign="center">
                            <Heading size="sm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan')}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Line
                                data={{ ...dataLeft, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    x: {
                                      offset: true,
                                      ticks: { callback: (index) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    y: {
                                      min: yMin,
                                      max: yMax,
                                      ticks: {
                                        format: {
                                          notation: "standard"
                                        },
                                      },
                                    },
                                  },
                                  spanGaps: true,
                                  elements: { point: { radius: 2 } },
                                  plugins: {
                                    datalabels: {
                                      display: false
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                          <Box textAlign="center">
                            <Heading size="sm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.chart.downside')}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Line
                                data={{ ...dataRight, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    x: {
                                      offset: true,
                                      ticks: { callback: (index) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    y: {
                                      min: yMin,
                                      max: yMax,
                                      ticks: {
                                        format: {
                                          notation: "standard"
                                        },
                                      },
                                    },
                                  },
                                  spanGaps: true,
                                  elements: { point: { radius: 2 } },
                                  plugins: {
                                    datalabels: {
                                      display: false
                                    }
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                          {/* 保存済み設定グラフ */}
                          {gapChartDataForSavedConfig.map((config) => (
                            <Box key={config.configId} textAlign="center">
                              <Heading size="sm" mb={1}>
                                {config.name}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)">
                                <Line
                                  data={{ ...config.data, labels: commonLabels }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                      x: {
                                        offset: true,
                                        ticks: { callback: (index) => formatYearMonthByLocale(commonLabels[index]) },
                                      },
                                      y: {
                                        min: yMin,
                                        max: yMax,
                                        ticks: {
                                          format: {
                                            notation: "standard"
                                          },
                                        },
                                      },
                                    },
                                    spanGaps: true,
                                    elements: { point: { radius: 2 } },
                                    plugins: {
                                      datalabels: {
                                        display: false
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Flex>
                      );
                    })()}
                  </Box>
                )}

                {/* 設備生産数量からのギャップ (Production Quantity & Gap Chart) */}
                {productionQuantityChartDataOriginal && (
                  <Box mt={4} mb={4}>
                    <Heading size="md" mb={2}>
                      {t('features.InvestmentScreening.ReportAndDraft.heading.productionQuantityGap')}
                    </Heading>
                    <Text>
                      unit: pcs
                    </Text>
                    {(() => {
                      const dataLeft = productionQuantityChartDataOriginal;
                      const dataRight = productionQuantityChartDataModified ? productionQuantityChartDataModified : productionQuantityChartDataOriginal;
                      // 保存済み設定データも含めてY軸スケールを計算
                      const allDataSets = [dataLeft, dataRight];
                      productionQuantityChartDataForSavedConfig.forEach((config) => {
                        if (config.data) allDataSets.push(config.data);
                      });
                      const allData = allDataSets.flatMap(ds =>
                        ds?.datasets?.flatMap((dataset: any) => dataset?.data?.filter((v: any) => v !== null)) || []
                      );
                      const { yMin, yMax } = calcYAxisRange(allData, true);
                      const commonLabels = dataLeft.labels.length >= dataRight.labels.length ? dataLeft.labels : dataRight.labels;
                      return (
                        <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                          <Box textAlign="center">
                            <Heading size="sm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan')}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Chart
                                type="bar"
                                data={{ ...dataLeft, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    x: {
                                      offset: true,
                                      stacked: true,
                                      ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    y: {
                                      stacked: true,
                                      min: yMin,
                                      max: yMax,
                                      ticks: {
                                        format: {
                                          notation: "standard"
                                        }
                                      }
                                    },
                                  },
                                  elements: { point: { radius: 2 } },
                                  plugins: {
                                    datalabels: {
                                      display: false,
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                          <Box textAlign="center">
                            <Heading size="sm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.chart.downside')}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)">
                              <Chart
                                type="bar"
                                data={{ ...dataRight, labels: commonLabels }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    x: {
                                      offset: true,
                                      stacked: true,
                                      ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                    },
                                    y: {
                                      stacked: true,
                                      min: yMin,
                                      max: yMax,
                                      ticks: {
                                        format: {
                                          notation: "standard"
                                        }
                                      }
                                    },
                                  },
                                  elements: { point: { radius: 2 } },
                                  plugins: {
                                    datalabels: {
                                      display: false,
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                          {/* 保存済み設定グラフ */}
                          {productionQuantityChartDataForSavedConfig.map((config) => (
                            <Box key={config.configId} textAlign="center">
                              <Heading size="sm" mb={1}>
                                {config.name}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)">
                                <Chart
                                  type="bar"
                                  data={{ ...config.data, labels: commonLabels }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                      x: {
                                        offset: true,
                                        stacked: true,
                                        ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                      },
                                      y: {
                                        stacked: true,
                                        min: yMin,
                                        max: yMax,
                                        ticks: {
                                          format: {
                                            notation: "standard"
                                          }
                                        }
                                      },
                                    },
                                    elements: { point: { radius: 2 } },
                                    plugins: {
                                      datalabels: {
                                        display: false,
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Flex>
                      );
                    })()}
                  </Box>
                )}

                {/* 生産能力（ライン全体） - 増産投資の場合のみ表示 */}
                {projectData?.purpose === PurposeType.IncreasedProduction.code && productionCapacityChartData && productionCapacityChartDataModified && (() => {
                  const data = productionCapacityChartData.datasets.flatMap((ds: any) =>
                    (ds?.data?.filter((v: any) => v !== null) || []).map((v: any) => Number(v))
                  );
                  const { yMin, yMax } = calcYAxisRange(data, false);

                  // 増産開始年月のインデックスを見つける
                  return (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>
                        {t('features.InvestmentScreening.ReportAndDraft.heading.productionCapacityLine')}
                      </Heading>
                      <Text>
                        unit: pcs
                      </Text>
                      {(() => {
                        const dataLeft = productionCapacityChartData;
                        const dataRight = productionCapacityChartDataModified ? productionCapacityChartDataModified : productionCapacityChartData;
                        const { yMin, yMax, commonLabels } = computeCommonScale(
                          dataLeft,
                          dataRight,
                          false
                        );
                        return (
                          <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                            <Box textAlign="center">
                              <Heading size="sm" mb={1}>
                                {t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan')}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                <Chart
                                  type="bar"
                                  data={productionCapacityChartData}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      tooltip: {
                                        enabled: true,
                                        callbacks: {
                                          label: function (context) {
                                            let label = context.dataset.label || '';
                                            if (label) {
                                              label += ': ';
                                            }
                                            if (context.parsed.y !== null) {
                                              label += context.parsed.y.toLocaleString() + ' pcs';
                                            }
                                            return label;
                                          }
                                        }
                                      },
                                      legend: {
                                        display: true,
                                        position: 'top' as const,
                                        labels: {
                                          generateLabels: (chart: any): LegendItem[] => {
                                            return chart.data.datasets.map((dataset: any, index: number) => {
                                              return {
                                                text: dataset.label,
                                                fillStyle: dataset.backgroundColor,
                                                strokeStyle: dataset.borderColor,
                                                hidden: !chart.isDatasetVisible(index),
                                                datasetIndex: index,
                                                pointStyle: dataset.pointStyle,
                                              }
                                            });
                                          },
                                          usePointStyle: true // 凡例で pointStyle を使う
                                        },
                                      },
                                      datalabels: {
                                        display: false
                                      },
                                      annotation: {
                                        annotations: {
                                          ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                        }
                                      }
                                    },
                                    scales: {
                                      x: {
                                        offset: true,
                                        ticks: { callback: (index: any) => formatYearMonthByLocale(productionCapacityChartData.labels[index]) },
                                      },
                                      y: {
                                        min: yMin,
                                        max: yMax,
                                        ticks: {
                                          format: {
                                            notation: "standard"
                                          }
                                        }
                                      },
                                    },
                                    layout: {
                                      padding: {
                                        bottom: 20,
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box textAlign="center">
                              <Heading size="sm" mb={1}>
                                {t('features.InvestmentScreening.ReportAndDraft.chart.downside')}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                <Chart
                                  type="bar"
                                  data={productionCapacityChartDataModified}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      tooltip: {
                                        enabled: true,
                                        callbacks: {
                                          label: function (context) {
                                            let label = context.dataset.label || '';
                                            if (label) {
                                              label += ': ';
                                            }
                                            if (context.parsed.y !== null) {
                                              label += context.parsed.y.toLocaleString() + ' pcs';
                                            }
                                            return label;
                                          }
                                        }
                                      },
                                      legend: {
                                        display: true,
                                        position: 'top' as const,
                                        labels: {
                                          generateLabels: (chart: any): LegendItem[] => {
                                            return chart.data.datasets.map((dataset: any, index: number) => {
                                              return {
                                                text: dataset.label,
                                                fillStyle: dataset.backgroundColor,
                                                strokeStyle: dataset.borderColor,
                                                hidden: !chart.isDatasetVisible(index),
                                                datasetIndex: index,
                                                pointStyle: dataset.pointStyle,
                                              }
                                            });
                                          },
                                          usePointStyle: true // 凡例で pointStyle を使う
                                        },
                                      },
                                      datalabels: {
                                        display: false
                                      },
                                      annotation: {
                                        annotations: {
                                          ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                        }
                                      }
                                    },
                                    scales: {
                                      x: {
                                        offset: true,
                                        ticks: { callback: (index: any) => formatYearMonthByLocale(productionCapacityChartData.labels[index]) },
                                      },
                                      y: {
                                        min: yMin,
                                        max: yMax,
                                        ticks: {
                                          format: {
                                            notation: "standard"
                                          }
                                        }
                                      },
                                    },
                                    layout: {
                                      padding: {
                                        bottom: 20,
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                            {/* 保存済み設定グラフ */}
                            {productionCapacityChartDataForSavedConfig.map((config) => (
                              <Box key={config.configId} textAlign="center">
                                <Heading size="sm" mb={1}>
                                  {config.name}
                                </Heading>
                                <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                  <Chart
                                    type="bar"
                                    data={{ ...config.data }}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      plugins: {
                                        tooltip: {
                                          enabled: true,
                                          callbacks: {
                                            label: function (context) {
                                              let label = context.dataset.label || '';
                                              if (label) {
                                                label += ': ';
                                              }
                                              if (context.parsed.y !== null) {
                                                label += context.parsed.y.toLocaleString() + ' pcs';
                                              }
                                              return label;
                                            }
                                          }
                                        },
                                        legend: {
                                          display: true,
                                          position: 'top' as const,
                                          labels: {
                                            generateLabels: (chart: any): LegendItem[] => {
                                              return chart.data.datasets.map((dataset: any, index: number) => {
                                                return {
                                                  text: dataset.label,
                                                  fillStyle: dataset.backgroundColor,
                                                  strokeStyle: dataset.borderColor,
                                                  hidden: !chart.isDatasetVisible(index),
                                                  datasetIndex: index,
                                                  pointStyle: dataset.pointStyle,
                                                }
                                              });
                                            },
                                            usePointStyle: true
                                          },
                                        },
                                        datalabels: {
                                          display: false
                                        },
                                        annotation: {
                                          annotations: {
                                            ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                                          }
                                        }
                                      },
                                      scales: {
                                        x: {
                                          offset: true,
                                          ticks: { callback: (index: any) => formatYearMonthByLocale(config.data.labels[index]) },
                                        },
                                        y: {
                                          min: yMin,
                                          max: yMax,
                                          ticks: {
                                            format: {
                                              notation: "standard"
                                            }
                                          }
                                        },
                                      },
                                      layout: {
                                        padding: {
                                          bottom: 20,
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            )
                            )}
                          </Flex>
                        );
                      })()}
                    </Box>
                  );
                })()}

                {/* 生産キャパグラフ（既存ライン含む） (Existing Line Capacity Chart) */}
                {/* 増産では画面と同じく「生産能力（ライン全体）」の直下に表示する（新製品は「設備生産数量からのギャップ」の直下になる） */}
                {/* Simulation画面でOFFのまま事業部確認した案件は3キーともnullのため、見出しごと表示しない */}
                {existingLineCapacityChartDataOriginal && (
                  <Box mt={4} mb={4}>
                    <Heading size="md" mb={2}>
                      {t('features.InvestmentScreening.ReportAndDraft.heading.existingLineCapacity')}
                    </Heading>
                    <Text>
                      unit: pcs
                    </Text>
                    {(() => {
                      const dataLeft = existingLineCapacityChartDataOriginal;
                      const dataRight = existingLineCapacityChartDataModified ? existingLineCapacityChartDataModified : existingLineCapacityChartDataOriginal;
                      // 積み上げ後の合計と生産能力を考慮してY軸スケールを計算（保存済み設定も含める）
                      const allDataSets = [dataLeft, dataRight, ...existingLineCapacityChartDataForSavedConfig.map(config => config.data).filter(Boolean)];
                      const allData = allDataSets.flatMap((ds: any) => {
                        const stackTotals: number[] = [];
                        ds?.datasets?.filter((d: any) => d.type === 'bar').forEach((d: any) => {
                          d.data?.forEach((v: number, i: number) => { stackTotals[i] = (stackTotals[i] || 0) + (v || 0); });
                        });
                        const lineData = ds?.datasets?.filter((d: any) => d.type === 'line').flatMap((d: any) => d.data?.filter((v: any) => v !== null) || []) || [];
                        return [...stackTotals.filter(v => v !== undefined), ...lineData];
                      });
                      const { yMin, yMax } = calcYAxisRange(allData);
                      const commonLabels = dataLeft.labels.length >= dataRight.labels.length ? dataLeft.labels : dataRight.labels;

                      const renderChart = (key: string, title: string, chartData: any) => (
                        <Box key={key} textAlign="center">
                          <Heading size="sm" mb={1}>
                            {title}
                          </Heading>
                          <Box width="30vw" height="calc(30vw * 0.5625)">
                            <Chart
                              type="bar"
                              data={{
                                ...chartData,
                                labels: commonLabels,
                                // 折れ線（生産能力）は積み上げ対象外の軸に載せる
                                datasets: chartData.datasets.map((ds: any) =>
                                  ds.type === 'line' ? { ...ds, yAxisID: 'y1' } : ds
                                ),
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  x: {
                                    offset: true,
                                    stacked: true,
                                    ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                                  },
                                  y: {
                                    stacked: true,
                                    min: yMin,
                                    max: yMax,
                                    ticks: {
                                      format: {
                                        notation: "standard"
                                      }
                                    }
                                  },
                                  y1: {
                                    display: false,
                                    stacked: false,
                                    min: yMin,
                                    max: yMax,
                                  },
                                },
                                elements: { point: { radius: 2 } },
                                plugins: {
                                  datalabels: {
                                    display: false,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      );

                      return (
                        <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                          {renderChart("original", t('features.InvestmentScreening.ReportAndDraft.chart.originalPlan'), dataLeft)}
                          {renderChart("modified", t('features.InvestmentScreening.ReportAndDraft.chart.downside'), dataRight)}
                          {/* 保存済み設定グラフ */}
                          {existingLineCapacityChartDataForSavedConfig
                            .filter(config => config.data)
                            .map(config => renderChart(config.configId, config.name, config.data))}
                        </Flex>
                      );
                    })()}
                  </Box>
                )}
              </Box>

              {/* 生産能力（プロセス） - 更新投資または合理化投資の場合に表示 */}
              {(projectData?.purpose === PurposeType.UpdateAndOther.code || projectData?.purpose === PurposeType.Rationalization.code) && productionCapacityProcessChartDataList && productionCapacityProcessChartDataList.length > 0 && (
                <>
                  {productionCapacityProcessChartDataList.map((processChartData, processIndex) => {
                    const data = processChartData.beforeData?.datasets?.flatMap((ds: any) =>
                      (ds?.data?.filter((v: any) => v !== null) || []).map((v: any) => Number(v))
                    ) || [];
                    const { yMin, yMax } = calcYAxisRange(data, false);

                    // 共通のチャートオプション生成関数
                    const getChartOptions = (chartData: any) => ({
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            label: function (context: any) {
                              let label = context.dataset.label || '';
                              if (label) {
                                label += ': ';
                              }
                              if (context.parsed.y !== null) {
                                label += context.parsed.y.toLocaleString() + ' pcs';
                              }
                              return label;
                            }
                          }
                        },
                        legend: {
                          display: true,
                          position: 'top' as const,
                          labels: {
                            generateLabels: (chart: any): LegendItem[] => {
                              return chart.data.datasets.map((dataset: any, index: number) => {
                                return {
                                  text: dataset.label,
                                  fillStyle: dataset.backgroundColor,
                                  strokeStyle: dataset.borderColor,
                                  hidden: !chart.isDatasetVisible(index),
                                  datasetIndex: index,
                                  pointStyle: dataset.pointStyle,
                                }
                              });
                            },
                            usePointStyle: true
                          },
                        },
                        datalabels: {
                          display: false
                        },
                        annotation: {
                          annotations: {
                            ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                          }
                        }
                      },
                      scales: {
                        x: {
                          offset: true,
                          ticks: { callback: (index: any) => formatYearMonthByLocale(chartData?.labels?.[index]) || '' },
                        },
                        y: {
                          min: yMin,
                          max: yMax,
                          ticks: {
                            format: {
                              notation: "standard" as const
                            }
                          }
                        },
                      },
                      layout: {
                        padding: {
                          bottom: 20,
                        },
                      },
                    });

                    return (
                      <Box mt={4} key={processChartData.processId} id={`${PRODUCTION_CAPACITY_PROCESS_ITEM_PREFIX}${processIndex}`}>
                        <Heading size="md" mb={2}>
                          {t('features.InvestmentScreening.ReportAndDraft.chart.productionCapacityProcess', { name: processChartData.processName || processChartData.processId })}
                        </Heading>
                        <Text>
                          unit: pcs
                        </Text>
                        {/* 1行目: 更新前（合理化前）、更新前（合理化前）（ダウンサイド） */}
                        <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4} mb={4}>
                          {processChartData.beforeData && (
                            <Box textAlign="center">
                              <Heading size="sm" mb={1}>
                                {projectData?.purpose === PurposeType.Rationalization.code ? t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationBefore') : t('features.InvestmentScreening.ReportAndDraft.chart.updateBefore')}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                <Chart
                                  type="bar"
                                  data={processChartData.beforeData}
                                  options={getChartOptions(processChartData.beforeData)}
                                />
                              </Box>
                            </Box>
                          )}
                          {processChartData.beforeDownsideData && (
                            <Box textAlign="center">
                              <Heading size="sm" mb={1}>
                                {projectData?.purpose === PurposeType.Rationalization.code ? t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationBefore') : t('features.InvestmentScreening.ReportAndDraft.chart.updateBefore')}{t('features.InvestmentScreening.ReportAndDraft.chart.downsideSuffix')}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                <Chart
                                  type="bar"
                                  data={processChartData.beforeDownsideData}
                                  options={getChartOptions(processChartData.beforeDownsideData)}
                                />
                              </Box>
                            </Box>
                          )}
                        </Flex>
                        {/* 2行目: 更新後（合理化後）、更新後（合理化後）（ダウンサイド） */}
                        <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                          {processChartData.afterData && (
                            <Box textAlign="center">
                              <Heading size="sm" mb={1}>
                                {projectData?.purpose === PurposeType.Rationalization.code ? t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationAfter') : t('features.InvestmentScreening.ReportAndDraft.chart.updateAfter')}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                <Chart
                                  type="bar"
                                  data={processChartData.afterData}
                                  options={getChartOptions(processChartData.afterData)}
                                />
                              </Box>
                            </Box>
                          )}
                          {processChartData.afterDownsideData && (
                            <Box textAlign="center">
                              <Heading size="sm" mb={1}>
                                {projectData?.purpose === PurposeType.Rationalization.code ? t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationAfter') : t('features.InvestmentScreening.ReportAndDraft.chart.updateAfter')}{t('features.InvestmentScreening.ReportAndDraft.chart.downsideSuffix')}
                              </Heading>
                              <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                <Chart
                                  type="bar"
                                  data={processChartData.afterDownsideData}
                                  options={getChartOptions(processChartData.afterDownsideData)}
                                />
                              </Box>
                            </Box>
                          )}
                        </Flex>
                        {/* 3行目以降: 保存済み設定のグラフ */}
                        {productionCapacityProcessChartDataForSavedConfig.length > 0 && (
                          <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4} mt={4}>
                            {productionCapacityProcessChartDataForSavedConfig.map((savedConfig) => {
                              const processData = savedConfig.processData?.find(
                                (p: any) => p.processId === processChartData.processId
                              );

                              if (!processData?.afterData) return null;

                              return (
                                <Box key={savedConfig.configId} textAlign="center">
                                  <Heading size="sm" mb={1}>
                                    {savedConfig.name}
                                  </Heading>
                                  <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                                    <Chart
                                      type="bar"
                                      data={processData.afterData}
                                      options={getChartOptions(processData.afterData)}
                                    />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Flex>
                        )}
                      </Box>
                    );
                  })}
                </>
              )}

              {/* 合理化前後サマリーチャート - 合理化投資、または更新投資（簡易モード）の場合に表示 */}
              {(projectData?.purpose === PurposeType.Rationalization.code ||
                (isSimpleMode && projectData?.purpose === PurposeType.UpdateAndOther.code)) &&
                rationalizationSummaryChartDataList && rationalizationSummaryChartDataList.length > 0 && (
                  <>
                    {rationalizationSummaryChartDataList.map((processChartData, processIndex) => {
                      if (processChartData.items.length === 0) return null;

                      // グループ分け
                      const indicatorItems = processChartData.items.filter(item => item.group === "indicator");
                      const costItems = processChartData.items.filter(item => item.group === "cost");
                      const improvementEffectItems = processChartData.items.filter(item => item.group === "improvementEffect");

                      // 共通のチャートレンダリング関数
                      const renderChart = (item: typeof processChartData.items[0]) => {
                        const hasDepreciation = item.depreciationData && item.depreciationData.length > 0;

                        // 合理化前後データの最小・最大値を計算してY軸スケールを統一
                        const allData = hasDepreciation
                          ? [...item.beforeData, ...item.afterData, ...item.depreciationData!].filter(v => v !== null)
                          : [...item.beforeData, ...item.afterData].filter(v => v !== null);
                        const { yMin, yMax } = calcYAxisRange(allData as number[], false);

                        // Chart.jsデータ構造（複合チャート用）
                        const mixedChartData = {
                          labels: processChartData.labels,
                          datasets: [
                            {
                              type: 'line' as const,
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationBeforeLaborCost'),
                              data: item.beforeData,
                              borderColor: "rgba(255, 99, 132, 1)",
                              backgroundColor: "rgba(255, 99, 132, 0.2)",
                              fill: false,
                              tension: 0.1,
                              pointRadius: 2,
                              pointHoverRadius: 5,
                              order: 1,
                            },
                            {
                              type: 'line' as const,
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationAfterLaborCost'),
                              data: item.afterData,
                              borderColor: "rgba(75, 192, 192, 1)",
                              backgroundColor: "rgba(75, 192, 192, 0.2)",
                              fill: false,
                              tension: 0.1,
                              pointRadius: 2,
                              pointHoverRadius: 5,
                              order: 2,
                            },
                            {
                              type: 'bar' as const,
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.depreciation'),
                              data: item.depreciationData,
                              backgroundColor: "rgba(54, 162, 235, 0.6)",
                              borderColor: "rgba(54, 162, 235, 1)",
                              borderWidth: 1,
                              order: 3,
                            },
                          ],
                        };

                        // Chart.jsデータ構造（折れ線チャート用）
                        const lineChartData = {
                          labels: processChartData.labels,
                          datasets: [
                            {
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationBefore'),
                              data: item.beforeData,
                              borderColor: "rgba(255, 99, 132, 1)",
                              backgroundColor: "rgba(255, 99, 132, 0.2)",
                              fill: false,
                              tension: 0.1,
                              pointRadius: 2,
                              pointHoverRadius: 5,
                            },
                            {
                              label: t('features.InvestmentScreening.ReportAndDraft.chart.rationalizationAfter'),
                              data: item.afterData,
                              borderColor: "rgba(54, 162, 235, 1)",
                              backgroundColor: "rgba(54, 162, 235, 0.2)",
                              fill: false,
                              tension: 0.1,
                              pointRadius: 2,
                              pointHoverRadius: 5,
                            },
                          ],
                        };

                        const chartOptions = {
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            tooltip: {
                              enabled: true,
                              callbacks: {
                                label: function (context: any) {
                                  let label = context.dataset.label || '';
                                  if (label) {
                                    label += ': ';
                                  }
                                  if (context.parsed.y !== null) {
                                    label += context.parsed.y.toLocaleString() + ' ' + item.unit;
                                  }
                                  return label;
                                }
                              }
                            },
                            legend: {
                              display: true,
                              position: 'top' as const,
                            },
                            datalabels: {
                              display: false
                            },
                            annotation: {
                              annotations: {
                                ...generateAnnotationStarLabels(investmentStartYearMonth, projectData?.purpose),
                              }
                            }
                          },
                          scales: {
                            x: {
                              offset: true,
                              ticks: {
                                callback: (_: any, index: number) => {
                                  return processChartData.labels[index];
                                }
                              },
                            },
                            y: {
                              min: yMin,
                              max: yMax,
                              ticks: {
                                callback: (value: any) => value.toLocaleString(),
                              },
                              title: {
                                display: false,
                              }
                            },
                          },
                        };

                        return (
                          <Box key={item.itemId} textAlign="center" data-chart-item-id={item.itemId}>
                            <Heading size="sm" mb={1}>
                              {item.itemName}
                              {item.unit && (
                                <Text as="span" fontWeight="normal" fontSize="sm"> unit: {item.unit}</Text>
                              )}
                            </Heading>
                            <Box width="30vw" height="calc(30vw * 0.5625)" margin="0 auto">
                              {hasDepreciation ? (
                                <Chart
                                  type="bar"
                                  data={mixedChartData as any}
                                  options={chartOptions}
                                />
                              ) : (
                                <Line
                                  data={lineChartData}
                                  options={chartOptions}
                                />
                              )}
                            </Box>
                          </Box>
                        );
                      };

                      return (
                        <Box mt={4} key={processChartData.processId} id={`${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${processIndex}`}>
                          <Heading size="md" mb={4}>
                            {t('features.InvestmentScreening.ReportAndDraft.heading.rationalizationSummaryProcess', { id: processChartData.itemNo || processChartData.processId })}
                          </Heading>

                          {/* 指標 */}
                          {indicatorItems.length > 0 && (
                            <Box mb={6} id={`${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${processIndex}-indicator`}>
                              <Heading size="sm" mb={2} color="blue.600" data-group-label="indicator">
                                {t('features.InvestmentScreening.ReportAndDraft.label.indicators')}
                              </Heading>
                              <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                                {indicatorItems.map(renderChart)}
                              </Flex>
                            </Box>
                          )}

                          {/* 費用 */}
                          {costItems.length > 0 && (
                            <Box mb={6} id={`${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${processIndex}-cost`}>
                              <Heading size="sm" mb={2} color="blue.600" data-group-label="cost">
                                {t('features.InvestmentScreening.ReportAndDraft.label.expenses')}
                              </Heading>
                              <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                                {costItems.map(renderChart)}
                              </Flex>
                            </Box>
                          )}

                          {/* 改善効果 */}
                          {improvementEffectItems.length > 0 && (
                            <Box id={`${RATIONALIZATION_SUMMARY_CHART_ITEM_PREFIX}${processIndex}-improvementEffect`}>
                              <Heading size="sm" mb={2} color="blue.600" data-group-label="improvementEffect">
                                {t('features.InvestmentScreening.ReportAndDraft.label.improvementEffect')}
                              </Heading>
                              <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
                                {improvementEffectItems.map(renderChart)}
                              </Flex>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </>
                )}

            </>)}

            <Box>
              <Heading as="h2" size="md" mb={4}>
                {t('features.InvestmentScreening.ReportAndDraft.heading.finalInvestmentReason')}
              </Heading>
              <Text mb={2} style={{ whiteSpace: 'pre-wrap' }}>
                {projectData?.finalInvestmentReason}
              </Text>
            </Box>
          </Box>
        </Box>

        {/* 振り返り3画面コンテンツ（reviewDate 連動表示） */}
        {reviewDate && (reviewSalesChartData || reviewBusinessChartData || reviewFutureOutlook) && (() => {
          const RD_REVIEW_PREFIX = 'features.InvestmentScreening.ReportAndDraft.review';
          /**
           * 営業視点テキストのラベルを翻訳する（キーが未翻訳の場合はキー自体を返す）
           * @param group テキスト種別グループ名（salesTextLabel / salesSupplementaryLabel / salesCommentLabel）
           * @param key テキストキー
           * @returns 翻訳済みラベル文字列
           */
          const resolveSalesLabel = (group: 'salesTextLabel' | 'salesSupplementaryLabel' | 'salesCommentLabel', key: string) => {
            const fullKey = `${RD_REVIEW_PREFIX}.${group}.${key}`;
            const translated = t(fullKey);
            return translated === fullKey ? key : translated;
          };

          const fmtVal = (v: number, isMoney: boolean, summaryType: string) => {
            if (isMoney) {
              // 末尾の0を除去して表示（14.380 → 14.38）
              const dp = hasDecimal(selectedCurrency.name) ? 4 : 0;
              const formatted = formatNumber(v, selectedCurrency.name, dp);
              if (hasDecimal(selectedCurrency.name) && formatted.includes('.')) {
                return formatted.replace(/0+$/, '').replace(/\.$/, '');
              }
              return formatted;
            }
            return summaryType === "total" ? Math.round(v).toLocaleString() : v.toFixed(1);
          };

          // ReviewSales 棒グラフ用 options（振り返り境界縦線 + x軸 ticks 自動間引き + Y軸 suggestedMax）
          //   実画面 ReviewSales.createChartOptions の仕様を踏襲：
          //   - max は表示中の全 datasets から finite な値で算出、+10% 余白を Math.ceil
          //   - 0 を含めるため beginAtZero: true
          /**
           * 営業視点 Bar グラフ用の Chart.js options を生成する
           * @param boundaryIdx 振り返り境界インデックス（境界縦線の表示位置）
           * @param labelLen X軸ラベルの総数（境界が有効範囲内かどうかの判定に使用）
           * @param chartData Chart.js データオブジェクト（Y軸 max 算出に使用）
           * @returns Chart.js options オブジェクト
           */
          const buildSalesChartOptions = (boundaryIdx: number, labelLen: number, chartData: any) => {
            const showBoundary = boundaryIdx > 0 && boundaryIdx < labelLen;
            const allValues: number[] = (chartData?.datasets ?? [])
              .flatMap((ds: any) => Array.isArray(ds.data) ? ds.data : [])
              .filter((v: any) => typeof v === "number" && isFinite(v));
            const unifiedMax = allValues.length > 0
              ? (Math.ceil((Math.max(...allValues, 0)) * 1.1) || 1)
              : undefined;
            return {
              responsive: true,
              maintainAspectRatio: false,
              animation: false as const, // compareMode/PartNo 切替時の体感もっさり対策（複数グラフのreanimate抑制）
              plugins: {
                legend: { position: "top" as const },
                datalabels: false as any,
                annotation: showBoundary ? {
                  annotations: {
                    reviewBoundary: {
                      type: "line" as const,
                      xMin: boundaryIdx - 0.5,
                      xMax: boundaryIdx - 0.5,
                      borderColor: "rgba(229, 62, 62, 0.8)",
                      borderWidth: 2,
                      borderDash: [6, 4],
                      label: {
                        display: true,
                        content: t("common.label.review"),
                        position: "start" as const,
                        backgroundColor: "rgba(229, 62, 62, 0.8)",
                        color: "white",
                        font: { size: 10 },
                      },
                    },
                  },
                } : {},
              },
              scales: {
                x: {
                  ticks: {
                    maxRotation: 45,
                    autoSkip: true,
                    maxTicksLimit: Math.min(12, labelLen),
                    font: { size: 10 },
                  },
                },
                y: {
                  beginAtZero: true,
                  ...(unifiedMax !== undefined ? { suggestedMax: unifiedMax } : {}),
                  ticks: { maxTicksLimit: 8 },
                },
              },
            };
          };
          // C-2-5'-1: compareMode は 3画面共通の state で切替（Sticky配置の ButtonGroup で操作）
          const modeKey: "previousMode" | "originalPlanMode" = selectedReviewCompareMode === "previous" ? "previousMode" : "originalPlanMode";
          const isPrevMode = selectedReviewCompareMode === "previous";
          const headerColor = isPrevMode ? "blue.600" : "green.600";
          const headerLabel = isPrevMode
            ? t(`${RD_REVIEW_PREFIX}.compareModePrev`)
            : t(`${RD_REVIEW_PREFIX}.compareModePlan`);
          return (
            <Box mb={4} id="section-review" className="pdf-content">
              <Box p={4} bg="white" boxShadow="md" borderRadius="md">
                <Heading as="h2" size="md" mb={4}>
                  {t(`${RD_REVIEW_PREFIX}.heading`, { date: reviewDate ? formatDateByLocale(reviewDate, false, true, "-") : "" })}
                </Heading>

                {/* compareMode 切替（Sticky配置・3画面共通）
                    申請書ヘッダー（通貨セレクタ+ナビゲーションボタン群、行5705）が position: sticky; top: -2rem; zIndex: 1000 で
                    画面上端に常時固定されているため、その高さぶん下にずらして配置する */}
                {!isSimpleMode && (() => {
                  return (
                    <Box
                      position="sticky"
                      top="5rem"
                      zIndex={999}
                      bg="white"
                      py={2}
                      mb={4}
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <ButtonGroup size="sm" isAttached>
                        <Button
                          colorScheme={isPrevMode ? "blue" : "gray"}
                          variant={isPrevMode ? "solid" : "outline"}
                          onClick={() => setSelectedReviewCompareMode("previous")}
                        >
                          {t(`${RD_REVIEW_PREFIX}.compareModePrev`)}
                        </Button>
                        <Button
                          colorScheme={!isPrevMode ? "green" : "gray"}
                          variant={!isPrevMode ? "solid" : "outline"}
                          onClick={() => setSelectedReviewCompareMode("originalPlan")}
                        >
                          {t(`${RD_REVIEW_PREFIX}.compareModePlan`)}
                        </Button>
                      </ButtonGroup>
                    </Box>
                  );
                })()}

                {/* ===== ReviewSales ===== */}
                {displayReviewSalesChartData && (() => {
                  const modeData = displayReviewSalesChartData[modeKey];
                  const hasSections = !!modeData?.sections?.length;
                  const hasPriceByPN = !!modeData?.pricePartNoSections?.length;
                  const hasQtyByPN = !!modeData?.qtyPartNoSections?.length;
                  if (!modeData || (!hasSections && !hasPriceByPN && !hasQtyByPN)) return null;
                  const salesBoundaryIdx: number = displayReviewSalesChartData.boundary ?? 0;

                  // 共通：1セクション分の Box（タイトル + Bar + サマリーカード3枚）を生成
                  /**
                   * 営業視点の1セクション分（タイトル + Bar グラフ + サマリーカード3枚）を描画する
                   * @param key React key 用の一意な文字列
                   * @param title セクションタイトル
                   * @param unit 単位文字列
                   * @param chartData Chart.js データオブジェクト
                   * @param sv サマリー値オブジェクト（actualVsPlan / finalOutlook / forecastAccuracy）
                   */
                  const renderSalesSectionBox = (key: string, title: string, unit: string, chartData: any, sv: any, summaryType: string) => {
                    const labelLen: number = chartData?.labels?.length ?? 0;
                    const opts = buildSalesChartOptions(salesBoundaryIdx, labelLen, chartData);

                    return (
                      <Box key={key} mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                        <Box mb={3}>
                          <Text fontSize="md" fontWeight="bold">{title}</Text>
                          <Text fontSize="xs" color="gray.500">{t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{unit}</Text>
                        </Box>
                        {chartData && (
                          <Box h="280px" mb={3}>
                            <Bar data={chartData} options={opts} />
                          </Box>
                        )}
                        <SimpleGrid columns={3} spacing={3} mt={3}>
                          {[
                            {
                              // 実績ベースの計画比
                              label: t(`features.InvestmentScreening.Review.common.summaryActualVsPlan`,
                                {
                                  from: displayReviewSalesChartData.monthLabels[0],
                                  to: displayReviewSalesChartData.monthLabels[Math.min(displayReviewSalesChartData.boundary, displayReviewSalesChartData.monthLabels.length) - 1]
                                }),
                              planLabel: t(`common.label.plan`),
                              planValue: fmtVal(sv?.actualVsPlan?.planValue, unit !== "pcs", summaryType),
                              actualLabel: t(`common.label.actuals`),
                              actualValue: fmtVal(sv?.actualVsPlan?.actualValue, unit !== "pcs", summaryType),
                              rate: sv?.actualVsPlan?.rate,
                            },
                            {
                              // 軌道修正後の計画比
                              label: t("features.InvestmentScreening.Review.common.summaryAdjustedPlan",
                                {
                                  from: displayReviewSalesChartData.monthLabels[0],
                                  to: displayReviewSalesChartData.monthLabels[displayReviewSalesChartData.monthLabels.length - 1]
                                }),
                              planLabel: t(`common.label.plan`),
                              planValue: fmtVal(sv?.finalOutlook.planTotal, unit !== "pcs", summaryType),
                              actualLabel: t(`common.label.outlook`),
                              actualValue: fmtVal(sv?.finalOutlook.outlookTotal, unit !== "pcs", summaryType),
                              rate: sv?.finalOutlook?.rate,
                            },
                            {
                              // 前回予測の精度
                              label: t("features.InvestmentScreening.Review.common.summaryForecastAccuracy",
                                {
                                  from: displayReviewSalesChartData.monthLabels[Math.min(displayReviewSalesChartData.prevBoundary, displayReviewSalesChartData.monthLabels.length - 1)],
                                  to: displayReviewSalesChartData.monthLabels[Math.min(displayReviewSalesChartData.boundary, displayReviewSalesChartData.monthLabels.length) - 1]
                                }),
                              planLabel: t(`common.label.previousEstimate`),
                              planValue: fmtVal(sv?.forecastAccuracy.prevEstimate, unit !== "pcs", summaryType),
                              actualLabel: t(`common.label.actuals`),
                              actualValue: fmtVal(sv?.forecastAccuracy.currActual, unit !== "pcs", summaryType),
                              rate: sv?.forecastAccuracy?.rate,
                            },
                          ].map((card, idx) => {
                            const r = typeof card.rate === "number" ? card.rate : null;
                            const color = r === null ? "gray.500" : r < -10 ? "red.600" : r < 0 ? "orange.500" : "green.600";
                            return (
                              <Box key={idx} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                                <Text fontSize="xs" color="gray.500" mb={1}>{card.label}</Text>
                                <HStack>
                                  <Text fontSize="xs" color="gray.500">{card.planLabel} {card.planValue}</Text>
                                  <Text fontSize="xs" color="gray.500"> {t(`common.label.arrowTo`)} </Text>
                                  <Text fontSize="xs" color="black" fontWeight="bold">{card.actualLabel} {card.actualValue}</Text>
                                </HStack>
                                <Text fontSize="lg" fontWeight="bold" color={color}>
                                  {r === null ? "-" : `${r >= 0 ? "+" : ""}${r.toFixed(1)}%`}
                                </Text>
                              </Box>
                            );
                          })}
                        </SimpleGrid>
                      </Box>
                    );
                  };

                  return (
                    <Box ref={reviewSalesRef} mb={8}>
                      <Heading as="h3" size="sm" mb={4} pb={2} borderBottom="2px solid" borderColor="gray.300">
                        {t(`${RD_REVIEW_PREFIX}.salesSection`)}
                      </Heading>

                      {!isSimpleMode && (() => {
                        return (
                          <Box mb={3} p={2} bg={headerColor} color="white" borderRadius="md">
                            <Text fontSize="sm" fontWeight="bold">{headerLabel}</Text>
                          </Box>
                        );
                      })()}

                      {/* 売上計画の対比（全体集計） */}
                      {!isSimpleMode && hasSections && modeData.sections.map((sec: any) => (
                        renderSalesSectionBox(sec.id, t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesAmount`), sec.unit, sec.chartData ?? null, sec.summaryValues[selectedCurrency.name], sec.summaryType)
                      ))}

                      {/* 販売単価（PartNo別・PartNo2件以上のときセレクタで切替） */}
                      {!isSimpleMode && hasPriceByPN && (() => {
                        const pricePartNos: string[] = modeData.pricePartNoSections.map((s: any) => s.partNo);
                        // 選択中の PartNo が存在しない場合（初回ロード前など）は先頭を使う
                        const currentPriceKey = pricePartNos.includes(selectedReviewPriceByPartNo)
                          ? selectedReviewPriceByPartNo
                          : pricePartNos[0];
                        const selectedSec = modeData.pricePartNoSections.find((s: any) => s.partNo === currentPriceKey);
                        if (!selectedSec) return null;
                        return (
                          <Box mb={6}>
                            <HStack mb={2} justify="flex-end">
                              <Text fontSize="sm">{t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesPrice`)}</Text>
                              {pricePartNos.length > 1 && (
                                <Select size="sm" w="200px" value={currentPriceKey} onChange={(e) => setSelectedReviewPriceByPartNo(e.target.value)}>
                                  {pricePartNos.map((pn) => <option key={pn} value={pn}>{resolveDisplayPartNoByKey(pn, partNoData)}</option>)}
                                </Select>
                              )}
                            </HStack>
                            {renderSalesSectionBox(
                              `price-${selectedSec.partNo}`,
                              t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesPrice`), partNo: resolveDisplayPartNoByKey(selectedSec.partNo, partNoData) }),
                              selectedSec.unit,
                              selectedSec.chartData ?? null,
                              selectedSec.summaryValues[selectedCurrency.name],
                              selectedSec.summaryType
                            )}
                          </Box>
                        );
                      })()}

                      {/* 販売数量（PartNo別・PartNo2件以上のときセレクタで切替・1件のとき先頭PartNo固定表示） */}
                      {!isSimpleMode && hasQtyByPN && (() => {
                        const qtyPartNos: string[] = modeData.qtyPartNoSections.map((s: any) => s.partNo);
                        // PartNo が1件のときは先頭 PartNo に固定（"all" のまま「全体」タイトルにならないよう）
                        const currentQtyKey = qtyPartNos.length === 1
                          ? qtyPartNos[0]
                          : selectedReviewQtyByPartNo;
                        // "all": 全PartNo合算した chartData / summaryValues を動的生成（共通ヘルパー利用）
                        if (currentQtyKey === "all") {
                          const firstSec = modeData.qtyPartNoSections[0];
                          if (!firstSec?.chartData?.datasets) return null;
                          const aggChartData = aggregateChartDatasets(modeData.qtyPartNoSections, "chartData");
                          if (!aggChartData) return null;
                          // ※ summaryValues は計算結果が PartNo ごとに独立しているため、合算後の rate は単純加算では正しくない。
                          //   そのため "all" 時は rate を非表示にし、planValue / actualValue 等を合算した値だけ表示する設計とする。
                          const aggSummary = aggregateSummaryValues(modeData.qtyPartNoSections);
                          return (
                            <Box mb={6}>
                              <HStack mb={2} justify="flex-end">
                                <Text fontSize="sm">{t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesQuantity`)}</Text>
                                {qtyPartNos.length > 1 && (
                                  <Select size="sm" w="200px" value={currentQtyKey} onChange={(e) => setSelectedReviewQtyByPartNo(e.target.value)}>
                                    <option value="all">{t("common.label.all")}</option>
                                    {qtyPartNos.map((pn) => <option key={pn} value={pn}>{resolveDisplayPartNoByKey(pn, partNoData)}</option>)}
                                  </Select>
                                )}
                              </HStack>
                              {renderSalesSectionBox(
                                "qty-all",
                                t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesQuantity`), partNo: t("common.label.all") }),
                                firstSec.unit,
                                aggChartData,
                                aggSummary,
                                firstSec.summaryType
                              )}
                            </Box>
                          );
                        }
                        // 個別 PartNo
                        const selectedSec = modeData.qtyPartNoSections.find((s: any) => s.partNo === currentQtyKey);
                        if (!selectedSec) return null;
                        return (
                          <Box mb={6}>
                            <HStack mb={2} justify="flex-end">
                              <Text fontSize="sm">{t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesQuantity`)}</Text>
                              {qtyPartNos.length > 1 && (
                                <Select size="sm" w="200px" value={currentQtyKey} onChange={(e) => setSelectedReviewQtyByPartNo(e.target.value)}>
                                  <option value="all">{t("common.label.all")}</option>
                                  {qtyPartNos.map((pn) => <option key={pn} value={pn}>{resolveDisplayPartNoByKey(pn, partNoData)}</option>)}
                                </Select>
                              )}
                            </HStack>
                            {renderSalesSectionBox(
                              `qty-${selectedSec.partNo}`,
                              t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesQuantity`), partNo: resolveDisplayPartNoByKey(selectedSec.partNo, partNoData) }),
                              selectedSec.unit,
                              selectedSec.chartData ?? null,
                              selectedSec.summaryValues,
                              selectedSec.summaryType
                            )}
                          </Box>
                        );
                      })()}

                      {/* テキスト・コメント（compareMode 非依存・1度だけ表示） */}
                      {reviewSalesText && (() => {
                        const SALES_SCHEDULE_KEY = 'sales_schedule';
                        const textValues = reviewSalesText.textValues || {};
                        const supplementaryValues = reviewSalesText.supplementaryValues || {};
                        const comments = reviewSalesText.comments || {};

                        const scheduleEntry = textValues[SALES_SCHEDULE_KEY];
                        const otherTextEntries = Object.entries(textValues).filter(([k, v]) => k !== SALES_SCHEDULE_KEY && v);
                        const supplementaryEntries = Object.entries(supplementaryValues).filter(([, v]) => v);
                        const commentEntries = Object.entries(comments).filter(([, v]) => v);

                        const hasAny = !!scheduleEntry || otherTextEntries.length > 0 || supplementaryEntries.length > 0 || commentEntries.length > 0;
                        if (!hasAny) return null;

                        // 見出し+ラベル+内容の階層を視覚的に区別する共通ブロック
                        const renderItem = (key: string, label: string, value: string) => (
                          <Box key={key} mb={3} pl={4}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{label}</Text>
                            <Box p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                              <Text fontSize="sm" whiteSpace="pre-wrap">{value}</Text>
                            </Box>
                          </Box>
                        );

                        return (
                          <Box p={4} bg="gray.50" borderRadius="md">
                            {/* ① シェア・コメント */}
                            {commentEntries.length > 0 && (
                              <Box mb={4}>
                                <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.comments`)}</Text>
                                {commentEntries.map(([k, v]) => renderItem(k, resolveSalesLabel('salesCommentLabel', k), String(v)))}
                              </Box>
                            )}

                            {/* ② 販売期間（スケジュール対比）の所感 */}
                            {scheduleEntry && (
                              <Box mb={4}>
                                <Text fontSize="md" fontWeight="bold" mb={2}>{resolveSalesLabel('salesTextLabel', SALES_SCHEDULE_KEY)}</Text>
                                <Box pl={4}>
                                  <Box p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                                    <Text fontSize="sm" whiteSpace="pre-wrap">{String(scheduleEntry)}</Text>
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            {/* ③ テキスト入力項目（sales_schedule 以外） */}
                            {otherTextEntries.length > 0 && (
                              <Box mb={4}>
                                <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.impression`)}</Text>
                                {otherTextEntries.map(([k, v]) => renderItem(k, resolveSalesLabel('salesTextLabel', k), String(v)))}
                              </Box>
                            )}

                            {/* ④ 補足視点 */}
                            {supplementaryEntries.length > 0 && (
                              <Box>
                                <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.supplementary`)}</Text>
                                {supplementaryEntries.map(([k, v]) => renderItem(k, resolveSalesLabel('salesSupplementaryLabel', k), String(v)))}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                })()}

                {/* ===== ReviewBusiness ===== */}
                {displayReviewBusinessChartData && (() => {
                  const RD_BIZ = `${RD_REVIEW_PREFIX}`;
                  /**
                   * 事業視点テキストのラベルを翻訳する（キーが未翻訳の場合はキー自体を返す）
                   * @param group テキスト種別グループ名（businessTextLabel / businessCommentLabel）
                   * @param key テキストキー
                   * @returns 翻訳済みラベル文字列
                   */
                  const resolveBizLabel = (group: 'businessTextLabel' | 'businessCommentLabel', key: string) => {
                    const fullKey = `${RD_BIZ}.${group}.${key}`;
                    const translated = t(fullKey);
                    return translated === fullKey ? key : translated;
                  };
                  /**
                   * テーブル値を表示用文字列に整形する（null/undefined は "-"、回収不能は翻訳テキスト）
                   * @param v 表示する値
                   * @param suffix 単位を表すサフィックス文字列
                   * @returns 整形済みの表示文字列
                   */
                  const formatValue = (v: any, suffix: string = "") => {
                    if (v === null || v === undefined) return "-";
                    if (typeof v === "number") return `${v.toLocaleString()}${suffix}`;
                    if (typeof v === "string" && v === "回収不能") return t(`${RD_BIZ}.unit.unrecoverable`);
                    return String(v);
                  };
                  const tables = displayReviewBusinessChartData.tables || {};
                  const compareModeLabel = (modeKey: "previousMode" | "originalPlanMode") =>
                    modeKey === "previousMode" ? t(`${RD_BIZ}.compareModePrev`) : t(`${RD_BIZ}.compareModePlan`);
                  // 振り返り画面と同じグラフ種別で描画するための分岐
                  //   営業利益 (operatingProfit) / 生産数量 (lifecycle + 累積) → Line
                  //   製造原価 (manufacturingCost) / 設備稼働率 (operatingRate) → Bar
                  const boundaryIdx: number = displayReviewBusinessChartData.boundary ?? 0;
                  // 事業視点の各グラフ用 options（振り返り境界 annotation + X軸 ticks 自動間引き + Y軸 suggestedMax）
                  //   実画面 ReviewBusiness.createChartOptions の仕様を踏襲。営業利益(Line)/製造原価(Bar)/生産数量(Line)/設備稼働率(Bar) 共通。
                  //   営業利益のみ tooltip mode を "index" に切り替えるため interaction を外から追加可能。
                  /**
                   * 事業視点グラフ用の Chart.js options を生成する
                   * 営業利益(Line) / 製造原価(Bar) / 生産数量(Line) / 設備稼働率(Bar) の全種で共用する。
                   * @param chartData Chart.js データオブジェクト（Y軸 max 算出に使用）
                   * @param opts 追加オプション（lineInteraction: true で tooltip を index モードに切替）
                   * @returns Chart.js options オブジェクト
                   */
                  const buildBusinessChartOptions = (chartData: any, opts?: { lineInteraction?: boolean }) => {
                    const labelLen: number = chartData?.labels?.length ?? 0;
                    const showBoundary = boundaryIdx > 0 && boundaryIdx < labelLen;
                    const allValues: number[] = (chartData?.datasets ?? [])
                      .flatMap((ds: any) => Array.isArray(ds.data) ? ds.data : [])
                      .filter((v: any) => typeof v === "number" && isFinite(v));
                    const unifiedMax = allValues.length > 0
                      ? (Math.ceil((Math.max(...allValues, 0)) * 1.1) || 1)
                      : undefined;
                    return {
                      responsive: true,
                      maintainAspectRatio: false,
                      animation: false as const, // compareMode/PartNo 切替時の体感もっさり対策（複数グラフのreanimate抑制）
                      ...(opts?.lineInteraction ? { interaction: { mode: "index" as const, intersect: false } } : {}),
                      plugins: {
                        legend: { position: "top" as const },
                        datalabels: false as any,
                        annotation: showBoundary ? {
                          annotations: {
                            reviewBoundary: {
                              type: "line" as const,
                              xMin: boundaryIdx - 0.5,
                              xMax: boundaryIdx - 0.5,
                              borderColor: "rgba(229, 62, 62, 0.8)",
                              borderWidth: 2,
                              borderDash: [6, 4],
                              label: {
                                display: true,
                                content: t("common.label.review"),
                                position: "start" as const,
                                backgroundColor: "rgba(229, 62, 62, 0.8)",
                                color: "white",
                                font: { size: 10 },
                              },
                            },
                          },
                        } : {},
                      },
                      scales: {
                        x: {
                          ticks: {
                            maxRotation: 45,
                            autoSkip: true,
                            maxTicksLimit: Math.min(12, labelLen),
                            font: { size: 10 },
                          },
                        },
                        y: {
                          beginAtZero: true,
                          ...(unifiedMax !== undefined ? { suggestedMax: unifiedMax } : {}),
                          ticks: { maxTicksLimit: 8 },
                        },
                      },
                    };
                  };
                  /**
                   * グラフ種別に応じて Line または Bar コンポーネントを描画する
                   * @param chartType グラフ種別（"line" / "bar"）
                   * @param chartData Chart.js データオブジェクト
                   * @param options Chart.js options（省略時は buildBusinessChartOptions で生成）
                   * @param plugins Chart.js プラグイン配列（塗りつぶしプラグイン等）
                   */
                  const renderChart = (chartType: "line" | "bar", chartData: any, options?: any, plugins?: any[]) => (
                    chartType === "line"
                      ? <Line data={chartData} options={options ?? buildBusinessChartOptions(chartData, { lineInteraction: true })} plugins={plugins as any} />
                      : <Bar data={chartData} options={options ?? buildBusinessChartOptions(chartData)} plugins={plugins as any} />
                  );
                  // 営業利益のタイトルを「営業利益の対比」表記へ補正（実画面と揃える）
                  /**
                   * セクション ID に応じてタイトルを補正して返す
                   * @param id セクション ID（"operatingProfit" の場合は専用タイトルを返す）
                   * @param fallback デフォルトのタイトル文字列
                   * @returns 表示用タイトル文字列
                   */
                  const sectionDisplayTitle = (id: string, fallback: string) => {
                    switch (id) {
                      case "operatingProfit":
                        return t("features.InvestmentScreening.Review.ReviewBusiness.section.operatingProfitComparison");
                      case "manufacturingCost":// 製造原価
                        return t("features.InvestmentScreening.Review.ReviewBusiness.section.manufacturingCost");
                      default:
                        return fallback;
                    }
                  }

                  return (
                    <Box ref={reviewBusinessRef} mb={8}>
                      <Heading as="h3" size="sm" mb={4} pb={2} borderBottom="2px solid" borderColor="gray.300">
                        {t(`${RD_BIZ}.businessSection`)}
                      </Heading>

                      {/* compareMode ヘッダー（営業視点と同じ青/緑バー） */}
                      {!isSimpleMode && (() => {
                        return (
                          <Box mb={3} p={2} bg={headerColor} color="white" borderRadius="md">
                            <Text fontSize="sm" fontWeight="bold">{headerLabel}</Text>
                          </Box>
                        );
                      })()}

                      {/* ① 通貨依存セクション（営業利益・製造原価） — 選択された compareMode のみ表示 */}
                      {(() => {
                        const modeData = displayReviewBusinessChartData[modeKey];
                        // const fmt = (v: number) => v?.toLocaleString();
                        if (!modeData?.sections?.length) return null;
                        return (
                          <Box mb={6}>
                            {modeData.sections.map((sec: any) => {
                              const sv = sec.summaryValues[selectedCurrency.name] || {};
                              const chartType: "line" | "bar" = sec.id === "operatingProfit" ? "line" : "bar";
                              const displayTitle = sectionDisplayTitle(sec.id, sec.title);
                              const sectionChartOptions = chartType === "line"
                                ? buildBusinessChartOptions(sec.chartData, { lineInteraction: true })
                                : buildBusinessChartOptions(sec.chartData);
                              return (
                                <Box key={sec.id} mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                                  <Box mb={3}>
                                    <Text fontSize="md" fontWeight="bold">{displayTitle}</Text>
                                    <Text fontSize="xs" color="gray.500">{t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{sec.unit}</Text>
                                  </Box>
                                  {sec.chartData && (
                                    <Box h="280px" mb={3}>
                                      {renderChart(chartType, sec.chartData, sectionChartOptions)}
                                    </Box>
                                  )}
                                  <SimpleGrid columns={3} spacing={3} mt={3}>
                                    {[
                                      {
                                        // 実績ベースの計画比
                                        label: t(`features.InvestmentScreening.Review.common.summaryActualVsPlan`,
                                          {
                                            from: displayReviewBusinessChartData.monthLabels[0],
                                            to: displayReviewBusinessChartData.monthLabels[Math.min(displayReviewBusinessChartData.boundary, displayReviewBusinessChartData.monthLabels.length) - 1]
                                          }),
                                        rate: sv.actualVsPlan?.rate,
                                        planLabel: t(`common.label.previousEstimate`),
                                        planValue: fmtVal(sv.actualVsPlan?.planValue, sec.unit !== "pcs", sec.summaryType),
                                        actualLabel: t(`common.label.actuals`),
                                        actualValue: fmtVal(sv.actualVsPlan?.actualValue, sec.unit !== "pcs", sec.summaryType),
                                      },
                                      {
                                        // 軌道修正後の計画比
                                        label: t("features.InvestmentScreening.Review.common.summaryAdjustedPlan",
                                          {
                                            from: displayReviewBusinessChartData.monthLabels[0],
                                            to: displayReviewBusinessChartData.monthLabels[displayReviewBusinessChartData.monthLabels.length - 1]
                                          }),
                                        rate: sv.finalOutlook?.rate,
                                        planLabel: t(`common.label.previousEstimate`),
                                        planValue: fmtVal(sv.finalOutlook?.planTotal, sec.unit !== "pcs", sec.summaryType),
                                        actualLabel: t(`common.label.outlook`),
                                        actualValue: fmtVal(sv.finalOutlook?.outlookTotal, sec.unit !== "pcs", sec.summaryType),
                                      },
                                      {
                                        // 前回予測の精度
                                        label: t("features.InvestmentScreening.Review.common.summaryForecastAccuracy",
                                          {
                                            from: displayReviewBusinessChartData.monthLabels[Math.min(displayReviewBusinessChartData.prevBoundary, displayReviewBusinessChartData.monthLabels.length - 1)],
                                            to: displayReviewBusinessChartData.monthLabels[Math.min(displayReviewBusinessChartData.boundary, displayReviewBusinessChartData.monthLabels.length) - 1]
                                          }),
                                        rate: sv.forecastAccuracy?.rate,
                                        planLabel: t(`common.label.previousEstimate`),
                                        planValue: fmtVal(sv.forecastAccuracy?.prevEstimate, sec.unit !== "pcs", sec.summaryType),
                                        actualLabel: t(`common.label.actuals`),
                                        actualValue: fmtVal(sv.forecastAccuracy?.currActual, sec.unit !== "pcs", sec.summaryType),
                                      },
                                    ].map((card, idx) => {
                                      const r = typeof card.rate === "number" ? card.rate : null;
                                      const color = r === null ? "gray.500" : r < -10 ? "red.600" : r < 0 ? "orange.500" : "green.600";
                                      return (
                                        <Box key={idx} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                                          <Text fontSize="xs" color="gray.500" mb={1}>{card.label}</Text>
                                          <HStack>
                                            <Text fontSize="xs" color="gray.500">{card.planLabel} {card.planValue}</Text>
                                            <Text fontSize="xs" color="gray.500"> {t(`common.label.arrowTo`)} </Text>
                                            <Text fontSize="xs" color="black" fontWeight="bold">{card.actualLabel} {card.actualValue}</Text>
                                          </HStack>
                                          <Text fontSize="lg" fontWeight="bold" color={color}>
                                            {r === null ? "-" : `${r >= 0 ? "+" : ""}${r.toFixed(1)}%`}
                                          </Text>
                                        </Box>
                                      );
                                    })}
                                  </SimpleGrid>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })()}

                      {/* ② 通貨非依存グラフ（生産数量＝PartNo別・設備稼働率＝全体集計） — 選択された compareMode のみ表示 */}
                      {displayReviewBusinessChartData.nonCurrencyCharts && (() => {
                        const ncData = displayReviewBusinessChartData.nonCurrencyCharts[modeKey];
                        if (!ncData || Object.keys(ncData).length === 0) return null;
                        const pcByPN: any[] = Array.isArray(ncData.productionCapacityByPartNo) ? ncData.productionCapacityByPartNo : [];
                        const or = ncData.operatingRate;
                        if (pcByPN.length === 0 && !or) return null;
                        const prodCapPartNos: string[] = pcByPN.map((p: any) => p.partNo);
                        // 共通：生産数量1グラフ分の描画ヘルパー
                        /**
                         * 生産数量の1グラフ分（タイトル + ライフサイクル Line + 累積 Line + サマリーカード3枚）を描画する
                         * @param key React key 用の一意な文字列
                         * @param displayTitle 表示タイトル
                         * @param unit 単位文字列
                         * @param chartData ライフサイクル用 Chart.js データオブジェクト
                         * @param cumulativeChartData 累積用 Chart.js データオブジェクト
                         * @param summaryValues サマリー値オブジェクト（actualVsPlan / finalOutlook / forecastAccuracy）
                         */
                        const renderProdCapBox = (key: string, displayTitle: string, unit: string, chartData: any, cumulativeChartData: any, summaryValues: any) => (
                          <Box key={key} mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                            <Box mb={3}>
                              <Text fontSize="md" fontWeight="bold">{displayTitle}</Text>
                              <Text fontSize="xs" color="gray.500">{t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{unit}</Text>
                            </Box>
                            {chartData && (
                              <Box h="280px" mb={3}>
                                {renderChart("line", chartData, undefined, [buildLifecycleFillPlugin(boundaryIdx)])}
                              </Box>
                            )}
                            {cumulativeChartData && (
                              <>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={1}>{t(`${RD_BIZ}.nonCurrency.productionCapacityCumulative`)}</Text>
                                <Box h="280px" mb={3}>
                                  {renderChart("line", cumulativeChartData, undefined, [buildCumulativeFillPlugin(boundaryIdx)])}
                                </Box>
                              </>
                            )}
                            {summaryValues && (
                              <SimpleGrid columns={3} spacing={3} mt={3}>
                                {[
                                  {
                                    // 実績ベースの計画比
                                    label: t(`features.InvestmentScreening.Review.common.summaryActualVsPlan`,
                                      {
                                        from: displayReviewBusinessChartData.monthLabels[0],
                                        to: displayReviewBusinessChartData.monthLabels[Math.min(displayReviewBusinessChartData.boundary, displayReviewBusinessChartData.monthLabels.length) - 1]
                                      }),
                                    planLabel: t(`common.label.plan`),
                                    planValue: summaryValues.actualVsPlan?.planValue?.toLocaleString(),
                                    actualLabel: t(`common.label.actuals`),
                                    actualValue: summaryValues.actualVsPlan?.actualValue?.toLocaleString(),
                                    rate: summaryValues.actualVsPlan?.rate,
                                  },
                                  {
                                    // 軌道修正後の計画比
                                    label: t("features.InvestmentScreening.Review.common.summaryAdjustedPlan",
                                      {
                                        from: displayReviewBusinessChartData.monthLabels[0],
                                        to: displayReviewBusinessChartData.monthLabels[displayReviewBusinessChartData.monthLabels.length - 1]
                                      }),
                                    planLabel: t(`common.label.plan`),
                                    planValue: summaryValues.finalOutlook.planTotal?.toLocaleString(),
                                    actualLabel: t(`common.label.outlook`),
                                    actualValue: summaryValues.finalOutlook.outlookTotal?.toLocaleString(),
                                    rate: summaryValues.finalOutlook?.rate,
                                  },
                                  {
                                    // 前回予測の精度
                                    label: t("features.InvestmentScreening.Review.common.summaryForecastAccuracy",
                                      {
                                        from: displayReviewBusinessChartData.monthLabels[Math.min(displayReviewBusinessChartData.prevBoundary, displayReviewBusinessChartData.monthLabels.length - 1)],
                                        to: displayReviewBusinessChartData.monthLabels[Math.min(displayReviewBusinessChartData.boundary, displayReviewBusinessChartData.monthLabels.length) - 1]
                                      }),
                                    planLabel: t(`common.label.previousEstimate`),
                                    planValue: summaryValues.forecastAccuracy.prevEstimate?.toLocaleString(),
                                    actualLabel: t(`common.label.actuals`),
                                    actualValue: summaryValues.forecastAccuracy.currActual?.toLocaleString(),
                                    rate: summaryValues.forecastAccuracy?.rate
                                  },
                                ].map((card, idx) => {
                                  const r = typeof card.rate === "number" ? card.rate : null;
                                  const color = r === null ? "gray.500" : r < -10 ? "red.600" : r < 0 ? "orange.500" : "green.600";
                                  return (
                                    <Box key={idx} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                                      <Text fontSize="xs" color="gray.500" mb={1}>{card.label}</Text>
                                      <HStack>
                                        <Text fontSize="xs" color="gray.500">{card.planLabel} {card.planValue}</Text>
                                        <Text fontSize="xs" color="gray.500"> {t(`common.label.arrowTo`)} </Text>
                                        <Text fontSize="xs" color="black" fontWeight="bold">{card.actualLabel} {card.actualValue}</Text>
                                      </HStack>
                                      <Text fontSize="lg" fontWeight="bold" color={color}>
                                        {r === null ? "-" : `${r >= 0 ? "+" : ""}${r.toFixed(1)}%`}
                                      </Text>
                                    </Box>
                                  );
                                })}
                              </SimpleGrid>
                            )}
                          </Box>
                        );
                        // 生産数量セレクタの現在値（無効値の場合は "all"）
                        const currentProdCapKey = (selectedReviewProdCapByPartNo === "all" || prodCapPartNos.includes(selectedReviewProdCapByPartNo))
                          ? selectedReviewProdCapByPartNo
                          : "all";
                        const prodCapTitleAll = t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_BIZ}.nonCurrency.productionCapacity`), partNo: t("common.label.all") });
                        return (
                          <Box mb={6}>
                            {/* 生産数量（PartNo セレクタ + "all" 動的合算） */}
                            {pcByPN.length > 0 && (
                              <Box mb={6}>
                                <HStack mb={2} justify="flex-end">
                                  <Text fontSize="sm">{t(`${RD_BIZ}.nonCurrency.productionCapacity`)}</Text>
                                  <Select size="sm" w="200px" value={currentProdCapKey} onChange={(e) => setSelectedReviewProdCapByPartNo(e.target.value)}>
                                    <option value="all">{t("common.label.all")}</option>
                                    {prodCapPartNos.map((pn) => <option key={pn} value={pn}>{resolveDisplayPartNoByKey(pn, partNoData)}</option>)}
                                  </Select>
                                </HStack>
                                {currentProdCapKey === "all"
                                  ? (() => {
                                    const firstPc = pcByPN[0];
                                    if (!firstPc?.chartData?.datasets) return null;
                                    // chartData / cumulativeChartData の datasets を全 PartNo で月別合算（共通ヘルパー利用）
                                    const aggChartData = aggregateChartDatasets(pcByPN, "chartData");
                                    const aggCumulative = aggregateChartDatasets(pcByPN, "cumulativeChartData");
                                    // summaryValues 合算（C-2-5'-2 の販売数量 "all" と同じパターン・共通ヘルパー利用）
                                    const aggSummary = aggregateSummaryValues(pcByPN);
                                    return renderProdCapBox(
                                      "pc-all",
                                      prodCapTitleAll,
                                      firstPc.unit || t(`${RD_BIZ}.unit.piece`),
                                      aggChartData,
                                      aggCumulative,
                                      aggSummary,
                                    );
                                  })()
                                  : (() => {
                                    const pc = pcByPN.find((p: any) => p.partNo === currentProdCapKey);
                                    if (!pc) return null;
                                    return renderProdCapBox(
                                      `pc-${pc.partNo}`,
                                      t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_BIZ}.nonCurrency.productionCapacity`), partNo: resolveDisplayPartNoByKey(pc.partNo, partNoData) }),
                                      pc.unit || t(`${RD_BIZ}.unit.piece`),
                                      pc.chartData,
                                      pc.cumulativeChartData,
                                      pc.summaryValues,
                                    );
                                  })()}
                              </Box>
                            )}
                            {/* 設備稼働率（実画面と同じく Bar・全体集計） */}
                            {or && (
                              <Box mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                                <Box mb={3}>
                                  <Text fontSize="md" fontWeight="bold">{t(`${RD_BIZ}.nonCurrency.operatingRate`)}</Text>
                                  <Text fontSize="xs" color="gray.500">{t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{or.unit || t(`${RD_BIZ}.unit.percent`)}</Text>
                                </Box>
                                {or.chartData && (
                                  <Box h="280px" mb={3}>
                                    {renderChart("bar", or.chartData)}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}

                      {/* ③ テーブル系（生データ3値方式・compareMode 非依存）
                            ReviewBusiness画面準拠の章構成：
                              (1) 単一値比較項目（回収期間・人員数）
                              (2) 歩留・サイクルタイム（期間平均）— PartNoセレクタで「全体（多数モデル=max/min/avg／単一モデル=単値）」または「個別PartNo（境界値1行）」を切替 */}
                      {tables && (() => {
                        const yieldByPN = (tables.yieldByPartNo ?? {}) as Record<string, any>;
                        const ctByPN = (tables.cycleTimeByPartNo ?? {}) as Record<string, any>;
                        const yieldCtPartNos = Array.from(new Set([
                          ...Object.keys(yieldByPN),
                          ...Object.keys(ctByPN),
                        ]));
                        const hasSingleValueData = !!tables.payback || !!tables.headcount;
                        const hasYieldCtData = yieldCtPartNos.length > 0 || tables.yield || tables.cycleTime || tables.yieldStats || tables.cycleTimeStats;
                        // PartNo が1件のときは先頭 PartNo に固定（セレクタ非表示と連動して個別PartNo表示にする）
                        const currentYieldCtKey = yieldCtPartNos.length === 1
                          ? yieldCtPartNos[0]
                          : (selectedReviewYieldCtByPartNo === "all" || yieldCtPartNos.includes(selectedReviewYieldCtByPartNo))
                            ? selectedReviewYieldCtByPartNo
                            : "all";
                        const isYieldCtAll = currentYieldCtKey === "all";
                        const RB_PREFIX = "features.InvestmentScreening.Review.ReviewBusiness";
                        return (
                          <>
                            {/* (1) 単一値比較項目（回収期間・人員数） */}
                            {hasSingleValueData && (
                              <Box mb={6} p={4} bg="gray.50" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold" mb={3}>
                                  {t(`${RB_PREFIX}.section.singleValueItems`)}
                                </Text>
                                <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table">
                                  <colgroup>
                                    <col style={{ width: "40%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "20%" }} />
                                  </colgroup>
                                  <Thead>
                                    <Tr>
                                      <Th>{t(`${RD_BIZ}.tableColumns.item`)}</Th>
                                      <Th>{t(`${RD_BIZ}.tableColumns.plan`)}</Th>
                                      <Th>{t(`${RD_BIZ}.tableColumns.prev`)}</Th>
                                      <Th>{t(`${RD_BIZ}.tableColumns.review`)}</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {tables.payback && (
                                      <Tr>
                                        <Td>{t(`${RB_PREFIX}.item.paybackPeriod`)}</Td>
                                        <Td>{formatValue(tables.payback.plan, t(`${RD_BIZ}.unit.month`))}</Td>
                                        <Td>{formatValue(tables.payback.prev, t(`${RD_BIZ}.unit.month`))}</Td>
                                        <Td>{formatValue(tables.payback.review, t(`${RD_BIZ}.unit.month`))}</Td>
                                      </Tr>
                                    )}
                                    {tables.headcount && (
                                      <Tr>
                                        <Td>{t(`${RB_PREFIX}.item.headcount`)}</Td>
                                        <Td>{formatValue(tables.headcount.plan, t(`${RD_BIZ}.unit.person`))}</Td>
                                        <Td>{formatValue(tables.headcount.prev, t(`${RD_BIZ}.unit.person`))}</Td>
                                        <Td>{formatValue(tables.headcount.review, t(`${RD_BIZ}.unit.person`))}</Td>
                                      </Tr>
                                    )}
                                  </Tbody>
                                </Table>
                              </Box>
                            )}

                            {/* (2) 歩留・サイクルタイム（期間平均） — PartNoセレクタで「全体」/「個別PartNo」を切替 */}
                            {hasYieldCtData && (
                              <Box mb={6} p={4} bg="gray.50" borderRadius="md">
                                <HStack mb={3} justify="space-between">
                                  <Text fontSize="md" fontWeight="bold">{t(`${RB_PREFIX}.yieldCycleTimeFiltered`)}</Text>
                                  {yieldCtPartNos.length > 1 && (
                                    <HStack spacing={2}>
                                      <Text fontSize="xs" color="gray.500">{t("common.label.partNo")}</Text>
                                      <Select size="sm" w="220px" value={currentYieldCtKey} onChange={(e) => setSelectedReviewYieldCtByPartNo(e.target.value)}>
                                        <option value="all">{t(`${RB_PREFIX}.option.overall`)}</option>
                                        {yieldCtPartNos.map((pn) => <option key={pn} value={pn}>{resolveDisplayPartNoByKey(pn, partNoData)}</option>)}
                                      </Select>
                                    </HStack>
                                  )}
                                </HStack>
                                {isYieldCtAll ? (
                                  // 全体表示
                                  (() => {
                                    const hasStats = !!tables.yieldStats || !!tables.cycleTimeStats;
                                    if (hasStats) {
                                      // 多数モデル: max/min/avg 統計値テーブル
                                      return (
                                        <>
                                          {(["yieldStats", "cycleTimeStats"] as const).map((statKey) => {
                                            const stats = tables[statKey];
                                            if (!stats) return null;
                                            const unitKey = statKey === "yieldStats" ? "percent" : "sec";
                                            const unit = t(`${RD_BIZ}.unit.${unitKey}`);
                                            const title = statKey === "yieldStats"
                                              ? t(`${RB_PREFIX}.item.yield`)
                                              : t(`${RB_PREFIX}.item.cycleTime`);
                                            return (
                                              <Box key={statKey} mb={4}>
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{title}</Text>
                                                <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table">
                                                  <colgroup>
                                                    <col style={{ width: "40%" }} />
                                                    <col style={{ width: "20%" }} />
                                                    <col style={{ width: "20%" }} />
                                                    <col style={{ width: "20%" }} />
                                                  </colgroup>
                                                  <Thead>
                                                    <Tr>
                                                      <Th>{t(`${RD_BIZ}.tableColumns.item`)}</Th>
                                                      <Th>{t(`${RD_BIZ}.tableColumns.max`)}</Th>
                                                      <Th>{t(`${RD_BIZ}.tableColumns.min`)}</Th>
                                                      <Th>{t(`${RD_BIZ}.tableColumns.avg`)}</Th>
                                                    </Tr>
                                                  </Thead>
                                                  <Tbody>
                                                    {(["plan", "prev", "review"] as const).map((dataKey) => {
                                                      const row: any = stats[dataKey];
                                                      if (!row) return null;
                                                      return (
                                                        <Tr key={dataKey}>
                                                          <Td>{t(`${RD_BIZ}.tableColumns.${dataKey}`)}</Td>
                                                          <Td>{formatValue(row.max, unit)}</Td>
                                                          <Td>{formatValue(row.min, unit)}</Td>
                                                          <Td>{formatValue(row.avg, unit)}</Td>
                                                        </Tr>
                                                      );
                                                    })}
                                                  </Tbody>
                                                </Table>
                                              </Box>
                                            );
                                          })}
                                        </>
                                      );
                                    }
                                    // 単一モデル: 単値1行表示
                                    // PartNo が1件の場合は yieldByPartNo の値（BOM期間平均）を優先して表示
                                    // ReviewBusiness 画面の分岐1（PartNo個別表示）と同じデータを参照
                                    const singlePn = yieldCtPartNos.length === 1 ? yieldCtPartNos[0] : null;
                                    const singleYield = singlePn ? yieldByPN[singlePn] : null;
                                    const singleCt = singlePn ? ctByPN[singlePn] : null;
                                    return (
                                      <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table">
                                        <colgroup>
                                          <col style={{ width: "40%" }} />
                                          <col style={{ width: "20%" }} />
                                          <col style={{ width: "20%" }} />
                                          <col style={{ width: "20%" }} />
                                        </colgroup>
                                        <Thead>
                                          <Tr>
                                            <Th>{t(`${RD_BIZ}.tableColumns.item`)}</Th>
                                            <Th>{t(`${RD_BIZ}.tableColumns.plan`)}</Th>
                                            <Th>{t(`${RD_BIZ}.tableColumns.prev`)}</Th>
                                            <Th>{t(`${RD_BIZ}.tableColumns.review`)}</Th>
                                          </Tr>
                                        </Thead>
                                        <Tbody>
                                          {(singleYield || tables.yield) && (
                                            <Tr>
                                              <Td>{t(`${RB_PREFIX}.item.yield`)}</Td>
                                              <Td>{formatValue(singleYield ? singleYield.plan : tables.yield?.plan, t(`${RD_BIZ}.unit.percent`))}</Td>
                                              <Td>{formatValue(singleYield ? singleYield.prev : tables.yield?.prev, t(`${RD_BIZ}.unit.percent`))}</Td>
                                              <Td>{formatValue(singleYield ? singleYield.current : tables.yield?.review, t(`${RD_BIZ}.unit.percent`))}</Td>
                                            </Tr>
                                          )}
                                          {(singleCt || tables.cycleTime) && (
                                            <Tr>
                                              <Td>{t(`${RB_PREFIX}.item.cycleTime`)}</Td>
                                              <Td>{formatValue(singleCt ? singleCt.plan : tables.cycleTime?.plan, t(`${RD_BIZ}.unit.sec`))}</Td>
                                              <Td>{formatValue(singleCt ? singleCt.prev : tables.cycleTime?.prev, t(`${RD_BIZ}.unit.sec`))}</Td>
                                              <Td>{formatValue(singleCt ? singleCt.current : tables.cycleTime?.review, t(`${RD_BIZ}.unit.sec`))}</Td>
                                            </Tr>
                                          )}
                                        </Tbody>
                                      </Table>
                                    );
                                  })()
                                ) : (
                                  // 個別PartNo選択時: 境界値1行のみ
                                  <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table">
                                    <colgroup>
                                      <col style={{ width: "40%" }} />
                                      <col style={{ width: "20%" }} />
                                      <col style={{ width: "20%" }} />
                                      <col style={{ width: "20%" }} />
                                    </colgroup>
                                    <Thead>
                                      <Tr>
                                        <Th>{t(`${RD_BIZ}.tableColumns.item`)}</Th>
                                        <Th>{t(`${RD_BIZ}.tableColumns.plan`)}</Th>
                                        <Th>{t(`${RD_BIZ}.tableColumns.prev`)}</Th>
                                        <Th>{t(`${RD_BIZ}.tableColumns.review`)}</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {yieldByPN[currentYieldCtKey] && (
                                        <Tr>
                                          <Td>{t(`${RB_PREFIX}.item.yield`)}（{currentYieldCtKey}）</Td>
                                          <Td>{formatValue(yieldByPN[currentYieldCtKey]?.plan, t(`${RD_BIZ}.unit.percent`))}</Td>
                                          <Td>{formatValue(yieldByPN[currentYieldCtKey]?.prev, t(`${RD_BIZ}.unit.percent`))}</Td>
                                          <Td>{formatValue(yieldByPN[currentYieldCtKey]?.current, t(`${RD_BIZ}.unit.percent`))}</Td>
                                        </Tr>
                                      )}
                                      {ctByPN[currentYieldCtKey] && (
                                        <Tr>
                                          <Td>{t(`${RB_PREFIX}.item.cycleTime`)}（{currentYieldCtKey}）</Td>
                                          <Td>{formatValue(ctByPN[currentYieldCtKey]?.plan, t(`${RD_BIZ}.unit.sec`))}</Td>
                                          <Td>{formatValue(ctByPN[currentYieldCtKey]?.prev, t(`${RD_BIZ}.unit.sec`))}</Td>
                                          <Td>{formatValue(ctByPN[currentYieldCtKey]?.current, t(`${RD_BIZ}.unit.sec`))}</Td>
                                        </Tr>
                                      )}
                                    </Tbody>
                                  </Table>
                                )}
                              </Box>
                            )}
                          </>
                        );
                      })()}

                      {/* ⑥ 生産数量実績（PartNo × 月）— 会計年度（4月始まり）12ヶ月ブロックに分割表示 */}
                      {tables.productionActuals && Object.keys(tables.productionActuals).length > 0 && (() => {
                        const actuals = tables.productionActuals as Record<string, number[]>;
                        const labels: string[] = displayReviewBusinessChartData.monthLabels || [];
                        const partNos = Object.keys(actuals);
                        if (partNos.length === 0) return null;
                        const monthLen = Math.max(...partNos.map(pn => (actuals[pn]?.length ?? 0)), labels.length);
                        if (monthLen === 0) return null;

                        // 各月インデックスから「会計年度」を判定して、会計年度ごとにインデックス配列をグループ化
                        // ラベルは "YYYY/M" 形式想定。月 4-12 → その年が会計年度、月 1-3 → 前年が会計年度
                        const fiscalYearByIdx: (number | null)[] = Array.from({ length: monthLen }).map((_, i) => {
                          const lbl = labels[i];
                          if (!lbl) return null;
                          const parts = lbl.split(/[\/\-]/);
                          const y = parseInt(parts[0], 10);
                          const m = parseInt(parts[1], 10);
                          if (!isFinite(y) || !isFinite(m)) return null;
                          return m >= 4 ? y : y - 1;
                        });
                        // 会計年度ごとのインデックスをまとめる（順序維持）
                        const blocks: { fiscalYear: number | null; indices: number[] }[] = [];
                        let currentBlock: { fiscalYear: number | null; indices: number[] } | null = null;
                        for (let i = 0; i < monthLen; i++) {
                          const fy = fiscalYearByIdx[i];
                          if (!currentBlock || currentBlock.fiscalYear !== fy) {
                            currentBlock = { fiscalYear: fy, indices: [i] };
                            blocks.push(currentBlock);
                          } else {
                            currentBlock.indices.push(i);
                          }
                        }
                        // 会計年度ブロックのタイトル文言
                        const blockTitle = (fy: number | null): string => {
                          if (fy === null) return t(`${RD_BIZ}.tables.productionActuals`);
                          return `${t(`${RD_BIZ}.tables.productionActuals`)}（${fy}${t(`${RD_BIZ}.tableColumns.fiscalYear`)}）`;
                        };
                        return (
                          <Box mb={6} p={4} bg="gray.50" borderRadius="md">
                            <Text fontSize="md" fontWeight="bold" mb={3}>{t(`${RD_BIZ}.tables.productionActuals`)}</Text>
                            {blocks.map((block, blockIdx) => (
                              <Box key={`pa-block-${blockIdx}`} mb={blockIdx < blocks.length - 1 ? 4 : 0}>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{blockTitle(block.fiscalYear)}</Text>
                                <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table">
                                  <Thead>
                                    <Tr>
                                      <Th>{t(`${RD_BIZ}.tableColumns.partNo`)}</Th>
                                      {block.indices.map((idx) => (
                                        <Th key={idx}>{labels[idx] ?? `${idx + 1}${t(`${RD_BIZ}.tableColumns.month`)}`}</Th>
                                      ))}
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {partNos.map((pn) => (
                                      <Tr key={pn}>
                                        <Td>{resolveDisplayPartNoByKey(pn, partNoData)}</Td>
                                        {block.indices.map((idx) => (
                                          <Td key={idx}>{formatValue(actuals[pn]?.[idx] ?? null)}</Td>
                                        ))}
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}

                      {/* ⑦ テキスト・コメント（compareMode 非依存・1度だけ表示） */}
                      {reviewBusinessText && (() => {
                        const textValues = reviewBusinessText.textValues || {};
                        const comments = reviewBusinessText.comments || {};
                        const textEntries = Object.entries(textValues).filter(([, v]) => v);
                        const commentEntries = Object.entries(comments).filter(([, v]) => v);
                        const hasAny = textEntries.length > 0 || commentEntries.length > 0;
                        if (!hasAny) return null;
                        const renderItem = (key: string, label: string, value: string) => (
                          <Box key={key} mb={3} pl={4}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{label}</Text>
                            <Box p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                              <Text fontSize="sm" whiteSpace="pre-wrap">{value}</Text>
                            </Box>
                          </Box>
                        );
                        return (
                          <Box p={4} bg="gray.50" borderRadius="md">
                            {commentEntries.length > 0 && (
                              <Box mb={4}>
                                <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.comments`)}</Text>
                                {commentEntries.map(([k, v]) => renderItem(k, resolveBizLabel('businessCommentLabel', k), String(v)))}
                              </Box>
                            )}
                            {textEntries.length > 0 && (
                              <Box>
                                <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.impression`)}</Text>
                                {textEntries.map(([k, v]) => renderItem(k, resolveBizLabel('businessTextLabel', k), String(v)))}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                })()}

                {/* ===== ReviewFuture（今後の見込み・テキスト8項目） ===== */}
                {/* 営業視点・事業視点のコメントブロックと同じ「ラベル + 白枠ボックス + 改行保持テキスト」のレイアウトで表示 */}
                {reviewFutureOutlook && (() => {
                  const RD_FUTURE = "features.InvestmentScreening.Review.ReviewFuture";
                  const FUTURE_ITEM_IDS = [
                    "further_sales",
                    "further_productivity",
                    "sales_corrective",
                    "manufacturing_corrective",
                    "consultation",
                    "strategy",
                    "achievements",
                    "continued_investment",
                  ] as const;
                  const entries = FUTURE_ITEM_IDS
                    .map((id) => ({ id, label: t(`${RD_FUTURE}.items.${id}.label`), value: reviewFutureOutlook[id] }))
                    .filter((e) => typeof e.value === "string" && e.value.trim().length > 0);
                  if (entries.length === 0) return null;
                  // 営業・事業視点コメントの renderItem と同じ見た目（ラベル太字＋白枠ボックス＋改行保持テキスト）
                  const renderItem = (key: string, label: string, value: string) => (
                    <Box key={key} mb={3} pl={4}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{label}</Text>
                      <Box p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
                        <Text fontSize="sm" whiteSpace="pre-wrap">{value}</Text>
                      </Box>
                    </Box>
                  );
                  return (
                    <Box ref={reviewFutureRef} mb={8}>
                      <Heading as="h3" size="sm" mb={4} pb={2} borderBottom="2px solid" borderColor="gray.300">
                        {t(`${RD_REVIEW_PREFIX}.futureSection`)}
                      </Heading>
                      <Box p={4} bg="gray.50" borderRadius="md">
                        {entries.map((e) => renderItem(e.id, e.label, e.value))}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          );
        })()}

        {/* PDF出力専用：振り返り3画面コンテンツ（重複なし・縦並べ） */}
        {/* captureJobs完了後（isPdfReviewRendering=true）にのみマウントし、captureJobsのDOMクローン対象から除外する */}
        {!!reviewDate && isPdfReviewRendering && (
          <Box
            ref={pdfReviewRef}
            position="absolute"
            left="-9999px"
            top="0"
            width="1466px"
            bg="white"
          >
            <PdfReviewContent
              displayReviewSalesChartData={displayReviewSalesChartData}
              displayReviewBusinessChartData={displayReviewBusinessChartData}
              reviewFutureOutlook={reviewFutureOutlook}
              reviewSalesText={reviewSalesText}
              reviewBusinessText={reviewBusinessText}
              reviewDate={reviewDate ?? null}
              isSimpleMode={isSimpleMode}
              t={t}
              selectedCurrencyName={selectedCurrency.name}
            />
          </Box>
        )}

        <Box mb={4} id={"section-11"} className={"pdf-content"}>
          <Box p={4} bg={"white"} boxShadow={"md"} borderRadius={"md"}>
            <Heading as={"h2"} size={"md"} mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.heading.estimateCheckList')}
            </Heading>
            <Box>
              {equipmentAmounts
                // 表示すべき設備のみを事前フィルタリング
                .filter((equipment) => {
                  const equipmentId = equipment.equipmentId ?? "";
                  return !!estimateCheckListData[equipmentId]?.estimateCheckList;
                })
                // フィルタリング後に処理
                .map((equipment, index) => {
                  const equipmentId = equipment.equipmentId ?? "";
                  const estimateCheckList: EstimateCheckListFormType =
                    estimateCheckListData[equipmentId]?.estimateCheckList ?? null;

                  const estimateCheckListRef = (el: HTMLDivElement | null) => {
                    estimateCheckListRefs.current[index] = el ?? null;
                  };

                  return (
                    <Box key={equipmentId || index} mt={5} ref={estimateCheckListRef}>
                      <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={2}>
                        <Box>
                          <Box fontWeight={"bold"}>{equipment.equipmentName}</Box>
                          {/* 通貨・為替レート表示 */}
                          <Text fontSize="sm">
                            {t('features.InvestmentScreening.ReportAndDraft.label.currency')} : {estimateCheckList?.currency?.currencyName ?? "JPY"} / {t('features.InvestmentScreening.ReportAndDraft.label.exchangeRate')} : {formatExchangeRate(
                              estimateCheckList?.currency?.currencyId
                                ? currencyData.find(c => c.currencyId === estimateCheckList?.currency?.currencyId)?.exchangeRate
                                : 1
                            )}
                          </Text>
                        </Box>
                      </Box>
                      <Box>
                        <HStack alignItems={"flex-start"}>
                          <Box flex={1}>
                            <TableContainer>
                              <Table
                                size={"sm"}
                                className={"mm-ceas-table"}
                                cellSpacing={0}
                                style={ecTableStyle}
                              >
                                <colgroup>
                                  <col width={"40%"} />
                                  <col width={"60%"} />
                                </colgroup>
                                <Tbody>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.equipmentType}</Td>
                                    <Td style={ecTdStyle}>{Object.values(equipmentType).find(r => r.value === estimateCheckList?.equipmentType?.equipmentType)?.name ?? estimateCheckList?.equipmentType?.equipmentType ?? ""}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.reverseAuction}</Td>
                                    <Td style={ecTdStyle}>{Object.values(reverseAuction).find(r => r.value === estimateCheckList?.reverseAuction?.reverseAuction)?.name ?? estimateCheckList?.reverseAuction?.reverseAuction ?? ""}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.inHouseReview}</Td>
                                    <Td style={ecTdStyle}>{Object.values(inHouseReview).find(r => r.value === estimateCheckList?.inHouseReview?.inHouseReview)?.name ?? estimateCheckList?.inHouseReview?.inHouseReview ?? ""}</Td>
                                  </Tr>
                                  {estimateCheckList?.inHouseReview?.inHouseReview === inHouseReview.isFalse.value && (
                                    <Tr>
                                      <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{EstimateCheckListCategory.inHouseReviewNoReason}</Td>
                                      <Td style={ecTdStyleTextArea}>{estimateCheckList?.inHouseReview?.inHouseReviewNoReason ?? ""}</Td>
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.competitiveEstimate}</Td>
                                    <Td style={ecTdStyle}>{Object.values(competitiveEstimate).find(r => r.value === estimateCheckList?.competitiveEstimate?.competitiveEstimate)?.name ?? estimateCheckList?.competitiveEstimate?.competitiveEstimate ?? ""}</Td>
                                  </Tr>
                                  {estimateCheckList?.competitiveEstimate?.competitiveEstimate === competitiveEstimate.isFalse.value && (
                                    <Tr>
                                      <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{EstimateCheckListCategory.competitiveEstimateNoReason}</Td>
                                      <Td style={ecTdStyleTextArea}>{estimateCheckList?.competitiveEstimate?.competitiveEstimateNoReason ?? ""}</Td>
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.pastPerformance}</Td>
                                    <Td style={ecTdStyle}>{Object.values(pastPerformance).find(r => r.value === estimateCheckList?.pastPerformance?.pastPerformance)?.name ?? estimateCheckList?.pastPerformance?.pastPerformance ?? ""}</Td>
                                  </Tr>
                                  {estimateCheckList?.pastPerformance?.pastPerformance === pastPerformance.isTrue.value && (
                                    <Tr>
                                      <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{EstimateCheckListCategory.purchaseDate}</Td>
                                      <Td style={ecTdStyle}>{dayjs(estimateCheckList?.pastPerformance?.purchaseDate).format(t('common.dateFormat.dayjsFullDate')) ?? ""}</Td>
                                    </Tr>
                                  )}
                                  {estimateCheckList?.pastPerformance?.pastPerformance === pastPerformance.isTrue.value && (
                                    <Tr>
                                      <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{EstimateCheckListCategory.purchasePrice}</Td>
                                      <Td style={ecTdStyle}>
                                        <HStack>
                                          <Text>{formatNumber(estimateCheckList?.pastPerformance?.purchasePrice ?? 0, "")}</Text>
                                          <Text>JPY</Text>
                                        </HStack>
                                      </Td>
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.supplier}</Td>
                                    <Td style={ecTdStyle}>{Object.values(supplier).find(r => r.value === estimateCheckList?.supplier?.supplier)?.name ?? estimateCheckList?.supplier?.supplier ?? ""}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{EstimateCheckListCategory.orderDate}</Td>
                                    <Td style={ecTdStyle}>{estimateCheckList?.supplier?.orderDate ? dayjs(estimateCheckList?.supplier?.orderDate).format(t('common.dateFormat.dayjsFullDate')) : ""}</Td>
                                  </Tr>
                                  {estimateCheckList?.supplier?.supplier === supplier.distributor.value && (
                                    <Tr>
                                      <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{EstimateCheckListCategory.distributorReason}</Td>
                                      <Td style={ecTdStyleTextArea}>{estimateCheckList?.supplier?.distributorReason ?? ""}</Td>
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.negotiation}</Td>
                                    <Td style={ecTdStyle}>
                                      <HStack>
                                        <Text>{formatNumber(estimateCheckList?.negotiation?.negotiationCount ?? 0, "")}</Text>
                                        <Text>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.countUnit')}</Text>
                                      </HStack>
                                    </Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.negotiationBackground}</Td>
                                    <Td style={ecTdStyleTextArea}>{estimateCheckList?.negotiation?.negotiationBackground ?? ""}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.paymentType}</Td>
                                    <Td style={ecTdStyle}>{Object.values(paymentType).find(r => r.value === estimateCheckList?.paymentType?.advancePayment)?.name ?? estimateCheckList?.paymentType?.advancePayment ?? ""}</Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.paymentBackground}</Td>
                                    <Td style={ecTdStyleTextArea}>{estimateCheckList?.paymentType?.paymentBackground ?? ""}</Td>
                                  </Tr>
                                  {estimateCheckList?.paymentType?.advancePayment === paymentType.isTrue.value && (
                                    <Tr>
                                      <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.procurementApprovalRequired')}</Td>
                                      <Td style={ecTdStyle}>{Object.values(procurementApproval).find(r => r.value === estimateCheckList?.paymentType?.procurementApproval)?.name ?? estimateCheckList?.paymentType?.procurementApproval ?? ""}</Td>
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.ourProductUsage}</Td>
                                    <Td style={ecTdStyle}>{Object.values(ourProductUsageStatus).find(r => r.value === estimateCheckList?.ourProductUsage?.usageStatus)?.name ?? estimateCheckList?.ourProductUsage?.usageStatus ?? ""}</Td>
                                  </Tr>
                                  {estimateCheckList?.ourProductUsage?.usageStatus === ourProductUsageStatus.used.value && (
                                    <Tr>
                                      <Td style={{ ...ecTdStyle, textIndent: "10px" }}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.productName')}</Td>
                                      <Td style={ecTdStyleTextArea}>{estimateCheckList?.ourProductUsage?.productName ?? ""}</Td>
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.purchaseWithoutOutsourcing}</Td>
                                    <Td style={ecTdStyleTextArea}>{estimateCheckList?.purchaseWithoutOutsourcing?.reason ?? ""}</Td>
                                  </Tr>
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                          <Box flex={1.8}>
                            <TableContainer>
                              <Table
                                size="sm"
                                className="mm-ceas-table"
                                cellSpacing={0}
                                style={ecTableStyle}
                              >
                                <colgroup>
                                  <col width="20%" />
                                  <col width="80%" />
                                </colgroup>
                                <Tbody>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory?.equipmentComparison}</Td>
                                    <Td style={ecTdStyle}>
                                      <Box>
                                        {estimateCheckList?.equipmentComparison?.equipmentComparison?.length > 0 && (
                                          <TableContainer>
                                            <Table
                                              size={"sm"}
                                              className={"mm-ceas-table"}
                                              cellSpacing={0}
                                              style={ecTableStyle}
                                            >
                                              <colgroup>
                                                <col width="20%" />
                                                <col width="20%" />
                                                <col width="20%" />
                                                <col width="20%" />
                                                <col width="20%" />
                                              </colgroup>
                                              <Thead>
                                                <Tr>
                                                  <Th style={ecThStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.itemName')}</Th>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Th key={idx} style={ecThStyle}>{item.companyName === EC_IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName}</Th>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison.length >= 2 &&
                                                    <Th style={ecThStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.superiority')}</Th>
                                                  }
                                                </Tr>
                                              </Thead>
                                              <Tbody>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.basePrice')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <HStack>
                                                        <Text>{formatNumber(Number(item.basePrice) ?? 0, "")}</Text>
                                                        <Text>{estimateCheckList?.currency?.currencyName ?? "JPY"}</Text>
                                                      </HStack>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[0], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.quantity')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <HStack>
                                                        <Text>{formatNumber(Number(item.unit) ?? 0, "")}</Text>
                                                        <Text>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.quantity').replace('台数', '台')}</Text>
                                                      </HStack>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[1], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.optionPrice')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <HStack>
                                                        <Text>{formatNumber(Number(item.optionPrice) ?? 0, "")}</Text>
                                                        <Text>{estimateCheckList?.currency?.currencyName ?? "JPY"}</Text>
                                                      </HStack>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[2], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.features')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <Text>{item.features}</Text>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[3], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.totalAmount')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <HStack>
                                                        <Text>{formatNumber(Number(item.totalPrice) ?? 0, "")}</Text>
                                                        <Text>{estimateCheckList?.currency?.currencyName ?? "JPY"}</Text>
                                                      </HStack>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[4], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                {/* 日本円換算額（JPY以外の場合のみ表示） */}
                                                {estimateCheckList?.currency?.currencyName &&
                                                  estimateCheckList?.currency?.currencyName !== "JPY" && (
                                                    <Tr>
                                                      <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.jpyEquivalent')}</Td>
                                                      {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => {
                                                        // 最新の為替レートを取得
                                                        const latestRate = currencyData.find(
                                                          c => c.currencyId === estimateCheckList?.currency?.currencyId
                                                        )?.exchangeRate ?? 1;
                                                        const jpyAmount = Number(item.totalPrice) * latestRate;
                                                        return (
                                                          <Td key={idx} style={ecTdStyle}>
                                                            <HStack>
                                                              <Text>{formatNumber(jpyAmount, "")}</Text>
                                                              <Text>JPY</Text>
                                                            </HStack>
                                                          </Td>
                                                        );
                                                      })}
                                                      {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                        <Td style={ecTdStyle}></Td>
                                                      }
                                                    </Tr>
                                                  )}
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.deliveryDays')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <HStack>
                                                        <Text>{formatNumber(Number(item.deadline) ?? 0, "")}</Text>
                                                        <Text>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.deliveryDays').replace('納期', '日')}</Text>
                                                      </HStack>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[5], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.precision')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <Text>{Object.values(equipmentComparisonItems.quality).find(q => q.value === item.quality)?.name ?? item.quality}</Text>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[6], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.serviceSystem')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <Text>{Object.values(equipmentComparisonItems.system).find(s => s.value === item.system)?.name ?? item.system}</Text>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[7], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.repeat')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <Text>{Object.values(equipmentComparisonItems.repeat).find(r => r.value === item.repeat)?.name ?? item.repeat}</Text>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[8], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                                <Tr>
                                                  <Td style={ecTdStyle}>{t('features.InvestmentScreening.ReportAndDraft.estimateChecklist.issues')}</Td>
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.map((item, idx) => (
                                                    <Td key={idx} style={ecTdStyle}>
                                                      <Text>{item.issue}</Text>
                                                    </Td>
                                                  ))}
                                                  {estimateCheckList?.equipmentComparison?.equipmentComparison?.length >= 2 &&
                                                    <Td style={ecTdStyle}>
                                                      <Text>{displaySuperiority(estimateCheckList?.equipmentComparison?.superiority?.[9], estimateCheckList?.equipmentComparison?.equipmentComparison ?? [])}</Text>
                                                    </Td>
                                                  }
                                                </Tr>
                                              </Tbody>
                                            </Table>
                                          </TableContainer>
                                        )}
                                      </Box>
                                    </Td>
                                  </Tr>
                                  <Tr>
                                    <Td style={ecTdStyle}>{EstimateCheckListCategory.selectReason}</Td>
                                    <Td style={ecTdStyleTextArea}>{estimateCheckList?.selectReason?.selectReason ?? ""}</Td>
                                  </Tr>
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </HStack>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Box>

        <Box mb={4} id="section-12" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.heading.tenRulesCheckList')}
            </Heading>
            <Box>
              {equipmentAmounts
                // 表示すべき設備のみを事前フィルタリング（旧形式データは除外）
                .filter((equipment) => {
                  const equipmentId = equipment.equipmentId ?? "";
                  return !!checkListDataList[equipmentId]?.checkList
                    && !oldFormatEquipmentIds.includes(equipmentId);
                })
                // フィルタリング後に処理
                .map((equipment, index) => {
                  const equipmentId = equipment.equipmentId ?? "";

                  const checkList: CheckListDataType = checkListDataList[equipmentId]?.checkList ?? null;
                  const checkListRef = (el: HTMLDivElement | null) => {
                    checkListRefs.current[index] = el ?? null;
                  };

                  return (
                    <Box key={equipmentId || index} mt={5} ref={checkListRef}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box fontWeight="bold">{equipment.equipmentName}</Box>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.title')}</Box>
                      </Box>
                      <Box ref={checkListRef}>
                        {/* １０か条チェックリストの確認（読み取り専用ステータス） */}
                        <Box
                          mb={3} p={3}
                          border="1px solid" borderColor="gray.200" borderRadius="md"
                          display="flex" alignItems="center" justifyContent="space-between" gap={4}
                        >
                          <Box minW={0}>
                            <Box fontWeight="bold" fontSize="smdm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.automationChecklist.tenPrinciples.title')}
                            </Box>
                            <Link
                              href="https://portal-minebea.app.lumapps.com/home/ls/content/804067131309523/804067131309523"
                              isExternal color="blue.600" fontWeight="semibold" fontSize="md" wordBreak="break-all"
                            >
                              https://portal-minebea.app.lumapps.com/home/ls/content/804067131309523/804067131309523
                            </Link>
                          </Box>
                          {(() => {
                            const tenPrinciplesChecked = !!(checkList as any)?.tenPrinciplesChecked;
                            return (
                              <Box
                                flexShrink={0} px={3} py={1} borderRadius="full"
                                fontSize="md" fontWeight="bold" border="1px solid"
                                borderColor={tenPrinciplesChecked ? "green.500" : "gray.300"}
                                bg={tenPrinciplesChecked ? "green.50" : "gray.50"}
                                color={tenPrinciplesChecked ? "green.700" : "gray.500"}
                              >
                                {tenPrinciplesChecked
                                  ? `✓ ${t('features.InvestmentScreening.ReportAndDraft.automationChecklist.tenPrinciples.checked')}`
                                  : t('features.InvestmentScreening.ReportAndDraft.automationChecklist.tenPrinciples.unchecked')}
                              </Box>
                            );
                          })()}
                        </Box>
                        {/* 遊休設備の確認（読み取り専用ステータス） */}
                        <Box
                          mb={3} p={3}
                          border="1px solid" borderColor="gray.200" borderRadius="md"
                          display="flex" alignItems="center" justifyContent="space-between" gap={4}
                        >
                          <Box minW={0}>
                            <Box fontWeight="bold" fontSize="smdm" mb={1}>
                              {t('features.InvestmentScreening.ReportAndDraft.automationChecklist.idleEquipment.title')}
                            </Box>
                            <Link
                              href="https://portal-minebea.app.lumapps.com/site-list/ls/content/1467325079239043/1467325079239043"
                              isExternal color="blue.600" fontWeight="semibold" fontSize="md" wordBreak="break-all"
                            >
                              https://portal-minebea.app.lumapps.com/site-list/ls/content/1467325079239043/1467325079239043
                            </Link>
                          </Box>
                          {(() => {
                            const idleChecked = !!(checkList as any)?.idleEquipmentChecked;
                            return (
                              <Box
                                flexShrink={0} px={3} py={1} borderRadius="full"
                                fontSize="md" fontWeight="bold" border="1px solid"
                                borderColor={idleChecked ? "green.500" : "gray.300"}
                                bg={idleChecked ? "green.50" : "gray.50"}
                                color={idleChecked ? "green.700" : "gray.500"}
                              >
                                {idleChecked
                                  ? `✓ ${t('features.InvestmentScreening.ReportAndDraft.automationChecklist.idleEquipment.checked')}`
                                  : t('features.InvestmentScreening.ReportAndDraft.automationChecklist.idleEquipment.unchecked')}
                              </Box>
                            );
                          })()}
                        </Box>
                        {/*
                          表形式の１０か条チェックリスト（旧データ互換表示）は、
                          この設備が新形式（外部リンク＋確認方式）のため非表示。
                          旧形式データの表形式表示は、このブロックの下にある
                          「旧形式データ（フォールバック表示）」の描画ブロックを参照。
                        */}
                      </Box>
                    </Box>
                  );
                })}
              {equipmentAmounts
                // 旧形式データ（フォールバック表示）。この対応より前に保存されたチェックリストは
                // 従来どおり表形式で表示し続ける。新規に保存されるデータは上のブロック（外部リンク＋確認方式）で表示される。
                .filter((equipment) => {
                  const equipmentId = equipment.equipmentId ?? "";
                  return !!checkListDataList[equipmentId]?.checkList
                    && oldFormatEquipmentIds.includes(equipmentId);
                })
                .map((equipment, index) => {
                  const equipmentId = equipment.equipmentId ?? "";
                  const checkList: CheckListDataType = checkListDataList[equipmentId]?.checkList ?? null;

                  return (
                    <Box key={`old-format-${equipmentId || index}`} mt={5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box fontWeight="bold">{equipment.equipmentName}</Box>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box fontWeight="bold">{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.title')}</Box>
                      </Box>
                      <Box>
                        <TableContainer>
                          <Table size="sm" className="mm-ceas-table" style={{ tableLayout: "auto", borderCollapse: "separate" }} cellSpacing={0} >
                            <colgroup>
                              <col width="50px" />
                              <col width="150px" />
                              <col width="220px" />
                              <col width="30px" />
                              <col width="250px" />
                              <col width="120px" />
                              <col />
                            </colgroup>
                            <Thead position="sticky" top="0" zIndex="10" bg="white" >
                              <Tr height={`${CHECKLIST_ROW_HEIGHT}px`}>
                                <Th>No.</Th>
                                <Th>{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.condition')}</Th>
                                <Th>{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.contrary')}</Th>
                                <Th colSpan={2}>{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.checkItem')}</Th>
                                <Th>{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.applicable')}</Th>
                                <Th>{t('features.InvestmentScreening.ReportAndDraft.automationChecklist.memo')}</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {checkList && Object.keys(checkList)
                                .filter((key) => key !== 'idleEquipmentChecked')
                                .sort((a, b) => Number(a) - Number(b))
                                .flatMap((key: string) => {
                                  const group = (checkList as any)[key];
                                  const checkItems = group?.checkItems ?? {};
                                  const sortedSubKeys = Object.keys(checkItems).sort((a, b) => (checkItems[a]?.order ?? 0) - (checkItems[b]?.order ?? 0));
                                  const rowCount = sortedSubKeys.length;
                                  if (rowCount === 0) {
                                    return [(<Tr key={key} height={`${CHECKLIST_ROW_HEIGHT}px`}>
                                      <Td rowSpan={1} style={{ padding: "0px", textAlign: "center" }}>{key}</Td>
                                      <Td rowSpan={1} whiteSpace="break-spaces">{getChecklistContentName(group)}</Td>
                                      <Td style={cTdStyleTextArea} rowSpan={1} colSpan={5} height={`${CHECKLIST_ROW_HEIGHT}px`}>
                                        <Text>{group?.memo ?? ""}</Text>
                                      </Td>
                                    </Tr>)];
                                  } else {
                                    return (
                                      sortedSubKeys.map((subKey: string, subIdx) => {
                                        const itemSnapshot = checkItems[subKey];
                                        const displaySubKey = itemSnapshot?.order ? String.fromCharCode(96 + itemSnapshot.order) : "";
                                        if (subIdx === 0) {
                                          return (<Tr key={`${key}-${subKey}`} height={`${CHECKLIST_ROW_HEIGHT}px`}>
                                            <Td rowSpan={rowCount} style={{ padding: "0px", textAlign: "center" }}>{key}</Td>
                                            <Td rowSpan={rowCount} whiteSpace="break-spaces">{getChecklistContentName(group)}</Td>
                                            <Td rowSpan={rowCount} whiteSpace="break-spaces">
                                              {getChecklistConflictingConsiderations(group).map((text: string, i: number) => {
                                                return (<Text key={i}>{text}</Text>);
                                              })}
                                            </Td>
                                            <Td style={{ padding: "0px", textAlign: "center" }}>{displaySubKey}</Td>
                                            <Td whiteSpace="break-spaces">{getChecklistContentName(itemSnapshot)}</Td>
                                            <Td>
                                              <Text>{getApplicableNameByCode(itemSnapshot?.value) ?? ""}</Text>
                                            </Td>
                                            <Td style={cTdStyleTextArea} rowSpan={rowCount} height={`${CHECKLIST_ROW_HEIGHT * rowCount}px`}>
                                              <Text>{group?.memo ?? ""}</Text>
                                            </Td>
                                          </Tr>)
                                        } else {
                                          return (
                                            <Tr key={`${key}-${subKey}`} height={`${CHECKLIST_ROW_HEIGHT}px`}>
                                              <Td style={{ padding: "0px", textAlign: "center" }}>{displaySubKey}</Td>
                                              <Td whiteSpace="break-spaces">{getChecklistContentName(itemSnapshot)}</Td>
                                              <Td style={{ borderRight: "0px" }}>
                                                <Text>{getApplicableNameByCode(itemSnapshot?.value) ?? ""}</Text>
                                              </Td>
                                            </Tr>
                                          )
                                        }
                                      })
                                    )
                                  }
                                })}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Box>

        <Box mb={4} id="section-13" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.attachments')}
            </Heading>
            <Box mt={4} className="pdf-content">
              <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.bomSnapshot')} folderName="BomSnapshot" />
            </Box>
            <Box mt={4} className="pdf-content">
              <UploadedFiles title={t('features.InvestmentScreening.ReportAndDraft.uploadedFiles.otherFiles')} folderName="Others" />
            </Box>
            {(() => {
              const fileList = uploadedFilesByFolder ?? [];
              const hasBomSnapshotFiles = (fileList.find(f => f.folderName === "BomSnapshot")?.files?.length ?? 0) > 0;
              const hasOthersFiles = (fileList.find(f => f.folderName === "Others")?.files?.length ?? 0) > 0;
              // BOM申請、その他のどちらにもアップロードファイルのないときtrue
              const hasNoFiles = !hasBomSnapshotFiles && !hasOthersFiles;
              return hasNoFiles && (<Text color="gray.500" fontSize="sm">{t('features.InvestmentScreening.ReportAndDraft.message.noAttachedDocuments')}</Text>);
            })()}
          </Box>
        </Box>

        <Box mb={4} id="section-14" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.confirmedPersons')}
            </Heading>
            <Box>
              {confirmedPersonsList.length > 0 ? (
                <Box borderWidth="1px" borderRadius="md" overflow="hidden" borderColor="gray.200">
                  <Table id={CONFIRMER_LIST_BOX_PREFIX} colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="10%" textAlign="center">No.</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="25%">{t('features.InvestmentScreening.ReportAndDraft.table.confirmDivision')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="40%">{t('features.InvestmentScreening.ReportAndDraft.table.name')}</Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="25%" textAlign="center">{t('features.InvestmentScreening.ReportAndDraft.table.latestConfirmDateTime')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(() => {
                        // 確認区分ごとにグループ化
                        const grouped = confirmedPersonsList.reduce((acc, person) => {
                          if (!acc[person.divisionId]) {
                            acc[person.divisionId] = [];
                          }
                          acc[person.divisionId].push(person);
                          return acc;
                        }, {} as Record<string, ConfirmedPerson[]>);

                        // ソート順
                        const sortOrder = ["company", "companyHead", "sales", "procurement"];
                        const sortedEntries = Object.entries(grouped).sort(
                          ([a], [b]) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
                        );

                        let rowNo = 0;
                        return sortedEntries.flatMap(([divisionId, persons]) => {
                          rowNo++;
                          const currentRowNo = rowNo;
                          return persons.map((person, index) => (
                            <Tr key={`${divisionId}-${index}`}>
                              {index === 0 && (
                                <Td
                                  rowSpan={persons.length}
                                  borderRight="1px solid"
                                  borderColor="gray.200"
                                  textAlign="center"
                                  verticalAlign="middle"
                                >
                                  {currentRowNo}
                                </Td>
                              )}
                              {index === 0 && (
                                <Td
                                  rowSpan={persons.length}
                                  borderRight="1px solid"
                                  borderColor="gray.200"
                                  textAlign="left"
                                  verticalAlign="middle"
                                >
                                  {person.confirmKbn}
                                </Td>
                              )}
                              <Td borderRight="1px solid" borderColor="gray.200">
                                {person.employeeName}
                              </Td>
                              <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                                {person.latestConfirmTime
                                  ? formatLocalDateTime(person.latestConfirmTime)
                                  : <Text color="gray.400" fontSize="sm">-</Text>
                                }
                              </Td>
                            </Tr>
                          ));
                        });
                      })()}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500">{t('features.InvestmentScreening.ReportAndDraft.message.noConfirmedPersons')}</Text>
              )}
            </Box>
          </Box>
        </Box>

        <Box mb={4} id="section-15" className="pdf-content">
          <Box p={4} bg="white" boxShadow="md" borderRadius="md">
            <Heading as="h2" size="md" mb={4}>
              {t('features.InvestmentScreening.ReportAndDraft.button.approvalHistory')}
            </Heading>
            <Box>
              <ApprovalListComponent approverList={approverList} />
            </Box>
          </Box>
        </Box>

        {/* 確認ボタン */}
        {
          flowKb === FlowKb.Confirmation && (
            <Flex justify="flex-end" mt="20px">
              <Box>
                <Button
                  ml="10px"
                  colorScheme="blue"
                  name="companyDivision"
                  onClick={handleModalOpen}
                  isDisabled={!pagePermissions?.eabledCompanyDivision}
                  ref={refs["companyDivision"]}
                >
                  {t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.company')}
                </Button>
                <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
              </Box>
              <Box>
                <Button
                  ml="10px"
                  colorScheme="blue"
                  name="companyHeadDivision"
                  onClick={handleModalOpen}
                  isDisabled={!pagePermissions?.eabledCompanyHeadDivision}
                  ref={refs["companyHeadDivision"]}
                >
                  {t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.companyHead')}
                </Button>
                <ConfirmorList confirmorInfo={names["companyHeadDivision"]} width={buttonWidths["companyHeadDivision"]} />
              </Box>
              <Box>
                <Button
                  ml="10px"
                  colorScheme="blue"
                  name="salesDivision"
                  onClick={handleModalOpen}
                  isDisabled={!pagePermissions?.eabledSalesDivision}
                  ref={refs["salesDivision"]}
                >
                  {t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.sales')}
                </Button>
                <ConfirmorList confirmorInfo={names["salesDivision"]} width={buttonWidths["salesDivision"]} />
                {hasSalesRestriction(parseViewRestriction(projectData?.screenViewRestriction)) && (
                  <Text ml="10px" mt="4px" fontSize="sm" color="gray.600" maxW={buttonWidths["salesDivision"]}>
                    {t('features.InvestmentScreening.ReportAndDraft.message.salesConfirmExempt')}
                  </Text>
                )}
              </Box>
              <Box>
                <Button
                  ml="10px"
                  colorScheme="blue"
                  name="procurementDivision"
                  onClick={handleModalOpen}
                  isDisabled={!pagePermissions?.eabledProcurementDivision}
                  ref={refs["procurementDivision"]}
                >
                  {t('features.InvestmentScreening.ReportAndDraft.confirmorDivision.procurement')}
                </Button>
                <ConfirmorList confirmorInfo={names["procurementDivision"]} width={buttonWidths["procurementDivision"]} />
              </Box>
            </Flex>
          )
        }

        <Flex justify="space-between" mt={4}>
          <Button colorScheme="blue" isDisabled={!pagePermissions?.eabledPdfDownload} onClick={handlePdfDownload}>
            {t('features.InvestmentScreening.ReportAndDraft.button.pdfDownload')}
          </Button>
          {flowKb === FlowKb.Approval && flowId !== WorkflowId.Remand ? (
            <Button
              colorScheme="blue"
              onClick={handleDetailModalOpen}
              isDisabled={!pagePermissions?.eabledApproval}
            >
              {t('features.InvestmentScreening.ReportAndDraft.button.approvalConfirm')}
            </Button>
          ) : (
            <Button
              colorScheme="blue"
              isDisabled={!pagePermissions?.eabledApprovalRequest}
              onClick={handleDraftingModalOpen}
            >
              {t('features.InvestmentScreening.ReportAndDraft.button.draftApplication')}
            </Button>
          )}
        </Flex>
      </Container >

      {/* 承認モーダルコンポーネント */}
      < InvestmentScreeningDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleDetailModalClose}
        projectId={projectId}
        flowId={flowId}
        approverList={approverList}
        summary={{
          projectName: projectData?.projectName || "",
          applicationNumber: projectData?.applicationNumber || "",
          applicantName: executiveManager[0]?.employeeName || "",
          // 申請書セクション（L6290付近）と同じ表示値を使用
          applicationAmount: getRequestedAmount(
            convEquipmentAmounts.filter((e): e is { amount: string } => e.amount !== undefined),
            selectedCurrency.name
          ),
          applicationDate: projectData?.applicationMonth ?? '',
        }}
      />

      {/* 確認モーダル */}
      < Modal isOpen={isOpenConfirmModal} onClose={onCloseConfirmModal} size="md" >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`${TITLE}${t('features.InvestmentScreening.ReportAndDraft.modal.confirmSuffix')}`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t('features.InvestmentScreening.ReportAndDraft.modal.confirmText')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={(e) => handleConfirm(e)}>
              {t('common.button.confirm')}
            </Button>
            <Button variant="ghost" onClick={onCloseConfirmModal}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

      {/* 起案申請前確認モーダル（クロスチェック・資材担当役員確認） */}
      {isOpenPreCheckModal && (
        <PreCheckModal
          isOpen={isOpenPreCheckModal}
          onClose={onClosePreCheckModal}
          onConfirmed={handlePreCheckConfirmed}
          crossCheckRequired={projectData?.crossCheckRequired ?? false}
          materialOfficerRequired={projectData?.materialOfficerRequired ?? false}
          rateType={rateType}
          ratePeriod={ratePeriod}
        />
      )}

      {/* 承認依頼モーダル */}
      < Modal isOpen={isOpenApprovalReqModal} onClose={onCloseApprovalReqModal} size="md" >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`${TITLE}${t('features.InvestmentScreening.ReportAndDraft.modal.confirmSuffix')}`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t('features.InvestmentScreening.ReportAndDraft.modal.approvalReqText')}</Text>
            {/* 過去プロジェクトのPart No不整合の警告 */}
            {missingPastPartNos.length > 0 && (
              <Alert status="warning" mt={3} borderRadius="md" alignItems="flex-start" bg="orange.50" borderColor="orange.200" borderWidth="1px">
                <AlertIcon />
                <Text whiteSpace="pre-wrap" fontSize="sm">
                  {t('features.InvestmentScreening.ReportAndDraft.modal.pastPartNoMismatchWarning', { partNos: missingPastPartNos.join(", ") })}
                </Text>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={(e) => handleApprovalReq(e)}>
              {t('features.InvestmentScreening.ReportAndDraft.modal.approvalReqButton')}
            </Button>
            <Button variant="ghost" onClick={onCloseApprovalReqModal}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

    </Box >
  );
};

/**
 * 起案申請前確認モーダル（子コンポーネント）
 * チェックボックスのstate変更で親コンポーネント全体が再レンダリングされるのを防ぐため分離
 */
const PreCheckModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  crossCheckRequired: boolean;
  materialOfficerRequired: boolean;
  rateType: string;
  ratePeriod: string;
}> = ({ isOpen, onClose, onConfirmed, crossCheckRequired, materialOfficerRequired, rateType, ratePeriod }) => {
  const [monitoringChecked, setMonitoringChecked] = useState(false);
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('features.InvestmentScreening.ReportAndDraft.modal.preCheckHeader')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>{t('features.InvestmentScreening.ReportAndDraft.modal.preCheckDescription')}</Text>
          <VStack spacing={3} align="stretch">
            <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
              <Flex align="center" mb={2}>
                <Text fontSize="sm" minW="160px">{t('features.InvestmentScreening.ReportAndDraft.modal.preCheckCrossCheck')}</Text>
                <Text color={crossCheckRequired ? "blue.600" : "gray.500"} fontWeight="bold" fontSize="sm">
                  {crossCheckRequired ? t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRequired') : t('features.InvestmentScreening.ReportAndDraft.modal.preCheckNotRequired')}
                </Text>
              </Flex>
              <Flex align="center">
                <Text fontSize="sm" minW="160px">{t('features.InvestmentScreening.ReportAndDraft.modal.preCheckMaterialOfficer')}</Text>
                <Text color={materialOfficerRequired ? "blue.600" : "gray.500"} fontWeight="bold" fontSize="sm">
                  {materialOfficerRequired ? t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRequired') : t('features.InvestmentScreening.ReportAndDraft.modal.preCheckNotRequired')}
                </Text>
              </Flex>
            </Box>
            <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
              <Flex align="center">
                <Text fontSize="sm" minW="160px">{t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRate')}</Text>
                <Text fontWeight="bold" fontSize="sm">
                  {rateType && ratePeriod
                    ? `${rateType === 'actual' ? t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRateActual') : t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRatePlan')}(${rateType === 'actual' ? t('common.dateFormat.yearMonth', { year: ratePeriod.substring(0, 4), month: ratePeriod.substring(5, 7) }) : t('common.dateFormat.fiscalYearStr', { year: ratePeriod })})`
                    : t('features.InvestmentScreening.ReportAndDraft.modal.preCheckRateNotSet')}
                </Text>
              </Flex>
            </Box>
          </VStack>
          <Text mt={3} fontSize="xs" color="gray.500" whiteSpace="pre-line">
            {t('features.InvestmentScreening.ReportAndDraft.modal.preCheckChangeNote')}
          </Text>
          <Box mt={4} borderWidth="1px" borderRadius="md" p={4} bg="orange.50" borderColor="orange.200">
            <Checkbox
              isChecked={monitoringChecked}
              onChange={(e) => setMonitoringChecked(e.target.checked)}
              colorScheme="orange"
            >
              <Text fontSize="sm" fontWeight="bold" whiteSpace="pre-line">
                {t('features.InvestmentScreening.ReportAndDraft.modal.preCheckMonitoringQuestion')}
              </Text>
            </Checkbox>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onConfirmed} isDisabled={Boolean(!((monitoringChecked && (rateType && ratePeriod)) && true))}>
            {t('features.InvestmentScreening.ReportAndDraft.modal.preCheckConfirmed')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReportAndDraft;
